// ─── Imports ───
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getHabboImageUrl } from "./HabboImage";
import { toast } from "react-toastify";
import { getAllItems } from '../services/api';
import { searchFurniItems, getImagerBlob, upsertImagerBlob, getSharedImagerBlob, upsertSharedImagerBlob, getMyImagerScenes, saveImagerScene, updateImagerScene, deleteImagerScene } from '../services/api';
import ContentBox from './ContentBox';
import ColorPicker from './ColorPicker';
import IMAGER_UPDATES from '../data/ImagerUpdates.json';
import FONTS from '../data/fonts.json';

const mod8 = (n) => ((Number(n) % 8) + 8) % 8;

// ─── Debug Flags (set to true locally to test, never commit as true) ───
const DEBUG_FORCE_NEW_USER = false;

// ─── Constants / Option Data ───
const ACTIONS = [
    { value: "std", label: "Stand" },
    { value: "wav", label: "Wave" },
    { value: "wlk", label: "Walk" },
    { value: "sit", label: "Sit" },
    { value: "lay", label: "Lay" },
];

const GESTURES = [
    { value: "std", label: "Normal" },
    { value: "sml", label: "Smile" },
    { value: "agr", label: "Angry" },
    { value: "sad", label: "Sad" },
    { value: "srp", label: "Surprised" },
    { value: "eyb", label: "Eye Blink" },
    { value: "spk", label: "Speak" },
];

const CARRY_ITEMS = [
    { value: "", label: "None" },
    { value: "1", label: "Drink" },
    { value: "2", label: "Carrot" },
    { value: "3", label: "Ice Cream" },
    { value: "5", label: "Habbo Cola" },
    { value: "6", label: "Coffee" },
    { value: "9", label: "Love Potion" },
    { value: "11", label: "Fishing Rod" },
];

const IMAGE_PRESETS = [
    { name: "Default", width: 650, height: 500 },
    { name: "Article Header", width: 700, height: 359 },
    { name: "Discord Banner", width: 960, height: 540 },
    { name: "Vertical Banner", width: 160, height: 600 },
];

const TEXT_FONT_OPTIONS = [
    { label: "Jersey 10", value: "'Jersey 10', sans-serif" },
    { label: "Jersey 10 Regular", value: "'Jersey 10 Regular', sans-serif" },

    { label: "Volter", value: "VolterGoldfish, sans-serif" },
    { label: "Volter Bold", value: "VolterBoldGoldfish, VolterGoldfish, sans-serif" },
    { label: "Instrument Serif", value: "'Instrument Serif', serif" },

    { label: "Arial", value: "Arial, Helvetica, sans-serif" },
    { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
    { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
    { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
    { label: "Trebuchet MS", value: "'Trebuchet MS', Helvetica, sans-serif" },

    { label: "Georgia", value: "Georgia, serif" },
    { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
    { label: "Palatino", value: "'Palatino Linotype', Palatino, serif" },
    { label: "Garamond", value: "Garamond, serif" },

    { label: "Courier New", value: "'Courier New', Courier, monospace" },
    { label: "Lucida Console", value: "'Lucida Console', Monaco, monospace" },

    { label: "Impact", value: "Impact, Haettenschweiler, sans-serif" },
    { label: "Comic Sans MS", value: "'Comic Sans MS', cursive, sans-serif" },
];

// ─── Habbo Font Tool ───
const FONT_GLYPH_BASE = '/img/fontgen';

function charToFontGlyph(char, filenamePattern, folder) {
    if (char === ' ') return { src: `${FONT_GLYPH_BASE}/${folder}/+.gif`, draw: false };
    const lower = char.toLowerCase();
    if (lower >= 'a' && lower <= 'z') {
        return { src: `${FONT_GLYPH_BASE}/${folder}/${filenamePattern === 'letter1' ? lower + '1' : lower}.gif`, draw: true };
    }
    return null;
}

async function generateHabboFontDataUrl(text, folder, filenamePattern, offset = 0) {
    const glyphs = text.split('').map(c => charToFontGlyph(c, filenamePattern, folder));
    const imgs = await Promise.all(glyphs.map(g => g?.src
        ? new Promise(res => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = g.src; })
        : Promise.resolve(null)
    ));

    if (!imgs.some(Boolean)) return null;

    let totalW = 0;
    let currentX = 0;
    imgs.forEach((img, i) => {
        if (img) {
            totalW = Math.max(totalW, currentX + img.naturalWidth);
            if (i < imgs.length - 1) {
                currentX += img.naturalWidth + offset;
            }
        }
    });

    const maxH = imgs.reduce((m, img) => Math.max(m, img ? img.naturalHeight : 0), 0);
    if (!totalW || !maxH) return null;

    const canvas = document.createElement('canvas');
    canvas.width = totalW; canvas.height = maxH;
    const ctx = canvas.getContext('2d');

    let x = 0;
    imgs.forEach((img, i) => {
        if (img && glyphs[i]) {
            if (glyphs[i].draw) ctx.drawImage(img, x, maxH - img.naturalHeight);
            x += img.naturalWidth + offset;
        }
    });
    return canvas.toDataURL('image/png');
}

// ─── Furni Colourer Tool ───
const FURNI_COLOURER_GROUPS = {
    'Classic Rares': [
        'rare_dragonlamp', 'rare_fountain', 'rare_icecream', 'rare_fan',
        { name: 'rare_elephant_statue', direction: 6 },
        'scifirocket', 'scifiport', 'scifidoor', 'pillow', 'sleepingbag',
        'rare_beehive_bulb', 'marquee', 'pillar', 'wooden_screen',
        'rare_parasol', 'rubberchair', 'barrier', 'traffic_light',
    ],
    'Plasto': [
        'table_plasto_bigsquare', 'table_plasto_4leg', 'table_plasto_round',
        'table_plasto_square', 'chair_plasto', 'chair_plasty',
    ],
    'Iced': [
        'couch_norja2', 'shelves_norja2', 'chair_norja2',
        'divider_nor12', 'divider_nor22', 'soft_sofa_norja2', 'soft_sofachair_norja2',
    ],
    'Area': [
        { name: 'sofachair_silo2', direction: 1 }, { name: 'sofa_silo2', direction: 1 },
        { name: 'table_silo_small2' }, { name: 'table_silo_med2' },
        { name: 'divider_silo12', direction: 0 },
    ],
    'Gothic': [
        { name: 'gothic_chair', direction: 6 }, { name: 'gothic_sofa', direction: 6 },
        { name: 'gothic_stool', direction: 6 },
    ],
    'Trophies': ['prizetrophy', 'hween_trophy'],
};
const FURNI_COLOURER_API = 'https://furni.franklyorigins.net';
const normaliseFurniEntry = (e) =>
    typeof e === 'string'
        ? { name: e, direction: 2 }
        : { name: e.name, direction: e.direction ?? 2 };

// ─── Icon Rendering Constants ───
const ICON_DIR = 2;
const ICON_HEAD = 2;

const ICON_OFFSETS = {
    head: { default: { x: 0, y: 14 } },
    expressions: { default: { x: 0, y: 14 } },
    actions: {
        default: { x: 0, y: -40 },
        sit: { x: -6, y: -30 },
        lay: { x: 10, y: 0 },
        wav: { x: -4, y: 14 },
        wlk: { x: 0, y: -40 },
    },
    carry: {
        none: { x: -6, y: -18 },
        item: { x: -6, y: -18 },
    },
    shadow: { default: { x: -2, y: -40 } },
    frankify: { default: { x: -2, y: 14 } },
    body: { default: { x: 0, y: 0 } },
};

const iconStyleFor = (group, key) => {
    const g = ICON_OFFSETS[group] || {};
    const off = (key && g[key]) || g.default || { x: 0, y: 0 };
    if (!off.x && !off.y) return undefined;
    return { transform: `translate(${off.x}px, ${off.y}px)` };
};

// ─── Furni Icon Cache ───
const FURNI_ICON_CACHE_KEY = "habbo_furni_icon_cache_v1";
const FURNI_ICON_TTL_MS = 1000 * 60 * 60 * 24 * 14;
const furniIconMem = new Map();
const furniIconInFlight = new Map();
const furniIconQueue = [];
let furniIconActive = 0;
const FURNI_ICON_MAX_CONCURRENCY = 3;

const readFurniIconCache = () => {
    try {
        const raw = sessionStorage.getItem(FURNI_ICON_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

const writeFurniIconCache = (cache) => {
    try {
        sessionStorage.setItem(FURNI_ICON_CACHE_KEY, JSON.stringify(cache));
    } catch { }
};

const getCachedFurniIcon = (code) => {
    const key = String(code || "").trim();
    if (!key) return null;
    if (furniIconMem.has(key)) return furniIconMem.get(key) || null;
    const cache = readFurniIconCache();
    const hit = cache[key];
    if (hit && hit.url && (!hit.t || Date.now() - hit.t < FURNI_ICON_TTL_MS)) {
        furniIconMem.set(key, hit.url);
        return hit.url;
    }
    return null;
};

const onFurniIconJobDone = () => {
    furniIconActive -= 1;
    if (furniIconQueue.length) pumpFurniIconQueue();
};

const pumpFurniIconQueue = () => {
    while (furniIconActive < FURNI_ICON_MAX_CONCURRENCY && furniIconQueue.length) {
        const job = furniIconQueue.shift();
        if (!job) continue;
        furniIconActive += 1;
        job()
            .catch(() => { })
            .finally(onFurniIconJobDone);
    }
};

async function resolveFurniIconUrl({ code, apiUrl, signal }) {
    const key = String(code || "").trim();
    if (!key) return null;

    const cached = getCachedFurniIcon(key);
    if (cached) return cached;

    if (furniIconInFlight.has(key)) return furniIconInFlight.get(key);

    const base = (apiUrl || process.env.REACT_APP_API_URL).trim();
    const url = `${base}furni-imaging/furni-info/${encodeURIComponent(key)}`;

    const p = new Promise((resolve) => {
        furniIconQueue.push(async () => {
            try {
                const res = await fetch(url, { signal, headers: { Accept: "application/json" } });
                if (!res.ok) return resolve(null);
                const data = await res.json();
                const icon = data?.icon?.variants?.[0]?.url || null;
                if (icon) {
                    furniIconMem.set(key, icon);
                    const next = readFurniIconCache();
                    next[key] = { url: icon, t: Date.now() };
                    writeFurniIconCache(next);
                }
                resolve(icon || null);
            } catch {
                resolve(null);
            }
        });
        pumpFurniIconQueue();
    });

    furniIconInFlight.set(key, p);
    try {
        return await p;
    } finally {
        furniIconInFlight.delete(key);
    }
}

// ─── Figure Cache ───
const FIG_CACHE_KEY = "habbo_fig_cache_v1";

const HOTEL_API_BASE = {
    COM: "https://origins.habbo.com",
    BR: "https://origins.habbo.com.br",
    ES: "https://origins.habbo.es",
};

const readFigCache = () => {
    try {
        const raw = sessionStorage.getItem(FIG_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

const writeFigCache = (cache) => {
    try {
        sessionStorage.setItem(FIG_CACHE_KEY, JSON.stringify(cache));
    } catch {}
};

const inFlightLookups = new Map();

async function resolveFigureString({ username, hotel, signal }) {
    const name = (username || "").trim();
    const h = (hotel || "COM").toUpperCase();
    if (!name) throw new Error("Missing username");

    const key = `${h}:${name}`.toLowerCase();

    const cache = readFigCache();
    if (cache[key]?.figureString) return cache[key].figureString;

    if (inFlightLookups.has(key)) return inFlightLookups.get(key);

    const p = (async () => {
        const base = HOTEL_API_BASE[h] || HOTEL_API_BASE.COM;
        const url = `${base}/api/public/users?name=${encodeURIComponent(name)}`;

        const res = await fetch(url, { signal, headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const fig = data?.figureString;
        if (!fig) throw new Error("No figureString in response");

        const nextCache = readFigCache();
        nextCache[key] = { figureString: fig, t: Date.now() };
        writeFigCache(nextCache);

        return fig;
    })();

    inFlightLookups.set(key, p);
    try {
        return await p;
    } finally {
        inFlightLookups.delete(key);
    }
}

// ─── Shared UI Components ───
function TextNumberInput({ value, min, max, onCommit, step }) {
    const [draft, setDraft] = useState(String(value ?? ""));

    useEffect(() => {
        setDraft(String(value ?? ""));
    }, [value]);

    const commit = () => {
        const raw = String(draft || "").trim();
        const parsed = Number(raw);
        const safe = Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : value;
        setDraft(String(safe));
        onCommit?.(safe);
    };

    const nudge = (delta) => {
        const current = Number(draft) || 0;
        const next = Math.max(min, Math.min(max, current + delta));
        setDraft(String(next));
        onCommit?.(next);
    };

    return (
        <div className="flex items-center gap-1">
            {step != null && (
                <button type="button" onClick={() => nudge(-step)} className="shrink-0 w-8 h-10 border border-black bg-gray-100 hover:bg-gray-200 font-['Jersey_10'] text-base flex items-center justify-center">−</button>
            )}
            <input
                type="text"
                inputMode="numeric"
                value={draft}
                onChange={(e) => {
                    const next = e.target.value;
                    if (/^\d*$/.test(next)) setDraft(next);
                }}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        commit();
                        e.currentTarget.blur();
                    }
                }}
                className="h-10 w-full min-w-0 border border-black px-2 bg-white text-sm text-center"
            />
            {step != null && (
                <button type="button" onClick={() => nudge(step)} className="shrink-0 w-8 h-10 border border-black bg-gray-100 hover:bg-gray-200 font-['Jersey_10'] text-base flex items-center justify-center">+</button>
            )}
            {step == null && <span className="text-[10px] font-['Jersey_10'] uppercase opacity-70">px</span>}
        </div>
    );
}

const RetryImg = React.forwardRef(function RetryImg(
    { src, alt = "", className = "", style, maxTries = 4, crossOrigin, onError, ...rest },
    ref
) {
    const [tryN, setTryN] = useState(0);
    const [permanentlyFailed, setPermanentlyFailed] = useState(false);

    useEffect(() => {
        setTryN(0);
        setPermanentlyFailed(false);
    }, [src]);

    const bust = (u, n) => {
        if (!u) return u;
        const join = u.includes("?") ? "&" : "?";
        return `${u}${join}rt=${n}`;
    };

    const handleError = (e) => {
        if (tryN < maxTries) {
            setTryN((n) => n + 1);
        } else {
            setPermanentlyFailed(true);
        }
        onError?.(e);
    };

    if (permanentlyFailed) return null;

    return (
        <img
            ref={ref}
            src={bust(src, tryN)}
            alt={alt}
            className={className}
            style={style}
            loading="eager"
            decoding="async"
            {...(crossOrigin ? { crossOrigin } : {})}
            onError={handleError}
            {...rest}
        />
    );
});

function IconRow({ title, selectedLabel, children, noScroll }) {
    return (
        <div className="w-full">
            <div className="text-sm font-['Jersey_10'] uppercase mb-1">
                {title} <span className="opacity-70">[{selectedLabel ?? ""}]</span>
            </div>
            <div className={noScroll ? "flex gap-2" : "habbo-scroll flex gap-2 overflow-x-auto pb-2 pr-1"}>{children}</div>
        </div>
    );
}

function IconButton({ selected, disabled, title, onClick, src, alt = "", imgStyle }) {
    return (
        <button
            type="button"
            onClick={disabled ? undefined : onClick}
            title={title}
            disabled={disabled}
            className={[
                "shrink-0 w-12 h-12 border",
                selected ? "border-gray-800" : "border-gray-300",
                "flex items-center justify-center overflow-hidden",
                selected ? "bg-gold " : "bg-gray-200",
                disabled ? "opacity-40 cursor-not-allowed" : selected ? "hover:bg-gold" : "hover:bg-gray-300",
            ].join(" ")}
        >
            <img src={src} alt={alt} className="block max-w-none max-h-none" style={imgStyle} draggable={false} />
        </button>
    );
}

function BgDropdown({ value, onChange, disabled, bgConfig, toolbarMode, bgLabel, disabledKeys }) {
    const [open, setOpen] = useState(false);
    const idRef = useRef(Math.random().toString(36).slice(2));

    useEffect(() => {
        const onAny = (e) => {
            const id = e?.detail?.id;
            if (id && id !== idRef.current) setOpen(false);
        };
        window.addEventListener("habboimager:dropdown", onAny);
        return () => window.removeEventListener("habboimager:dropdown", onAny);
    }, []);

    const keys = useMemo(() => {
        const cfg = bgConfig && typeof bgConfig === "object" ? bgConfig : {};
        return Object.keys(cfg).length ? Object.keys(cfg) : ["default"];
    }, [bgConfig]);

    const makeThumb = (key) => {
        const cfg = (bgConfig && bgConfig[key]) || { type: "flat", thumb: { type: "flat" } };
        const thumb = cfg.thumb || { type: cfg.type === "tile" ? "tile" : cfg.type === "layered" ? "image" : "flat" };

        const borderClass = thumb.border === "white" ? "border-white/50" : "border-black";

        if (cfg.type === "room_tile" && cfg.url) {
            return (
                <div
                    className={`w-10 h-10 bg-black border ${borderClass}`}
                    style={{
                        backgroundImage: `url(${cfg.url})`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "64px 41px",
                        backgroundPosition: "-15px 0px",
                        imageRendering: "pixelated",
                    }}
                />
            );
        }

        if (thumb.type === "tile" && cfg.type === "tile" && cfg.url) {
            const w = cfg.size?.w || 64;
            const h = cfg.size?.h || 32;
            return (
                <div
                    className={`w-10 h-10 border ${borderClass}`}
                    style={{
                        backgroundImage: `url(${cfg.url})`,
                        backgroundRepeat: "repeat",
                        backgroundSize: `${w}px ${h}px`,
                        backgroundPosition: "0px 0px",
                        imageRendering: "pixelated",
                    }}
                />
            );
        }

        if (thumb.type === "image" && cfg.layers) {
            const layerKey = thumb.layerKey || "bg";
            const src = cfg.layers[layerKey] || cfg.layers.bg;
            const ox = thumb.offset?.x ?? 0;
            const oy = thumb.offset?.y ?? 0;

            return (
                <div className={`w-10 h-10 border ${borderClass} overflow-hidden relative`} style={{ imageRendering: "pixelated" }}>
                    <img
                        src={src}
                        alt=""
                        className="block max-w-none max-h-none select-none"
                        style={{ imageRendering: "pixelated", position: "absolute", left: ox, top: oy }}
                        draggable={false}
                    />
                </div>
            );
        }

        return <div className={`w-10 h-10 border ${borderClass} bg-gray-200`} style={{ imageRendering: "pixelated" }} />;
    };

    const currentPreview = makeThumb(value);

    return (
        <div className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={
                    disabled
                        ? undefined
                        : () =>
                            setOpen((s) => {
                                const next = !s;
                                if (next)
                                    window.dispatchEvent(
                                        new CustomEvent("habboimager:dropdown", { detail: { id: idRef.current } })
                                    );
                                return next;
                            })
                }
                title="Background"
                className={toolbarMode ? [
                    "flex items-center gap-1.5 px-3 h-10 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap",
                    disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100",
                    open ? "bg-gold" : "",
                ].join(" ") : [
                    "w-10 h-10 border border-black bg-offwhite",
                    "flex items-center justify-center hover:bg-gray-200",
                    disabled ? "opacity-40 cursor-not-allowed" : "",
                ].join(" ")}
            >
                {toolbarMode ? (
                    <>
                        <div className="w-5 h-5 shrink-0 overflow-hidden border border-black/30" style={{ imageRendering: "pixelated" }}>
                            <div style={{ transform: "scale(0.5)", transformOrigin: "0 0", width: "40px", height: "40px" }}>
                                {currentPreview}
                            </div>
                        </div>
                        <span>{bgLabel || "Background"}</span>
                    </>
                ) : currentPreview}
            </button>

            {open && !disabled && (
                <div className={toolbarMode ? "absolute left-0 top-full mt-0 z-50 border border-black bg-offwhite shadow" : "absolute right-[-48px] mt-2 z-20 border border-black bg-offwhite shadow"}>
                    <div className="p-2 grid gap-2" style={{ gridTemplateColumns: "repeat(4, 40px)" }}>
                        {keys.map((k) => {
                            const cfg = bgConfig && bgConfig[k];
                            const title = cfg?.label || k;
                            const isKeyDisabled = disabledKeys && disabledKeys.has(k);

                            return (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={isKeyDisabled ? undefined : () => {
                                        onChange(k);
                                        setOpen(false);
                                    }}
                                    disabled={isKeyDisabled}
                                    className={[
                                        "w-10 h-10 border border-black flex items-center justify-center",
                                        isKeyDisabled ? "opacity-30 cursor-not-allowed" : "hover:bg-gray-200",
                                    ].join(" ")}
                                    title={isKeyDisabled ? `${title} (not available at this canvas size)` : title}
                                >
                                    {makeThumb(k)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function LooksDropdown({ disabled, items, onToggle, additionalItems, onSelectAdditional }) {
    const [open, setOpen] = useState(false);
    const idRef = useRef(Math.random().toString(36).slice(2));

    useEffect(() => {
        const onAny = (e) => {
            const id = e?.detail?.id;
            if (id && id !== idRef.current) setOpen(false);
        };
        window.addEventListener("habboimager:dropdown", onAny);
        return () => window.removeEventListener("habboimager:dropdown", onAny);
    }, []);

    return (
        <div className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={
                    disabled
                        ? undefined
                        : () =>
                            setOpen((s) => {
                                const next = !s;
                                if (next) window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: idRef.current } }));
                                return next;
                            })
                }
                title="Toggle Habbos in Scene"
                className={[
                    "w-10 h-10 border border-black bg-offwhite/50",
                    "flex items-center justify-center hover:bg-gray-200",
                    disabled ? "opacity-40 cursor-not-allowed" : "",
                ].join(" ")}
            >
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12Z" />
                    <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
                </svg>
            </button>

            {open && !disabled && (
                <div className="absolute left-4 mt-2 z-50 w-[236px] max-h-[300px] overflow-y-auto habbo-scroll border border-black bg-offwhite/90 shadow">
                    <div className="flex items-center justify-between border-t border-black/10 cursor-pointer py-1 group px-2" >
                        <div className="text-xs font-['Jersey_10'] uppercase opacity-60">
                            Saved Habbos
                        </div>
                    </div>

                    <div className="p-2 pt-0 grid gap-2" style={{ gridTemplateColumns: "repeat(4, 48px)" }}>
                        {items.slice(0, 64).map((s) => {
                            const isOn = !!s.isOn;

                            return (
                                <button
                                    key={s.ownerKey}
                                    type="button"
                                    onClick={() => onToggle?.(s.ownerKey)}
                                    title={`${s.habboName} (${s.hotel})`}
                                    className={[
                                        "w-12 h-12 border flex items-center justify-center overflow-hidden",
                                        isOn
                                            ? "border-gray-800 bg-gold hover:bg-gold"
                                            : "border-gray-300 bg-gray-200 hover:bg-gray-300 opacity-50 hover:opacity-80",
                                    ].join(" ")}
                                >
                                    <HabboThumb
                                        src={s.thumbUrl}
                                        className="block max-w-none max-h-none"
                                        style={{ imageRendering: "pixelated", transform: "translate(0px, 16px)" }}
                                    />
                                </button>
                            );
                        })}

                        {!items.length && (
                            <div className="text-xs opacity-70 px-2 py-1">
                                No other loaded Habbos
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between border-t border-black/10 cursor-pointer py-1 group px-2" >
                        <div className="text-xs font-['Jersey_10'] uppercase opacity-60">
                            Additional Scene Habbos
                        </div>
                    </div>

                    <div
                        className="p-2 pt-0 mx-2 grid gap-2 max-h-[200px] overflow-y-auto overflow-x-hidden"
                        style={{ gridTemplateColumns: "repeat(4, 48px)" }}
                    >
                        {(additionalItems || []).map((h) => (
                            <button
                                key={h.id}
                                type="button"
                                onClick={() => onSelectAdditional?.(h.id)}
                                title={`${h.habboName} (${h.hotel})`}
                                className="w-12 h-12 border border-yellow-700 bg-yellow-300 flex items-center justify-center overflow-hidden"
                            >
                                <img
                                    src={h.thumbUrl}
                                    alt=""
                                    className="block max-w-none max-h-none"
                                    style={{ imageRendering: "pixelated", transform: "translate(0px, 16px)" }}
                                    draggable={false}
                                />
                            </button>
                        ))}

                        {!additionalItems?.length && (
                            <div style={{ gridColumn: "1 / -1" }} className="text-xs opacity-70 px-2 py-1 whitespace-nowrap">
                                No additional scene Habbos
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Furni Search Cache ───

const FURNI_SEARCH_CACHE_KEY = "habbo_furni_search_list_v1";

const readFurniSearchCache = () => {
    try {
        const raw = sessionStorage.getItem(FURNI_SEARCH_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
        return null;
    }
};

const writeFurniSearchCache = (items) => {
    try {
        sessionStorage.setItem(FURNI_SEARCH_CACHE_KEY, JSON.stringify(items));
    } catch {}
};

const clearFurniSearchCache = () => {
    try {
        sessionStorage.removeItem(FURNI_SEARCH_CACHE_KEY);
    } catch {}
};

// ─── Furni Search Modal ───
function HabboThumb({ src, className, style }) {
    return (
        <img
            src={src}
            alt=""
            className={className}
            style={style}
            draggable={false}
        />
    );
}

function FurniSearchThumb({ item, onSelect }) {
    const [useFallback, setUseFallback] = useState(false);
    const [failed, setFailed] = useState(false);

    const code = String(item.code || '').replace(/%2a/gi, '*');
    const star = code.indexOf('*');
    const base = star === -1 ? code : code.slice(0, star);
    const rest = star === -1 ? '' : code.slice(star);

    const iconSrc = `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(`${base}_small${rest}`)}&direction=0&state=1&frame=0&width=120&height=120`;
    const fullSrc = `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(code)}&direction=0&state=0&frame=0&width=120&height=120`;

    const src = useFallback ? fullSrc : iconSrc;

    const handleError = () => {
        if (!useFallback) {
            setUseFallback(true);
        } else {
            setFailed(true);
        }
    };

    return (
        <button
            type="button"
            title={`${item.label}\n${item.code}`}
            onClick={() => onSelect?.(item)}
            className="flex flex-col items-center border border-gray-300 bg-gray-100 hover:bg-yellow-100 hover:border-black transition-colors overflow-hidden"
            style={{ width: 72 }}
        >
            <div className="w-full flex items-center justify-center overflow-hidden bg-gray-200" style={{ height: 60 }}>
                {failed ? (
                    <div className="text-sm font-['Jersey_10'] opacity-40 uppercase text-center px-1">No image</div>
                ) : (
                    <img
                        src={src}
                        alt=""
                        onError={handleError}
                        className="block"
                        style={{ imageRendering: 'pixelated', maxWidth: 52, maxHeight: 52 }}
                    />
                )}
            </div>
            <div
                className="w-full px-0.5 py-0.5 text-center font-['Jersey_10'] text-sm leading-tight truncate"
                style={{ maxWidth: 72 }}
            >
                {item.label}
            </div>
        </button>
    );
}

function FurniSearchModal({ open, onClose, onSelect }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [loadState, setLoadState] = useState('idle'); // idle | loading | ready | error
    const searchInputRef = useRef(null);
    const debounceRef = useRef(null);
    const abortRef = useRef(null);


    useEffect(() => {
        if (open) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        } else {
            setSearchTerm('');
            setResults([]);
            setLoadState('idle');
        }
    }, [open]);

    // Debounced search - fires 400ms after user stops typing
    useEffect(() => {
        const term = searchTerm.trim();

        if (term.length < 3) {
            setResults([]);
            setLoadState('idle');
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(async () => {
            if (abortRef.current) abortRef.current.abort();
            const ac = new AbortController();
            abortRef.current = ac;

            setLoadState('loading');

            try {
                const data = await searchFurniItems(term, ac.signal);
                if (ac.signal.aborted) return;

                if (data?.success && Array.isArray(data.items)) {
                    setResults(data.items.map((item) => ({
                        value: item.id,
                        label: item.name,
                        code: item.code,
                        original: item,
                    })));
                    setLoadState('ready');
                } else {
                    setResults([]);
                    setLoadState('error');
                }
            } catch (err) {
                if (ac.signal.aborted) return;
                try {
                    await new Promise((r) => setTimeout(r, 600));
                    if (ac.signal.aborted) return;
                    const retryData = await searchFurniItems(term, ac.signal);
                    if (ac.signal.aborted) return;
                    if (retryData?.success && Array.isArray(retryData.items)) {
                        setResults(retryData.items.map((item) => ({
                            value: item.id,
                            label: item.name,
                            code: item.code,
                            original: item,
                        })));
                        setLoadState('ready');
                    } else {
                        setResults([]);
                        setLoadState('error');
                    }
                } catch {
                    if (ac.signal.aborted) return;
                    setResults([]);
                    setLoadState('error');
                }
            }
        }, 800);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchTerm]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
            <div className="border border-black bg-offwhite shadow flex flex-col w-[min(460px,92%)] max-h-[85%]">
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-black">
                    <span className="font-['Jersey_10'] uppercase text-lg tracking-widest">Search Furni</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-7 h-7 flex items-center justify-center border border-black bg-gray-200 hover:bg-red-500 hover:text-white font-['Jersey_10'] text-base leading-none"
                    >×</button>
                </div>

                {/* Search input */}
                <div className="p-2 border-b border-black/10">
                    <div className="relative flex items-center">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Type 3+ letters to search…"
                            className="w-full px-3 py-2 border border-black text-sm font-sans bg-white pr-7 placeholder:text-black/40 focus:outline-none"
                        />
                        {loadState === 'loading' && (
                            <div className="absolute right-2.5 w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin pointer-events-none" />
                        )}
                        {loadState !== 'loading' && searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2 text-black/40 hover:text-black text-lg leading-none"
                            >×</button>
                        )}
                    </div>
                </div>

                {/* Results */}
                <div className="flex-1 overflow-y-auto habbo-scroll p-3 min-h-0">
                    {loadState === 'idle' && (
                        <div className="text-center py-8 font-['Jersey_10'] text-sm opacity-40 uppercase">Type at least 3 letters to search</div>
                    )}
                    {loadState === 'loading' && (
                        <div className="flex flex-col items-center justify-center py-10 gap-3">
                            <div className="w-8 h-8 border-4 border-black/20 border-t-black rounded-full animate-spin" />
                            <div className="font-['Jersey_10'] text-sm opacity-60 uppercase">Searching…</div>
                        </div>
                    )}
                    {loadState === 'error' && (
                        <div className="text-center py-8 font-['Jersey_10'] text-sm text-red-600 uppercase">Search failed - please try again</div>
                    )}
                    {loadState === 'ready' && results.length === 0 && (
                        <div className="text-center py-8 font-['Jersey_10'] text-sm opacity-40 uppercase">No results for "{searchTerm}"</div>
                    )}
                    {loadState === 'ready' && results.length > 0 && (
                        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, 72px)' }}>
                            {results.map((f) => (
                                <FurniSearchThumb key={f.value} item={f} onSelect={onSelect} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const FurniDropdown = React.memo(({ disabled, items, addedItems, onRemoveAdded, count, onAdd, onOpenSearch, searchCompleteRef, openRight, toolbarMode }) => {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState('latest');
    const idRef = useRef(Math.random().toString(36).slice(2));
    const allItemsRef = useRef([]);

    useEffect(() => {
        if (searchCompleteRef) {
            searchCompleteRef.current = () => {
                setOpen(true);
                setExpanded('added');
            };
        }
    }, [searchCompleteRef]);

    const getIconForCode = (code, fallback) => {
        const key = String(code || "").trim();
        if (!key) return fallback || "";
        return buildFranklyIconFromClass(key) || fallback || "";
    };

    const latestResolvedRef = useRef(new Map());
    const latestIndexRef = useRef({ ready: false, byLabel: new Map(), byCode: new Map(), codeList: [] });

    const normLabelKey = (s) => String(s || "").trim().toLowerCase();

    const normCodeKey = (s) =>
        String(s || "")
            .trim()
            .toLowerCase()
            .replace(/%2a/g, "*")
            .replace(/\s+/g, "")
            .replace(/-/g, "_");

    const ensureLatestIndex = () => {
        if (latestIndexRef.current.ready) return;
        const list = Array.isArray(allItemsRef.current) ? allItemsRef.current : [];

        const byLabel = new Map();
        const byCode = new Map();
        const codeList = [];

        for (let i = 0; i < list.length; i += 1) {
            const it = list[i];
            const label = String(it?.label || "").trim();
            const code = String(it?.code || "").trim();
            if (label) byLabel.set(label.toLowerCase(), it);
            if (code) {
                const ck = normCodeKey(code);
                byCode.set(ck, it);
                codeList.push({ ck, it });
            }
        }

        latestIndexRef.current = { ready: true, byLabel, byCode, codeList };
    };

    const codeCandidatesFromFurniIndexName = (name) => {
        const raw = String(name || "").trim();
        if (!raw) return [];
        const stripped = raw.replace(/^furni_/i, "").replace(/_name$/i, "").trim();
        const normSmall = stripped.replace(/small\d+/gi, "small").replace(/_small\d+/gi, "_small");
        const noSmall = stripped.replace(/_?small\d*/gi, "");
        const candidates = [raw, stripped, normSmall, noSmall];
        const uniq = [];
        const seen = new Set();
        for (let c of candidates) {
            const k = normCodeKey(c);
            if (k && !seen.has(k)) {
                seen.add(k);
                uniq.push(k);
            }
        }
        return uniq;
    };

    const bestByCodeClosest = (targetKey) => {
        const t = normCodeKey(targetKey);
        if (!t) return null;
        ensureLatestIndex();
        const list = Array.isArray(allItemsRef.current) ? allItemsRef.current : [];
        let best = null;
        let bestScore = -1;
        for (let i = 0; i < list.length; i++) {
            const it = list[i];
            const ck = normCodeKey(it.code);
            const lk = String(it.label || "").toLowerCase();
            const inc = ck.includes(t) || t.includes(ck) || lk.includes(t);
            if (!inc) continue;
            const lenPenalty = Math.abs(ck.length - t.length) / 50;
            const score = 1 - lenPenalty;
            if (score > bestScore) {
                bestScore = score;
                best = it;
            }
        }
        return bestScore >= 0.4 ? best : null;
    };
    const buildFranklyIconFromClass = (classnameOrCode) => {
        const raw = String(classnameOrCode || "").trim();
        if (!raw) return "";
        const s = raw.replace(/%2a/gi, "*");
        const star = s.indexOf("*");
        const base = star === -1 ? s : s.slice(0, star);
        const rest = star === -1 ? "" : s.slice(star);
        const furni = `${base}_small${rest}`;
        return `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(furni)}&direction=0&state=1&frame=0&width=120&height=120`;
    };

    const cleanFurniIndexNameToClass = (name) => {
        let s = String(name || "").trim();
        if (!s) return "";
        s = s.replace(/^furni_/i, "");
        s = s.replace(/_name$/i, "");
        s = s.replace(/small\d+/gi, "small");
        s = s.replace(/_small\d+/gi, "_small");
        s = s.replace(/\d+(?=\*)/g, "");
        s = s.trim();
        return s;
    };

    const resolveLatestIconSrc = (f) => {
        const key = normLabelKey(f?.name || f?.label || "");
        if (!key) return String(f?.thumbUrl || "");

        const cached = latestResolvedRef.current.get(`__src__:${key}`);
        if (cached !== undefined) return cached || String(f?.thumbUrl || "");

        ensureLatestIndex();
        const { byLabel } = latestIndexRef.current;

        const exact = byLabel.get(key);
        if (exact?.code) {
            const src = buildFranklyIconFromClass(exact.code);
            latestResolvedRef.current.set(`__src__:${key}`, src);
            return src;
        }

        const candidates = codeCandidatesFromFurniIndexName(f?.name || "");
        for (let i = 0; i < candidates.length; i += 1) {
            const hit = bestByCodeClosest(candidates[i]);
            if (hit?.code) {
                const src = buildFranklyIconFromClass(hit.code);
                latestResolvedRef.current.set(`__src__:${key}`, src);
                return src;
            }
        }

        const cleaned = cleanFurniIndexNameToClass(f?.name || "");
        if (cleaned) {
            const src = buildFranklyIconFromClass(cleaned);
            latestResolvedRef.current.set(`__src__:${key}`, src);
            return src;
        }

        latestResolvedRef.current.set(`__src__:${key}`, "");
        return String(f?.thumbUrl || "");
    };

    useEffect(() => {
        const onAny = (e) => {
            const id = e?.detail?.id;
            if (id && id !== idRef.current) setOpen(false);
        };
        window.addEventListener("habboimager:dropdown", onAny);
        return () => window.removeEventListener("habboimager:dropdown", onAny);
    }, []);

    const prevAddedLenRef = useRef(0);

    useEffect(() => {
        const len = addedItems?.length || 0;
        const prev = prevAddedLenRef.current;
        prevAddedLenRef.current = len;

        if (len > 0 && prev === 0 && expanded === "") {
            setExpanded("added");
        }
    }, [addedItems?.length, expanded]);

    return (
        <div className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={
                    disabled
                        ? undefined
                        : () =>
                            setOpen((s) => {
                                const next = !s;
                                if (next) window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: idRef.current } }));
                                return next;
                            })
                }
                title="Add Furni to Scene"
                className={toolbarMode ? [
                    "w-full flex flex-col items-center justify-center py-3 gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest",
                    disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100",
                    open ? "bg-gold" : "",
                ].join(" ") : [
                    "w-10 h-10 border border-black bg-offwhite/50",
                    "flex items-center justify-center hover:bg-gray-200",
                    disabled ? "opacity-40 cursor-not-allowed" : "",
                ].join(" ")}
            >
                <img src="/img/icons/icon_2.png" alt="" className="h-[18px] w-[18px]" style={{ imageRendering: "pixelated" }} />
                {toolbarMode && <span>Add Furni</span>}
            </button>

            {open && !disabled && (
                <div className="absolute z-50 border border-black bg-offwhite/90 shadow w-[260px] left-[81px] top-0">
                    <div className="text-right w-full p-2 pb-0 text-sm font-['Jersey_10'] tracking-widest uppercase opacity-80 select-none">
                        Furni [{count}]
                    </div>

                    <div className="p-2 border-b border-black/10">
                        <button
                            type="button"

                            onClick={() => onOpenSearch?.()}
                            className={[
                                "w-full h-9 border border-black bg-coal text-offwhite font-['Jersey_10'] tracking-widest uppercase text-sm",
                                "flex items-center justify-center gap-2 hover:bg-black transition-colors",
                            ].join(" ")}
                        >
                            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" aria-hidden="true">
                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            Search Furni
                        </button>
                    </div>

                    <div className="max-h-[300px] overflow-y-auto habbo-scroll p-2">
                        <div
                            className="flex items-center justify-between cursor-pointer py-1 group"
                            onClick={() => setExpanded((cur) => (cur === 'latest' ? '' : 'latest'))}
                        >
                            <div className="text-xs font-['Jersey_10'] tracking-widest uppercase opacity-60">Latest Releases</div>
                            <svg className={`w-3 h-3 transition-transform ${expanded === 'latest' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>

                        {expanded === 'latest' && (
                            <div className="grid gap-2 mt-1 mb-3" style={{ gridTemplateColumns: "repeat(auto-fill, 48px)" }}>
                                {items.map((f) => {
                                    const src = f.code
                                        ? buildFranklyIconFromClass(f.code)
                                        : (f.thumbUrl || "");
                                    return (
                                        <button
                                            key={f.id}
                                            type="button"

                                            onClick={() => onAdd?.(f.id)}
                                            className={[
                                                "w-12 h-12 border flex items-center justify-center overflow-hidden border-gray-300 bg-gray-200 hover:bg-gray-300",
                                            ].join(" ")}
                                        >
                                            <RetryImg
                                                src={src}
                                                alt=""
                                                className="block max-w-none max-h-none"
                                                style={{ imageRendering: "pixelated" }}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        <div
                            className="flex items-center justify-between cursor-pointer py-1 mt-1 border-t border-black/10 group"
                            onClick={() => setExpanded((cur) => (cur === 'added' ? '' : 'added'))}
                        >
                            <div className="text-xs font-['Jersey_10'] tracking-widest uppercase opacity-60">Added Furni</div>
                            <svg className={`w-3 h-3 transition-transform ${expanded === 'added' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>

                        {expanded === 'added' && (
                            <div className="grid gap-2 mt-1" style={{ gridTemplateColumns: "repeat(auto-fill, 48px)" }}>
                                {addedItems.map((f) => (
                                    <div key={f.id} className="relative w-12 h-12">
                                        <button
                                            type="button"

                                            onClick={() => onAdd?.(f.id)}
                                            className={[
                                                "w-12 h-12 border flex items-center justify-center overflow-hidden border-gray-800 bg-yellow-100 hover:bg-yellow-200",
                                            ].join(" ")}
                                        >
                                            <RetryImg src={f.thumbUrl} alt="" className="block max-w-none max-h-none" style={{ imageRendering: "pixelated" }} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); onRemoveAdded?.(f.id); }}
                                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 border border-black text-white flex items-center justify-center text-sm leading-none hover:bg-red-600 z-10"
                                        >×</button>
                                    </div>
                                ))}
                                {addedItems.length === 0 && <div className="text-sm opacity-40 italic p-1 col-span-full text-center py-4">No added furni</div>}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

function SpeechBubbleDropdown({ disabled, sceneHabbos, bubbles, onAddBubble, onRemoveBubble, onReorder, onSelectHabbo, openRight, toolbarMode, openRightOffset }) {
    const [open, setOpen] = useState(false);
    const idRef = useRef(Math.random().toString(36).slice(2));
    const colorInputRef = useRef(null);

    const [pickId, setPickId] = useState("");
    const [text, setText] = useState("");
    const [color, setColor] = useState("#e0b250");
    const [tone, setTone] = useState("say");
    const dragIdRef = useRef(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const lastSwapRef = useRef(null);

    useEffect(() => {
        const onAny = (e) => {
            const id = e?.detail?.id;
            if (id && id !== idRef.current) setOpen(false);
        };
        window.addEventListener("habboimager:dropdown", onAny);
        return () => window.removeEventListener("habboimager:dropdown", onAny);
    }, []);

    useEffect(() => {
        const first = (sceneHabbos && sceneHabbos[0] && sceneHabbos[0].id) || "";
        if (!first) {
            if (pickId) setPickId("");
            return;
        }
        const exists = (sceneHabbos || []).some((h) => h.id === pickId);
        if (!pickId || !exists) setPickId(first);
    }, [sceneHabbos, pickId]);

    const atLimit = (bubbles || []).length >= 5;
    const canAdd = !!pickId && !!text.trim() && !atLimit;

    return (
        <div className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={
                    disabled
                        ? undefined
                        : () =>
                            setOpen((s) => {
                                const next = !s;
                                if (next) window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: idRef.current } }));
                                return next;
                            })
                }
                title="Speech Bubbles"
                className={toolbarMode ? [
                    "w-full flex flex-col items-center justify-center py-3 gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest",
                    disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100",
                    open ? "bg-gold" : "",
                ].join(" ") : [
                    "w-10 h-10 border border-black bg-offwhite/50",
                    "flex items-center justify-center hover:bg-gray-200",
                    disabled ? "opacity-40 cursor-not-allowed" : "",
                ].join(" ")}
            >
                <img src="/img/icons/icon_359.png" alt="" className="h-[18px] w-[18px]" style={{ imageRendering: "pixelated" }} />
                {toolbarMode && <span>Speech</span>}
            </button>

            {open && !disabled && (
                <div className={["absolute z-50 border max-h-[380px] overflow-y-auto overflow-x-hidden habbo-scroll border-black bg-offwhite/90 shadow w-[340px]", openRight ? "top-0" : "left-[0px] max-[420px]:left-[-50px] mt-2 max-w-[92vw] max-[420px]:w-[240px] sm:w-[414px]"].join(" ")} style={openRight ? { left: openRightOffset != null ? openRightOffset : "100%" } : {}} onPointerDown={(e) => e.stopPropagation()}>
                    <div className="text-right w-full p-2 pb-0 text-sm font-['Jersey_10'] uppercase opacity-80 select-none">
                        Speech Bubbles [{(bubbles || []).length}/5]
                    </div>

                    <div className="p-2 pt-2">
                        <div className="flex gap-2 overflow-x-auto habbo-scroll pb-1">
                            {(sceneHabbos || []).map((h) => {
                                const on = pickId === h.id;
                                return (
                                    <button
                                        key={h.id}
                                        type="button"
                                        onClick={() => { setPickId(h.id); onSelectHabbo?.(h.id); }}
                                        title={`${h.habboName} (${h.hotel})`}
                                        className={[
                                            "shrink-0 w-12 h-12 border flex items-center justify-center overflow-hidden",
                                            on ? "border-gray-800 bg-yellow-300" : "border-gray-300 bg-gray-200 hover:bg-gray-300",
                                        ].join(" ")}
                                    >
                                        <img
                                            src={h.thumbUrl}
                                            alt=""
                                            className="block max-w-none max-h-none"
                                            style={{ imageRendering: "pixelated", transform: "translate(0px, 16px)" }}
                                            draggable={false}
                                        />
                                    </button>
                                );
                            })}

                            {!sceneHabbos?.length && (
                                <div className="text-xs opacity-70 px-2 py-1">
                                    No Habbos in this scene
                                </div>
                            )}
                        </div>

                        <div className="mt-3 flex items-center gap-2 max-[420px]:flex-wrap">
                            <input
                                type="text"
                                value={text}
                                maxLength={80}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Message (max 80)"
                                className="flex-1 min-w-0 h-10 border border-black px-2 bg-white text-[9px] font-volter"
                            />

                            <div className="relative w-10 h-10 border border-black bg-white">
                                <ColorPicker
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    className="absolute inset-0"
                                    style={{ backgroundColor: color }}
                                    title="Pick outline colour"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={() => setTone((s) => (s === "say" ? "shout" : "say"))}
                                className="shrink-0 whitespace-nowrap h-10 px-3 border border-black font-['Jersey_10'] uppercase text-sm tracking-widest bg-white hover:bg-gray-100"
                                title="Toggle Say/Shout"
                            >
                                {tone}
                            </button>

                            <button
                                type="button"
                                disabled={!canAdd}
                                onClick={!canAdd ? undefined : () => {
                                    const t = text.trim();
                                    const tTone = tone;
                                    const tColor = color;
                                    setText("");
                                    setColor("#e0b250");
                                    setTone("say");
                                    onAddBubble?.(pickId, t, tColor, tTone === "shout");
                                }}
                                className={[
                                    "shrink-0 whitespace-nowrap h-10 px-3 border border-black font-['Jersey_10'] uppercase text-sm tracking-widest",
                                    canAdd ? "bg-yellow-500 hover:bg-yellow-400" : "bg-gray-200 opacity-50 cursor-not-allowed",
                                ].join(" ")}
                            >
                                Add
                            </button>
                        </div>

                        <div className="mt-3 grid gap-2">
                            {(bubbles || []).map((b) => {
                                const over = isDragging && dragOverId === b.id && dragIdRef.current && dragIdRef.current !== b.id;
                                return (
                                    <div
                                        key={b.id}
                                        draggable
                                        onDragStart={() => {
                                            dragIdRef.current = b.id;
                                            lastSwapRef.current = null;
                                            setIsDragging(true);
                                            setDragOverId(b.id);
                                        }}
                                        onDragEnd={() => {
                                            dragIdRef.current = null;
                                            lastSwapRef.current = null;
                                            setIsDragging(false);
                                            setDragOverId(null);
                                        }}
                                        onDragOver={(e) => {
                                            if (!dragIdRef.current) return;
                                            e.preventDefault();
                                            if (dragOverId !== b.id) setDragOverId(b.id);
                                            const from = dragIdRef.current;
                                            if (from && from !== b.id && lastSwapRef.current !== b.id) {
                                                lastSwapRef.current = b.id;
                                                onReorder?.(from, b.id);
                                            }
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            dragIdRef.current = null;
                                            lastSwapRef.current = null;
                                            setIsDragging(false);
                                            setDragOverId(null);
                                        }}
                                        className={[
                                            "relative flex items-center gap-2 border min-w-0 bg-white/60 px-2 py-1 select-none",
                                            "font-volter text-[9px]",
                                            isDragging ? "cursor-grabbing" : "cursor-grab",
                                        ].join(" ")}
                                        style={{ borderColor: b.color || "#000" }}
                                    >
                                        {over && <div className="absolute left-0 right-0 -top-[2px] h-[3px] bg-yellow-500" />}

                                        <button
                                            type="button"
                                            onClick={() => onRemoveBubble?.(b.id)}
                                            className="w-6 h-6 border border-black bg-gray-200 hover:bg-gray-300 flex items-center justify-center shrink-0"
                                            aria-label="Remove"
                                            title="Remove"
                                        >
                                            <span className="font-['Jersey_10'] text-sm leading-none">X</span>
                                        </button>

                                        <div className="min-w-0 flex-1 overflow-hidden">
                                            <span
                                                className={[
                                                    "block truncate font-volter text-[9px]",
                                                    b.shout ? "font-volterBold" : "",
                                                ].join(" ")}
                                            >
                                                {String(b.text || "")}
                                            </span>

                                            <span className="block truncate opacity-70 font-volter text-[9px]">
                                                ({String(b.displayName || "").trim()})
                                            </span>
                                        </div>

                                        <div
                                            className="w-3 h-3 border border-black shrink-0"
                                            style={{ backgroundColor: b.color || "#000" }}
                                            title="Bubble colour"
                                        />
                                    </div>
                                );
                            })}

                            {!bubbles?.length && (
                                <div className="text-xs opacity-70">
                                    No speech bubbles
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TextDropdown({ disabled, activeText, onAdd, onUpdate, onCommit, openRight, toolbarMode }) {
    const [open, setOpen] = useState(false);
    const idRef = useRef(Math.random().toString(36).slice(2));
    const isEdit = !!activeText;

    useEffect(() => {
        const onAny = (e) => {
            const id = e?.detail?.id;
            if (id && id !== idRef.current) setOpen(false);
        };
        window.addEventListener("habboimager:dropdown", onAny);
        return () => window.removeEventListener("habboimager:dropdown", onAny);
    }, []);

    return (
        <div className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={
                    disabled
                        ? undefined
                        : () =>
                            setOpen((s) => {
                                const next = !s;
                                if (next) window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: idRef.current } }));
                                return next;
                            })
                }
                title={isEdit ? "Edit Text" : "Add Text"}
                className={toolbarMode ? [
                    "w-full flex flex-col items-center justify-center py-3 gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest",
                    disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100",
                    open || isEdit ? "bg-gold" : "",
                ].join(" ") : [
                    "w-10 h-10 border border-black bg-offwhite/50",
                    "flex items-center justify-center hover:bg-gray-200",
                    disabled ? "opacity-40 cursor-not-allowed" : "",
                    isEdit ? "bg-gold" : "",
                ].join(" ")}
            >
                <img src="/img/icons/icon_80.png" alt="" className="h-[18px] w-[18px]" style={{ imageRendering: "pixelated" }} />
                {toolbarMode && <span>Text</span>}
            </button>

            {open && !disabled && (
                <div className="absolute z-50 border border-black bg-offwhite/90 shadow w-[260px] left-[81px] top-0" onPointerDown={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-3 py-2 border-b border-black/10">
                        <span className="font-['Jersey_10'] uppercase text-sm tracking-widest opacity-80">
                            {isEdit ? "Edit Text" : "Add Text"}
                        </span>
                        <button type="button" onClick={() => setOpen(false)} className="text-xs opacity-40 hover:opacity-100 leading-none">✕</button>
                    </div>

                    {!isEdit && (
                        <div className="p-3">
                            <button
                                type="button"
                                onClick={() => {
                                    onAdd?.();
                                }}
                                className="w-full h-10 border border-black bg-gold hover:bg-yellow-300 font-['Jersey_10'] uppercase tracking-widest text-sm"
                            >
                                Add Text Layer
                            </button>
                        </div>
                    )}

                    {isEdit && (
                        <div className="p-3 flex flex-col gap-3">
                            <input
                                type="text"
                                value={activeText.text}
                                maxLength={120}
                                onChange={(e) => onUpdate?.(activeText.id, { text: e.target.value })}
                                onBlur={() => onCommit?.()}
                                className="h-10 border border-black px-2 bg-white text-sm"
                                placeholder="Text"
                            />

                            <select
                                value={activeText.fontFamily}
                                onChange={(e) => { onCommit?.(); onUpdate?.(activeText.id, { fontFamily: e.target.value }); }}
                                className="h-10 border border-black px-2 bg-white text-sm"
                            >
                                {TEXT_FONT_OPTIONS.map((f) => (
                                    <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                            </select>

                            <div className="grid grid-cols-2 gap-2">
                                <div className="flex flex-col gap-1">
                                    <span className="text-sm font-['Jersey_10'] uppercase opacity-70">Text Colour</span>
                                    <ColorPicker
                                        value={activeText.color || "#ffffff"}
                                        onChange={(e) => onUpdate?.(activeText.id, { color: e.target.value })}
                                        onOpen={() => onCommit?.()}
                                        className="w-10 h-10 border border-black bg-white p-1"
                                    />
                                </div>

                                <div className="flex flex-col gap-1">
                                    <span className="text-sm font-['Jersey_10'] uppercase opacity-70">Stroke Colour</span>
                                    <ColorPicker
                                        value={activeText.strokeColor || "#000000"}
                                        onChange={(e) => onUpdate?.(activeText.id, { strokeColor: e.target.value })}
                                        onOpen={() => onCommit?.()}
                                        className="w-10 h-10 border border-black bg-white p-1"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <label className="flex flex-col gap-1">
                                    <span className="text-sm font-['Jersey_10'] uppercase opacity-70">Size</span>
                                    <TextNumberInput
                                        value={activeText.fontSize}
                                        min={8}
                                        max={128}
                                        step={1}
                                        onCommit={(value) => { onCommit?.(); onUpdate?.(activeText.id, { fontSize: value }); }}
                                    />
                                </label>

                                <label className="flex flex-col gap-1">
                                    <span className="text-sm font-['Jersey_10'] uppercase opacity-70">Stroke</span>
                                    <TextNumberInput
                                        value={activeText.strokeWidth}
                                        min={0}
                                        max={200}
                                        step={1}
                                        onCommit={(value) => { onCommit?.(); onUpdate?.(activeText.id, { strokeWidth: value }); }}
                                    />
                                </label>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => { onCommit?.(); onUpdate?.(activeText.id, { fontWeight: activeText.fontWeight === "bold" ? "normal" : "bold" }); }}
                                    className="h-10 border border-black bg-white hover:bg-gray-100 font-['Jersey_10'] uppercase tracking-widest text-sm"
                                >
                                    {activeText.fontWeight === "bold" ? "Unbold" : "Bold"}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => { onCommit?.(); onUpdate?.(activeText.id, { italic: !activeText.italic }); }}
                                    className="h-10 border border-black bg-white hover:bg-gray-100 font-['Jersey_10'] uppercase tracking-widest text-sm"
                                >
                                    {activeText.italic ? "Unitalic" : "Italic"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Separate component so it can hold local state for debounced colour/tolerance updates
function OutlineEffectRow({ layerId, effect, onEffectChange, onRemoveEffect }) {
    const [localColor, setLocalColor] = React.useState(effect.color || '#000000');
    const [localTolerance, setLocalTolerance] = React.useState(typeof effect.tolerance === 'number' ? effect.tolerance : 0);
    const debounceRef = React.useRef(null);
    const tolDebounceRef = React.useRef(null);

    React.useEffect(() => { setLocalColor(effect.color || '#000000'); }, [effect.color]);
    React.useEffect(() => { setLocalTolerance(typeof effect.tolerance === 'number' ? effect.tolerance : 0); }, [effect.tolerance]);

    const handleColorChange = (e) => {
        const val = e.target.value;
        setLocalColor(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onEffectChange?.(layerId, 'outline', { color: val });
        }, 80);
    };

    const handleToleranceChange = (e) => {
        const val = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
        setLocalTolerance(val);
        clearTimeout(tolDebounceRef.current);
        tolDebounceRef.current = setTimeout(() => {
            onEffectChange?.(layerId, 'outline', { tolerance: val });
        }, 80);
    };

    const displayColor = /^#[0-9a-fA-F]{6}$/.test(localColor) ? localColor : '#000000';

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
                <span className="font-['Jersey_10'] uppercase text-xs tracking-widest shrink-0 flex-1">Outline Sprite:</span>
                <button
                    type="button"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); clearTimeout(debounceRef.current); onEffectChange?.(layerId, 'outline', { enabled: !effect.enabled }); }}
                    className={["px-2 py-0.5 border border-black font-['Jersey_10'] uppercase text-xs", effect.enabled ? "bg-gold" : "bg-offwhite hover:bg-gray-200"].join(" ")}
                >
                    {effect.enabled ? "On" : "Off"}
                </button>
                <button
                    type="button"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); clearTimeout(debounceRef.current); clearTimeout(tolDebounceRef.current); onRemoveEffect?.(layerId, 'outline'); }}
                    className="w-5 h-5 border border-black/40 bg-offwhite hover:bg-red-100 flex items-center justify-center text-xs leading-none"
                    title="Remove outline effect"
                >×</button>
            </div>
            {effect.enabled && (
                <>
                    <div className="flex items-center gap-2">
                        <span className="font-['Jersey_10'] uppercase text-xs tracking-widest shrink-0 w-14">Colour:</span>
                        <ColorPicker
                            value={displayColor}
                            onChange={handleColorChange}
                            onOpen={() => {}}
                            className="w-6 h-6 shrink-0"
                            title="Outline colour"
                        />
                        <span className="font-['Jersey_10'] text-xs text-coal/60 uppercase">{displayColor}</span>
                    </div>
                    <div className="flex items-center gap-2"
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        <span className="font-['Jersey_10'] uppercase text-xs tracking-widest shrink-0 w-14">Tolerance:</span>
                        <input
                            type="range"
                            min={0}
                            max={255}
                            value={localTolerance}
                            onChange={handleToleranceChange}
                            className="flex-1 h-1 accent-coal cursor-pointer"
                            title={`Tolerance: ${localTolerance} - how dark a pixel must be to be included`}
                        />
                        <span className="font-['Jersey_10'] text-xs text-coal/60 w-7 text-right">{localTolerance}</span>
                    </div>
                </>
            )}
        </div>
    );
}

function WaterEffectRow({ layerId, effect, onEffectChange, onRemoveEffect, isFurni }) {
    const [localColor, setLocalColor] = React.useState(effect.color || '#0a6e78');
    const [localHeight, setLocalHeight] = React.useState(typeof effect.height === 'number' ? effect.height : 20);
    const colorDebounceRef = React.useRef(null);
    const heightDebounceRef = React.useRef(null);

    React.useEffect(() => { setLocalColor(effect.color || '#0a6e78'); }, [effect.color]);
    React.useEffect(() => { setLocalHeight(typeof effect.height === 'number' ? effect.height : 20); }, [effect.height]);

    const handleColorChange = (e) => {
        const val = e.target.value;
        setLocalColor(val);
        clearTimeout(colorDebounceRef.current);
        colorDebounceRef.current = setTimeout(() => {
            onEffectChange?.(layerId, 'water', { color: val });
        }, 80);
    };

    const handleHeightChange = (e) => {
        const val = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
        setLocalHeight(val);
        clearTimeout(heightDebounceRef.current);
        heightDebounceRef.current = setTimeout(() => {
            onEffectChange?.(layerId, 'water', { height: val });
        }, 80);
    };

    const displayColor = /^#[0-9a-fA-F]{6}$/.test(localColor) ? localColor : '#0a6e78';

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
                <span className="font-['Jersey_10'] uppercase text-xs tracking-widest shrink-0 flex-1">Water Effect:</span>
                {isFurni && (
                    <button
                        type="button"
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            const next = (effect.curveMode || 'adaptive') === 'adaptive' ? 'parabola' : 'adaptive';
                            onEffectChange?.(layerId, 'water', { curveMode: next });
                        }}
                        className="w-6 h-5 border border-black/40 bg-offwhite hover:bg-gray-200 flex items-center justify-center shrink-0"
                        title={(effect.curveMode || 'adaptive') === 'adaptive' ? 'Curve: Isometric - click to switch to Parabola' : 'Curve: Parabola - click to switch to Isometric'}
                    >
                        {(effect.curveMode || 'adaptive') === 'adaptive' ? (
                            <svg viewBox="0 0 12 10" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                                <path d="M1 2 L6 9 L11 2"/>
                            </svg>
                        ) : (
                            <svg viewBox="0 0 12 10" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                                <path d="M1 2 Q6 9 11 2"/>
                            </svg>
                        )}
                    </button>
                )}
                <button
                    type="button"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); clearTimeout(colorDebounceRef.current); onEffectChange?.(layerId, 'water', { enabled: !effect.enabled }); }}
                    className={["px-2 py-0.5 border border-black font-['Jersey_10'] uppercase text-xs", effect.enabled ? "bg-gold" : "bg-offwhite hover:bg-gray-200"].join(" ")}
                >
                    {effect.enabled ? "On" : "Off"}
                </button>
                <button
                    type="button"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); clearTimeout(colorDebounceRef.current); clearTimeout(heightDebounceRef.current); onRemoveEffect?.(layerId, 'water'); }}
                    className="w-5 h-5 border border-black/40 bg-offwhite hover:bg-red-100 flex items-center justify-center text-xs leading-none"
                    title="Remove water effect"
                >×</button>
            </div>
            {effect.enabled && (
                <>
                    <div className="flex items-center gap-2">
                        <span className="font-['Jersey_10'] uppercase text-xs tracking-widest shrink-0 w-14">Colour:</span>
                        <ColorPicker
                            value={displayColor}
                            onChange={handleColorChange}
                            onOpen={() => {}}
                            className="w-6 h-6 shrink-0"
                            title="Water colour"
                        />
                        <span className="font-['Jersey_10'] text-xs text-coal/60 uppercase">{displayColor}</span>
                    </div>
                    <div className="flex items-center gap-2"
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        <span className="font-['Jersey_10'] uppercase text-xs tracking-widest shrink-0 w-14">Height:</span>
                        <input
                            type="range"
                            min={0}
                            max={100}
                            value={localHeight}
                            onChange={handleHeightChange}
                            className="flex-1 h-1 accent-coal cursor-pointer"
                            title={`Height: ${localHeight}% - how far water rises at the centre`}
                        />
                        <span className="font-['Jersey_10'] text-xs text-coal/60 w-7 text-right">{localHeight}%</span>
                    </div>
                </>
            )}
        </div>
    );
}

function LayersDropdown({
                            disabled,
                            layers,
                            onMove,
                            onMoveStep,
                            onToggleVisible,
                            onToggleLocked,
                            onRename,
                            onOpacityChange,
                            onEffectChange,
                            onRemoveEffect,
                            onPropertiesOpen,
                            selectedId,
                            selectedIds,
                            onSelectLayer,
                            alwaysOpen,
                        }) {

    const [open, setOpen] = useState(false);
    const idRef = useRef(Math.random().toString(36).slice(2));
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const renameInputRef = useRef(null);
    const [propertiesOpenId, setPropertiesOpenId] = useState(null);

    const startRename = (l) => {
        setRenamingId(l.id);
        setRenameValue(l.name || "");
        setTimeout(() => renameInputRef.current?.select(), 0);
    };

    const commitRename = (id) => {
        const trimmed = renameValue.trim();
        if (trimmed) onRename?.(id, trimmed);
        setRenamingId(null);
    };

    const cancelRename = () => setRenamingId(null);

    useEffect(() => {
        const onAny = (e) => {
            const id = e?.detail?.id;
            if (id && id !== idRef.current) setOpen(false);
        };
        window.addEventListener("habboimager:dropdown", onAny);
        return () => window.removeEventListener("habboimager:dropdown", onAny);
    }, []);

    const [dragOverId, setDragOverId] = useState(null);
    const dragIdRef = useRef(null);

    const canDrag = (l) => !l.permaLock && !l.locked;

    const stopDrag = () => {
        dragIdRef.current = null;
        setDragOverId(null);
    };

    const handleDrop = (overId) => {
        const dragId = dragIdRef.current;
        stopDrag();
        if (!dragId || !overId || dragId === overId) return;
        onMove?.(dragId, overId);
    };

    return (
        <div className={alwaysOpen ? "flex flex-col flex-1 min-h-0" : "relative"}>
            {!alwaysOpen && (
                <button
                    type="button"
                    disabled={disabled}
                    onClick={
                        disabled
                            ? undefined
                            : () =>
                                setOpen((s) => {
                                    const next = !s;
                                    if (next) window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: idRef.current } }));
                                    return next;
                                })
                    }
                    title="Layers"
                    className={[
                        "w-10 h-10 border border-black bg-offwhite/50",
                        "flex items-center justify-center hover:bg-gray-200",
                        disabled ? "opacity-40 cursor-not-allowed" : "",
                    ].join(" ")}
                >
                    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3 3 8l9 5 9-5-9-5Z" />
                        <path d="M3 12l9 5 9-5" />
                        <path d="M3 16l9 5 9-5" />
                    </svg>
                </button>
            )}

            {(alwaysOpen || (open && !disabled)) && (
                <div className={alwaysOpen ? "flex flex-col flex-1 min-h-0 border-t border-black/10" : "absolute right-0 mt-2 z-[200] border border-black bg-offwhite/90 shadow w-[300px]"}>
                    <div
                        className={alwaysOpen ? "flex-1 min-h-0 overflow-y-auto habbo-scroll" : "max-h-[700px] overflow-y-auto habbo-scroll"}
                        onDragLeave={() => setDragOverId(null)}
                        onDrop={(e) => {
                            e.preventDefault();
                            stopDrag();
                        }}
                    >
                        <div className={alwaysOpen ? "flex flex-col justify-end min-h-full" : ""}>
                        {layers.map((l, idx) => {
                            const eyeDisabled = !l.canHide;
                            const lockDisabled = !!l.permaLock;
                            const rowDisabled = !canDrag(l);

                            const isOver = dragOverId === l.id && dragIdRef.current && dragIdRef.current !== l.id;
                            const isSelected = Array.isArray(selectedIds) && selectedIds.length
                                ? selectedIds.includes(l.id)
                                : selectedId === l.id;

                            return (
                                <div
                                    key={l.id}
                                    className={[
                                        "relative flex flex-col border-b border-black/10 select-none px-2 py-2",
                                        isSelected ? "bg-yellow-200" : "",
                                        rowDisabled ? "opacity-70" : "cursor-grab active:cursor-grabbing",
                                    ].join(" ")}
                                    draggable={canDrag(l) && renamingId !== l.id && propertiesOpenId !== l.id}
                                    onClick={(e) => {
                                        if (rowDisabled || renamingId === l.id) return;
                                        onSelectLayer?.(l.id, e.shiftKey);
                                    }}
                                    onDragStart={(e) => {
                                        if (!canDrag(l) || renamingId === l.id) return;
                                        dragIdRef.current = l.id;
                                        e.dataTransfer.effectAllowed = "move";
                                        e.dataTransfer.setData("text/plain", l.id);
                                    }}
                                    onDragEnd={stopDrag}
                                    onDragOver={(e) => {
                                        if (!dragIdRef.current) return;
                                        e.preventDefault();
                                        setDragOverId(l.id);
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        handleDrop(l.id);
                                    }}
                                    title={canDrag(l) ? "Drag to reorder" : l.permaLock ? "" : "Layer locked"}
                                >
                                    {isOver && <div className="absolute left-0 right-0 -top-[1px] h-[2px] bg-yellow-500" />}

                                    {/* Main row: thumbnail + name + buttons */}
                                    <div className="flex items-center gap-2">

                                        {/* Thumbnail */}
                                        <div className="w-12 h-12 border border-black/40 bg-gray-200 overflow-hidden flex items-center justify-center shrink-0">
                                            {l.thumbBg ? (
                                                <div className="w-full h-full" style={{ backgroundColor: l.thumbBg.color, opacity: l.thumbBg.opacity }} />
                                            ) : l.thumbUrl ? (
                                                <img src={l.thumbUrl} alt="" className="block max-w-none max-h-none" style={l.thumbStyle} draggable={false} />
                                            ) : (
                                                <span className="text-[10px] opacity-50 font-['Jersey_10'] uppercase">Empty</span>
                                            )}
                                        </div>

                                        {/* Name + sublabel */}
                                        <div className="min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
                                            {renamingId === l.id ? (
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        ref={renameInputRef}
                                                        value={renameValue}
                                                        onChange={(e) => setRenameValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") commitRename(l.id);
                                                            else if (e.key === "Escape") cancelRename();
                                                        }}
                                                        className="flex-1 min-w-0 border border-black px-1.5 py-1 font-['Jersey_10'] uppercase text-sm bg-white"
                                                        autoFocus
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => commitRename(l.id)}
                                                        className="w-7 h-7 border border-black bg-offwhite hover:bg-green-100 flex items-center justify-center shrink-0"
                                                        title="Confirm rename"
                                                    >
                                                        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div
                                                    className="font-['Jersey_10'] uppercase text-sm tracking-wider leading-tight truncate cursor-text hover:underline decoration-dotted"
                                                    title="Click to rename"
                                                    onClick={() => startRename(l)}
                                                >
                                                    {l.name}
                                                </div>
                                            )}
                                            {l.subLabel && <div className="text-xs opacity-50 uppercase font-['Jersey_10'] truncate mt-0.5">{l.subLabel}</div>}
                                        </div>

                                        {/* Right controls */}
                                        <div className="flex items-center gap-1 shrink-0">

                                            {/* Up / Down */}
                                            <div className={[
                                                "flex flex-col gap-px",
                                                (
                                                    (rowDisabled || !layers[idx - 1] || !!layers[idx - 1]?.permaLock) &&
                                                    (rowDisabled || !layers[idx + 1] || !!layers[idx + 1]?.permaLock)
                                                ) ? "invisible" : "",
                                            ].join(" ")}>
                                                <button
                                                    type="button"
                                                    disabled={rowDisabled || !layers[idx - 1] || !!layers[idx - 1]?.permaLock}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => { e.stopPropagation(); onMoveStep?.(l.id, +1); }}
                                                    className={["w-7 h-[26px] border border-black text-xs flex items-center justify-center", rowDisabled || !layers[idx - 1] || !!layers[idx - 1]?.permaLock ? "bg-gray-100 text-black/30 cursor-not-allowed" : "bg-offwhite hover:bg-gray-200"].join(" ")}
                                                >▲</button>
                                                <button
                                                    type="button"
                                                    disabled={rowDisabled || !layers[idx + 1] || !!layers[idx + 1]?.permaLock}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => { e.stopPropagation(); onMoveStep?.(l.id, -1); }}
                                                    className={["w-7 h-[26px] border border-black text-xs flex items-center justify-center", rowDisabled || !layers[idx + 1] || !!layers[idx + 1]?.permaLock ? "bg-gray-100 text-black/30 cursor-not-allowed" : "bg-offwhite hover:bg-gray-200"].join(" ")}
                                                >▼</button>
                                            </div>

                                            {/* Eye + Lock + Properties stacked column */}
                                            <div className="flex flex-col gap-px">
                                                <div className="flex gap-px">
                                                    {/* Eye */}
                                                    <button
                                                        type="button"
                                                        draggable={false}
                                                        onDragStart={(e) => e.preventDefault()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => { e.stopPropagation(); if (!eyeDisabled) onToggleVisible?.(l.id); }}
                                                        className={["w-8 h-8 border border-black flex items-center justify-center", eyeDisabled ? "invisible" : l.visible ? "bg-offwhite hover:bg-gray-200" : "bg-gray-300 hover:bg-gray-200"].join(" ")}
                                                        title={eyeDisabled ? "" : l.visible ? "Hide layer" : "Show layer"}
                                                        disabled={eyeDisabled}
                                                    >
                                                        {l.visible ? (
                                                            <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                <path d="M1.5 12s4-7.5 10.5-7.5S22.5 12 22.5 12s-4 7.5-10.5 7.5S1.5 12 1.5 12Z" />
                                                                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                                            </svg>
                                                        ) : (
                                                            <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                <path d="M3 3l18 18" /><path d="M10.6 10.6A2.8 2.8 0 0 0 12 15a3 3 0 0 0 3-3c0-.5-.1-1-.3-1.4" />
                                                                <path d="M6.6 6.6C4 8.3 2.2 11 1.5 12c.7 1 4 7.5 10.5 7.5 1.8 0 3.4-.4 4.8-1" />
                                                                <path d="M9.2 4.9A10.5 10.5 0 0 1 12 4.5c6.5 0 9.8 6.5 10.5 7.5-.3.5-1.3 2.4-3.1 4.1" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                    {/* Lock */}
                                                    <button
                                                        type="button"
                                                        draggable={false}
                                                        onDragStart={(e) => e.preventDefault()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => { e.stopPropagation(); if (!lockDisabled) onToggleLocked?.(l.id); }}
                                                        className={["w-8 h-8 border border-black flex items-center justify-center", lockDisabled ? "invisible" : l.locked ? "bg-gray-300 hover:bg-gray-200" : "bg-offwhite hover:bg-gray-200"].join(" ")}
                                                        title={lockDisabled ? "" : l.locked ? "Unlock layer" : "Lock layer"}
                                                        disabled={lockDisabled}
                                                    >
                                                        {l.locked || l.permaLock ? (
                                                            <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                <path d="M7 11V8a5 5 0 0 1 10 0v3" /><path d="M6 11h12v10H6V11Z" />
                                                            </svg>
                                                        ) : (
                                                            <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                <path d="M7 11V8a5 5 0 0 1 9.5-2" /><path d="M6 11h12v10H6V11Z" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </div>
                                                {/* Properties - full-width thin button below eye+lock */}
                                                {!l.permaLock ? (
                                                    <button
                                                        type="button"
                                                        draggable={false}
                                                        onDragStart={(e) => e.preventDefault()}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const opening = propertiesOpenId !== l.id;
                                                            setPropertiesOpenId(opening ? l.id : null);
                                                            if (opening) onPropertiesOpen?.(l.id);
                                                        }}
                                                        className={["w-full h-5 border border-black flex items-center justify-center gap-1 font-['Jersey_10'] uppercase text-[11px] tracking-wider", propertiesOpenId === l.id ? "bg-yellow-200 hover:bg-yellow-300" : "bg-offwhite hover:bg-gray-200"].join(" ")}
                                                        title="Layer Effects"
                                                    >
                                                        <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                                            <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
                                                            <circle cx="9" cy="6" r="2" fill="currentColor" stroke="none" /><circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" /><circle cx="9" cy="18" r="2" fill="currentColor" stroke="none" />
                                                        </svg>
                                                        Effects
                                                    </button>
                                                ) : (
                                                    <div className="w-full h-5" />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Layer Properties panel */}
                                    {propertiesOpenId === l.id && !l.permaLock && (
                                        <div
                                            className="mt-2 px-2 py-2 bg-white border border-black/20 flex flex-col gap-2"
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            draggable={false}
                                            onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        >
                                            {/* Opacity */}
                                            <div className="flex items-center gap-2">
                                                <span className="font-['Jersey_10'] uppercase text-xs tracking-widest shrink-0 w-14">Opacity:</span>
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    value={typeof l.opacity === "number" ? l.opacity : 100}
                                                    draggable={false}
                                                    onDragStart={(e) => e.preventDefault()}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onChange={(e) => onOpacityChange?.(l.id, Number(e.target.value))}
                                                    className="flex-1 accent-gold"
                                                />
                                                <span className="font-['Jersey_10'] text-sm w-9 text-right shrink-0">{typeof l.opacity === "number" ? l.opacity : 100}%</span>
                                            </div>
                                            {/* Active effects - only shown when the effect has been added to this layer */}
                                            {l.effects?.outline && (
                                                <>
                                                    <div className="border-t border-black/10" />
                                                    <OutlineEffectRow
                                                        layerId={l.id}
                                                        effect={l.effects.outline}
                                                        onEffectChange={onEffectChange}
                                                        onRemoveEffect={onRemoveEffect}
                                                    />
                                                </>
                                            )}
                                            {l.effects?.water && (
                                                <>
                                                    <div className="border-t border-black/10" />
                                                    <WaterEffectRow
                                                        layerId={l.id}
                                                        effect={l.effects.water}
                                                        onEffectChange={onEffectChange}
                                                        onRemoveEffect={onRemoveEffect}
                                                        isFurni={l.kind === 'furni'}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {!layers.length && <div className="px-3 py-3 text-xs opacity-70">No layers</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PreviousScenesPanel({ scenes, activeSceneId, onLoad, onRemove, className = "" }) {
    return (
        <div className={["w-full h-[400px] overflow-y-auto habbo-scroll", className].join(" ")}>
            <div className="text-sm font-['Jersey_10'] uppercase mb-2">
                Previous Images
            </div>

            <div className="flex flex-col gap-2">
                {scenes.map((s) => {
                    const selected = s.id === activeSceneId;
                    const imageName = s.imageName || "(Untitled)";

                    return (
                        <div key={s.id} className="flex gap-2 items-center">
                            <button
                                type="button"
                                onClick={() => onLoad(s)}
                                className={[
                                    "flex-1 px-3 py-2 border text-left font-['Jersey_10'] text-sm uppercase truncate",
                                    selected ? "border-gray-800 bg-gold" : "border-gray-300 bg-gray-100 hover:bg-gray-200",
                                ].join(" ")}
                                title={imageName}
                            >
                                {imageName}
                            </button>

                            <button
                                type="button"
                                title="Delete image"
                                className="mr-2 w-8 h-8 bg-red-500 text-white border border-black flex items-center justify-center text-sm leading-none hover:bg-red-400 font-['Jersey_10']"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onRemove(s.id);
                                }}
                            >
                                ×
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


const clampSpriteScale = (value) => {
    const n = Number(value);
    return n === 2 || n === 4 ? n : 1;
};

const HABBO_PIXEL = 1;

const spriteWrapperTransform = (pos, sourceSize, scale, crop, rotation) => {
    const sc = clampSpriteScale(scale);
    const sw = (sourceSize?.w || 0) * sc;
    const sh = (sourceSize?.h || 0) * sc;
    const cl = (crop?.left || 0) * sc;
    const ct = (crop?.top  || 0) * sc;
    const tx = pos.x - Math.round(sw / 2) + cl;
    const ty = pos.y - Math.round(sh / 2) + ct;
    const rot = rotation ? ` rotate(${rotation}deg)` : '';
    return `translate(${tx}px, ${ty}px)${rot}`;
};

const normaliseSpriteCrop = (crop, width, height) => {
    const w = Math.max(1, Math.round(Number(width) || 0));
    const h = Math.max(1, Math.round(Number(height) || 0));

    const left = Math.max(0, Math.min(w - 1, Math.round(Number(crop?.left) || 0)));
    const top = Math.max(0, Math.min(h - 1, Math.round(Number(crop?.top) || 0)));
    const maxRight = Math.max(0, w - left - 1);
    const right = Math.max(0, Math.min(maxRight, Math.round(Number(crop?.right) || 0)));
    const maxBottom = Math.max(0, h - top - 1);
    const bottom = Math.max(0, Math.min(maxBottom, Math.round(Number(crop?.bottom) || 0)));

    return { left, top, right, bottom };
};

const getSpriteFrameMetrics = (sourceSize, scale = 1, crop = null) => {
    const width = Math.max(0, Math.round(Number(sourceSize?.w) || 0));
    const height = Math.max(0, Math.round(Number(sourceSize?.h) || 0));
    const safeScale = clampSpriteScale(scale);

    if (!width || !height) {
        return {
            sourceW: width,
            sourceH: height,
            scale: safeScale,
            crop: { left: 0, top: 0, right: 0, bottom: 0 },
            frameW: 0,
            frameH: 0,
            drawW: 0,
            drawH: 0,
            offsetX: 0,
            offsetY: 0,
        };
    }

    const safeCrop = normaliseSpriteCrop(crop, width, height);
    const drawW = Math.max(1, width - safeCrop.left - safeCrop.right);
    const drawH = Math.max(1, height - safeCrop.top - safeCrop.bottom);

    return {
        sourceW: width,
        sourceH: height,
        scale: safeScale,
        crop: safeCrop,
        frameW: drawW * safeScale,
        frameH: drawH * safeScale,
        drawW,
        drawH,
        offsetX: safeCrop.left * safeScale,
        offsetY: safeCrop.top * safeScale,
    };
};

function SpriteActionBar({ visible, cropMode, scaleValue, onDuplicate, onScale, onCropToggle, showCrop = true, flipXActive, flipYActive, onFlipX, onFlipY, rotationValue, onRotation, onRotationBlur, onRemove, multiSelect = false }) {
    if (!visible) return null;
    const tDisabled = multiSelect;

    const handleRotationKeyDown = (e) => {
        if (e.key === "ArrowUp") {
            e.preventDefault();
            onRotation?.((rotationValue || 0) + (e.shiftKey ? 45 : 1));
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            onRotation?.((rotationValue || 0) - (e.shiftKey ? 45 : 1));
        }
    };

    return (
        <div
            className="absolute bottom-3 left-1/2 z-30 flex flex-wrap justify-center gap-2 -translate-x-1/2 w-full max-w-[calc(100%-32px)] no-capture"
            data-capture-hide="1"
        >
            <button
                type="button"
                onClick={onDuplicate}
                className="h-10 border border-black bg-offwhite/90 px-4 font-['Jersey_10'] text-sm uppercase tracking-widest hover:bg-gray-200 shrink-0"
                title="Duplicate selected sprite"
            >
                Duplicate
            </button>

            <div className={["flex items-center gap-1 border border-black bg-offwhite/90 px-2 py-1 shrink-0", tDisabled ? "opacity-40" : ""].join(" ")} title={tDisabled ? "Scale is disabled while multiple sprites are selected" : undefined}>
                <span className="px-1 font-['Jersey_10'] text-sm uppercase tracking-widest opacity-70">Scale</span>
                {[1, 2, 4].map((value) => (
                    <button
                        key={value}
                        type="button"
                        disabled={tDisabled}
                        onClick={() => !tDisabled && onScale(value)}
                        className={[
                            "h-8 min-w-[48px] border border-black px-2 font-['Jersey_10'] text-sm uppercase tracking-widest",
                            tDisabled ? "cursor-not-allowed bg-white" : (scaleValue === value ? "bg-gold" : "bg-white hover:bg-gray-200"),
                        ].join(" ")}
                        title={`Scale selected sprite to ${value}x`}
                    >
                        {value}x
                    </button>
                ))}
            </div>

            <div className={["flex items-center gap-1 border border-black bg-offwhite/90 px-2 py-1 shrink-0", tDisabled ? "opacity-40" : ""].join(" ")} title={tDisabled ? "Rotation is disabled while multiple sprites are selected" : undefined}>
                <span className="px-1 font-['Jersey_10'] text-sm uppercase tracking-widest opacity-70">Rotate</span>
                <input
                    type="number"
                    value={rotationValue || 0}
                    disabled={tDisabled}
                    onChange={(e) => onRotation?.(Number(e.target.value) || 0)}
                    onKeyDown={handleRotationKeyDown}
                    onBlur={onRotationBlur}
                    className={["w-14 h-8 border border-black px-1 font-['Jersey_10'] text-sm text-center bg-white", tDisabled ? "cursor-not-allowed" : ""].join(" ")}
                    step="1"
                    title="Rotation in degrees. Arrow keys ±1°, Shift+Arrow ±45°"
                />
                <span className="font-['Jersey_10'] text-sm opacity-50">°</span>
            </div>

            <div className={["flex items-center gap-1 border border-black bg-offwhite/90 px-2 py-1 shrink-0", tDisabled ? "opacity-40" : ""].join(" ")} title={tDisabled ? "Flip is disabled while multiple sprites are selected" : undefined}>
                <span className="px-1 font-['Jersey_10'] text-sm uppercase tracking-widest opacity-70">Flip</span>
                <button
                    type="button"
                    disabled={tDisabled}
                    onClick={() => !tDisabled && onFlipX?.()}
                    className={[
                        "h-8 min-w-[36px] border border-black px-2 font-['Jersey_10'] text-sm uppercase tracking-widest",
                        tDisabled ? "cursor-not-allowed bg-white" : (flipXActive ? "bg-gold" : "bg-white hover:bg-gray-200"),
                    ].join(" ")}
                    title="Flip horizontally"
                >
                    X
                </button>
                <button
                    type="button"
                    disabled={tDisabled}
                    onClick={() => !tDisabled && onFlipY?.()}
                    className={[
                        "h-8 min-w-[36px] border border-black px-2 font-['Jersey_10'] text-sm uppercase tracking-widest",
                        tDisabled ? "cursor-not-allowed bg-white" : (flipYActive ? "bg-gold" : "bg-white hover:bg-gray-200"),
                    ].join(" ")}
                    title="Flip vertically"
                >
                    Y
                </button>
            </div>

            {showCrop && (
                <button
                    type="button"
                    disabled={tDisabled}
                    onClick={() => !tDisabled && onCropToggle?.()}
                    className={[
                        "h-10 border border-black px-4 font-['Jersey_10'] text-sm uppercase tracking-widest shrink-0",
                        tDisabled ? "opacity-40 cursor-not-allowed bg-offwhite/90" : (cropMode ? "bg-gold" : "bg-offwhite/90 hover:bg-gray-200"),
                    ].join(" ")}
                    title={tDisabled ? "Crop is disabled while multiple sprites are selected" : (cropMode ? "Finish crop" : "Crop selected sprite")}
                >
                    {cropMode ? "Finish Crop" : "Crop"}
                </button>
            )}

            <button
                type="button"
                onClick={onRemove}
                className="flex items-center gap-1.5 h-10 border border-black bg-red-500 text-white px-4 font-['Jersey_10'] text-sm uppercase tracking-widest hover:bg-red-600 shrink-0"
                title="Remove selected sprite"
            >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 6h18" />
                    <path d="M8 6V4h8v2" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                </svg>
                Remove
            </button>
        </div>
    );
}

// ─── Unified Sprite Helpers ───

function _habboThumbUrl(snap) {
    const n = (snap?.habboName || '').trim();
    const h = (snap?.hotel || 'COM').toUpperCase();
    const fig = snap?.figureString || null;
    return getHabboImageUrl({
        habbo: fig ? null : n,
        figure: fig || null,
        hotel: h,
        direction: 2,
        head_direction: 2,
        action: 'std',
        gesture: 'std',
        size: 'b',
        shadow: true,
        crr: null,
        custom: null,
    });
}

function spriteToLayer(s, furniItemsByIdRef) {
    const kindMap = { furni: 'furni', habbo: 'habbo', customImage: 'custom_image', text: 'text' };
    const idPrefixMap = { furni: 'furni', habbo: 'habbo', customImage: 'custom_image', text: 'text' };
    const kind = kindMap[s.type];
    const layerId = `${idPrefixMap[s.type]}:${s.id}`;

    let name, subLabel, thumbUrl, thumbStyle;
    if (s.type === 'furni') {
        const meta = (furniItemsByIdRef.current && furniItemsByIdRef.current.get(s.furniId)) || {};
        name = s.layerName || meta.name || s.name || 'Furni';
        subLabel = 'Furni';
        thumbUrl = s.url || meta.url || meta.thumbUrl || '';
        thumbStyle = { imageRendering: 'pixelated' };
    } else if (s.type === 'habbo') {
        name = s.layerName || ((s.snap?.habboName || 'Habbo') + '').trim();
        subLabel = 'Habbo';
        thumbUrl = _habboThumbUrl(s.snap);
        thumbStyle = { imageRendering: 'pixelated' };
    } else if (s.type === 'customImage') {
        name = s.layerName || 'Custom Image';
        subLabel = 'Image';
        thumbUrl = s.url;
        thumbStyle = {};
    } else {
        name = s.layerName || 'Text';
        subLabel = String(s.text || '').trim() || 'Text';
        thumbUrl = '';
        thumbStyle = {};
    }

    return {
        id: layerId,
        kind,
        name,
        subLabel,
        thumbUrl,
        thumbStyle,
        permaLock: null,
        canHide: true,
        visible: s.visible !== false,
        locked: !!s.locked,
        opacity: s.opacity ?? 100,
        effects: s.effects || {},
    };
}

function buildRoomTileGrid(size, cfg = {}) {
    const canvasW = Math.max(1, Math.round(Number(size?.w) || 0));
    const canvasH = Math.max(1, Math.round(Number(size?.h) || 0));

    const tileW = Math.max(1, Math.round(Number(cfg.tileW) || 64));
    const tileH = Math.max(1, Math.round(Number(cfg.tileH) || 41));
    const rowStepY = Math.max(1, Math.round(Number(cfg.rowStepY) || 16));

    const startYRatio = Number.isFinite(Number(cfg.startYRatio)) ? Number(cfg.startYRatio) : 0.35;
    const offsetX = Math.round(Number(cfg.offsetX) || 0);
    const offsetY = Math.round(Number(cfg.offsetY) || 0);

    const originX = Math.round(canvasW / 2 - tileW / 2) + offsetX;
    const originY = Math.round(canvasH * startYRatio) + offsetY;
    const staggerX = Math.round(tileW / 2);

    const cols = Math.ceil((canvasW + tileW * 4) / tileW);
    const rowsUp = Math.ceil((originY + tileH * 2) / rowStepY);
    const rowsDown = Math.ceil((canvasH - originY + tileH * 2) / rowStepY);

    const out = [];
    let index = 0;

    for (let gy = -rowsUp; gy <= rowsDown; gy += 1) {
        const y = originY + gy * rowStepY;
        const rowShiftX = Math.abs(gy % 2) ? staggerX : 0;

        for (let gx = -cols; gx <= cols; gx += 1) {
            const x = originX + gx * tileW - rowShiftX;

            if (x > canvasW + tileW || x + tileW < -tileW || y > canvasH + tileH || y + tileH < -tileH) continue;

            out.push({
                key: `${gx}:${gy}`,
                index,
                gx,
                gy,
                row: gy,
                col: gx,
                x,
                y,
                w: tileW,
                h: tileH,
            });

            index += 1;
        }
    }

    return out.sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y;
        return a.x - b.x;
    });
}

function drawRoomTileDragHighlight(ctx, t, targetVis, diamondH) {
    const hw = t.w / 2;
    const hh = diamondH / 2;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(t.x + hw, t.y);          // top
    ctx.lineTo(t.x + t.w, t.y + hh);   // right
    ctx.lineTo(t.x + hw, t.y + diamondH); // bottom
    ctx.lineTo(t.x, t.y + hh);          // left
    ctx.closePath();
    ctx.clip();
    ctx.fillStyle = targetVis ? "rgba(50,210,70,0.6)" : "rgba(210,55,55,0.6)";
    ctx.fillRect(t.x, t.y, t.w, diamondH);
    ctx.restore();
}

function hitTestRoomTile(px, py, tiles, tileW, tileH) {
    const hw = tileW / 2;
    const hh = tileH / 2;
    for (let i = tiles.length - 1; i >= 0; i--) {
        const t = tiles[i];
        const dx = Math.abs(px - (t.x + hw)) / hw;
        const dy = Math.abs(py - (t.y + hh)) / hh;
        if (dx + dy <= 1.0) return t;
    }
    return null;
}

const roomTileImageCache = new Map();
const roomTileRasterCache = new Map();

function loadRoomTileImage(src) {
    if (!src) return Promise.resolve(null);
    if (roomTileImageCache.has(src)) return roomTileImageCache.get(src);

    const p = new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });

    roomTileImageCache.set(src, p);
    return p;
}

async function buildRoomTileRasterUrl(size, cfg = {}) {
    const canvasW = Math.max(1, Math.round(Number(size?.w) || 0));
    const canvasH = Math.max(1, Math.round(Number(size?.h) || 0));
    const key = JSON.stringify({
        w: canvasW,
        h: canvasH,
        url: cfg.url || "",
        tileW: Number(cfg.tileW) || 64,
        tileH: Number(cfg.tileH) || 41,
        rowStepY: Number(cfg.rowStepY) || 16,
        startYRatio: Number(cfg.startYRatio) || 0.35,
        offsetX: Number(cfg.offsetX) || 0,
        offsetY: Number(cfg.offsetY) || 0,
    });

    if (roomTileRasterCache.has(key)) return roomTileRasterCache.get(key);

    const img = await loadRoomTileImage(cfg.url);
    if (!img) return null;

    const tiles = buildRoomTileGrid({ w: canvasW, h: canvasH }, cfg);
    const canvas = document.createElement("canvas");
    canvas.width = canvasW;
    canvas.height = canvasH;

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;

    for (let i = 0; i < tiles.length; i += 1) {
        const t = tiles[i];
        ctx.drawImage(img, t.x, t.y, t.w, t.h);
    }

    const url = canvas.toDataURL("image/png");
    roomTileRasterCache.set(key, url);
    return url;
}

async function drawRoomTileBackgroundToCtx(ctx, size, cfg = {}) {
    const img = await loadRoomTileImage(cfg.url);
    if (!img) return;

    const tiles = buildRoomTileGrid(size, cfg);
    for (let i = 0; i < tiles.length; i += 1) {
        const t = tiles[i];
        ctx.drawImage(img, t.x, t.y, t.w, t.h);
    }
}

function deriveBgLayers(bg, bgKey, bgLayerVis) {
    const layeredOrder = ['bg', 'bg_1', 'bg_2', 'bg_3'];
    const layeredTop = bg.type === 'layered' && bg.topLayerEnabled ? bg.topLayer : null;
    const layeredTopKey = typeof layeredTop === 'string' ? layeredTop : (layeredTop && typeof layeredTop === 'object' ? layeredTop.key : null);
    const layeredTopAllowHide = layeredTop && typeof layeredTop === 'object' ? !!layeredTop.allowHide : false;

    const bgTopKey =
        bg.type === 'layered' && bg.topLayerEnabled
            ? (layeredTopKey && bg.layers?.[layeredTopKey] ? layeredTopKey : layeredOrder.slice().reverse().find((k) => bg.layers?.[k]))
            : null;

    const bgTopCanHide = layeredTopAllowHide && !!layeredTopKey && bgTopKey === layeredTopKey;

    if (bg.type === 'layered') {
        return layeredOrder
            .filter((k) => !!bg.layers?.[k])
            .map((k) => {
                const isBottom = k === 'bg';
                const isTop = !!bgTopKey && k === bgTopKey;
                const numMatch = /^bg_(\d+)$/.exec(k);
                const num = numMatch ? Number(numMatch[1]) : null;
                const name = isBottom ? 'Background' : isTop ? 'Top Layer' : num ? `Scene Prop ${num}` : 'Scene Prop';
                const permaLock = isBottom ? 'bottom' : isTop ? 'top' : null;
                const explicitlyNoHide = Array.isArray(bg.rules?.noHide) && bg.rules.noHide.includes(k);
                const layerId = `bg:${k}`;
                const hiddenByUser = bgLayerVis && layerId in bgLayerVis ? bgLayerVis[layerId] === false : false;
                return {
                    id: layerId,
                    kind: 'bg',
                    name,
                    subLabel: 'Background',
                    thumbUrl: bg.layers?.[k] || '',
                    thumbStyle: { imageRendering: 'pixelated' },
                    permaLock,
                    canHide: !isBottom && (!isTop || bgTopCanHide) && !explicitlyNoHide,
                    visible: !hiddenByUser,
                    locked: !!permaLock,
                    opacity: 100,
                    effects: {},
                };
            });
    }

    const layerId = `bg:${bg.key || bgKey}`;
    const hiddenByUser = bgLayerVis && layerId in bgLayerVis ? bgLayerVis[layerId] === false : false;
    return [{
        id: layerId,
        kind: 'bg',
        name: 'Background',
        subLabel: 'Background',
        thumbUrl: bg.url || '',
        thumbStyle: { imageRendering: 'pixelated' },
        permaLock: 'bottom',
        canHide: false,
        visible: !hiddenByUser,
        locked: true,
        opacity: 100,
        effects: {},
    }];
}

function migrateSceneToSprites(sceneObj, savedLayers) {
    const layerMap = new Map((savedLayers || []).map((l) => [l.id, l]));

    const layerMeta = (layerId) => {
        const l = layerMap.get(layerId) || {};
        return {
            visible: l.visible !== false,
            locked: !!l.locked,
            opacity: l.opacity ?? 100,
            layerName: l.name || '',
            effects: l.effects || {},
        };
    };

    const habboSprites = (sceneObj.sceneSprites || []).map((sp) => ({
        type: 'habbo',
        ...sp,
        ...layerMeta(`habbo:${sp.id}`),
    }));

    const furniSpritesList = (sceneObj.furniSprites || []).map((sp) => ({
        type: 'furni',
        ...sp,
        ...layerMeta(`furni:${sp.id}`),
    }));

    const customImageList = (sceneObj.customImages || []).map((img) => ({
        type: 'customImage',
        ...img,
        ...layerMeta(`custom_image:${img.id}`),
    }));

    const textList = (sceneObj.textSprites || []).map((t) => ({
        type: 'text',
        ...t,
        ...layerMeta(`text:${t.id}`),
    }));

    const userSprites = [...habboSprites, ...furniSpritesList, ...customImageList, ...textList];

    if (!savedLayers || !savedLayers.length) return userSprites;

    const ORDER_PREFIXES = ['habbo:', 'furni:', 'custom_image:', 'text:'];
    const posMap = new Map();
    savedLayers.forEach((l, i) => {
        if (ORDER_PREFIXES.some((p) => l.id.startsWith(p))) posMap.set(l.id, i);
    });

    const spriteLayerId = (s) => {
        const prefixMap = { habbo: 'habbo', furni: 'furni', customImage: 'custom_image', text: 'text' };
        return `${prefixMap[s.type]}:${s.id}`;
    };

    return [...userSprites].sort((a, b) => {
        const ia = posMap.get(spriteLayerId(a)) ?? 9999;
        const ib = posMap.get(spriteLayerId(b)) ?? 9999;
        return ia - ib;
    });
}

// ─── Main Component ───
const PROIMAGER_VERSION = "BETA_2_2_1";

const HYBRID_DEFAULTS = {
    header: "Frankly Origins ProImager: Frankly Staff",
    icon: "/img/icons/icon_343.png",
    storageKey: "proimager_meta_v1",
    scenesKey: "proimager_scenes_v3",
    activeSceneKey: "proimager_active_scene_id_v3",
    overrideUserId: null,
    disableUpload: false,
    hiddenAssetCategories: [],
    presets: null, // null = use IMAGE_PRESETS default
    containerClassName: "select-none mt-10 min-w-[940px]",
};

function applyOutlineEffect(imgEl, color, tolerance = 0) {
    const w = imgEl.naturalWidth || imgEl.width || 0;
    const h = imgEl.naturalHeight || imgEl.height || 0;
    if (!w || !h) return null;
    const hex = /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#000000';
    const r2 = parseInt(hex.slice(1, 3), 16);
    const g2 = parseInt(hex.slice(3, 5), 16);
    const b2 = parseInt(hex.slice(5, 7), 16);
    const t = Math.max(0, Math.min(255, tolerance));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgEl, 0, 0);
    const imageData = ctx.getImageData(0, 0, w, h);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) continue;
        if (d[i] <= t && d[i + 1] <= t && d[i + 2] <= t) {
            d[i] = r2; d[i + 1] = g2; d[i + 2] = b2;
        } else {
            d[i + 3] = 0;
        }
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
}

function applyWaterEffect(imgEl, color, heightPercent = 20, kind = 'habbo', curveMode = 'adaptive') {
    const w = imgEl.naturalWidth || imgEl.width || 0;
    const h = imgEl.naturalHeight || imgEl.height || 0;
    if (!w || !h) return null;
    const hex = /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#0a6e78';
    const wr = parseInt(hex.slice(1, 3), 16);
    const wg = parseInt(hex.slice(3, 5), 16);
    const wb = parseInt(hex.slice(5, 7), 16);
    // Ripple colour: water colour lightened 45% toward white
    const rr = Math.round(wr + (255 - wr) * 0.45);
    const rg = Math.round(wg + (255 - wg) * 0.45);
    const rb = Math.round(wb + (255 - wb) * 0.45);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgEl, 0, 0);
    const imageData = ctx.getImageData(0, 0, w, h);
    const d = imageData.data;
    const base = Math.max(0, Math.min(100, heightPercent)) / 100;
    const useFurniAdaptive = kind === 'furni' && curveMode !== 'parabola';
    // Pass 1: per-column water top row and first opaque pixel (surface) in the water zone.
    const waterTopRows = new Int32Array(w);
    const surfaceY = new Int32Array(w).fill(-1);
    if (useFurniAdaptive) {
        const cx = (w - 1) / 2;
        const maxLift = w * Math.tan(Math.PI / 6) / 4; // ≈ w * 0.144
        // t: 0 = pure parabola/U (narrow), 1 = pure linear V (wide)
        const t = h > 0 ? Math.max(0, Math.min(1, (w / h - 0.5) / 1.5)) : 0;
        for (let x = 0; x < w; x++) {
            const nx = cx > 0 ? Math.abs(x - cx) / cx : 0; // 0 = centre, 1 = edge
            const liftFrac = nx * nx * (1 - t) + nx * t;
            waterTopRows[x] = Math.max(0, Math.round(h * (1 - base)) - Math.round(maxLift * liftFrac));
            for (let y = waterTopRows[x]; y < h; y++) {
                if (d[(y * w + x) * 4 + 3] > 0) { surfaceY[x] = y; break; }
            }
        }
    } else {
        const maxLift = Math.max(2, h * 0.10 * Math.min(1, h / w));
        for (let x = 0; x < w; x++) {
            const nx = w > 1 ? (2 * x / (w - 1)) - 1 : 0; // -1 to 1
            waterTopRows[x] = Math.max(0, Math.round(h * (1 - base)) - Math.round(maxLift * nx * nx));
            for (let y = waterTopRows[x]; y < h; y++) {
                if (d[(y * w + x) * 4 + 3] > 0) { surfaceY[x] = y; break; }
            }
        }
    }
    // Pass 2: apply ripple + water overlay
    for (let x = 0; x < w; x++) {
        if (surfaceY[x] === -1) continue;
        for (let y = waterTopRows[x]; y < h; y++) {
            const idx = (y * w + x) * 4;
            if (d[idx + 3] === 0) continue;
            if (y === surfaceY[x]) {
                // Ripple: bright line at the water surface
                d[idx] = rr; d[idx + 1] = rg; d[idx + 2] = rb;
            } else {
                // Water fill: blend 68% toward dark water colour
                const oR = d[idx], oG = d[idx + 1], oB = d[idx + 2];
                d[idx]     = Math.round(oR * 0.32 + wr * 0.68);
                d[idx + 1] = Math.round(oG * 0.32 + wg * 0.68);
                d[idx + 2] = Math.round(oB * 0.32 + wb * 0.68);
            }
        }
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
}

function HabboImager({ userData, config = {} }) {
    const resolvedConfig = { ...HYBRID_DEFAULTS, ...config, header: `${(config?.header ?? HYBRID_DEFAULTS.header)} [${PROIMAGER_VERSION}]` };
    const isStaffUpdatesPage = !resolvedConfig.disableUpload;

    const DEFAULT_PLACEHOLDER_HABBO = "jejsjdbsbsjwieirjejsndbfhs";

    // ─── Habbo Identity & Pose ───
    const [habboNameInput, setHabboNameInput] = useState("");
    const [habboName, setHabboName] = useState(DEFAULT_PLACEHOLDER_HABBO);
    const [hotel, setHotel] = useState("COM");
    const [figureString, setFigureString] = useState(null);

    const lookupAbortRef = useRef(null);

    const [direction, setDirection] = useState(2);
    const [headDirection, setHeadDirection] = useState(2);

    const [action, setAction] = useState("std");
    const [gesture, setGesture] = useState("std");
    const [size, setSize] = useState("b");

    // ─── Shadow & Effects ───
    const [shadow, setShadow] = useState(true);
    const shadowUserSetRef = useRef(false);
    const shadowSuppressedByPoseRef = useRef(false);

    const [frankify, setFrankify] = useState(false);
    const [useFrankHatForDefault, setUseFrankHatForDefault] = useState(false);

    // ─── Active Sprite Properties ───
    const [crr, setCrr] = useState("");

    // ─── Zoom & Camera ───
    const [zoom, setZoom] = useState(1);
    const [, setShowZoomNotice] = useState(false);
    const zoomNoticeTimerRef = useRef(null);

    const [tileMode, setTileMode] = useState(false);
    const [tileSize, setTileSize] = useState(500);
    const tileModeRef = useRef(false);
    const tileSizeRef = useRef(500);
    tileModeRef.current = tileMode;
    tileSizeRef.current = tileSize;
    const preTileModeSize = useRef(null);
    const isLoadingSceneRef = useRef(false);
    const [showTilePreview, setShowTilePreview] = useState(false);
    const [tilePreviewUrl, setTilePreviewUrl] = useState(null);
    const tilePreviewDebounceRef = useRef(null);

    const [previewBg, setPreviewBg] = useState("default");
    const [showTransparency, setShowTransparency] = useState(false);
    const [gridTint, setGridTint] = useState("");
    const [whiteBgTint, setWhiteBgTint] = useState("");
    const [roomTileBgColor, setRoomTileBgColor] = useState("#000000");
    const [imageBorderEnabled, setImageBorderEnabled] = useState(false);
    const [imageBorderColor, setImageBorderColor] = useState("#ffffff");
    const [imageBorderWidth, setImageBorderWidth] = useState(5);
    const [roomTileEditMode, setRoomTileEditMode] = useState(false);
    const [roomTileHoverKey, setRoomTileHoverKey] = useState(null);
    const roomTileHoverImgSize = { w: 66, h: 33 };
    const roomTileHoverRafRef = useRef(null);
    const roomTileHoverPosRef = useRef({ x: 0, y: 0 });
    const roomTileDragRef = useRef({ active: false, targetVis: false, visited: null, changes: {} });
    const roomTileOverlayCanvasRef = useRef(null);
    const [showMoodlight, setShowMoodlight] = useState(false);
    const [showImageBorderPanel, setShowImageBorderPanel] = useState(false);
    const [moodlightColor, setMoodlightColor] = useState("#7c3aed");
    const [moodlightOpacity, setMoodlightOpacity] = useState(0.5);
    const [activeSpriteScale, setActiveSpriteScale] = useState(1);
    const [activeSpriteCrop, setActiveSpriteCrop] = useState(null);
    const [activeSpriteSourceSize, setActiveSpriteSourceSize] = useState({ w: 0, h: 0 });
    const [activeSpriteFlipX, setActiveSpriteFlipX] = useState(false);
    const [activeSpriteFlipY, setActiveSpriteFlipY] = useState(false);
    const [activeSpriteRotation, setActiveSpriteRotation] = useState(0);

    // ─── Scene & Sprite State ───
    const [spriteSelected, setSpriteSelected] = useState(false);
    const [spritePos, setSpritePos] = useState({ x: 0, y: 0 });

    const [activeFurniId, setActiveFurniId] = useState(null);
    const [activeLayerId, setActiveLayerId] = useState(null);
    const [activeSceneHabboId, setActiveSceneHabboId] = useState(null);

    // ─── Multi-selection (Shift+click) ───
    const [selectedLayerIds, setSelectedLayerIds] = useState([]);
    const selectedLayerIdsRef = useRef([]);
    useEffect(() => { selectedLayerIdsRef.current = selectedLayerIds; }, [selectedLayerIds]);
    const groupDragRef = useRef(null);
    const multiSelectActive = selectedLayerIds.length > 1;

    useEffect(() => {
        setSelectedLayerIds((prev) => {
            if (!activeLayerId) return prev.length ? [] : prev;
            if (prev.includes(activeLayerId)) return prev;
            return [activeLayerId];
        });
    }, [activeLayerId]);

    const [sprites, setSprites] = useState([]);

    useEffect(() => {
        setSelectedLayerIds((prev) => {
            if (prev.length <= 1) return prev;
            const prefix = { furni: "furni", habbo: "habbo", customImage: "custom_image", text: "text" };
            const valid = new Set(sprites.map((s) => `${prefix[s.type] || s.type}:${s.id}`));
            const next = prev.filter((lid) => lid.startsWith("bg:") || lid === "habbo:__active__" || valid.has(lid));
            return next.length === prev.length ? prev : next;
        });
    }, [sprites]);

    const [bgLayerVis, setBgLayerVis] = useState({});  // Record<layerId, boolean>
    const bgLayerVisRef = useRef({});
    const [midLayerOrder, setMidLayerOrder] = useState([]); // ordered IDs for all mid (non-permaLocked) layers
    bgLayerVisRef.current = bgLayerVis;
    const [moodlightVisible, setMoodlightVisible] = useState(false);

    const editingSceneHabboIdRef = useRef(null);
    const [editingSceneHabboId, setEditingSceneHabboId] = useState(null);

    const FORCE_SAVE_COOLDOWN_MS = 1000 * 30;
    const forceSaveKeyRef = useRef("");

    const [forceSaveNowTs, setForceSaveNowTs] = useState(() => Date.now());

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [showOpenDropdown, setShowOpenDropdown] = useState(false);
    const [showAddHabboPanel, setShowAddHabboPanel] = useState(false);
    const [newHabboSize, setNewHabboSize] = useState("b");
    const [contextMenu, setContextMenu] = useState(null);
    const [cropMode, setCropMode] = useState(false);
    const [snapGuides, setSnapGuides] = useState({ x: null, y: null });
    const cropDragRef = useRef(null);
    const [iconRowsLoaded, setIconRowsLoaded] = useState(false);
    const [iconLoadEpoch, setIconLoadEpoch] = useState(0);
    const iconRowsLoadKeyRef = useRef(null);
    const beginIconLoad = () => { setIconRowsLoaded(false); setIconLoadEpoch((e) => e + 1); };
    const iconUrlParamsRef = useRef(null);
    const zoomScrollRef = useRef(null);

    const toggleFullscreen = async () => {
        try {
            if (!isFullscreen) {
                await workspaceRef.current?.requestFullscreen?.();
                setIsFullscreen(true);
            } else {
                await document.exitFullscreen?.();
                setIsFullscreen(false);
            }
        } catch (error) {
            console.error("Fullscreen error:", error);
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }, []);

    useEffect(() => {
        if (!contextMenu) return undefined;
        const closeMenu = () => setContextMenu(null);
        window.addEventListener("scroll", closeMenu, true);
        return () => window.removeEventListener("scroll", closeMenu, true);
    }, [contextMenu]);


    const getForceSaveKey = () => {
        const uid = getCurrentUserIdForDb();
        return uid ? `habboimager:forceSave:lastAt:${uid}` : "habboimager:forceSave:lastAt:anon";
    };

    const readLastForceSaveAt = () => {
        try {
            const raw = localStorage.getItem(getForceSaveKey());
            return raw ? parseInt(raw, 10) || 0 : 0;
        } catch { return 0; }
    };

    const writeLastForceSaveAt = (ts) => {
        try { localStorage.setItem(getForceSaveKey(), String(ts)); } catch {}
    };

    useEffect(() => {
        forceSaveKeyRef.current = getForceSaveKey();
        setForceSaveNowTs(Date.now());
    }, []);

    useEffect(() => {
        const t = setInterval(() => setForceSaveNowTs(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const lastForceSaveAtRef = useRef(0);
    const [forceSaveTick, setForceSaveTick] = useState(0);

    useEffect(() => {
        const t = setInterval(() => {
            const last = Number(lastForceSaveAtRef.current) || 0;
            if (!last) return;
            if (Date.now() - last >= FORCE_SAVE_COOLDOWN_MS) return;
            setForceSaveTick((x) => x + 1);
        }, 1000);
        return () => clearInterval(t);
    }, []);

    const endEditSceneHabbo = (opts = {}) => {
        const restore = opts.restore !== false;
        const id = editingSceneHabboIdRef.current;

        if (id && !restore) {
            const fresh = makeSnapshot({});
            setSprites((prev) =>
                prev.map((s) =>
                    s.id === id && s.type === 'habbo'
                        ? {
                            ...s,
                            snap: {
                                ...fresh,
                                scale: s.snap?.scale,
                                flipX: s.snap?.flipX,
                                flipY: s.snap?.flipY,
                                crop: s.snap?.crop ?? null,
                                rotation: s.snap?.rotation ?? 0,
                                sourceSize: s.snap?.sourceSize,
                            },
                        }
                        : s
                )
            );
        }

        editingSceneHabboIdRef.current = null;
        setEditingSceneHabboId(null);
        setActiveSceneHabboId(null);

        if (restore && preEditActiveRef.current) {
            const { snap, pos } = preEditActiveRef.current;
            applySnapshot(snap, { keepView: true });
            setSpritePos({ ...pos });
        }

        preEditActiveRef.current = null;

        setActiveLayerId(null);
    };

    // Backward-compat derived arrays - render section still uses these names
    const furniSprites = useMemo(() => sprites.filter((s) => s.type === 'furni'), [sprites]);
    const sceneSprites = useMemo(() => sprites.filter((s) => s.type === 'habbo'), [sprites]);
    const customImages = useMemo(() => sprites.filter((s) => s.type === 'customImage'), [sprites]);
    const textSprites = useMemo(() => sprites.filter((s) => s.type === 'text'), [sprites]);

    const furniSpritesRef = useRef([]);
    useEffect(() => {
        furniSpritesRef.current = furniSprites;
    }, [furniSprites]);

    // ─── Undo / Redo History ───
    const historyRef = useRef({ past: [], future: [] });
    const historyStateRef = useRef(null);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const [furniItems, setFurniItems] = useState([]);
    const furniItemsByIdRef = useRef(new Map());
    useEffect(() => {
        furniItemsByIdRef.current = new Map(furniItems.map((x) => [x.id, x]));
    }, [furniItems]);

    const [, setSearchableFurni] = useState([]);
    const [userFurni, setUserFurni] = useState([]);

    useEffect(() => {
        const combined = [...furniItems, ...userFurni];
        furniItemsByIdRef.current = new Map(combined.map((x) => [x.id, x]));
    }, [furniItems, userFurni]);

    const handleSearchSelect = async (option) => {
        if (!option || !option.original) return;
        const item = option.original;
        const userId = `usr_${item.id}`;

        if (userFurni.some((u) => u.id === userId)) {
            toast.info(`${item.name} is already in your list.`);
            return;
        }

        const raw = String(item.code || "").trim();
        if (!raw) {
            toast.error(`Missing furni code for ${item.name}`);
            return;
        }

        const s = raw.replace(/%2a/gi, "*");
        const star = s.indexOf("*");
        const base = star === -1 ? s : s.slice(0, star);
        const rest = star === -1 ? "" : s.slice(star);
        const smallClass = `${base}_small${rest}`;

        const thumbUrl = `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(smallClass)}&direction=0&state=1&frame=0&width=120&height=120`;
        const renderUrl = `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(s)}&direction=0&state=0&frame=0&width=120&height=120`;

        // Add immediately so the icon appears without waiting for the variant fetch
        setUserFurni((prev) => [...prev, {
            id: userId,
            name: item.name,
            code: s,
            thumbUrl,
            url: renderUrl,
            images: [[renderUrl]],
            shadowImages: [],
            isUserAdded: true,
        }]);

        try {
            const apiUrl = (process.env.REACT_APP_API_URL).trim();
            const res = await fetch(`${apiUrl}furni-imaging/furni-info/${encodeURIComponent(s)}`, {
                headers: { Accept: "application/json" },
            });

            let images = [[renderUrl]];

            if (res.ok) {
                const data = await res.json();
                const variants = Array.isArray(data?.normal?.variants) ? data.normal.variants : [];

                if (variants.length) {
                    const hasState = variants.some((v) => Number.isFinite(v?.state));
                    const hasDir = variants.some((v) => Number.isFinite(v?.direction));

                    if (!hasState && !hasDir) {
                        const rots = variants.map((v) => v?.url || "x").filter(Boolean);
                        images = [rots.length ? rots : ["x"]];
                    } else {
                        let maxState = 0;

                        for (let i = 0; i < variants.length; i += 1) {
                            const v = variants[i];
                            const st = hasState && Number.isFinite(v?.state) ? Number(v.state) : 0;
                            if (st > maxState) maxState = st;
                        }

                        const byState = Array.from({ length: maxState + 1 }, () => []);

                        for (let i = 0; i < variants.length; i += 1) {
                            const v = variants[i];
                            const st = hasState && Number.isFinite(v?.state) ? Number(v.state) : 0;
                            byState[st].push(v);
                        }

                        if (hasState && byState.length > 1) {
                            const seen = new Map();
                            const compact = [];

                            for (let sI = 0; sI < byState.length; sI += 1) {
                                const arr = byState[sI];
                                if (!arr || !arr.length) continue;

                                const sig = arr
                                    .map((v) => String(v?.url || "").split("?")[0].trim())
                                    .filter(Boolean)
                                    .join("|");

                                if (!sig) continue;

                                if (!seen.has(sig)) {
                                    seen.set(sig, compact.length);
                                    compact.push(arr);
                                }
                            }

                            if (compact.length) {
                                for (let i = 0; i < compact.length; i += 1) byState[i] = compact[i];
                                byState.length = compact.length;
                                maxState = byState.length - 1;
                            }
                        }

                        let maxDir = 0;

                        if (hasDir) {
                            for (let i = 0; i < variants.length; i += 1) {
                                const v = variants[i];
                                const dr = Number.isFinite(v?.direction) ? Number(v.direction) : 0;
                                if (dr > maxDir) maxDir = dr;
                            }
                        } else {
                            for (let sI = 0; sI < byState.length; sI += 1) {
                                const n = byState[sI].length - 1;
                                if (n > maxDir) maxDir = n;
                            }
                        }

                        const grid = Array.from({ length: maxState + 1 }, () => Array.from({ length: maxDir + 1 }, () => "x"));

                        for (let sI = 0; sI < byState.length; sI += 1) {
                            const arr = byState[sI];
                            for (let j = 0; j < arr.length; j += 1) {
                                const v = arr[j];
                                const dr = hasDir && Number.isFinite(v?.direction) ? Number(v.direction) : j;
                                if (dr >= 0 && dr < grid[sI].length) grid[sI][dr] = v?.url || "x";
                            }
                        }

                        images = grid.map((row) => row.filter(Boolean));
                    }
                }
            }

            // Update the entry (already added above) with full variant data
            setUserFurni((prev) => prev.map((f) => f.id !== userId ? f : {
                ...f,
                images,
                shadowImages: Array.isArray(item?.shadowImages) ? item.shadowImages : [],
            }));
        } catch (error) {
            console.error(error);
            // Basic entry is already visible; silent failure is fine here
        }
    };

    const handleRemoveUserFurni = (id) => {
        setUserFurni(prev => prev.filter(f => f.id !== id));
        setSprites(prev => prev.filter(s => {
            if (s.type !== 'furni') return true;
            if (s.furniId === id) {
                if (activeFurniId === s.id) {
                    setActiveFurniId(null);
                    setSpriteSelected(false);
                }
                return false;
            }
            return true;
        }));
    };

    useEffect(() => {
        const loadSearchData = async () => {
            try {
                const response = await getAllItems();
                if (response.success && Array.isArray(response.items)) {
                    setSearchableFurni(response.items.map(item => ({
                        value: item.id,
                        label: `${item.name} (${item.code})`,
                        original: item
                    })));
                }
            } catch (error) {
                console.error("Failed to load furni search data", error);
            }
        };
        loadSearchData();
    }, []);

    useEffect(() => {
        const combined = [...furniItems, ...userFurni];
        furniItemsByIdRef.current = new Map(combined.map((x) => [x.id, x]));
    }, [furniItems, userFurni]);

    const furniDragRef = useRef(null);
    const rotationHistoryPushedRef = useRef(false); // prevents pushHistory on every arrow-key repeat / onChange

    const onFurniPointerMove = (e) => {
        const st = furniDragRef.current;
        if (!st) return;

        e.preventDefault();

        const dx = e.clientX - st.startClientX;
        const dy = e.clientY - st.startClientY;

        if (Math.abs(dx) + Math.abs(dy) > 3) st.moved = true;

        const snapped = applyWeakSpriteSnap(
            {
                x: st.startPos.x + dx / (zoom || 1),
                y: st.startPos.y + dy / (zoom || 1),
            },
            { type: "furni", id: st.id },
            { axisSnap: !!e.shiftKey },
        );

        st.nextX = snapped.x;
        st.nextY = snapped.y;

        if (!st.raf) {
            st.raf = requestAnimationFrame(() => {
                st.raf = 0;
                if (!st.el) return;
                // Snap to the Habbo pixel grid mid-drag so the sprite never visually
                // occupies a half pixel - both axes round to integer browser pixels.
                const sx = snapToSpritePixel(st.nextX, st.sc);
                const sy = snapToSpritePixel(st.nextY, st.sc);
                const tx = sx - Math.round(st.sw / 2) + st.cl;
                const ty = sy - Math.round(st.sh / 2) + st.ct;
                const rot = st.rotation ? ` rotate(${st.rotation}deg)` : '';
                st.el.style.transform = `translate(${tx}px, ${ty}px)${rot}`;
                st.el.style.willChange = 'transform';
            });
        }
    };

    const [speechBubbles, setSpeechBubbles] = useState([]);

    const speechPartsBase = useMemo(
        () => ({
            frnt: `${process.env.PUBLIC_URL}/img/spbb_frnt.png`,
            mid: `${process.env.PUBLIC_URL}/img/spbb_mid.png`,
            end: `${process.env.PUBLIC_URL}/img/spbb_end.png`,
        }),
        []
    );

    const [previewSize, setPreviewSize] = useState({ w: 650, h: 500 });
    const previewRenderedSizeRef = useRef({ w: 650, h: 500 });
    const [canvasWInput, setCanvasWInput] = useState(650);
    const [canvasHInput, setCanvasHInput] = useState(500);

    useEffect(() => {
        // Skip during scene loads - loadScene sets canvas size directly and handles tileMode itself
        if (isLoadingSceneRef.current) {
            isLoadingSceneRef.current = false;
            return;
        }
        if (tileMode) {
            preTileModeSize.current = { ...previewSize };
            setPreviewSize({ w: tileSize, h: tileSize });
            setCanvasWInput(tileSize);
            setCanvasHInput(tileSize);
        } else {
            if (preTileModeSize.current) {
                setPreviewSize(preTileModeSize.current);
                setCanvasWInput(preTileModeSize.current.w);
                setCanvasHInput(preTileModeSize.current.h);
                preTileModeSize.current = null;
            }
            setShowTilePreview(false);
            setTilePreviewUrl(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tileMode, tileSize]);
    const [showClearSceneModal, setShowClearSceneModal] = useState(false);
    const [showCanvasSizePanel, setShowCanvasSizePanel] = useState(false);
    const [showAddEffectDropdown, setShowAddEffectDropdown] = useState(false);
    const [showImageUploadPanel, setShowImageUploadPanel] = useState(false);
    const [uploadedImageFile, setUploadedImageFile] = useState(null);
    const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
    const [isUploadingCustomImage, setIsUploadingCustomImage] = useState(false);
    const [activeCustomImageId, setActiveCustomImageId] = useState(null);
    const customImgDragRef = useRef(null);

    const [activeTextId, setActiveTextId] = useState(null);
    const textDragRef = useRef(null);

    // Habbo Font panel
    const [showHabboFontPanel, setShowHabboFontPanel] = useState(false);
    const [habboFontText, setHabboFontText] = useState('');
    const [habboFontFolder, setHabboFontFolder] = useState('Original');
    const [habboFontPreviewUrl, setHabboFontPreviewUrl] = useState(null);
    const [habboFontGenerating, setHabboFontGenerating] = useState(false);

    // Furni Colourer panel
    const [showFurniColorerPanel, setShowFurniColorerPanel] = useState(false);
    const [furniColorerGroup, setFurniColorerGroup] = useState(Object.keys(FURNI_COLOURER_GROUPS)[0]);
    const [furniColorerHex, setFurniColorerHex] = useState('#ffffff');
    const [furniColorerPreviews, setFurniColorerPreviews] = useState({});
    const [furniColorerLoading, setFurniColorerLoading] = useState({});
    const [furniColorerApplying, setFurniColorerApplying] = useState(false);

    const textLayerKey = useMemo(
        () => textSprites.map((t) => `${t.id}:${String(t.text || "")}`).join("|"),
        [textSprites]
    );

    const getActiveTextSprite = () => {
        if (!activeTextId) return null;
        return textSprites.find((t) => t.id === activeTextId) || null;
    };

    const updateTextSprite = (id, patch) => {
        if (!id) return;
        setSprites((prev) => prev.map((s) => (s.id === id && s.type === 'text' ? { ...s, ...patch } : s)));
    };

    const addTextSprite = () => {
        pushHistory();
        const id = makeId();
        const next = {
            id,
            text: "New Text",
            pos: { x: 0, y: 0 },
            fontFamily: "VolterGoldfish, sans-serif",
            fontSize: 24,
            color: "#ffffff",
            strokeColor: "#000000",
            strokeWidth: 0,
            fontWeight: "normal",
            italic: false,
            sourceSize: { w: 120, h: 32 },
        };

        setSprites((prev) => [...prev, { type: 'text', ...next, visible: true, locked: false, opacity: 100, layerName: '', effects: {} }]);
        setActiveTextId(id);
        setActiveCustomImageId(null);
        setActiveFurniId(null);
        setActiveSceneHabboId(null);
        setSpriteSelected(false);
        setActiveLayerId(`text:${id}`);
        setCropMode(false);
        setContextMenu(null);
    };

    const removeActiveTextSprite = () => {
        if (!activeTextId) return;
        pushHistory();
        setSprites((prev) => prev.filter((s) => !(s.id === activeTextId && s.type === 'text')));
        setActiveTextId(null);
        setActiveLayerId(null);
    };

    const getTextBoxMetrics = (t) => {
        const fontPx = Math.max(8, Math.round(Number(t?.fontSize) || 24));
        const weight = t?.fontWeight === "bold" ? "bold" : "normal";
        const italic = t?.italic ? "italic" : "normal";
        const font = `${italic} ${weight} ${fontPx}px ${t?.fontFamily || "sans-serif"}`;
        const strokeWidth = Math.max(0, Number(t?.strokeWidth) || 0);
        const text = String(t?.text || "");
        const w = Math.max(24, measureTextW(text || " ", font) + strokeWidth * 2 + 8);
        const h = Math.max(16, Math.ceil(fontPx + strokeWidth * 2 + 8));
        return { w, h, font, fontPx, strokeWidth };
    };

    const wrapTilePos = (x, y) => {
        if (!tileModeRef.current) return { x, y };
        const sz = tileSizeRef.current;
        const half = sz / 2;
        const wrap = (v) => {
            let r = ((v + half) % sz);
            if (r < 0) r += sz;
            return r - half;
        };
        return { x: wrap(x), y: wrap(y) };
    };

    const snapToSpritePixel = (value, scale) => {
        return Math.round(value / HABBO_PIXEL) * HABBO_PIXEL;
    };

    const [customBgUrl, setCustomBgUrl] = useState(null);
    const [customBgFit, setCustomBgFit] = useState("cover");
    const [showBgFitModal, setShowBgFitModal] = useState(false);
    const [pendingBgFile, setPendingBgFile] = useState(null);
    const bgUploadInputRef = useRef(null);

    const [showAssetsModal, setShowAssetsModal] = useState(false);
    const [assetsManifest, setAssetsManifest] = useState(null);
    const [openAssetCategory, setOpenAssetCategory] = useState(null);
    const auxDropdownIdRef = useRef(Math.random().toString(36).slice(2));
    const addHabboDropdownIdRef = useRef(Math.random().toString(36).slice(2));

    useEffect(() => {
        const onAny = (e) => {
            const id = e?.detail?.id;
            if (id && id !== auxDropdownIdRef.current) {
                setShowImageUploadPanel(false);
                setShowAssetsModal(false);
                setShowHabboFontPanel(false);
                setShowFurniColorerPanel(false);
            }
            if (id && id !== addHabboDropdownIdRef.current) {
                setShowAddHabboPanel(false);
            }
        };
        window.addEventListener("habboimager:dropdown", onAny);
        return () => window.removeEventListener("habboimager:dropdown", onAny);
    }, []);

    useLayoutEffect(() => {
        const el = previewRef.current;
        if (!el) return;

        const apply = () => {
            const r = el.getBoundingClientRect();
            previewRenderedSizeRef.current = { w: Math.round(r.width || 0), h: Math.round(r.height || 0) };
        };

        apply();

        const ro = new ResizeObserver(() => apply());
        ro.observe(el);

        return () => ro.disconnect();
    }, []);

    const textMeasureRef = useRef(null);
    const measureTextW = (s, font) => {
        let ctx = textMeasureRef.current;
        if (!ctx) {
            const c = document.createElement("canvas");
            ctx = c.getContext("2d");
            textMeasureRef.current = ctx;
        }
        ctx.font = font || "9px VolterGoldfish, sans-serif";
        return Math.ceil(ctx.measureText(String(s || "")).width || 0);
    };

    const recolorCacheRef = useRef(new Map());
    const recolorSpeechPart = useCallback((src, hex) => {
        const key = `${src}::${hex}`;
        const cached = recolorCacheRef.current.get(key);
        if (cached) return Promise.resolve(cached);

        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                const w = img.naturalWidth || img.width || 0;
                const h = img.naturalHeight || img.height || 0;
                if (!w || !h) {
                    recolorCacheRef.current.set(key, src);
                    resolve(src);
                    return;
                }

                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.imageSmoothingEnabled = false;
                ctx.drawImage(img, 0, 0);

                const d = ctx.getImageData(0, 0, w, h);
                const data = d.data;

                const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || "").trim());
                const rgb = m ? m[1] : "e0b250";
                const rr = parseInt(rgb.slice(0, 2), 16);
                const gg = parseInt(rgb.slice(2, 4), 16);
                const bb = parseInt(rgb.slice(4, 6), 16);

                for (let i = 0; i < data.length; i += 4) {
                    const a = data[i + 3];
                    if (!a) continue;
                    const r = data[i + 0];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    const dr = r - 224;
                    const dg = g - 178;
                    const db = b - 80;

                    if (dr * dr + dg * dg + db * db <= 30 * 30) {
                        data[i + 0] = rr;
                        data[i + 1] = gg;
                        data[i + 2] = bb;
                    }

                }

                ctx.putImageData(d, 0, 0);

                const out = canvas.toDataURL("image/png");
                recolorCacheRef.current.set(key, out);
                resolve(out);
            };
            img.onerror = () => {
                recolorCacheRef.current.set(key, src);
                resolve(src);
            };
            img.src = src;
        });
    }, []);

    const addSpeechBubble = async (sceneHabboId, rawText, colorHex, isShout) => {
        const habbo = sceneSprites.find((x) => x.id === sceneHabboId) || null;
        const hotel = (habbo?.snap?.hotel || "COM").toUpperCase();
        const typedName = ((habbo?.snap?.habboName || "Habbo") + "").trim();

        const msgBody = (rawText || "").replace(/\s+/g, " ").trim();
        if (!sceneHabboId || !msgBody) return;

        const id = String(Date.now()) + "_" + Math.random().toString(16).slice(2);
        const cleanColor = ((colorHex || "#e0b250") + "").trim().slice(0, 7);
        const text80 = msgBody.slice(0, 80);

        pushHistory();
        setSpeechBubbles((prev) => {
            if (prev.length >= 5) return prev;
            return prev.concat([
                {
                    id,
                    sceneHabboId,
                    text: text80,
                    color: cleanColor,
                    displayName: typedName,
                    shout: !!isShout,
                    assets: null,
                },
            ]);
        });

        const base = HOTEL_API_BASE[hotel] || HOTEL_API_BASE.COM;
        const url = `${base}/api/public/users?name=${encodeURIComponent(typedName)}`;
        try {
            const res = await fetch(url, { headers: { Accept: "application/json" } });
            if (res.ok) {
                const data = await res.json();
                const proper = ((data?.name || data?.username || "") + "").trim();
                if (proper) {
                    setSpeechBubbles((prev) => prev.map((b) => (b.id === id ? { ...b, displayName: proper } : b)));
                }
            }
        } catch { }

        const frnt = await recolorSpeechPart(speechPartsBase.frnt, cleanColor);
        const mid = await recolorSpeechPart(speechPartsBase.mid, cleanColor);
        const end = await recolorSpeechPart(speechPartsBase.end, cleanColor);

        setSpeechBubbles((prev) => prev.map((b) => (b.id === id ? { ...b, assets: { frnt, mid, end } } : b)));
    };

    const removeSpeechBubble = (id) => {
        pushHistory();
        setSpeechBubbles((prev) => prev.filter((b) => b.id !== id));
    };

    const reorderSpeechBubbles = (fromId, toId) => {
        if (!fromId || !toId || fromId === toId) return;
        pushHistory();
        setSpeechBubbles((prev) => {
            const a = prev.findIndex((x) => x.id === fromId);
            const b = prev.findIndex((x) => x.id === toId);
            if (a < 0 || b < 0) return prev;
            const next = prev.slice();
            const [m] = next.splice(a, 1);
            next.splice(b, 0, m);
            return next;
        });
    };

    useEffect(() => {
        const missing = (speechBubbles || []).filter((b) => !b.assets || b.assetsColor !== b.color);
        if (!missing.length) return;

        let cancelled = false;

        (async () => {
            for (const b of missing) {
                const c = (b.color || "#e0b250").slice(0, 7);
                const frnt = await recolorSpeechPart(speechPartsBase.frnt, c);
                const mid = await recolorSpeechPart(speechPartsBase.mid, c);
                const end = await recolorSpeechPart(speechPartsBase.end, c);
                if (cancelled) return;

                setSpeechBubbles((prev) =>
                    prev.map((x) => (x.id === b.id ? { ...x, assets: { frnt, mid, end }, assetsColor: c } : x))
                );
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [speechBubbles, speechPartsBase, recolorSpeechPart]);

    const sceneHabboDragRef = useRef(null);

    const onSceneHabboPointerMove = (e) => {
        const st = sceneHabboDragRef.current;
        if (!st) return;

        e.preventDefault();
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}

        const dx = (e.clientX - st.startClientX) / (zoom || 1);
        const dy = (e.clientY - st.startClientY) / (zoom || 1);

        if (Math.abs(dx) + Math.abs(dy) > 3) st.moved = true;

        const snapped = applyWeakSpriteSnap(
            {
                x: st.startPos.x + dx,
                y: st.startPos.y + dy,
            },
            { type: "sceneHabbo", id: st.id },
            { axisSnap: !!e.shiftKey },
        );

        st.nextX = snapped.x;
        st.nextY = snapped.y;

        if (!st.raf) {
            st.raf = requestAnimationFrame(() => {
                st.raf = 0;
                if (st.el) {
                    const sx = snapToSpritePixel(st.nextX, st.sc);
                    const sy = snapToSpritePixel(st.nextY, st.sc);
                    const tx = sx - Math.round(st.sw / 2) + st.cl;
                    const ty = sy - Math.round(st.sh / 2) + st.ct;
                    const rot = st.rotation ? ` rotate(${st.rotation}deg)` : '';
                    st.el.style.transform = `translate(${tx}px, ${ty}px)${rot}`;
                    st.el.style.willChange = "transform";
                }
            });
        }
    };

    const onSceneHabboPointerUp = (e) => {
        const st = sceneHabboDragRef.current;
        sceneHabboDragRef.current = null;
        clearSnapGuides();

        window.removeEventListener("pointermove", onSceneHabboPointerMove);
        window.removeEventListener("pointerup", onSceneHabboPointerUp);
        window.removeEventListener("pointercancel", onSceneHabboPointerUp);

        if (!st) return;

        if (st.raf) cancelAnimationFrame(st.raf);

        if (st.el) st.el.style.willChange = "";

        if (st.moved) {
            pushHistory();
            const _shWrapped = wrapTilePos(snapToSpritePixel(st.nextX, st.sc), snapToSpritePixel(st.nextY, st.sc));
            setSprites((prev) =>
                prev.map((s) =>
                    s.id !== st.id || s.type !== 'habbo'
                        ? s
                        : { ...s, pos: _shWrapped }
                )
            );
        }

        if (!st.moved) {
            selectLayerById(`habbo:${st.id}`);
        } else {
            editingSceneHabboIdRef.current = st.id;
            setEditingSceneHabboId(st.id);

            setActiveSceneHabboId(st.id);
            setActiveLayerId(`habbo:${st.id}`);
            setActiveFurniId(null);
            setSpriteSelected(true);

            if (st.snap) applySnapshot(st.snap, { keepView: true });
            setSpritePos(wrapTilePos(snapToSpritePixel(st.nextX, st.sc), snapToSpritePixel(st.nextY, st.sc)));
            endSpriteDrag();
        }
    };

    const onFurniPointerUp = (e) => {
        const st = furniDragRef.current;
        furniDragRef.current = null;
        clearSnapGuides();

        window.removeEventListener("pointermove", onFurniPointerMove);
        window.removeEventListener("pointerup", onFurniPointerUp);
        window.removeEventListener("pointercancel", onFurniPointerUp);

        if (!st) return;

        if (st.raf) cancelAnimationFrame(st.raf);
        if (st.el) st.el.style.willChange = '';

        if (st.moved && Number.isFinite(st.nextX) && Number.isFinite(st.nextY)) {
            pushHistory();
            updateFurniSprite(st.id, {
                pos: wrapTilePos(snapToSpritePixel(st.nextX, st.sc), snapToSpritePixel(st.nextY, st.sc)),
            });
        }
    };

    const onCustomImgPointerMove = (e) => {
        const st = customImgDragRef.current;
        if (!st) return;
        e.preventDefault();
        const dx = (e.clientX - st.startClientX) / (zoom || 1);
        const dy = (e.clientY - st.startClientY) / (zoom || 1);
        if (Math.abs(dx) + Math.abs(dy) > 3) st.moved = true;
        st.nextX = st.startPos.x + dx;
        st.nextY = st.startPos.y + dy;
        if (!st.raf) {
            st.raf = requestAnimationFrame(() => {
                st.raf = 0;
                if (st.el) {
                    const sx = snapToSpritePixel(st.nextX, st.sc);
                    const sy = snapToSpritePixel(st.nextY, st.sc);
                    const tx = sx - Math.round(st.sw / 2) + st.cl;
                    const ty = sy - Math.round(st.sh / 2) + st.ct;
                    const rot = st.rotation ? ` rotate(${st.rotation}deg)` : '';
                    st.el.style.transform = `translate(${tx}px, ${ty}px)${rot}`;
                    st.el.style.willChange = 'transform';
                }
            });
        }
    };

    const onCustomImgPointerUp = (e) => {
        const st = customImgDragRef.current;
        customImgDragRef.current = null;
        window.removeEventListener("pointermove", onCustomImgPointerMove);
        window.removeEventListener("pointerup", onCustomImgPointerUp);
        window.removeEventListener("pointercancel", onCustomImgPointerUp);
        if (!st) return;
        if (st.raf) cancelAnimationFrame(st.raf);
        if (st.el) st.el.style.willChange = "";
        if (st.moved) {
            pushHistory();
            setSprites((prev) => prev.map((s) => s.id !== st.id || s.type !== 'customImage' ? s : { ...s, pos: wrapTilePos(snapToSpritePixel(st.nextX, st.sc), snapToSpritePixel(st.nextY, st.sc)) }));
        }
    };

    const onTextPointerMove = (e) => {
        const st = textDragRef.current;
        if (!st) return;

        e.preventDefault();

        const dx = (e.clientX - st.startClientX) / (zoom || 1);
        const dy = (e.clientY - st.startClientY) / (zoom || 1);

        if (Math.abs(dx) + Math.abs(dy) > 3) st.moved = true;

        const snapped = applyWeakSpriteSnap(
            {
                x: st.startPos.x + dx,
                y: st.startPos.y + dy,
            },
            { type: "text", id: st.id },
            { axisSnap: !!e.shiftKey },
        );

        st.nextX = snapped.x;
        st.nextY = snapped.y;

        if (!st.raf) {
            st.raf = requestAnimationFrame(() => {
                st.raf = 0;
                if (st.el) {
                    const scaleX = (st.flipX ? -1 : 1) * st.scale;
                    const scaleY = (st.flipY ? -1 : 1) * st.scale;
                    const sx = snapToSpritePixel(st.nextX, st.scale);
                    const sy = snapToSpritePixel(st.nextY, st.scale);
                    st.el.style.transform = `translate(calc(-50% + ${sx}px), calc(-50% + ${sy}px)) rotate(${st.rotation}deg) scaleX(${scaleX}) scaleY(${scaleY})`;
                    st.el.style.willChange = "transform";
                }
            });
        }
    };

    const onTextPointerUp = () => {
        const st = textDragRef.current;
        textDragRef.current = null;
        clearSnapGuides();

        window.removeEventListener("pointermove", onTextPointerMove);
        window.removeEventListener("pointerup", onTextPointerUp);
        window.removeEventListener("pointercancel", onTextPointerUp);

        if (!st) return;
        if (st.raf) cancelAnimationFrame(st.raf);
        if (st.el) st.el.style.willChange = "";

        if (st.moved) {
            pushHistory();
            setSprites((prev) =>
                prev.map((s) =>
                    s.id !== st.id || s.type !== 'text'
                        ? s
                        : { ...s, pos: wrapTilePos(snapToSpritePixel(st.nextX, st.scale), snapToSpritePixel(st.nextY, st.scale)) }
                )
            );
        }
    };

    const [pendingLoad, setPendingLoad] = useState(null);
    const [showLoadScenePrompt, setShowLoadScenePrompt] = useState(false);

    const [showAlreadyInScenePrompt, setShowAlreadyInScenePrompt] = useState(false);
    const [showUpdatesModal, setShowUpdatesModal] = useState(false);
    const [updatesData, setUpdatesData] = useState(null);
    const [showNewImageModal, setShowNewImageModal] = useState(false);
    const [newImageName, setNewImageName] = useState("");
    const [newImageWidth, setNewImageWidth] = useState(500);
    const [newImageHeight, setNewImageHeight] = useState(500);
    const [newImagePreset, setNewImagePreset] = useState("Standard");
    const [newImageUsePreset, setNewImageUsePreset] = useState(true);
    const [newImageTileMode, setNewImageTileMode] = useState(false);
    const [newImageTileSize, setNewImageTileSize] = useState(320);
    const [showFurniSearch, setShowFurniSearch] = useState(false);
    const furniSearchCompleteRef = useRef(null);

    const [sceneLoading, setSceneLoading] = useState(false);
    const sceneLoadingTimerRef = useRef(null);

    const beginSceneLoading = () => {
        setSceneLoading(true);
        if (sceneLoadingTimerRef.current) window.clearTimeout(sceneLoadingTimerRef.current);
        sceneLoadingTimerRef.current = window.setTimeout(() => setSceneLoading(false), 1000);
    };

    useEffect(() => {
        return () => {
            if (sceneLoadingTimerRef.current) window.clearTimeout(sceneLoadingTimerRef.current);
        };
    }, []);

    const previewRef = useRef(null);
    const workspaceRef = useRef(null);

    const [cameraPos, setCameraPos] = useState({ x: 0, y: 0 });
    const panNormRef = useRef({ x: 0, y: 0 });
    const [workspaceOffset, setWorkspaceOffset] = useState({ x: 0, y: 0 });
    const workspacePanRef = useRef(null);

    const onWorkspacePanMove = (e) => {
        const st = workspacePanRef.current;
        if (!st) return;
        e.preventDefault();
        setWorkspaceOffset({
            x: st.startOffset.x + (e.clientX - st.startClientX),
            y: st.startOffset.y + (e.clientY - st.startClientY),
        });
    };

    const onWorkspacePanUp = () => {
        workspacePanRef.current = null;
        window.removeEventListener("pointermove", onWorkspacePanMove);
        window.removeEventListener("pointerup", onWorkspacePanUp);
        window.removeEventListener("pointercancel", onWorkspacePanUp);
    };

    // ─── Camera Helpers ───
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    const getCameraBoundsForZoom = (z) => {
        const el = previewRef.current;
        if (!el) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

        const rect = el.getBoundingClientRect();
        const vw = rect.width;
        const vh = rect.height;

        const maxX = (vw * Math.max(z - 1, 0.5)) / 2;
        const maxY = (vh * Math.max(z - 1, 0.5)) / 2;

        return { minX: -maxX, maxX, minY: -maxY, maxY };
    };

    const getCameraBounds = () => getCameraBoundsForZoom(zoom);

    const changeZoom = (newZoom) => {
        if (newZoom === 1) {
            setZoom(1);
            setWorkspaceOffset({ x: 0, y: 0 });
            setCameraPos({ x: 0, y: 0 });
            return;
        }
        const ratio = newZoom / zoom;
        setWorkspaceOffset((prev) => ({ x: prev.x * ratio, y: prev.y * ratio }));
        setZoom(newZoom);
    };

    const setCameraSafe = (next, z = zoom) => {
        const b = getCameraBoundsForZoom(z);
        setCameraPos({
            x: clamp(next.x, b.minX, b.maxX),
            y: clamp(next.y, b.minY, b.maxY),
        });
    };

    useEffect(() => {
        const b = getCameraBoundsForZoom(zoom);
        const denomX = b.maxX || 0;
        const denomY = b.maxY || 0;
        panNormRef.current = {
            x: denomX ? cameraPos.x / denomX : 0,
            y: denomY ? cameraPos.y / denomY : 0,
        };
    }, [cameraPos.x, cameraPos.y, zoom]);

    useEffect(() => {
        const b = getCameraBounds();
        setCameraPos((p) => ({
            x: clamp(p.x, b.minX, b.maxX),
            y: clamp(p.y, b.minY, b.maxY),
        }));
    }, [zoom]);

    // ─── Export ───
    const [isCapturing, setIsCapturing] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFileName, setExportFileName] = useState("");
    const [exportScale, setExportScale] = useState("1");
    const [exportFormat, setExportFormat] = useState("png");
    const [exportCrispText, setExportCrispText] = useState(true);
    const [exportCustomSpriteRender, setExportCustomSpriteRender] = useState("pixelated");
    const exportSettingsPerSceneRef = useRef({});

    const openExportModal = () => {
        const saved = activeSceneId ? exportSettingsPerSceneRef.current[activeSceneId] : null;
        setExportFileName(saved?.fileName ?? `franklyorigins_${Date.now()}`);
        setExportScale(saved?.scale ?? "1");
        setExportFormat(saved?.format ?? "png");
        setExportCrispText(saved?.crispText ?? true);
        setExportCustomSpriteRender(saved?.customSpriteRender ?? "pixelated");
        setShowExportModal(true);
    };

    const savePreviewPng = async ({ scale = 1, format = "png", fileName = "", returnUrl = false, crispText = true, customSpriteRender = "pixelated" } = {}) => {
        if (!returnUrl) setIsCapturing(true);

        try {
            await document.fonts.ready;

            const W = previewSize.w;
            const H = previewSize.h;
            const smoothCustomSprites = customSpriteRender === "smooth";

            const speechVis = layers.find((l) => l.id === "speech:__all__")?.visible !== false;
            const exportBubblesBase = speechVis && Array.isArray(speechBubbles) ? speechBubbles.slice(-5) : [];
            const exportBubbles = exportBubblesBase.length
                ? await Promise.all(
                    exportBubblesBase.map(async (b) => {
                        const c = ((b.color || "#e0b250") + "").trim().slice(0, 7);
                        const frnt = await recolorSpeechPart(speechPartsBase.frnt, c);
                        const mid = await recolorSpeechPart(speechPartsBase.mid, c);
                        const end = await recolorSpeechPart(speechPartsBase.end, c);
                        return { ...b, assets: { frnt, mid, end }, assetsColor: c };
                    })
                )
                : [];

            const canvas = document.createElement("canvas");
            canvas.width = W * scale;
            canvas.height = H * scale;
            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingEnabled = false;
            if (scale > 1) ctx.scale(scale, scale);

            const loadImg = (src, label = "") =>
                new Promise((resolve) => {
                    if (!src) return resolve(null);
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.onload = () => resolve(img);
                    img.onerror = (e) => {
                        const tag = label ? ` [${label}]` : "";
                        console.error(
                            `[HabboImager export] Failed to load sprite${tag}.\n` +
                            `  URL: ${src}\n` +
                            `  This is likely a CORS or network error. Check that the imager ` +
                            `endpoint has Access-Control-Allow-Origin set correctly.`,
                            e
                        );
                        resolve(null);
                    };
                    img.src = src;
                });

            const drawSpriteToCanvas = (img, op) => {
                if (!img) return;
                const metrics = getSpriteFrameMetrics(
                    { w: img.naturalWidth || img.width || 0, h: img.naturalHeight || img.height || 0 },
                    op.scale || 1,
                    op.crop || null
                );
                if (!metrics.drawW || !metrics.drawH) return;

                const sc = metrics.scale;
                const dx = W / 2 + op.x - Math.round(metrics.sourceW * sc / 2) + metrics.crop.left * sc;
                const dy = H / 2 + op.y - Math.round(metrics.sourceH * sc / 2) + metrics.crop.top * sc;
                const rotation = op.rotation || 0;
                const flipX = !!op.flipX;
                const flipY = !!op.flipY;

                const wantSmooth = !!op.isCustomImage && smoothCustomSprites;
                const prevSmoothing = ctx.imageSmoothingEnabled;
                if (wantSmooth) {
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = "high";
                }

                if (op.isCustomImage && !rotation && !flipX && !flipY) {
                    ctx.drawImage(
                        img,
                        metrics.crop.left,
                        metrics.crop.top,
                        metrics.drawW,
                        metrics.drawH,
                        dx,
                        dy,
                        metrics.frameW,
                        metrics.frameH
                    );
                } else {
                    // Round the centre so rotation/flip transforms never pivot on a sub-pixel boundary.
                    const cx = Math.round(dx + metrics.frameW / 2);
                    const cy = Math.round(dy + metrics.frameH / 2);
                    const hdx = Math.round(-metrics.frameW / 2);
                    const hdy = Math.round(-metrics.frameH / 2);

                    ctx.save();
                    ctx.translate(cx, cy);
                    if (rotation) ctx.rotate((rotation * Math.PI) / 180);
                    if (flipX) ctx.scale(-1, 1);
                    if (flipY) ctx.scale(1, -1);
                    ctx.drawImage(
                        img,
                        metrics.crop.left,
                        metrics.crop.top,
                        metrics.drawW,
                        metrics.drawH,
                        hdx,
                        hdy,
                        metrics.frameW,
                        metrics.frameH
                    );
                    ctx.restore();
                }

                if (wantSmooth) ctx.imageSmoothingEnabled = prevSmoothing;
            };

            // Fill base background
            if (previewBg === "default") {
                if (!showTransparency) {
                    ctx.fillStyle = validWhiteBgTint || "#f5f5f5";
                    ctx.fillRect(0, 0, W, H);
                }
            } else if (previewBg === "custom_bg" && customBgUrl) {
                const bgImg = await new Promise((resolve) => {
                    const i = new window.Image();
                    i.crossOrigin = "anonymous";
                    i.onload = () => resolve(i);
                    i.onerror = () => resolve(null);
                    i.src = customBgUrl;
                });
                if (bgImg) {
                    if (customBgFit === "tile") {
                        const pat = ctx.createPattern(bgImg, "repeat");
                        if (pat) { ctx.fillStyle = pat; ctx.fillRect(0, 0, W, H); }
                    } else if (customBgFit === "stretch") {
                        ctx.drawImage(bgImg, 0, 0, W, H);
                    } else if (customBgFit === "contain") {
                        const bgScale = Math.min(W / bgImg.naturalWidth, H / bgImg.naturalHeight);
                        const dw = bgImg.naturalWidth * bgScale, dh = bgImg.naturalHeight * bgScale;
                        ctx.drawImage(bgImg, (W - dw) / 2, (H - dh) / 2, dw, dh);
                    } else {
                        const bgScale = Math.max(W / bgImg.naturalWidth, H / bgImg.naturalHeight);
                        const dw = bgImg.naturalWidth * bgScale, dh = bgImg.naturalHeight * bgScale;
                        ctx.drawImage(bgImg, (W - dw) / 2, (H - dh) / 2, dw, dh);
                    }
                }
            } else if (activeBg.type === "room_tile" && previewBg !== "custom_bg") {
                ctx.fillStyle = validRoomTileBgColor;
                ctx.fillRect(0, 0, W, H);
                const rtImg = await loadRoomTileImage(activeBg.url);
                if (rtImg) {
                    const rtTiles = buildRoomTileGrid({ w: W, h: H }, activeBg);
                    for (const t of rtTiles) {
                        if (bgLayerVis[`rt:${t.gx},${t.gy}`] === false) continue;
                        ctx.drawImage(rtImg, t.x, t.y, t.w, t.h);
                    }
                }
            }

            const drawOps = [];

            for (const l of layers) {
                if (l.visible === false) continue;
                if (l.kind === "speech") continue;

                if (l.kind === "moodlight") {
                    drawOps.push({
                        z: layerZ.get(l.id) || 0,
                        type: "moodlight",
                        color: moodlightColor || "#ffffff",
                        opacity: typeof moodlightOpacity === "number" ? moodlightOpacity : 0.5,
                    });
                    continue;
                }

                const z = layerZ.get(l.id) || 0;
                const layerOpacity = typeof l.opacity === "number" ? l.opacity : 100;

                if (l.kind === "bg") {
                    if (activeBg.type === "tile") {
                        const _tileW = activeBg.size.w * tileScale;
                        const _tileH = activeBg.size.h * tileScale;
                        const _tileOffX = ((Math.round(W / 2 - _tileW / 2)) % _tileW + _tileW) % _tileW;
                        // Anchor the vertical phase to the Room Tile grid origin so all grids align.
                        const _rt = previewBgConfig.room_tile || {};
                        const _startYRatio = Number.isFinite(Number(_rt.startYRatio)) ? Number(_rt.startYRatio) : 0.35;
                        const _originY = Math.round(H * _startYRatio) + Math.round(Number(_rt.offsetY) || 0);
                        const _tileOffY = ((_originY % _tileH) + _tileH) % _tileH;
                        drawOps.push({ z, type: "tile", url: activeBg.url, tileW: _tileW, tileH: _tileH, tileOffX: _tileOffX, tileOffY: _tileOffY, label: `bg:tile`, layerOpacity });
                    } else if (activeBg.type === "layered") {
                        const k = (l.id.split(":")[1] || "").trim();
                        const src = activeBg.layers?.[k];
                        if (src) drawOps.push({ z, type: "centered", url: src, label: `bg:${k}`, layerOpacity });
                    }
                } else if (l.kind === "furni") {
                    const spId = l.id.split(":")[1];
                    const sp = furniSprites.find((x) => x.id === spId) || null;
                    if (sp) {
                        const url = getFurniRenderUrl(sp);
                        drawOps.push({
                            z, type: "sprite", url,
                            x: sp.pos.x, y: sp.pos.y,
                            flipX: !!sp.flipped, flipY: !!sp.flipY,
                            rotation: sp.rotation || 0,
                            scale: clampSpriteScale(sp.scale),
                            crop: sp.crop || null,
                            label: `furni:${sp.name || spId}`,
                            layerOpacity, outlineEffect: l.effects?.outline || null, waterEffect: l.effects?.water ? { ...l.effects.water, _kind: l.kind } : null,
                        });
                    }
                } else if (l.kind === "habbo") {
                    if (l.id === "habbo:__active__") {
                        if (hasActiveHabbo) {
                            drawOps.push({
                                z, type: "sprite", url: croppedURL,
                                x: spritePos.x, y: spritePos.y,
                                flipX: !!activeSpriteFlipX, flipY: !!activeSpriteFlipY,
                                rotation: activeSpriteRotation || 0,
                                scale: clampSpriteScale(activeSpriteScale),
                                crop: activeSpriteCrop || null,
                                label: `habbo:${habboName}`,
                                layerOpacity, outlineEffect: l.effects?.outline || null, waterEffect: l.effects?.water ? { ...l.effects.water, _kind: l.kind } : null,
                            });
                        }
                    } else {
                        const spId = l.id.split(":")[1];
                        const sp = sceneSprites.find((x) => x.id === spId) || null;
                        if (sp) {
                            const url = getUrlForSnap(sp.snap) + "&crop=true";
                            drawOps.push({
                                z, type: "sprite", url,
                                x: sp.pos.x, y: sp.pos.y,
                                flipX: !!sp.snap?.flipX, flipY: !!sp.snap?.flipY,
                                rotation: sp.snap?.rotation || 0,
                                scale: clampSpriteScale(sp.snap?.scale),
                                crop: sp.snap?.crop || null,
                                label: `habbo:${sp.snap?.habboName || spId}`,
                                layerOpacity, outlineEffect: l.effects?.outline || null, waterEffect: l.effects?.water ? { ...l.effects.water, _kind: l.kind } : null,
                            });
                        }
                    }
                } else if (l.kind === "custom_image") {
                    const imgId = l.id.split(":")[1];
                    const img = customImages.find((x) => x.id === imgId) || null;
                    if (img) {
                        drawOps.push({
                            z, type: "sprite", url: img.url, isCustomImage: true,
                            x: img.pos.x, y: img.pos.y,
                            flipX: !!img.flipX, flipY: !!img.flipY,
                            rotation: img.rotation || 0,
                            scale: clampSpriteScale(img.scale),
                            crop: img.crop || null,
                            label: `custom_image:${imgId}`,
                            layerOpacity, outlineEffect: l.effects?.outline || null, waterEffect: l.effects?.water ? { ...l.effects.water, _kind: l.kind } : null,
                        });
                    }
                } else if (l.kind === "text") {
                    const textId = l.id.split(":")[1];
                    const t = textSprites.find((x) => x.id === textId) || null;
                    if (t) {
                        drawOps.push({
                            z, type: "text",
                            text: String(t.text || ""),
                            x: t.pos.x, y: t.pos.y,
                            rotation: t.rotation || 0,
                            flipX: !!t.flipX, flipY: !!t.flipY,
                            scale: clampSpriteScale(t.scale),
                            fontFamily: t.fontFamily,
                            fontSize: Math.max(8, Math.round(Number(t.fontSize) || 24)),
                            fontWeight: t.fontWeight || "normal",
                            italic: !!t.italic,
                            color: t.color || "#ffffff",
                            strokeColor: t.strokeColor || "#000000",
                            strokeWidth: Math.max(0, Number(t.strokeWidth) || 0),
                            label: `text:${textId}`,
                            layerOpacity,
                        });
                    }
                }
            }

            drawOps.sort((a, b) => a.z - b.z);

            const uniqueOps = drawOps
                .filter((op) => op.url)
                .reduce((acc, op) => {
                    if (!acc.has(op.url)) acc.set(op.url, op.label || op.url);
                    return acc;
                }, new Map());

            const loaded = await Promise.all(
                [...uniqueOps.entries()].map(([url, label]) => loadImg(url, label))
            );
            const imgMap = new Map([...uniqueOps.keys()].map((url, i) => [url, loaded[i]]));

            ctx.save();

            for (const op of drawOps) {
                if (op.type === "moodlight") {
                    ctx.save();
                    ctx.globalAlpha = op.opacity;
                    ctx.fillStyle = op.color;
                    ctx.fillRect(0, 0, W, H);
                    ctx.restore();
                    continue;
                }

                const opAlpha = (op.layerOpacity != null && op.layerOpacity < 100) ? op.layerOpacity / 100 : 1;

                if (op.type === "text") {
                    const italic = op.italic ? "italic" : "normal";
                    const weight = op.fontWeight === "bold" ? "bold" : "normal";
                    const tmpScale = scale * (op.scale || 1);
                    const fontFamilyLower = (op.fontFamily || "").toLowerCase();
                    const isPixelFont = fontFamilyLower.includes("volter") || fontFamilyLower.includes("jersey");

                    let tw, th, tmp;

                    if (isPixelFont) {
                        const fakeT = { fontFamily: op.fontFamily, fontSize: op.fontSize, fontWeight: op.fontWeight, italic: op.italic, strokeWidth: op.strokeWidth, text: op.text };
                        const metrics = getTextBoxMetrics(fakeT);
                        tw = metrics.w;
                        th = metrics.h;
                        tmp = document.createElement("canvas");
                        tmp.width = Math.max(1, tw);
                        tmp.height = Math.max(1, th);
                        const tctx = tmp.getContext("2d");
                        tctx.font = `${italic} ${weight} ${op.fontSize}px ${op.fontFamily}`;
                        tctx.textAlign = "center";
                        tctx.textBaseline = "middle";
                        tctx.fillStyle = op.color;
                        tctx.fillText(op.text, Math.round(tw / 2), Math.round(th / 2));
                        if (op.strokeWidth > 0) {
                            tctx.globalCompositeOperation = "destination-over";
                            tctx.lineJoin = "round";
                            tctx.lineWidth = op.strokeWidth * 2;
                            tctx.strokeStyle = op.strokeColor;
                            tctx.strokeText(op.text, Math.round(tw / 2), Math.round(th / 2));
                            tctx.globalCompositeOperation = "source-over";
                        }
                        const pd = tctx.getImageData(0, 0, tmp.width, tmp.height);
                        for (let i = 3; i < pd.data.length; i += 4) pd.data[i] = pd.data[i] >= 128 ? 255 : 0;
                        tctx.putImageData(pd, 0, 0);
                    } else {
                        const fakeT = { fontFamily: op.fontFamily, fontSize: op.fontSize * tmpScale, fontWeight: op.fontWeight, italic: op.italic, strokeWidth: op.strokeWidth * tmpScale, text: op.text };
                        const metrics = getTextBoxMetrics(fakeT);
                        const twFull = metrics.w;
                        const thFull = metrics.h;
                        tw = twFull / tmpScale;
                        th = thFull / tmpScale;
                        tmp = document.createElement("canvas");
                        tmp.width = Math.max(1, twFull);
                        tmp.height = Math.max(1, thFull);
                        const tctx = tmp.getContext("2d");
                        tctx.font = `${italic} ${weight} ${op.fontSize * tmpScale}px ${op.fontFamily}`;
                        tctx.textAlign = "center";
                        tctx.textBaseline = "middle";
                        tctx.fillStyle = op.color;
                        tctx.fillText(op.text, Math.round(twFull / 2), Math.round(thFull / 2));
                        if (op.strokeWidth > 0) {
                            tctx.globalCompositeOperation = "destination-over";
                            tctx.lineJoin = "round";
                            tctx.lineWidth = op.strokeWidth * tmpScale * 2;
                            tctx.strokeStyle = op.strokeColor;
                            tctx.strokeText(op.text, Math.round(twFull / 2), Math.round(thFull / 2));
                            tctx.globalCompositeOperation = "source-over";
                        }
                        if (crispText) {
                            // Crisp Text: threshold all font antialiasing fringe, same as pixel fonts.
                            const pd = tctx.getImageData(0, 0, tmp.width, tmp.height);
                            for (let i = 3; i < pd.data.length; i += 4) pd.data[i] = pd.data[i] >= 128 ? 255 : 0;
                            tctx.putImageData(pd, 0, 0);
                        }
                    }

                    const baseCx = Math.round(W / 2 + op.x);
                    const baseCy = Math.round(H / 2 + op.y);
                    const textOffsets = tileMode
                        ? [[-W, -H], [-W, 0], [-W, H], [0, -H], [0, 0], [0, H], [W, -H], [W, 0], [W, H]]
                        : [[0, 0]];
                    for (const [ox, oy] of textOffsets) {
                        ctx.save();
                        ctx.imageSmoothingEnabled = !crispText && !isPixelFont;
                        if (!crispText && !isPixelFont) ctx.imageSmoothingQuality = "high";
                        if (opAlpha < 1) ctx.globalAlpha = opAlpha;
                        ctx.translate(baseCx + ox, baseCy + oy);
                        if (op.rotation) ctx.rotate((op.rotation * Math.PI) / 180);
                        if (op.scale && op.scale !== 1) ctx.scale(op.scale, op.scale);
                        if (op.flipX) ctx.scale(-1, 1);
                        if (op.flipY) ctx.scale(1, -1);
                        ctx.drawImage(tmp, Math.round(-tw / 2), Math.round(-th / 2), tw, th);
                        ctx.restore();
                    }
                    tmp.width = 0;
                    continue;
                }

                let img = imgMap.get(op.url);
                if (!img) continue;

                if (op.outlineEffect?.enabled) {
                    const processed = applyOutlineEffect(img, op.outlineEffect.color || '#000000', op.outlineEffect.tolerance ?? 0);
                    if (processed) img = processed;
                }

                if (op.waterEffect?.enabled) {
                    const processed = applyWaterEffect(img, op.waterEffect.color || '#0a6e78', op.waterEffect.height ?? 20, op.waterEffect._kind || 'habbo', op.waterEffect.curveMode || 'adaptive');
                    if (processed) img = processed;
                }

                if (op.type === "tile") {
                    ctx.save();
                    if (opAlpha < 1) ctx.globalAlpha = opAlpha;
                    const tileW = Math.round(op.tileW);
                    const tileH = Math.round(op.tileH);
                    const tileOffX = typeof op.tileOffX === "number" ? op.tileOffX : 0;
                    const tileOffY = typeof op.tileOffY === "number" ? op.tileOffY : 0;
                    const startX = tileOffX - tileW;
                    const startY = tileOffY - tileH;
                    for (let y = startY; y < H + tileH; y += tileH) {
                        for (let x = startX; x < W + tileW; x += tileW) {
                            ctx.drawImage(img, x, y, tileW, tileH);
                        }
                    }
                    if (previewBg === "grey_grid" && validGridTint) {
                        ctx.save();
                        ctx.globalCompositeOperation = "screen";
                        ctx.fillStyle = `${validGridTint}80`;
                        ctx.fillRect(0, 0, W, H);
                        ctx.restore();
                    }
                    ctx.restore();
                } else if (op.type === "centered") {
                    ctx.save();
                    if (opAlpha < 1) ctx.globalAlpha = opAlpha;
                    const dw = Math.round(img.naturalWidth);
                    const dh = Math.round(img.naturalHeight);
                    const dx = Math.round(W / 2 - dw / 2);
                    const dy = Math.round(H / 2 - dh / 2);
                    ctx.drawImage(img, dx, dy, dw, dh);
                    ctx.restore();
                } else if (op.type === "sprite") {
                    if (opAlpha < 1) ctx.globalAlpha = opAlpha;
                    if (tileMode) {
                        for (const ox of [-W, 0, W]) {
                            for (const oy of [-H, 0, H]) {
                                drawSpriteToCanvas(img, { ...op, x: op.x + ox, y: op.y + oy });
                            }
                        }
                    } else {
                        drawSpriteToCanvas(img, op);
                    }
                    if (opAlpha < 1) ctx.globalAlpha = 1;
                } else if (op.type === "custom_image") {
                    const dw = img.naturalWidth || img.width || 0;
                    const dh = img.naturalHeight || img.height || 0;
                    if (dw && dh) {
                        if (tileMode) {
                            for (const ox of [-W, 0, W]) {
                                for (const oy of [-H, 0, H]) {
                                    const dx = Math.round(W / 2 + op.x + ox - dw / 2);
                                    const dy = Math.round(H / 2 + op.y + oy - dh / 2);
                                    ctx.drawImage(img, dx, dy, dw, dh);
                                }
                            }
                        } else {
                            const dx = Math.round(W / 2 + op.x - dw / 2);
                            const dy = Math.round(H / 2 + op.y - dh / 2);
                            ctx.drawImage(img, dx, dy, dw, dh);
                        }
                    }
                }
            }

            ctx.restore();

            if (exportBubbles.length) {
                const bubbleScale = 0.5;
                const rowH = Math.round(40 * bubbleScale);
                let topHabboY = null;
                sceneSprites.forEach((sp) => {
                    const cy = H / 2 + (cameraPos.y || 0) + sp.pos.y;
                    const t = cy - 50;
                    if (topHabboY === null || t < topHabboY) topHabboY = t;
                });
                const gapY = 2;
                const minTopMargin = H * 0.2;
                const stackOffset = Math.max(0, exportBubbles.length - 1) * (rowH + gapY);
                const minBaseY = minTopMargin + stackOffset;
                const baseY = topHabboY !== null ? Math.max(minBaseY, Math.round(topHabboY) - 35) : Math.max(minBaseY, 120);
                const leftW = Math.round(33 * bubbleScale);
                const rightW = Math.round(33 * bubbleScale);
                // Must match the DOM padX so the export and live preview agree on text x.
                const padX = Math.max(8, Math.round(16 * bubbleScale));
                const maxMidW = Math.max(20, Math.floor(W - 10 - (leftW + rightW)));

                const uniq = Array.from(new Set(
                    exportBubbles.flatMap((b) => [
                        b.assets?.frnt || speechPartsBase.frnt,
                        b.assets?.mid  || speechPartsBase.mid,
                        b.assets?.end  || speechPartsBase.end,
                    ])
                ));
                const bubbleLoaded = await Promise.all(uniq.map((src) => loadImg(src, "speech-bubble")));
                const bubbleImgMap = new Map();
                uniq.forEach((u, i) => { if (bubbleLoaded[i]) bubbleImgMap.set(u, bubbleLoaded[i]); });

                const regFont  = "9px VolterGoldfish, sans-serif";
                const boldFont = "9px VolterBoldGoldfish, VolterGoldfish, sans-serif";
                const measureW = (s, font) => { ctx.font = font; return Math.ceil(ctx.measureText(String(s || "")).width || 0); };
                const truncate = (s, maxW, font) => {
                    const str = String(s || "");
                    if (measureW(str, font) <= maxW) return str;
                    const ell = "…"; const ellW = measureW(ell, font);
                    let lo = 0, hi = str.length;
                    while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (measureW(str.slice(0, mid), font) + ellW <= maxW) lo = mid; else hi = mid - 1; }
                    return str.slice(0, lo) + ell;
                };

                for (let i = 0; i < exportBubbles.length; i++) {
                    const b = exportBubbles[i];
                    const sp = sceneSprites.find((x) => x.id === b.sceneHabboId) || null;
                    const username = (((b.displayName && b.displayName !== "Habbo") ? b.displayName : sp?.snap?.habboName || "Habbo") + "").trim();
                    const msgRaw = String(b.text || "").replace(/\s+/g, " ").trim();
                    const namePart = `${username}: `;
                    const nameW = measureW(namePart, boldFont);
                    const msgFont = b.shout ? boldFont : regFont;
                    const msg = truncate(msgRaw, Math.max(0, maxMidW - padX * 2 - nameW), msgFont);
                    const textW = nameW + measureW(msg, msgFont);
                    const midW = Math.min(maxMidW, Math.max(20, textW + padX * 2 + Math.round(14 * bubbleScale)));
                    const totalW = leftW + midW + rightW;
                    const xCenter = Math.round(W / 2 + (cameraPos.x || 0) + (sp?.pos?.x || 0));
                    let left = Math.round(xCenter - totalW / 2);
                    if (left < 5) left = 5;
                    if (left > W - 5 - totalW) left = Math.round(W - 5 - totalW);
                    const y = baseY - (exportBubbles.length - 1 - i) * (rowH + gapY);
                    const frntIm = bubbleImgMap.get(b.assets?.frnt || speechPartsBase.frnt) || null;
                    const midIm  = bubbleImgMap.get(b.assets?.mid  || speechPartsBase.mid)  || null;
                    const endIm  = bubbleImgMap.get(b.assets?.end  || speechPartsBase.end)  || null;
                    const fSrcW = frntIm ? (frntIm.naturalWidth  || 33) : 33;
                    const eSrcW = endIm  ? (endIm.naturalWidth   || 33) : 33;
                    const mSrcW = midIm  ? (midIm.naturalWidth   || 4)  : 4;
                    const mSrcH = midIm  ? (midIm.naturalHeight  || 40) : 40;
                    const dispH = rowH + 1;
                    const downscalePart = (img, srcW, srcH, dW, dH) => {
                        const out = document.createElement("canvas");
                        out.width = Math.max(1, dW); out.height = Math.max(1, dH);
                        const octx = out.getContext("2d");
                        if (!img || !dW || !dH) return out;
                        const tmp = document.createElement("canvas");
                        tmp.width = srcW; tmp.height = srcH;
                        const tctx = tmp.getContext("2d");
                        tctx.imageSmoothingEnabled = false;
                        tctx.drawImage(img, 0, 0, srcW, srcH);
                        const src = tctx.getImageData(0, 0, srcW, srcH).data;
                        const dst = octx.createImageData(dW, dH);
                        for (let dy = 0; dy < dH; dy++) {
                            const sy = Math.floor((dy + 0.5) * srcH / dH);
                            for (let dx = 0; dx < dW; dx++) {
                                const sx = Math.floor((dx + 0.5) * srcW / dW);
                                const si = (sy * srcW + sx) * 4;
                                const di = (dy * dW + dx) * 4;
                                dst.data[di]     = src[si];
                                dst.data[di + 1] = src[si + 1];
                                dst.data[di + 2] = src[si + 2];
                                dst.data[di + 3] = src[si + 3];
                            }
                        }
                        octx.putImageData(dst, 0, 0);
                        return out;
                    };
                    const frntC   = downscalePart(frntIm, fSrcW, mSrcH, leftW, dispH);
                    const endC    = downscalePart(endIm,  eSrcW, mSrcH, rightW, dispH);
                    const midTileW = Math.max(1, Math.round(mSrcW * bubbleScale));
                    const midTile = downscalePart(midIm,  mSrcW, mSrcH, midTileW, dispH);
                    ctx.imageSmoothingEnabled = false;
                    ctx.drawImage(frntC, left, y, leftW, dispH);
                    for (let xx = 0; xx < midW; xx += midTileW) {
                        const tw = Math.min(midTileW, midW - xx);
                        ctx.drawImage(midTile, 0, 0, tw, dispH, left + leftW + xx, y, tw, dispH);
                    }
                    ctx.drawImage(endC, left + leftW + midW, y, rightW, dispH);
                    const drawBubbleText = (str, font, strW, destX) => {
                        if (!str) return;
                        const bTmp = document.createElement("canvas");
                        bTmp.width  = Math.max(1, strW + 4);
                        bTmp.height = Math.max(1, rowH + 4);
                        const btctx = bTmp.getContext("2d");
                        btctx.font = font; btctx.textBaseline = "middle"; btctx.fillStyle = "#000";
                        btctx.fillText(str, 0, bTmp.height / 2);
                        // Volter is a pixel font - threshold alpha to remove antialiasing fringe.
                        const bpd = btctx.getImageData(0, 0, bTmp.width, bTmp.height);
                        for (let qi = 3; qi < bpd.data.length; qi += 4) bpd.data[qi] = bpd.data[qi] >= 128 ? 255 : 0;
                        btctx.putImageData(bpd, 0, 0);
                        ctx.imageSmoothingEnabled = false;
                        ctx.drawImage(bTmp, destX, y - 2, bTmp.width, bTmp.height);
                    };
                    drawBubbleText(namePart, boldFont, nameW, left + leftW + padX);
                    drawBubbleText(msg, msgFont, measureW(msg, msgFont), left + leftW + padX + nameW);
                }
            }

            // Image border - top layer, drawn after all sprites and speech bubbles
            if (imageBorderEnabled) {
                const bw = Math.max(1, Math.round(imageBorderWidth));
                ctx.save();
                ctx.fillStyle = imageBorderColor || "#ffffff";
                ctx.fillRect(0, 0, W, bw);
                ctx.fillRect(0, H - bw, W, bw);
                ctx.fillRect(0, bw, bw, H - bw * 2);
                ctx.fillRect(W - bw, bw, bw, H - bw * 2);
                ctx.restore();
            }

            if (returnUrl) {
                return canvas.toDataURL("image/png");
            }

            const mimeType = format === "webp" ? "image/webp" : "image/png";
            const ext = format === "webp" ? "webp" : "png";
            const baseName = (fileName || "").trim() || `franklyorigins_${Date.now()}`;

            canvas.toBlob((blob) => {
                if (!blob) return;
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${baseName}.${ext}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            }, mimeType);

        } finally {
            if (!returnUrl) setIsCapturing(false);
        }
    };

    // ─── Zoom Dragging ───
    const bgPanRef = useRef(null);

    const canPanBg =
        !isCapturing &&
        !sceneLoading &&
        !spriteSelected &&
        !activeFurniId &&
        !activeCustomImageId &&
        !activeTextId;

    const onBgPanMove = (e) => {
        const st = bgPanRef.current;
        if (!st) return;
        e.preventDefault();

        const dx = (e.clientX - st.startClientX);
        const dy = (e.clientY - st.startClientY);

        setCameraSafe({
            x: st.startCam.x + dx,
            y: st.startCam.y + dy,
        });
    };

    const onBgPanUp = () => {
        bgPanRef.current = null;
        window.removeEventListener("pointermove", onBgPanMove);
        window.removeEventListener("pointerup", onBgPanUp);
        window.removeEventListener("pointercancel", onBgPanUp);
    };

    // ─── Drag Refs ───
    const spriteElRef = useRef(null);

    const dragRef = useRef({
        active: false,
        pointerId: null,
        startClientX: 0,
        startClientY: 0,
        originX: 0,
        originY: 0,
        nextX: 0,
        nextY: 0,
        raf: 0,
    });

    const flushDrag = () => {
        const d = dragRef.current;
        d.raf = 0;

        const el = spriteElRef.current;
        if (!el) return;

        const sx = snapToSpritePixel(d.nextX, d.sc);
        const sy = snapToSpritePixel(d.nextY, d.sc);
        const tx = sx - Math.round((d.sw || 0) / 2) + (d.cl || 0);
        const ty = sy - Math.round((d.sh || 0) / 2) + (d.ct || 0);
        const rot = d.rotation ? ` rotate(${d.rotation}deg)` : '';
        el.style.transform = `translate3d(${tx}px, ${ty}px, 0px)${rot}`;
        el.style.willChange = "transform";
    };

    const scheduleDragFlush = () => {
        const d = dragRef.current;
        if (d.raf) return;
        d.raf = window.requestAnimationFrame(flushDrag);
    };

    const endSpriteDrag = () => {
        const d = dragRef.current;

        if (d.active) {
            if (Math.round(d.nextX) !== Math.round(d.originX) || Math.round(d.nextY) !== Math.round(d.originY)) {
                pushHistory();
            }
            const _spdWrapped = wrapTilePos(snapToSpritePixel(d.nextX, d.sc), snapToSpritePixel(d.nextY, d.sc));
            setSpritePos(_spdWrapped);
        }

        clearSnapGuides();

        d.active = false;
        d.pointerId = null;

        if (d.raf) {
            window.cancelAnimationFrame(d.raf);
            d.raf = 0;
        }

        const el = spriteElRef.current;
        if (el) el.style.willChange = "";
    };

    // ─── Sprite Dragging ───
    const onSpritePointerDown = (e, opts = {}) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const layerId = opts.layerId;
        const layer = layers.find((l) => l.id === layerId);
        if (layer && (layer.locked || layer.permaLock)) return;

        setActiveLayerId(layerId);
        setActiveCustomImageId(null);

        if (layerId.startsWith("habbo:")) {
            const hid = layerId.slice("habbo:".length);
            setActiveSceneHabboId(hid);
            setActiveFurniId(null);
            beginIconLoad();
        }

        setSpriteSelected(true);

        const d = dragRef.current;
        d.active = true;
        d.pointerId = e.pointerId;
        d.startClientX = e.clientX;
        d.startClientY = e.clientY;
        d.originX = spritePos.x;
        d.originY = spritePos.y;
        d.nextX = spritePos.x;
        d.nextY = spritePos.y;
        // Capture transform offsets so flushDrag matches spriteWrapperTransform
        const _sc = clampSpriteScale(activeSpriteScale);
        d.sc = _sc;
        d.sw = (activeSpriteSourceSize?.w || 0) * _sc;
        d.sh = (activeSpriteSourceSize?.h || 0) * _sc;
        d.cl = (activeSpriteCrop?.left || 0) * _sc;
        d.ct = (activeSpriteCrop?.top  || 0) * _sc;
        d.rotation = activeSpriteRotation || 0;

        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const onSpritePointerMove = (e) => {
        const d = dragRef.current;
        if (!d.active || d.pointerId !== e.pointerId) return;

        const dx = (e.clientX - d.startClientX) / (zoom || 1);
        const dy = (e.clientY - d.startClientY) / (zoom || 1);

        const snapped = applyWeakSpriteSnap(
            {
                x: d.originX + dx,
                y: d.originY + dy,
            },
            activeLayerId === "habbo:__active__"
                ? { type: "activeHabbo", id: "__active__" }
                : activeSceneHabboId
                    ? { type: "sceneHabbo", id: activeSceneHabboId }
                    : null,
            { axisSnap: !!e.shiftKey },
        );

        d.nextX = snapped.x;
        d.nextY = snapped.y;

        scheduleDragFlush();
    };

    const onSpritePointerUp = (e) => {
        const d = dragRef.current;
        if (d.pointerId !== e.pointerId) return;
        endSpriteDrag();
    };

    // ─── Multi-selection helpers & group drag ───
    // Shift+click toggles a sprite in/out of the multi-selection.
    const toggleLayerSelection = (layerId) => {
        const cur = selectedLayerIdsRef.current;
        if (cur.includes(layerId)) {
            const next = cur.filter((x) => x !== layerId);
            setSelectedLayerIds(next);
            if (activeLayerId === layerId) {
                const fallback = next[next.length - 1] || null;
                if (fallback) selectLayerById(fallback);
                else { setActiveLayerId(null); setSpriteSelected(false); }
            }
        } else {
            setSelectedLayerIds([...cur, layerId]);
            selectLayerById(layerId);
        }
    };

    const onGroupPointerMove = (e) => {
        const g = groupDragRef.current;
        if (!g || g.pointerId !== e.pointerId) return;
        e.preventDefault();
        const dx = (e.clientX - g.startClientX) / (zoom || 1);
        const dy = (e.clientY - g.startClientY) / (zoom || 1);
        if (!g.moved && Math.abs(dx) + Math.abs(dy) > 3) {
            g.moved = true;
            if (!g.pushed) { pushHistory(); g.pushed = true; }
        }
        g.dx = dx;
        g.dy = dy;
        if (g.moved && !g.raf) {
            g.raf = requestAnimationFrame(() => {
                g.raf = 0;
                // Snap during drag so the whole group always sits on integer pixels.
                const sdx = snapToSpritePixel(g.dx, 1);
                const sdy = snapToSpritePixel(g.dy, 1);
                setSprites((prev) => prev.map((s) => {
                    const it = g.items.find((i) => i.id === s.id);
                    return it ? { ...s, pos: { x: it.startX + sdx, y: it.startY + sdy } } : s;
                }));
            });
        }
    };

    const onGroupPointerUp = () => {
        const g = groupDragRef.current;
        groupDragRef.current = null;
        window.removeEventListener("pointermove", onGroupPointerMove);
        window.removeEventListener("pointerup", onGroupPointerUp);
        window.removeEventListener("pointercancel", onGroupPointerUp);
        if (!g) return;
        if (g.raf) cancelAnimationFrame(g.raf);
        if (g.moved) {
            setSprites((prev) => prev.map((s) => {
                const it = g.items.find((i) => i.id === s.id);
                if (!it) return s;
                const sc = it.sc || 1;
                return { ...s, pos: wrapTilePos(snapToSpritePixel(it.startX + g.dx, sc), snapToSpritePixel(it.startY + g.dy, sc)) };
            }));
        } else if (g.clickedLayerId) {
            // A plain click (no drag) on a multi-selected sprite collapses to that one.
            setSelectedLayerIds([g.clickedLayerId]);
            selectLayerById(g.clickedLayerId);
        }
    };

    // Returns true if a group drag was started (caller should stop its own single-drag setup).
    const beginGroupDrag = (e, clickedLayerId) => {
        const ids = selectedLayerIdsRef.current;
        if (ids.length < 2 || !ids.includes(clickedLayerId)) return false;
        const items = [];
        for (const lid of ids) {
            const rawId = lid.slice(lid.indexOf(":") + 1);
            const sp = sprites.find((s) => s.id === rawId);
            if (!sp || !sp.pos) continue;
            const sc = clampSpriteScale(sp.snap?.scale || sp.scale || 1);
            items.push({ id: rawId, startX: sp.pos.x, startY: sp.pos.y, sc });
        }
        if (items.length < 2) return false;
        groupDragRef.current = {
            pointerId: e.pointerId,
            startClientX: e.clientX,
            startClientY: e.clientY,
            items, dx: 0, dy: 0, moved: false, pushed: false, raf: 0,
            clickedLayerId,
        };
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
        window.addEventListener("pointermove", onGroupPointerMove, { passive: false });
        window.addEventListener("pointerup", onGroupPointerUp, { passive: false });
        window.addEventListener("pointercancel", onGroupPointerUp, { passive: false });
        return true;
    };

    // Shared intercept placed at the top of every sprite pointer-down handler.
    // Returns true when the gesture was consumed (shift-toggle or group-drag start).
    const handleSpriteSelectPointerDown = (e, layerId) => {
        if (e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            toggleLayerSelection(layerId);
            return true;
        }
        if (beginGroupDrag(e, layerId)) {
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
        return false;
    };

    // ─── Scene Persistence ───
    const SCENES_KEY = resolvedConfig.scenesKey;
    const ACTIVE_SCENE_ID_KEY = resolvedConfig.activeSceneKey;
    const MAX_SCENES = 1000;

    const IMAGER_META_KEY = resolvedConfig.storageKey;

    const readMeta = () => {
        try {
            const raw = localStorage.getItem(IMAGER_META_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (!parsed || typeof parsed !== "object") return { updatedAt: 0, lastDbSyncAt: 0, dirty: false, lastDismissedUpdateAt: 0 };
            return {
                updatedAt: Number(parsed.updatedAt) || 0,
                lastDbSyncAt: Number(parsed.lastDbSyncAt) || 0,
                dirty: !!parsed.dirty,
                lastDismissedUpdateAt: Number(parsed.lastDismissedUpdateAt) || 0,
            };
        } catch {
            return { updatedAt: 0, lastDbSyncAt: 0, dirty: false, lastDismissedUpdateAt: 0 };
        }
    };

    const writeMeta = (meta) => {
        try {
            localStorage.setItem(IMAGER_META_KEY, JSON.stringify(meta));
        } catch {}
    };

    const touchMetaDirty = () => {
        const now = Date.now();
        const m = readMeta();
        const next = { ...m, updatedAt: now, dirty: true };
        writeMeta(next);
        return next;
    };

    const markDbSynced = (ts) => {
        const m = readMeta();
        const next = { ...m, lastDbSyncAt: ts, dirty: false };
        writeMeta(next);
        return next;
    };

    const uint8ToBase64 = (u8) => {
        let s = "";
        const chunk = 0x8000;
        for (let i = 0; i < u8.length; i += chunk) {
            s += String.fromCharCode.apply(null, u8.subarray(i, i + chunk));
        }
        return btoa(s);
    };

    const base64ToUint8 = (b64) => {
        const bin = atob(b64);
        const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        return u8;
    };

    const utf8ToBinarySafeB64 = (str) => btoa(unescape(encodeURIComponent(str)));
    const binarySafeB64ToUtf8 = (b64) => decodeURIComponent(escape(atob(b64)));

    const gzipStringToBase64 = async (str) => {
        try {
            if (typeof CompressionStream === "undefined") return utf8ToBinarySafeB64(str);
            const cs = new CompressionStream("gzip");
            const stream = new Blob([str]).stream().pipeThrough(cs);
            const ab = await new Response(stream).arrayBuffer();
            return uint8ToBase64(new Uint8Array(ab));
        } catch {
            return utf8ToBinarySafeB64(str);
        }
    };

    const gunzipBase64ToString = async (b64) => {
        try {
            if (typeof DecompressionStream === "undefined") return binarySafeB64ToUtf8(b64);
            const ds = new DecompressionStream("gzip");
            const u8 = base64ToUint8(b64);
            const stream = new Blob([u8]).stream().pipeThrough(ds);
            return await new Response(stream).text();
        } catch {
            return binarySafeB64ToUtf8(b64);
        }
    };

    const getCurrentUserIdForDb = () => {
        return resolvedConfig.overrideUserId || userData?.userId || null;
    };

    const fetchImagerBlobFromDb = async (userId) => {
        if (!userId) return null;
        // Shared-key variant: use the dedicated shared endpoint.
        if (resolvedConfig.overrideUserId) {
            if (!resolvedConfig.sharedKey) return null;
            try {
                const res = await getSharedImagerBlob(resolvedConfig.sharedKey);
                if (!res?.success || !res?.data) return null;
                return res.data;
            } catch {
                return null;
            }
        }
        try {
            const res = await getImagerBlob();
            if (!res?.success || !res?.data) return null;
            return res.data;
        } catch {
            return null;
        }
    };

    const pushImagerBlobToDb = async (userId, payload) => {
        if (!userId) return false;
        // Shared-key variant: use the dedicated shared endpoint.
        if (resolvedConfig.overrideUserId) {
            if (!resolvedConfig.sharedKey) return false;
            try {
                const res = await upsertSharedImagerBlob(resolvedConfig.sharedKey, payload.encoding, payload.blob, payload.updatedAt);
                return !!res?.success;
            } catch {
                return false;
            }
        }
        try {
            const res = await upsertImagerBlob(payload.encoding, payload.blob, payload.updatedAt);
            return !!res?.success;
        } catch {
            return false;
        }
    };

    const perRowSyncInFlight = useRef(new Set());

    const syncSceneToDb = async (scene, { silent = false } = {}) => {
        if (!scene?.id) return;
        const userId = getCurrentUserIdForDb();
        if (!userId || resolvedConfig.overrideUserId) return;
        if (perRowSyncInFlight.current.has(scene.id)) return;
        perRowSyncInFlight.current.add(scene.id);
        try {
            const rawJson = JSON.stringify(scene);
            const compressed = await gzipStringToBase64(rawJson);
            const envelope = JSON.stringify({ encoding: 'gzip+base64', blob: compressed });
            const storedBytes = new Blob([envelope]).size;

            // Update size info for the active scene
            if (scene.id === activeSceneId) {
                const pct = Math.round((storedBytes / SCENE_MAX_STORED_BYTES) * 100);
                setActiveSceneSizeInfo({ bytes: storedBytes, pct });
                if (pct >= 99) {
                    if (!silent) toast.error(`Scene is 100% full - remove content before saving.`);
                    return;
                }
                if (pct >= 90 && !silent) toast.warn(`Scene storage at ${pct}% - consider cleaning up sprites.`);
                else if (pct >= 80 && !silent) toast.warn(`Scene storage at ${pct}%.`);
            }

            if (storedBytes > SCENE_MAX_STORED_BYTES) {
                if (!silent) toast.error(`Scene is too large to save (${Math.round(storedBytes / 1024)}KB / ${Math.round(SCENE_MAX_STORED_BYTES / 1024)}KB max).`);
                return;
            }

            const title = scene.imageName || 'Untitled';
            const thumbnailUrl = scene.thumbnailUrl || '';
            if (scene.dbId) {
                const res = await updateImagerScene(scene.dbId, { title, sceneJson: envelope, thumbnailUrl, isPublic: false });
                if (!res?.success && !silent) toast.error(`Scene save failed: ${res?.message || 'Unknown error'}`);
            } else {
                const res = await saveImagerScene(title, envelope, thumbnailUrl, false);
                if (res?.success && res?.id) {
                    const newDbId = res.id;
                    setScenes((prev) => {
                        const idx = prev.findIndex((s) => s?.id === scene.id);
                        if (idx === -1) return prev;
                        const next = prev.slice();
                        next[idx] = { ...next[idx], dbId: newDbId };
                        writeScenesRaw(next);
                        return next;
                    });
                } else if (!silent) {
                    toast.error(`Scene save failed: ${res?.message || 'Unknown error'}`);
                }
            }
        } catch (err) {
            if (!silent) toast.error('Scene save failed - check your connection.');
        } finally {
            perRowSyncInFlight.current.delete(scene.id);
        }
    };

    const buildPersistPayloadFromSession = () => {
        const meta = readMeta();
        return {
            scenes: readScenes(),
            activeSceneId: readActiveSceneId(),
            updatedAt: Number(meta.updatedAt) || 0,
            lastDismissedUpdateAt: Number(meta.lastDismissedUpdateAt) || 0,
            lastDismissedVersion: meta.lastDismissedVersion || "",
        };
    };

    const buildCompressedRow = async () => {
        const payload = buildPersistPayloadFromSession();
        const json = JSON.stringify(payload);
        const blob = await gzipStringToBase64(json);
        return { encoding: "gzip+base64", blob, updatedAt: payload.updatedAt || Date.now() };
    };

    const decodeCompressedRowToPayload = async (row) => {
        if (!row || !row.blob) return null;
        const json = await gunzipBase64ToString(row.blob);
        const payload = json ? JSON.parse(json) : null;
        if (!payload || typeof payload !== "object") return null;
        return payload;
    };

    const makeId = () => String(Date.now()) + "_" + Math.random().toString(16).slice(2);

    const FURNI_API_URL = process.env.REACT_APP_FURNI_API_URL || "https://api.rhyss.dev/api/furni/latest";

    const normaliseFurni = (results, maxItems = Infinity) => {
        if (!Array.isArray(results)) return [];
        const items = [];

        for (let i = 0; i < results.length && items.length < maxItems; i++) {
            const item = results[i];

            const raw = Array.isArray(item?.largeImages) ? item.largeImages : [];
            const images = raw
                .map((row) => (Array.isArray(row) ? row.filter(Boolean) : []))
                .filter((row) => row.length);

            if (!images.length) continue;

            const rawShadow = Array.isArray(item?.largeShadowImages) ? item.largeShadowImages : [];
            const shadowImages = rawShadow
                .map((row) => (Array.isArray(row) ? row.filter(Boolean) : []))
                .filter((row) => row.length);

            const s = Math.floor(Math.random() * images.length);
            const r = Math.floor(Math.random() * (images[s]?.length || 1));
            const url = images[s][r];

            items.push({
                id: item._id || item.name || i,
                name: item.name || "",
                url,
                images,
                shadowImages,
            });
        }

        return items;
    };

    const buildFoFurniUrl = (code, dir, state, frame) => {
        const raw = String(code || "").trim();
        if (!raw) return "";
        const furni = raw.replace(/%2a/gi, "*");
        const d = Number.isFinite(dir) ? Number(dir) : 0;
        const s = Number.isFinite(state) ? Number(state) : 0;
        const fr = Number.isFinite(frame) ? Number(frame) : 0;
        return `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(furni)}&direction=${d}&state=${s}&frame=${fr}&width=120&height=120`;
    };

    const buildColoredFurniUrl = (code, dir, state, frame, hex) => {
        const raw = String(code || "").trim();
        if (!raw) return "";
        const furni = raw.replace(/%2a/gi, "*");
        const d = Number.isFinite(dir) ? Number(dir) : 0;
        const s = Number.isFinite(state) ? Number(state) : 0;
        const fr = Number.isFinite(frame) ? Number(frame) : 0;
        const h = encodeURIComponent(String(hex || "").trim());
        return `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(furni)}&direction=${d}&state=${s}&frame=${fr}&width=120&height=120&hex=${h}`;
    };

    const getFurniRenderUrl = (sp) => {
        if (!sp) return "";
        if (sp.hex) return buildColoredFurniUrl(sp.code, sp.rotI, sp.stateI, sp.frameI ?? 0, sp.hex);
        const fo = buildFoFurniUrl(sp.code, sp.rotI, sp.stateI, sp.frameI ?? 0);
        return fo || sp?.url || sp?.baseUrl || "";
    };

    const getActiveFurni = () => (activeFurniId ? furniSpritesRef.current.find((x) => x.id === activeFurniId) || null : null);

    const recomputeFurniVariants = (sp, nextStateI, nextRotI) => {
        const images = Array.isArray(sp?.images) ? sp.images : [];
        if (!images.length) return null;

        const sMax = images.length - 1;
        const s = clamp(Number(nextStateI) || 0, 0, sMax);

        const row = Array.isArray(images[s]) ? images[s] : [];
        if (!row.length) return null;

        const rMax = row.length - 1;
        const r = clamp(Number(nextRotI) || 0, 0, rMax);

        const code = String(sp?.code || "").trim();
        if (!code) {
            const rotations = row;
            const states = images.map((rr) => rr?.[r] || rr?.[0]).filter(Boolean);
            return { stateI: s, rotI: r, rotations, states };
        }

        const hex = sp?.hex || null;
        const fr = Number.isFinite(sp?.frameI) ? sp.frameI : 0;
        const buildUrl = hex
            ? (dir, st) => buildColoredFurniUrl(code, dir, st, fr, hex)
            : (dir, st) => buildFoFurniUrl(code, dir, st);
        const rotations = row.map((_, rotIndex) => buildUrl(rotIndex, s)).filter(Boolean);
        const states = images.map((_, stateIndex) => buildUrl(r, stateIndex)).filter(Boolean);

        return { stateI: s, rotI: r, rotations, states };
    };

    const setActiveFurniRotationIndex = async (nextI) => {
        const sp = getActiveFurni();
        if (!sp) return;
        const code = String(sp.code || "").trim();
        if (code) {
            // URL is built dynamically from code - all 7 directions are valid
            pushHistory();
            updateFurniSprite(sp.id, { rotI: Math.max(0, Math.min(6, nextI)) });
            return;
        }
        const patch = recomputeFurniVariants(sp, sp.stateI, nextI);
        if (!patch) return;
        pushHistory();
        updateFurniSprite(sp.id, patch);
    };

    const setActiveFurniStateIndex = async (nextI) => {
        const sp = getActiveFurni();
        if (!sp) return;
        const code = String(sp.code || "").trim();
        if (code) {
            pushHistory();
            updateFurniSprite(sp.id, { stateI: Math.max(0, Math.min(6, nextI)) });
            return;
        }
        const patch = recomputeFurniVariants(sp, nextI, sp.rotI);
        if (!patch) return;
        pushHistory();
        updateFurniSprite(sp.id, patch);
    };

    const setActiveFurniFrameIndex = (nextI) => {
        const sp = getActiveFurni();
        if (!sp) return;
        pushHistory();
        updateFurniSprite(sp.id, { frameI: Math.max(0, Number(nextI) || 0) });
    };

    const setActiveFurniFlipped = () => {
        const sp = getActiveFurni();
        if (!sp) return;
        updateFurniSprite(sp.id, { flipped: !sp.flipped });
    };

    const updateFurniSprite = (id, patch) => {
        setSprites((prev) => {
            const idx = prev.findIndex((x) => x.id === id && x.type === 'furni');
            if (idx === -1) return prev;
            const next = prev.slice();
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(FURNI_API_URL, { cache: "no-store" });
                if (!res.ok) return;
                const data = await res.json();

                const furniResults = data?.results || [];
                const baseList = normaliseFurni(furniResults, 20);

                // Show immediately with no codes so thumbnails appear right away
                setFurniItems(baseList.map((x) => ({ ...x, code: null, thumbUrl: x.url })));

                // Resolve each item's code by searching our own API by name
                const resolved = await Promise.all(
                    baseList.map(async (x) => {
                        try {
                            const result = await searchFurniItems(x.name);
                            const items = Array.isArray(result?.items) ? result.items : [];
                            const code = items[0]?.code || null;
                            return { ...x, code, thumbUrl: x.url };
                        } catch {
                            return { ...x, code: null, thumbUrl: x.url };
                        }
                    })
                );

                setFurniItems(resolved);
            } catch {}
        })();
    }, []);

    const habboKeyOf = (snap) => `${(snap?.hotel || "COM").toUpperCase()}:${(snap?.habboName || "").trim()}`.toLowerCase();

    const readScenes = () => {
        try {
            const raw = localStorage.getItem(SCENES_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const writeScenesRaw = (scenes) => {
        try {
            localStorage.setItem(SCENES_KEY, JSON.stringify(scenes));
        } catch {}
    };

    const writeScenes = (scenes) => {
        writeScenesRaw(scenes);
        touchMetaDirty();
    };

    const readActiveSceneId = () => {
        try {
            const id = localStorage.getItem(ACTIVE_SCENE_ID_KEY);
            return id || null;
        } catch {
            return null;
        }
    };

    const writeActiveSceneIdRaw = (id) => {
        try {
            if (id) localStorage.setItem(ACTIVE_SCENE_ID_KEY, id);
            else localStorage.removeItem(ACTIVE_SCENE_ID_KEY);
        } catch {}
    };

    const writeActiveSceneId = (id) => {
        writeActiveSceneIdRaw(id);
        touchMetaDirty();
    };

    const [scenes, setScenes] = useState([]);
    const [activeSceneId, setActiveSceneId] = useState(null);

    useEffect(() => {
        if (!activeSceneId) return;
        try {
            const raw = localStorage.getItem(`habboimager:history:${activeSceneId}`);
            const parsed = raw ? JSON.parse(raw) : null;
            const h = {
                past: Array.isArray(parsed?.past) ? parsed.past.slice(-30) : [],
                future: Array.isArray(parsed?.future) ? parsed.future.slice(0, 30) : [],
            };
            historyRef.current = h;
            setCanUndo(h.past.length > 0);
            setCanRedo(h.future.length > 0);
        } catch {
            historyRef.current = { past: [], future: [] };
            setCanUndo(false);
            setCanRedo(false);
        }
    }, [activeSceneId]);

    const [sceneOwnerKey, setSceneOwnerKey] = useState(null);

    const hasRealHabboRef = useRef(false);
    const [readyToShowHabbo, setReadyToShowHabbo] = useState(false);
    const hydratedRef = useRef(false);
    const restoringRef = useRef(false);

    const preEditActiveRef = useRef(null);

    // ─── Validation & Derived Flags ───
    const isValidHabboName = (name) => {
        const n = (name || "").trim();
        if (!n) return false;
        if (n === DEFAULT_PLACEHOLDER_HABBO) return false;
        return true;
    };

    const effectiveCustom = useFrankHatForDefault || frankify ? "frank_hat" : null;

    const ensureHotelParam = (url, hotelVal) => {
        if (!url) return url;
        if (url.includes("hotel=")) return url;
        const join = url.includes("?") ? "&" : "?";
        return `${url}${join}hotel=${encodeURIComponent((hotelVal || "COM").toUpperCase())}`;
    };

    // ─── Background Config ───
    const previewBgConfig = useMemo(
        () => ({
            default: { type: "flat", label: "Background", thumb: { type: "flat" } },
            room_tile: {
                key: "room_tile",
                label: "Room Tile",
                type: "room_tile",
                url: "/img/roomTile.png",
                tileW: 64,
                tileH: 41,
                rowStepY: 16,
                startYRatio: 0.35,
                offsetX: 0,
                offsetY: 0,
            },
            grid: {
                type: "tile",
                label: "Frankly Tile",
                url: `${process.env.PUBLIC_URL}/img/red_tile.webp`,
                size: { w: 64, h: 32 },
                thumb: { type: "tile" },
            },
            grey_grid: {
                type: "tile",
                label: "Grey Tile",
                url: `${process.env.PUBLIC_URL}/img/grey_tile.png`,
                size: { w: 64, h: 32 },
                thumb: { type: "tile" },
            },
            proimager: {
                type: "layered",
                label: "Iconic",
                topLayerEnabled: true,
                topLayer: { key: "bg_3", allowHide: true },
                layers: {
                    bg: `${process.env.PUBLIC_URL}/img/proimager/chicka_bg.png`,
                    bg_1: `${process.env.PUBLIC_URL}/img/proimager/chicka_bg_1.png`,
                    bg_2: `${process.env.PUBLIC_URL}/img/proimager/chicka_bg_2.png`,
                    bg_3: `${process.env.PUBLIC_URL}/img/proimager/chicka_bg_3.png`,
                },
                rules: {
                    bg_1: { yLine: -10 },
                    bg_2: { yLine: 10 },
                },
                thumb: { type: "image", layerKey: "bg", offset: { x: -300, y: -250 }, border: "black" },
            },
            fishingtv: {
                type: "layered",
                label: "FishingTV",
                topLayerEnabled: true,
                topLayer: { key: "bg_3", allowHide: true },
                layers: {
                    bg: `${process.env.PUBLIC_URL}/img/proimager/osiris_bg.png`,
                    bg_1: `${process.env.PUBLIC_URL}/img/proimager/osiris_bg_1.png`,
                    bg_2: `${process.env.PUBLIC_URL}/img/proimager/osiris_bg_2.png`,
                    bg_3: `${process.env.PUBLIC_URL}/img/proimager/osiris_bg_3.png`,
                },
                rules: {
                    bg_1: { yLine: -10 },
                    bg_2: { yLine: 10 },
                },
                thumb: { type: "image", layerKey: "bg", offset: { x: -420, y: -194 }, border: "black" },
            },
            caddy: {
                type: "layered",
                label: "Summoning",
                topLayerEnabled: true,
                topLayer: { key: "bg_1", allowHide: false },
                layers: {
                    bg: `${process.env.PUBLIC_URL}/img/proimager/caddy_bg.png`,
                    bg_1: `${process.env.PUBLIC_URL}/img/proimager/caddy_bg_1.png`,
                },
                rules: {},
                thumb: { type: "image", layerKey: "bg", offset: { x: -550, y: -40 }, border: "white" },
            },
            space: {
                type: "layered",
                label: "Space",
                layers: {
                    bg: `${process.env.PUBLIC_URL}/img/proimager/space_bg.png`,
                    bg_1: `${process.env.PUBLIC_URL}/img/proimager/space_bg_1.png`,
                },
                rules: {
                },
                thumb: { type: "image", layerKey: "bg", offset: { x: -165, y: -300 }, border: "white" },
            },
            sauna: {
                type: "layered",
                label: "Sauna",
                layers: {
                    bg: `${process.env.PUBLIC_URL}/img/proimager/sauna_bg.png`,
                    bg_1: `${process.env.PUBLIC_URL}/img/proimager/sauna_bg_1.png`,
                },
                rules: {
                    noHide: ["bg_1"],
                },
                thumb: { type: "image", layerKey: "bg", offset: { x: -165, y: -300 }, border: "white" },
            },
            studio: {
                type: "layered",
                label: "Frankly Studios",
                topLayerEnabled: true,
                topLayer: { key: "bg_3", allowHide: false },
                layers: {
                    bg: `${process.env.PUBLIC_URL}/img/proimager/franklystudios_bg.png`,
                    bg_1: `${process.env.PUBLIC_URL}/img/proimager/franklystudios_bg_1.png`,
                    bg_2: `${process.env.PUBLIC_URL}/img/proimager/franklystudios_bg_2.png`,
                    bg_3: `${process.env.PUBLIC_URL}/img/proimager/franklystudios_bg_3.png`,
                },
                rules: {},
                thumb: { type: "image", layerKey: "bg", offset: { x: -230, y: -90 }, border: "white" },
            },

        }),
        []
    );

    const getBgStyleForSceneThumb = (bgKey) => {
        const cfg = previewBgConfig[bgKey] || previewBgConfig.default;

        if (cfg.type === "tile") {
            return {
                backgroundImage: `url(${cfg.url})`,
                backgroundRepeat: "repeat",
                backgroundSize: `64px 32px`,
                backgroundPosition: `0px 0px`,
                imageRendering: "pixelated",
            };
        }

        if (cfg.type === "room_tile") {
            return {
                backgroundImage: `url(${cfg.url})`,
                backgroundRepeat: "repeat",
                backgroundSize: `64px 41px`,
                backgroundPosition: `0px 0px`,
                imageRendering: "pixelated",
            };
        }

        if (cfg.type === "layered") {
            const layerKey = cfg.thumb?.type === "image" ? cfg.thumb.layerKey : "bg";
            const src = cfg.layers?.[layerKey] || cfg.layers?.bg || "";
            const ox = Number(cfg.thumb?.offset?.x || 0);
            const oy = Number(cfg.thumb?.offset?.y || 0);

            if (!src) return { backgroundColor: "#e5e7eb", imageRendering: "pixelated" };

            return {
                backgroundImage: `url(${src})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "650px 500px",
                backgroundPosition: `${ox}px ${oy}px`,
                imageRendering: "pixelated",
            };
        }

        return { backgroundColor: "#e5e7eb", imageRendering: "pixelated" };
    };

    // ─── Hydration ───
    useEffect(() => {
        if (hydratedRef.current) return;
        (async () => {
            const sessionScenes = readScenes();
            const sessionMeta = readMeta();
            const sessionUpdatedAt = Number(sessionMeta.updatedAt) || 0;

            const userId = getCurrentUserIdForDb();
            const row = userId ? await fetchImagerBlobFromDb(userId) : null;
            const dbPayload = row ? await decodeCompressedRowToPayload(row) : null;
            const dbUpdatedAt = Number(dbPayload?.updatedAt) || 0;

            let chosenScenes = DEBUG_FORCE_NEW_USER ? [] : sessionScenes;
            let chosenActiveId = DEBUG_FORCE_NEW_USER ? null : readActiveSceneId();

            if (!DEBUG_FORCE_NEW_USER && dbPayload && dbUpdatedAt > sessionUpdatedAt) {
                chosenScenes = Array.isArray(dbPayload.scenes) ? dbPayload.scenes : [];
                chosenActiveId = dbPayload.activeSceneId || null;
                writeScenesRaw(chosenScenes);
                writeActiveSceneIdRaw(chosenActiveId || "");
                // Sync lastDismissed fields from DB blob if present
                const dbDismissed = Number(dbPayload.lastDismissedUpdateAt) || 0;
                const dbDismissedVersion = dbPayload.lastDismissedVersion || "";
                writeMeta({ updatedAt: dbUpdatedAt, lastDbSyncAt: Date.now(), dirty: false, lastDismissedUpdateAt: dbDismissed, lastDismissedVersion: dbDismissedVersion });
            } else {
                if (!sessionUpdatedAt && userId) writeMeta({ updatedAt: Date.now(), lastDbSyncAt: 0, dirty: true });
            }

            // ─── Merge per-row scenes from ImagerScene table ───
            if (!DEBUG_FORCE_NEW_USER && userId && !resolvedConfig.overrideUserId) {
                try {
                    const perRowRes = await getMyImagerScenes();
                    if (perRowRes?.success && Array.isArray(perRowRes?.scenes) && perRowRes.scenes.length > 0) {
                        const perRowScenes = (await Promise.all(
                            perRowRes.scenes
                                .filter((s) => s.SceneJson)
                                .map(async (s) => {
                                    try {
                                        const outer = JSON.parse(s.SceneJson);
                                        if (!outer || typeof outer !== 'object') return null;
                                        let sceneObj;
                                        if (outer.encoding === 'gzip+base64' && outer.blob) {
                                            const decompressed = await gunzipBase64ToString(outer.blob);
                                            sceneObj = JSON.parse(decompressed);
                                        } else {
                                            sceneObj = outer; // legacy uncompressed row
                                        }
                                        if (!sceneObj || typeof sceneObj !== 'object') return null;
                                        return { ...sceneObj, dbId: s.ID };
                                    } catch { return null; }
                                })
                        )).filter(Boolean);

                        if (perRowScenes.length > 0) {
                            const perRowLocalIdSet = new Set(perRowScenes.map((s) => s.id));
                            const perRowDbIdSet = new Set(perRowScenes.map((s) => s.dbId).filter(Boolean));
                            const localOnlyScenes = chosenScenes.filter((s) =>
                                !perRowLocalIdSet.has(s.id) && !(s.dbId && perRowDbIdSet.has(s.dbId))
                            );
                            chosenScenes = [...perRowScenes, ...localOnlyScenes];
                        }
                    }
                } catch {}
            }

            const upgraded = (chosenScenes || []).map((s) => {
                const parts = (s?.ownerKey || "").split(":");
                const h = (parts[0] || "COM").toUpperCase();
                const n = parts.slice(1).join(":") || "";
                if (!n) return s;
                const fig = s.ownerFigureString || getCachedFigureString(n, h) || null;
                const thumb = s.ownerThumbUrl || makeOwnerThumbUrl(n, h, fig);
                return { ...s, ownerFigureString: fig, ownerThumbUrl: thumb };
            });

            setScenes(upgraded);
            writeScenesRaw(upgraded);

            setActiveSceneId(chosenActiveId || null);
            if (chosenActiveId) writeActiveSceneIdRaw(chosenActiveId);

            hydratedRef.current = true;

            // First-time user: no scenes exist - open the create modal instead of a blank canvas
            // Skip for override/shared views which may legitimately start empty
            if ((!upgraded.length || DEBUG_FORCE_NEW_USER) && !resolvedConfig.overrideUserId) {
                setShowNewImageModal(true);
            }

            // Check for new updates since user last dismissed the modal
            if (!resolvedConfig.overrideUserId) {
                if (Array.isArray(IMAGER_UPDATES.versions) && IMAGER_UPDATES.versions.length > 0) {
                    setUpdatesData(IMAGER_UPDATES);
                    const meta = readMeta();
                    const latestVer = IMAGER_UPDATES.versions[0];
                    const latestUpdatedAt = latestVer.updatedAt;
                    // Staff-only entries (entry.staff) only surface on the staff/Sulake pages
                    const hasVisibleEntries = (latestVer.entries || []).some((e) => isStaffUpdatesPage || !e.staff);
                    if (latestUpdatedAt !== (meta.lastDismissedUpdateAt || 0) && hasVisibleEntries) {
                        setShowUpdatesModal(true);
                    }
                }
            }

            upgraded.forEach((s) => {
                const parts = (s?.ownerKey || "").split(":");
                const h = (parts[0] || "COM").toUpperCase();
                const n = parts.slice(1).join(":") || "";
                if (n) ensureOwnerFigureForScene(n, h, normalizeOwnerKey(s.ownerKey));
            });
        })();
    }, [userData]);

    const SCENE_MAX_STORED_BYTES = 2 * 1024 * 1024; // 2 MB cap per scene (compressed)
    const dbFlushInFlightRef = useRef(false);
    const [autoSaveStatus, setAutoSaveStatus] = useState(null);
    const autoSaveTimerRef = useRef(null);
    const [activeSceneSizeInfo, setActiveSceneSizeInfo] = useState(null); // { bytes, pct }

    const flushSessionToDbIfNeeded = async (force, isAutoSave = false) => {
        if (!hydratedRef.current) return false;
        if (dbFlushInFlightRef.current) return false;

        const userId = getCurrentUserIdForDb();
        if (!userId) return false;

        const meta = readMeta();
        const now = Date.now();
        // force bypasses the time throttle but still requires dirty - prevents auto-flush from
        // overwriting a more-recent DB save just because the user opened the page on another device.
        const due = meta.dirty && (force || now - (Number(meta.lastDbSyncAt) || 0) >= 1000 * 60 * 5);
        if (!due) return false;

        if (isAutoSave) {
            clearTimeout(autoSaveTimerRef.current);
            setAutoSaveStatus('saving');
        }

        dbFlushInFlightRef.current = true;
        try {
            const row = await buildCompressedRow();
            const ok = await pushImagerBlobToDb(userId, row);
            if (ok) markDbSynced(Date.now());
            if (isAutoSave) {
                setAutoSaveStatus(ok ? 'ok' : 'error');
                autoSaveTimerRef.current = setTimeout(() => setAutoSaveStatus(null), 2500);
            }
            return !!ok;
        } catch {
            if (isAutoSave) {
                setAutoSaveStatus('error');
                autoSaveTimerRef.current = setTimeout(() => setAutoSaveStatus(null), 2500);
            }
            return false;
        } finally {
            dbFlushInFlightRef.current = false;
        }
    };

    const forceSaveNow = async () => {
        const userId = getCurrentUserIdForDb();
        if (!userId) {
            toast.error("You must be logged in to save.");
            return;
        }

        if (!forceSaveKeyRef.current) forceSaveKeyRef.current = getForceSaveKey();

        const now = Date.now();
        const last = readLastForceSaveAt();
        const remaining = FORCE_SAVE_COOLDOWN_MS - (now - last);

        if (remaining > 0) {
            toast.info(`Save available in ${Math.ceil(remaining / 1000)}s`);
            return;
        }

        if (dbFlushInFlightRef.current) {
            toast.info("Save already in progress...");
            return;
        }

        writeLastForceSaveAt(now);
        setForceSaveNowTs(now);
        touchMetaDirty(); // ensure dirty so the flush always proceeds for an explicit user save

        const toastId = toast.info("Force saving...", { autoClose: false });
        try {
            // Save active scene as its own row first
            const activeScene = scenes.find((s) => s?.id === activeSceneId) || null;
            if (activeScene) await syncSceneToDb(activeScene);

            const ok = await flushSessionToDbIfNeeded(true);
            toast.dismiss(toastId);
            if (ok) toast.success("Save complete - Please wait 30 seconds before saving again.");
            else toast.error("Save failed.");
        } catch {
            toast.dismiss(toastId);
            toast.error("Save failed.");
        }
    };

    useEffect(() => {
        const t = setInterval(() => {
            flushSessionToDbIfNeeded(false, true);
        }, 1000 * 30);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === "hidden") flushSessionToDbIfNeeded(true);
        };
        window.addEventListener("visibilitychange", onVis);
        window.addEventListener("beforeunload", onVis);
        return () => {
            window.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("beforeunload", onVis);
        };
    }, []);

    // ─── Figure Resolution ───
    useEffect(() => {
        if (editingSceneHabboIdRef.current) return;

        if (!isValidHabboName(habboName)) {
            if (figureString !== null) setFigureString(null);
            return;
        }

        const key = `${(hotel || "COM").toUpperCase()}:${habboName}`.toLowerCase();
        const cache = readFigCache();
        const cachedFig = cache[key]?.figureString;

        if (cachedFig) {
            if (figureString !== cachedFig) setFigureString(cachedFig);
            return;
        }

        if (figureString) {
            const nextCache = readFigCache();
            nextCache[key] = { figureString, t: Date.now() };
            writeFigCache(nextCache);
            return;
        }

        if (lookupAbortRef.current) lookupAbortRef.current.abort();
        const ac = new AbortController();
        lookupAbortRef.current = ac;

        resolveFigureString({ username: habboName, hotel, signal: ac.signal })
            .then((fig) => {
                if (ac.signal.aborted) return;
                setFigureString(fig);
            })
            .catch(() => {
                if (ac.signal.aborted) return;
                setFigureString(null);
            });

        return () => {
            ac.abort();
        };
    }, [habboName, hotel, figureString]);

    // ─── Identity Params ───
    const identityParams = useMemo(() => {
        if (figureString) return { habbo: null, figure: figureString };
        return { habbo: habboName, figure: null };
    }, [figureString, habboName]);

    // (Snapshot Helpers)
    function makeSnapshot(overrides = {}) {
        const snap = {
            id: makeId(),
            habboName,
            hotel,
            figureString: figureString || null,
            direction,
            headDirection,
            action,
            gesture,
            size,
            crr,
            shadow,
            frankify,
            useFrankHatForDefault,
            zoom,
            previewBg,
            scale: clampSpriteScale(activeSpriteScale),
            crop: activeSpriteCrop || null,
            sourceSize: activeSpriteSourceSize || { w: 0, h: 0 },
            flipX: !!activeSpriteFlipX,
            flipY: !!activeSpriteFlipY,
            rotation: activeSpriteRotation || 0,
            ...overrides,
        };

        const custom = snap.useFrankHatForDefault || snap.frankify ? "frank_hat" : null;

        snap.thumbUrl = getHabboImageUrl({
            habbo: snap.figureString ? null : snap.habboName,
            figure: snap.figureString || null,
            hotel: snap.hotel,
            direction: snap.direction,
            head_direction: snap.headDirection,
            action: snap.action,
            gesture: snap.gesture,
            size: snap.size,
            shadow: snap.shadow,
            crr: snap.crr || null,
            custom,
        });

        return snap;
    }

    function applySnapshot(snap, opts = {}) {
        const keepView = !!opts.keepView;


        setHabboName(snap.habboName);
        setHotel(snap.hotel);
        setFigureString(snap.figureString || null);

        setDirection(snap.direction);
        setHeadDirection(snap.headDirection);
        setAction(snap.action);
        setGesture(snap.gesture);
        setSize(snap.size);

        setCrr(snap.crr || "");
        setShadow(!!snap.shadow);
        setFrankify(!!snap.frankify);
        setUseFrankHatForDefault(!!snap.useFrankHatForDefault);
        setActiveSpriteScale(clampSpriteScale(snap.scale));
        setActiveSpriteCrop(snap.crop || null);
        setActiveSpriteSourceSize(
            snap.sourceSize && Number(snap.sourceSize.w) > 0 && Number(snap.sourceSize.h) > 0
                ? { w: Math.round(Number(snap.sourceSize.w)), h: Math.round(Number(snap.sourceSize.h)) }
                : { w: 0, h: 0 }
        );
        setActiveSpriteFlipX(!!snap.flipX);
        setActiveSpriteFlipY(!!snap.flipY);
        setActiveSpriteRotation(snap.rotation || 0);

        if (!keepView) {
            setZoom(snap.zoom || 1);
            setPreviewBg(snap.previewBg || "default");
            setCameraPos({ x: 0, y: 0 });
        }
    }

    // (Scenes CRUD)
    const findSceneIndexById = (arr, id) => arr.findIndex((s) => s?.id === id);

    const setActiveSceneIdSafe = (id) => {
        setActiveSceneId(id);
        writeActiveSceneId(id);
    };

    const getCachedFigureString = (name, h) => {
        const n = (name || "").trim();
        const hotelKey = (h || "COM").toUpperCase();
        if (!n) return null;

        const key = `${hotelKey}:${n}`.toLowerCase();
        const cache = readFigCache();
        return cache[key]?.figureString || null;
    };

    const makeOwnerThumbUrl = (name, h, fig) =>
        getHabboImageUrl({
            habbo: fig ? null : name,
            figure: fig || null,
            hotel: (h || "COM").toUpperCase(),
            direction: 2,
            head_direction: 2,
            action: "std",
            gesture: "std",
            size: "b",
            shadow: true,
            crr: null,
            custom: null,
        });

    const normalizeLayerId = (v) => String(v || "");

    const getLayerThumbForSnap = (snap) => {
        const n = (snap?.habboName || "").trim();
        const h = (snap?.hotel || "COM").toUpperCase();
        return makeOwnerThumbUrl(n, h, snap?.figureString || null);
    };

    // Derived layers from unified sprites array - replaces reconcileLayers
    const layers = useMemo(() => {
        const bg = previewBgConfig[previewBg] || previewBgConfig.default || {};
        const bgLayers = deriveBgLayers(bg, previewBg, bgLayerVis);

        const userLayers = sprites.map((s) => spriteToLayer(s, furniItemsByIdRef));

        const moodlightLayer = {
            id: 'moodlight:__active__',
            kind: 'moodlight',
            name: 'Moodlight',
            subLabel: 'Overlay',
            thumbUrl: '',
            thumbStyle: {},
            thumbBg: { color: moodlightColor, opacity: moodlightOpacity },
            permaLock: null,
            canHide: true,
            visible: moodlightVisible,
            locked: false,
            opacity: 100,
            effects: {},
        };

        const speechLayer = speechBubbles.length > 0 ? [{
            id: 'speech:__all__',
            kind: 'speech',
            name: 'Speech Bubbles',
            subLabel: 'Speech',
            thumbUrl: '',
            thumbStyle: {},
            permaLock: 'speech',
            canHide: true,
            visible: true,
            locked: false,
            opacity: 100,
            effects: {},
        }] : [];

        const bgBottom = bgLayers.filter((l) => l.permaLock === 'bottom');
        const bgMid = bgLayers.filter((l) => !l.permaLock);
        const bgTop = bgLayers.filter((l) => l.permaLock === 'top');

        // Build lookup map for all moveable (mid) layers
        const midMap = new Map([
            ...bgMid.map((l) => [l.id, l]),
            ...userLayers.map((l) => [l.id, l]),
            [moodlightLayer.id, moodlightLayer],
        ]);

        // Order mid layers by midLayerOrder; append any not yet in the order
        let orderedMid;
        if (midLayerOrder.length > 0) {
            const seen = new Set();
            const fromOrder = midLayerOrder.filter((id) => {
                if (!midMap.has(id) || seen.has(id)) return false;
                seen.add(id);
                return true;
            }).map((id) => midMap.get(id));
            const remainder = [...midMap.values()].filter((l) => !seen.has(l.id));
            orderedMid = [...fromOrder, ...remainder];
        } else {
            // Fallback: original fixed order
            orderedMid = [...bgMid, ...userLayers, moodlightLayer];
        }

        return [...bgBottom, ...orderedMid, ...speechLayer, ...bgTop];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sprites, previewBg, bgLayerVis, moodlightVisible, moodlightColor, moodlightOpacity, speechBubbles.length, midLayerOrder]);

    // Sync midLayerOrder: keep it consistent with current sprites, bgMid IDs, and moodlight
    useEffect(() => {
        const bg = previewBgConfig[previewBg] || {};
        const bgMidIds = deriveBgLayers(bg, previewBg, {})
            .filter((l) => !l.permaLock)
            .map((l) => l.id);
        const _typeToPrefix = { furni: 'furni', habbo: 'habbo', customImage: 'custom_image', text: 'text' };
        const spriteLayerIds = sprites.map((s) => `${_typeToPrefix[s.type] || s.type}:${s.id}`);
        const moodId = 'moodlight:__active__';
        const allMidSet = new Set([...bgMidIds, ...spriteLayerIds, moodId]);

        setMidLayerOrder((prev) => {
            const kept = prev.filter((id) => allMidSet.has(id));
            const keptSet = new Set(kept);
            const newBgMid = bgMidIds.filter((id) => !keptSet.has(id));
            const newSprites = spriteLayerIds.filter((id) => !keptSet.has(id));
            const hasMood = keptSet.has(moodId);
            // New bgMid at bottom, new sprites at top, moodlight at very top if absent
            return [...newBgMid, ...kept, ...newSprites, ...(hasMood ? [] : [moodId])];
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sprites, previewBg]);

    // Deselect sprite when its layer becomes locked (moved here after layers is defined)
    useEffect(() => {
        const layer = layers.find((l) => l.id === activeLayerId);
        if (!layer || (!layer.locked && !layer.permaLock)) return;

        if (editingSceneHabboIdRef.current) endEditSceneHabbo({ restore: false });

        if (furniDragRef.current) onFurniPointerUp();
        if (sceneHabboDragRef.current) onSceneHabboPointerUp();
        endSpriteDrag();

        setSpriteSelected(false);
    }, [layers, activeLayerId]); // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        historyStateRef.current = {
            spritePos,
            activeSpriteScale,
            activeSpriteFlipX,
            activeSpriteFlipY,
            activeSpriteRotation,
            activeSpriteCrop,
            sprites,
            bgLayerVis,
            moodlightVisible,
            speechBubbles,
            moodlightColor,
            moodlightOpacity,
            previewSize,
            previewBg,
            customBgUrl,
            customBgFit,
            showTransparency,
            gridTint,
            whiteBgTint,
            roomTileBgColor,
            imageBorderEnabled,
            imageBorderColor,
            imageBorderWidth,
            midLayerOrder,
        };
    });

    const ensureOwnerFigureForScene = (ownerName, ownerHotel, ownerKey) => {
        const n = (ownerName || "").trim();
        const h = (ownerHotel || "COM").toUpperCase();
        if (!n) return;

        const applyFigToSprites = (fig) => {
            const k = (ownerKey || `${h}:${n}`).toLowerCase();

            setSprites((prev) =>
                prev.map((sp) => {
                    if (sp.type !== 'habbo') return sp;
                    if ((sp.ownerKey || "").toLowerCase() !== k) return sp;
                    if (sp?.snap?.figureString) return sp;

                    return {
                        ...sp,
                        snap: { ...sp.snap, figureString: fig, hotel: h, habboName: n },
                    };
                })
            );
        };

        const cached = getCachedFigureString(n, h);
        if (cached) {
            applyFigToSprites(cached);
            return;
        }

        resolveFigureString({ username: n, hotel: h, signal: undefined })
            .then((fig) => {
                if (!fig) return;
                applyFigToSprites(fig);
            })
            .catch(() => {});
    };

    const createEmptySceneForOwner = (ownerKey, ownerName, ownerHotel) => {
        const fig = getCachedFigureString(ownerName, ownerHotel);

        return {
            id: makeId(),
            createdAt: Date.now(),
            ownerKey,
            ownerFigureString: fig || null,
            ownerThumbUrl: makeOwnerThumbUrl(ownerName, ownerHotel, fig || null),
            previewBg: "default",
            activeSnap: null,
            enabledOwnerKeys: [],
            sprites: [],
            layers: [],
            sceneSprites: [],
            speechBubbles: [],
            furniSprites: [],
            customImages: [],
            textSprites: [],
            userFurni: [],
            spritePos: { x: 0, y: 0 },
            cameraPos: { x: 0, y: 0 },
            zoom: 1,
            tileMode: false,
            tileSize: 500,
        };
    };

    const saveScenesState = (updater) => {
        setScenes((prev) => {
            const next = updater(prev);
            writeScenes(next);
            return next;
        });
    };

    const normalizeOwnerKey = (k) => (k || "").toLowerCase();

    const getActiveSceneObj = () => scenes.find((s) => s?.id === activeSceneId) || null;

    const getSceneByOwnerKey = (ownerKey) => {
        const k = normalizeOwnerKey(ownerKey);
        return scenes.find((s) => normalizeOwnerKey(s?.ownerKey) === k) || null;
    };

    const makeDefaultSnapForOwnerKey = (ownerKey, view = {}) => {
        const parts = (ownerKey || "").split(":");
        const h = (parts[0] || "COM").toUpperCase();
        const n = parts.slice(1).join(":") || "";

        const s = getSceneByOwnerKey(ownerKey);
        const fig = s?.ownerFigureString || getCachedFigureString(n, h) || null;

        return makeSnapshot({
            habboName: n,
            hotel: h,
            figureString: fig,
            direction: 2,
            headDirection: 2,
            action: "std",
            gesture: "std",
            size: "b",
            shadow: true,
            crr: "",
            frankify: false,
            useFrankHatForDefault: false,
            zoom: view.zoom || 1,
            previewBg: view.previewBg || "default",
        });
    };

    const ensureSpriteForOwnerKey = (ownerKey) => {
        const k = (ownerKey || "").toLowerCase();
        const view = { zoom, previewBg };

        const snap = { ...makeDefaultSnapForOwnerKey(k, view), id: makeId() };

        setSprites((prev) => {
            if (prev.some((p) => p.type === 'habbo' && (p?.ownerKey || "").toLowerCase() === k)) return prev;
            return prev.concat([{ type: 'habbo', id: makeId(), ownerKey: k, snap, pos: { x: 0, y: 0 }, visible: true, locked: false, opacity: 100, layerName: '', effects: {} }]);
        });
    };

    const removeSpriteForOwnerKey = (ownerKey) => {
        const k = (ownerKey || "").toLowerCase();
        setSprites((prev) => prev.filter((p) => !(p.type === 'habbo' && (p?.ownerKey || "").toLowerCase() === k)));
    };

    const setEnabledOwnerKeysForActiveScene = (updater) => {
        if (!activeSceneId) return;

        saveScenesState((prev) => {
            const idx = findSceneIndexById(prev, activeSceneId);
            if (idx === -1) return prev;

            const existing = prev[idx];
            const ownerKey = normalizeOwnerKey(existing.ownerKey);
            const current = Array.isArray(existing.enabledOwnerKeys) ? existing.enabledOwnerKeys : [];

            const nextList = updater(current)
                .map((k) => normalizeOwnerKey(k))
                .filter(Boolean)
                .filter((k) => k !== ownerKey);

            const nextScene = { ...existing, enabledOwnerKeys: nextList };
            const next = prev.slice();
            next[idx] = nextScene;
            return next;
        });
    };

    const isOwnerEnabledInActiveScene = (ownerKey) => {
        const s = getActiveSceneObj();
        if (!s) return false;
        const base = normalizeOwnerKey(s.ownerKey);
        const k = normalizeOwnerKey(ownerKey);
        if (!k || k === base) return false;

        const list = Array.isArray(s.enabledOwnerKeys) ? s.enabledOwnerKeys : [];
        return list.map(normalizeOwnerKey).includes(k);
    };

    const toggleOwnerInActiveScene = (ownerKey) => {
        const active = getActiveSceneObj();
        if (!active) return;

        const base = normalizeOwnerKey(active.ownerKey);
        const k = normalizeOwnerKey(ownerKey);

        if (!k || k === base) return;

        const on = isOwnerEnabledInActiveScene(k);

        if (on) {
            removeSpriteForOwnerKey(k);
            setEnabledOwnerKeysForActiveScene((list) => list.filter((x) => normalizeOwnerKey(x) !== k));
            return;
        }

        setEnabledOwnerKeysForActiveScene((list) => list.concat([k]));
        ensureSpriteForOwnerKey(k);
    };

    const addFurniToActiveScene = (furniId) => {
        const item = furniItemsByIdRef.current.get(furniId);
        if (!item) return;

        const images = Array.isArray(item.images) ? item.images : [];
        if (!images.length || !images[0]?.length) return;
        pushHistory();

        const stateI = 0;
        const rotI = 0;

        const rotationsRow = images[stateI] || [];
        const hasCode = !!String(item.code || "").trim();

        const rotations = hasCode
            ? rotationsRow.map((_, rotIndex) => buildFoFurniUrl(item.code, rotIndex, stateI)).filter(Boolean)
            : rotationsRow;

        const states = hasCode
            ? images.map((_, stateIndex) => buildFoFurniUrl(item.code, rotI, stateIndex)).filter(Boolean)
            : images.map((row) => row?.[rotI] || row?.[0]).filter(Boolean);

        const url = hasCode
            ? buildFoFurniUrl(item.code, rotI, stateI)
            : rotations[rotI] || states[stateI] || item.url || "";

        if (!url) return;

        const id = makeId();

        setSprites((prev) => {
            return prev.concat([
                {
                    type: 'furni',
                    id,
                    furniId,
                    name: item.name || "",
                    code: item.code || "",
                    url,
                    images,
                    shadowImages: Array.isArray(item.shadowImages) ? item.shadowImages : [],
                    rotations,
                    rotI,
                    states,
                    stateI,
                    frameI: 0,
                    scale: 1,
                    crop: null,
                    sourceSize: { w: 0, h: 0 },
                    pos: { x: 0, y: 0 },
                    visible: true,
                    locked: false,
                    opacity: 100,
                    layerName: '',
                    effects: {},
                },
            ]);
        });

        if (hasCode && !item.isUserAdded) {
            const apiUrl = (process.env.REACT_APP_API_URL).trim();
            fetch(`${apiUrl}furni-imaging/furni-info/${encodeURIComponent(item.code)}`, {
                headers: { Accept: "application/json" },
            })
                .then(async (res) => {
                    if (!res.ok) return;
                    const data = await res.json();
                    const variants = Array.isArray(data?.normal?.variants) ? data.normal.variants : [];
                    if (!variants.length) return;

                    const hasState = variants.some((v) => Number.isFinite(v?.state));
                    const hasDir = variants.some((v) => Number.isFinite(v?.direction));

                    let newImages;

                    if (!hasState && !hasDir) {
                        newImages = [variants.map(() => "x")];
                    } else {
                        let maxState = 0;
                        for (const v of variants) {
                            const st = hasState && Number.isFinite(v?.state) ? Number(v.state) : 0;
                            if (st > maxState) maxState = st;
                        }

                        const byState = Array.from({ length: maxState + 1 }, () => []);
                        for (const v of variants) {
                            const st = hasState && Number.isFinite(v?.state) ? Number(v.state) : 0;
                            byState[st].push(v);
                        }

                        let maxDir = 0;
                        if (hasDir) {
                            for (const v of variants) {
                                const dr = Number.isFinite(v?.direction) ? Number(v.direction) : 0;
                                if (dr > maxDir) maxDir = dr;
                            }
                        } else {
                            for (const arr of byState) {
                                if (arr.length - 1 > maxDir) maxDir = arr.length - 1;
                            }
                        }

                        newImages = Array.from({ length: maxState + 1 }, () =>
                            Array.from({ length: maxDir + 1 }, () => "x")
                        );
                    }

                    const newRotations = newImages[0].map((_, rotIndex) =>
                        buildFoFurniUrl(item.code, rotIndex, 0)
                    ).filter(Boolean);
                    const newStates = newImages.map((_, stateIndex) =>
                        buildFoFurniUrl(item.code, 0, stateIndex)
                    ).filter(Boolean);

                    updateFurniSprite(id, { images: newImages, rotations: newRotations, states: newStates });
                })
                .catch(() => {});
        }
    };

    const removeScene = (sceneId) => {
        let nowEmpty = false;
        let removedDbId = null;
        saveScenesState((prev) => {
            const target = prev.find((s) => s.id === sceneId);
            removedDbId = target?.dbId || null;
            const next = prev.filter((s) => s.id !== sceneId);
            if (next.length === 0) nowEmpty = true;
            if (sceneId === activeSceneId) {
                const fallback = next[0]?.id || null;
                setSceneOwnerKey(null);
                setActiveSceneIdSafe(fallback);
            }
            return next;
        });
        if (removedDbId) deleteImagerScene(removedDbId).catch(() => {});
        if (nowEmpty) setShowNewImageModal(true);
        toast.info("Scene removed.");
    };

    const loadScene = (sceneObj) => {
        if (!sceneObj) return;
        setForceUiLocked(true);

        // Prevent the tileMode useEffect from overwriting canvas size - loadScene sets it directly
        isLoadingSceneRef.current = true;

        const sceneBgKey = sceneObj.previewBg || "default";
        const byBg =
            sceneObj.layersStateByBg && typeof sceneObj.layersStateByBg === "object"
                ? sceneObj.layersStateByBg[sceneBgKey]
                : null;

        const seedLayers = Array.isArray(byBg) ? byBg
            : Array.isArray(sceneObj.layersState) ? sceneObj.layersState
                : null;

        beginSceneLoading();
        restoringRef.current = true;

        setSceneOwnerKey(sceneObj.ownerKey || null);
        setActiveSceneIdSafe(sceneObj.id);

        const migratedSprites = (Array.isArray(sceneObj.sceneSprites) ? sceneObj.sceneSprites : []).map((sp) => ({
            ...sp,
            ownerKey:
                sp.ownerKey ||
                `${(sp?.snap?.hotel || "COM").toUpperCase()}:${(sp?.snap?.habboName || "").trim()}`.toLowerCase(),
        }));

        const ownerKey = normalizeOwnerKey(sceneObj.ownerKey);

        const ownerParts = (sceneObj.ownerKey || "").split(":");
        const ownerHotel = (ownerParts[0] || "COM").toUpperCase();
        const ownerName = ownerParts.slice(1).join(":") || "";

        const ownerSnap =
            sceneObj.activeSnap ||
            makeSnapshot({
                habboName: ownerName,
                hotel: ownerHotel,
                figureString: sceneObj.ownerFigureString || null,
            });

        const hasOwner = migratedSprites.some(
            (sp) => normalizeOwnerKey(sp.ownerKey) === ownerKey
        );

        const explicitSprites = Array.isArray(sceneObj.sceneSprites);
        const nextSprites = !ownerKey || hasOwner || explicitSprites
            ? migratedSprites
            : [
                {
                    id: makeId(),
                    ownerKey,
                    snap: ownerSnap,
                    pos: sceneObj.spritePos || { x: 0, y: 0 },
                },
                ...migratedSprites,
            ];

        // Build unified sprites from new or legacy format
        const unifiedSprites = sceneObj.sprites
            ? sceneObj.sprites
            : migrateSceneToSprites(
                {
                    sceneSprites: nextSprites,
                    furniSprites: Array.isArray(sceneObj.furniSprites) ? sceneObj.furniSprites : [],
                    customImages: Array.isArray(sceneObj.customImages) ? sceneObj.customImages : [],
                    textSprites: Array.isArray(sceneObj.textSprites) ? sceneObj.textSprites : [],
                },
                seedLayers || []
            );

        setSprites(unifiedSprites);

        // Determine moodlight visibility from seed layers (default off)
        const moodlightSeed = (seedLayers || []).find((l) => l.id === 'moodlight:__active__');
        setMoodlightVisible(moodlightSeed ? moodlightSeed.visible !== false : false);

        // Bg layer visibility from seed layers + persisted room tile cell vis
        const bgVisSeed = {};
        (seedLayers || []).forEach((l) => {
            if (l.id.startsWith('bg:')) bgVisSeed[l.id] = l.visible !== false;
        });
        if (sceneObj.roomTileCellVis && typeof sceneObj.roomTileCellVis === "object") {
            Object.entries(sceneObj.roomTileCellVis).forEach(([k, v]) => {
                if (k.startsWith("rt:")) bgVisSeed[k] = v;
            });
        }
        setBgLayerVis(bgVisSeed);

        // Initialize midLayerOrder from saved layer order
        {
            const bgCfgForLoad = previewBgConfig[sceneBgKey] || previewBgConfig.default || {};
            const bgMidSetForLoad = new Set(
                deriveBgLayers(bgCfgForLoad, sceneBgKey, {})
                    .filter((l) => !l.permaLock)
                    .map((l) => l.id)
            );
            const _p = { furni: 'furni', habbo: 'habbo', customImage: 'custom_image', text: 'text' };
            const unifiedSpriteIdSet = new Set(unifiedSprites.map((s) => `${_p[s.type] || s.type}:${s.id}`));
            const moodId = 'moodlight:__active__';
            const isMidId = (id) => id === moodId || bgMidSetForLoad.has(id)
                || unifiedSpriteIdSet.has(id) || /^(furni:|habbo:|custom_image:|text:)/.test(id);

            const initOrder = (seedLayers || []).map((l) => l.id).filter(isMidId);
            const initSet = new Set(initOrder);
            const missingBgMid = [...bgMidSetForLoad].filter((id) => !initSet.has(id));
            const missingSprites = [...unifiedSpriteIdSet].filter((id) => !initSet.has(id));
            const hasMood = initSet.has(moodId);
            setMidLayerOrder([...missingBgMid, ...initOrder, ...missingSprites, ...(hasMood ? [] : [moodId])]);
        }

        setSpeechBubbles(
            Array.isArray(sceneObj.speechBubbles)
                ? sceneObj.speechBubbles.map((b) => ({ ...b, shout: !!b.shout }))
                : []
        );
        setActiveCustomImageId(null);
        setActiveTextId(null);
        setUserFurni(Array.isArray(sceneObj.userFurni) ? sceneObj.userFurni : []);
        setSpritePos(sceneObj.spritePos || { x: 0, y: 0 });
        setCameraPos(sceneObj.cameraPos || { x: 0, y: 0 });
        setZoom(sceneObj.zoom || 1);
        setTileMode(!!sceneObj.tileMode);
        setTileSize(sceneObj.tileSize || 500);
        preTileModeSize.current = sceneObj.tileMode
            ? { w: 650, h: 500 }
            : null;

        const loadedBg = sceneObj.previewBg || "default";
        if (sceneObj.customBgFit) setCustomBgFit(sceneObj.customBgFit);
        if (loadedBg === "custom_bg") {
            const savedBgUrl = typeof sceneObj.customBgUrl === "string" && !sceneObj.customBgUrl.startsWith("blob:")
                ? sceneObj.customBgUrl
                : null;
            setCustomBgUrl(savedBgUrl);
            setPreviewBg(savedBgUrl ? "custom_bg" : "default");
        } else {
            setCustomBgUrl(null);
            setPreviewBg(loadedBg);
        }
        setShowTransparency(sceneObj.showTransparency || false);
        setGridTint(sceneObj.gridTint || "");
        setWhiteBgTint(sceneObj.whiteBgTint || "");
        setRoomTileBgColor(sceneObj.roomTileBgColor || "#000000");
        setImageBorderEnabled(sceneObj.imageBorderEnabled || false);
        setImageBorderColor(typeof sceneObj.imageBorderColor === "string" && /^#[0-9a-fA-F]{6}$/.test(sceneObj.imageBorderColor.trim()) ? sceneObj.imageBorderColor.trim() : "#ffffff");
        setImageBorderWidth(Number.isFinite(sceneObj.imageBorderWidth) ? sceneObj.imageBorderWidth : 10);
        setRoomTileEditMode(false);

        if (sceneObj.imageWidth && sceneObj.imageHeight) {
            setPreviewSize({ w: sceneObj.imageWidth, h: sceneObj.imageHeight });
            setCanvasWInput(sceneObj.imageWidth);
            setCanvasHInput(sceneObj.imageHeight);
        }

        setMoodlightColor(typeof sceneObj.moodlightColor === "string" && /^#[0-9a-fA-F]{6}$/.test(sceneObj.moodlightColor.trim()) ? sceneObj.moodlightColor.trim() : "#7c3aed");
        setMoodlightOpacity(Number.isFinite(sceneObj.moodlightOpacity) ? sceneObj.moodlightOpacity : 0.5);

        setActiveFurniId(null);

        const lockedIds = new Set(
            (seedLayers || [])
                .filter((x) => x && (x.locked || x.permaLock))
                .map((x) => String(x.id || ""))
        );

        const firstUnlocked = nextSprites.find((sp) => !lockedIds.has(`habbo:${sp.id}`)) || null;

        const only = nextSprites.length === 1 ? nextSprites[0] : null;
        const onlyLocked = !!only && lockedIds.has(`habbo:${only.id}`);

        const chosen = firstUnlocked || (onlyLocked ? only : null);
        const chosenId = chosen?.id || null;

        const chosenLayerId = chosenId ? `habbo:${chosenId}` : null;
        const chosenIsLocked = !!chosenLayerId && lockedIds.has(chosenLayerId);

        editingSceneHabboIdRef.current = chosenId;
        setEditingSceneHabboId(chosenId);

        setActiveSceneHabboId(chosenId);
        setActiveLayerId(chosenId ? `habbo:${chosenId}` : null);
        setSpriteSelected(!!firstUnlocked);

        setForceUiLocked(chosenIsLocked);

        if (chosen?.snap) applySnapshot(chosen.snap, { keepView: true });
        setSpritePos({ ...(chosen?.pos || { x: 0, y: 0 }) });

        hasRealHabboRef.current = true;
        setReadyToShowHabbo(true);
        restoringRef.current = false;

        // Auto-migrate legacy scenes (no dbId) to individual rows when loaded
        if (!sceneObj.dbId) syncSceneToDb(sceneObj);
    };

    useEffect(() => {
        if (!hydratedRef.current) return;
        if (!scenes.length) return;

        const activeId = readActiveSceneId();
        const byId = activeId ? scenes.find((s) => s.id === activeId) : null;
        loadScene(byId || scenes[0]);
    }, [scenes.length]);

    const autosaveTimerRef = useRef(null);

    useEffect(() => {
        if (!hydratedRef.current) return;
        if (!hasRealHabboRef.current) return;
        if (restoringRef.current) return;
        if (!activeSceneId) return;
        const hasAnyContent =
            isValidHabboName(habboName) ||
            sprites.length > 0 ||
            speechBubbles.length > 0;
        if (!hasAnyContent) return;

        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);

        autosaveTimerRef.current = window.setTimeout(() => {
            let capturedScene = null;
            saveScenesState((prev) => {
                const idx = findSceneIndexById(prev, activeSceneId);
                if (idx === -1) return prev;

                const existing = prev[idx];

                const editingId = editingSceneHabboIdRef.current;

                // Derive old-format arrays from unified sprites for backward compat
                const habboSprites = sprites.filter((s) => s.type === 'habbo');
                const nextSceneSprites =
                    editingId
                        ? habboSprites.map((sp) => {
                            if (sp.id !== editingId) return sp;
                            return { ...sp, snap: makeSnapshot({}), pos: { ...spritePos } };
                        })
                        : habboSprites;

                // Also save updated sprites with editing habbo patched
                const nextSprites = editingId
                    ? sprites.map((s) => {
                        if (s.id !== editingId || s.type !== 'habbo') return s;
                        return { ...s, snap: makeSnapshot({}), pos: { ...spritePos } };
                    })
                    : sprites;

                const hasHabboSprites = nextSceneSprites.length > 0;
                const owner = sceneOwnerKey || (hasHabboSprites ? habboKeyOf(makeSnapshot()) : null);
                if (!sceneOwnerKey && owner) setSceneOwnerKey(owner);

                const nextScene = {
                    ...existing,
                    ownerKey: existing.ownerKey || (hasHabboSprites ? owner : null),
                    ownerThumbUrl: (() => {
                        const ok = (existing.ownerKey || owner || "").split(":");
                        const oh = (ok[0] || hotel || "COM").toUpperCase();
                        const on = ok.slice(1).join(":") || habboName;

                        const fig = existing.ownerFigureString || getCachedFigureString(on, oh) || null;

                        return existing.ownerThumbUrl || makeOwnerThumbUrl(on, oh, fig);
                    })(),
                    ownerFigureString:
                        existing.ownerFigureString ||
                        getCachedFigureString(
                            (existing.ownerKey || owner || "").split(":").slice(1).join(":") || habboName,
                            ((existing.ownerKey || owner || "").split(":")[0] || hotel || "COM").toUpperCase()
                        ) ||
                        null,

                    previewBg,
                    customBgFit,
                    customBgUrl: (customBgUrl && !customBgUrl.startsWith('blob:')) ? customBgUrl : null,
                    showTransparency,
                    gridTint,
                    whiteBgTint,
                    roomTileBgColor,
                    imageBorderEnabled,
                    imageBorderColor,
                    imageBorderWidth,
                    roomTileCellVis: Object.fromEntries(Object.entries(bgLayerVisRef.current).filter(([k]) => k.startsWith("rt:"))),
                    moodlightColor,
                    moodlightOpacity,
                    activeSnap: makeSnapshot({}),
                    layers: serialiseLayers(layers),
                    layersStateByBg: {
                        ...(existing.layersStateByBg && typeof existing.layersStateByBg === "object" ? existing.layersStateByBg : {}),
                        [previewBg]: layers.map((l) => ({
                            id: l.id,
                            visible: l.visible !== false,
                            locked: !!l.locked,
                            name: l.name || "",
                            opacity: typeof l.opacity === "number" ? l.opacity : 100,
                            effects: typeof l.effects === "object" && l.effects !== null ? l.effects : {},
                        })),
                    },
                    layersState: layers.map((l) => ({
                        id: l.id,
                        visible: l.visible !== false,
                        locked: !!l.locked,
                        name: l.name || "",
                        opacity: typeof l.opacity === "number" ? l.opacity : 100,
                        effects: typeof l.effects === "object" && l.effects !== null ? l.effects : {},
                    })),

                    enabledOwnerKeys: Array.isArray(existing.enabledOwnerKeys) ? existing.enabledOwnerKeys : [],

                    // New unified format
                    sprites: nextSprites,

                    // Old format (backward compat)
                    sceneSprites: nextSceneSprites,

                    speechBubbles: speechBubbles.map((b) => ({
                        id: b.id,
                        sceneHabboId: b.sceneHabboId,
                        text: b.text,
                        color: b.color,
                        displayName: b.displayName,
                        shout: !!b.shout,
                    })),

                    furniSprites: sprites.filter((s) => s.type === 'furni'),
                    customImages: sprites.filter((s) => s.type === 'customImage'),
                    textSprites: sprites.filter((s) => s.type === 'text'),
                    userFurni,
                    spritePos,
                    cameraPos,
                    zoom,
                    tileMode,
                    tileSize,
                };

                capturedScene = nextScene;
                const next = prev.slice();
                next[idx] = nextScene;
                return next;
            });
            if (capturedScene) syncSceneToDb(capturedScene);
        }, 250);

        return () => {
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        };
    }, [
        activeSceneId,
        sceneOwnerKey,
        habboName,
        hotel,
        figureString,
        direction,
        headDirection,
        action,
        gesture,
        size,
        crr,
        shadow,
        frankify,
        useFrankHatForDefault,
        sprites,
        speechBubbles,
        spritePos,
        cameraPos,
        zoom,
        tileMode,
        tileSize,
        previewBg,
        customBgUrl,
        customBgFit,
        showTransparency,
        gridTint,
        whiteBgTint,
        roomTileBgColor,
        imageBorderEnabled,
        imageBorderColor,
        imageBorderWidth,
        moodlightColor,
        moodlightOpacity,
        bgLayerVis,
        moodlightVisible,
        activeSpriteScale,
        activeSpriteSourceSize,
        activeSpriteFlipX,
        activeSpriteFlipY,
        activeSpriteRotation,
        activeSpriteCrop,
    ]);

    useEffect(() => {
        const id = editingSceneHabboIdRef.current;
        if (!id) return;

        setSprites((prev) =>
            prev.map((s) => {
                if (s.id !== id || s.type !== 'habbo') return s;
                const fresh = makeSnapshot({});
                return {
                    ...s,
                    snap: {
                        ...fresh,
                        scale: s.snap?.scale,
                        flipX: s.snap?.flipX,
                        flipY: s.snap?.flipY,
                        crop: s.snap?.crop ?? null,
                        rotation: s.snap?.rotation ?? 0,
                        sourceSize: s.snap?.sourceSize,
                    },
                };
            })
        );
    }, [habboName, hotel, figureString, direction, headDirection, action, gesture, size, crr, shadow, frankify, useFrankHatForDefault]);

    // (Image Cache Busting)
    const [cacheEpoch, setCacheEpoch] = useState(0);
    const imgCacheRef = useRef(new Set());

    const withCache = (url) => {
        const join = url.includes("?") ? "&" : "?";
        return `${url}${join}cb=${cacheEpoch}`;
    };

    const ensureCached = (url) => {
        const cache = imgCacheRef.current;
        if (cache.has(url)) return;
        cache.add(url);
        const img = new Image();
        img.src = url;
    };

    // (Icon URLs)
    const iconBaseParams = useMemo(
        () => ({
            ...identityParams,
            hotel,
            direction: ICON_DIR,
            head_direction: ICON_HEAD,
            action: "std",
            gesture: "std",
            size: "b",
            shadow: true,
            crr: null,
            custom: null,
        }),
        [identityParams, hotel]
    );

    const iconUrl = (overrides = {}) => {
        const forced = overrides?.action === "sit" || overrides?.action === "lay" ? { ...overrides, shadow: false } : overrides;
        return withCache(getHabboImageUrl({ ...iconBaseParams, ...forced }));
    };

    // (Rotation / Carry Rules)
    const allowedHeadDirections = useMemo(() => {
        const base = direction;
        return [mod8(base - 2), mod8(base - 1), mod8(base), mod8(base + 1), mod8(base + 2)];
    }, [direction]);

    const isSitOrLay = action === "sit" || action === "lay";
    const canHoldAnyItem = !isSitOrLay;

    const isDirectionAllowedForRod = direction === 2 || direction === 4;
    const canHoldFishingRod = canHoldAnyItem && isDirectionAllowedForRod;

    const setBodyDirection = (d) => {
        const next = mod8(d);
        setDirection(next);
        setHeadDirection(next);
    };

    useEffect(() => {
        if (!allowedHeadDirections.includes(headDirection)) setHeadDirection(direction);
    }, [direction, allowedHeadDirections, headDirection]);

    useLayoutEffect(() => {
        const isHabbo = !!activeLayerId?.startsWith("habbo:");
        const isFurni = !!activeFurniId;

        if (!isHabbo && !isFurni) {
            setIconRowsLoaded(true);
            return;
        }

        const loadKey = {};
        iconRowsLoadKeyRef.current = loadKey;

        const params = iconUrlParamsRef.current || {};
        const { identityParams: ip, hotel: h, action: act, direction: dir, bodyDirOptions: bDirs, headDirOptions: hDirs } = params;

        const urls = [];

        if (isHabbo && ip && h) {
            const rotPoseAction = act === "sit" || act === "lay" ? act : "std";
            const rotShadow = rotPoseAction === "sit" || rotPoseAction === "lay" ? false : true;

            (bDirs || []).forEach((d) => {
                urls.push(withCache(getHabboImageUrl({ ...ip, hotel: h, direction: d, head_direction: d, action: rotPoseAction, gesture: "std", size: "a", shadow: rotShadow, crr: null, custom: null })));
            });
            (hDirs || []).forEach((d) => {
                urls.push(withCache(getHabboImageUrl({ ...ip, hotel: h, direction: rotPoseAction === "lay" ? d : dir, head_direction: d, action: rotPoseAction, gesture: "std", size: "b", shadow: rotShadow, crr: null, custom: null })));
            });
            ACTIONS.forEach((a) => {
                const forced = a.value === "sit" || a.value === "lay" ? { action: a.value, shadow: false } : { action: a.value };
                urls.push(withCache(getHabboImageUrl({ ...ip, hotel: h, direction: ICON_DIR, head_direction: ICON_HEAD, action: "std", gesture: "std", size: "b", shadow: true, crr: null, custom: null, ...forced })));
            });
            GESTURES.forEach((g) => {
                urls.push(withCache(getHabboImageUrl({ ...ip, hotel: h, direction: ICON_DIR, head_direction: ICON_HEAD, action: "std", gesture: g.value, size: "b", shadow: true, crr: null, custom: null })));
            });
            urls.push(withCache(getHabboImageUrl({ ...ip, hotel: h, direction: ICON_DIR, head_direction: ICON_HEAD, action: "std", gesture: "std", size: "b", shadow: false, crr: null, custom: null })));
            urls.push(withCache(getHabboImageUrl({ ...ip, hotel: h, direction: ICON_DIR, head_direction: ICON_HEAD, action: "std", gesture: "std", size: "b", shadow: true, crr: null, custom: "frank_hat" })));
        }

        if (isFurni) {
            const sp = furniSpritesRef.current.find((x) => x.id === activeFurniId);
            const code = String(sp?.code || "").trim();
            if (code) {
                const ri = sp?.rotI ?? 0, si = sp?.stateI ?? 0, fi = sp?.frameI ?? 0;
                [0, 1, 2, 3, 4, 5, 6].forEach((i) => {
                    urls.push(buildFoFurniUrl(code, i, si, fi));
                    urls.push(buildFoFurniUrl(code, ri, i, fi));
                    urls.push(buildFoFurniUrl(code, ri, si, i));
                });
            }
        }

        const unique = [...new Set(urls.filter(Boolean))];

        if (!unique.length) {
            setIconRowsLoaded(true);
            return;
        }

        let count = 0;
        const check = () => {
            count++;
            if (count >= unique.length && iconRowsLoadKeyRef.current === loadKey) {
                setIconRowsLoaded(true);
            }
        };
        unique.forEach((url) => {
            const img = new window.Image();
            img.onload = img.onerror = check;
            img.src = url;
        });
    }, [activeLayerId, activeFurniId, identityParams, iconLoadEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { if (showAssetsModal) loadAssetsManifest(); }, [showAssetsModal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (showFurniColorerPanel) loadFurniColorerGroup(furniColorerGroup, furniColorerHex);
    }, [showFurniColorerPanel, furniColorerGroup]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isSitOrLay && crr) setCrr("");
    }, [isSitOrLay, crr]);

    useEffect(() => {
        if (crr === "11" && !canHoldFishingRod) setCrr("");
    }, [crr, canHoldFishingRod]);

    useEffect(() => {
        if (action === "sit" || action === "lay") {
            if (shadow) shadowSuppressedByPoseRef.current = true;
            if (shadow) setShadow(false);
            return;
        }
        if (shadowSuppressedByPoseRef.current && !shadowUserSetRef.current) setShadow(true);
        shadowSuppressedByPoseRef.current = false;
    }, [action, shadow]);

    const setShadowSafe = (next) => {
        shadowUserSetRef.current = true;
        shadowSuppressedByPoseRef.current = false;
        setShadow(next);
    };

    const headDirOptions = useMemo(() => {
        if (action === "lay") return [2, 4];
        return allowedHeadDirections;
    }, [action, allowedHeadDirections]);


    // (Scene Switching / Loading)
    const addHabboToCurrentScene = async (nextName, nextHotel, sizeOverride) => {
        const n = (nextName || "").trim();
        const h = (nextHotel || "COM").toUpperCase();
        if (!n) return;

        let fig = getCachedFigureString(n, h);
        if (!fig) {
            try {
                fig = await resolveFigureString({ username: n, hotel: h, signal: undefined });
            } catch {}
        }

        const ownerKey = `${h}:${n}`.toLowerCase();
        const newId = makeId();

        const snap = makeSnapshot({
            habboName: n,
            hotel: h,
            figureString: fig || null,
            direction: 2,
            headDirection: 2,
            action: "std",
            gesture: "std",
            size: sizeOverride || "b",
            crr: "",
            shadow: true,
            frankify: false,
            useFrankHatForDefault: false,
            scale: 1,
            crop: null,
            sourceSize: { w: 0, h: 0 },
            flipX: false,
            flipY: false,
            rotation: 0,
        });

        pushHistory();
        setSprites((prev) => prev.concat([{ type: 'habbo', id: newId, ownerKey, snap, pos: { x: 0, y: 0 }, visible: true, locked: false, opacity: 100, layerName: '', effects: {} }]));

        editingSceneHabboIdRef.current = newId;
        setEditingSceneHabboId(newId);

        setActiveSceneHabboId(newId);
        setActiveLayerId(`habbo:${newId}`);

        applySnapshot(snap, { keepView: true });
        setSpritePos({ x: 0, y: 0 });
        hasRealHabboRef.current = true;
        setReadyToShowHabbo(true);
    };

    const createNewSceneForHabbo = (name, h) => {
        const nextName = (name || "").trim();
        const nextHotel = (h || "COM").toUpperCase();
        if (!nextName) return false;

        if (scenes.length >= MAX_SCENES) return false;

        beginSceneLoading();
        restoringRef.current = true;

        const ownerKey = `${nextHotel}:${nextName}`.toLowerCase();

        const snap = makeSnapshot({
            habboName: nextName,
            hotel: nextHotel,
            figureString: null,
        });

        const ownerSprite = {
            id: makeId(),
            ownerKey,
            snap,
            pos: { x: 0, y: 0 },
        };

        const newScene = createEmptySceneForOwner(ownerKey, nextName, nextHotel);
        newScene.imageName = nextName;

        saveScenesState((prev) => [newScene, ...prev].slice(0, MAX_SCENES));
        syncSceneToDb(newScene);

        setSceneOwnerKey(ownerKey);
        setActiveSceneIdSafe(newScene.id);

        setSprites([{ type: 'habbo', ...ownerSprite, visible: true, locked: false, opacity: 100, layerName: '', effects: {} }]);

        editingSceneHabboIdRef.current = ownerSprite.id;
        setEditingSceneHabboId(ownerSprite.id);

        setActiveSceneHabboId(ownerSprite.id);
        setActiveLayerId(`habbo:${ownerSprite.id}`);

        applySnapshot(snap, { keepView: true });

        restoringRef.current = false;
        hasRealHabboRef.current = true;
        setReadyToShowHabbo(true);
        return true;
    };

    const isHabboAlreadyInCurrentScene = (name, h) => {
        const n = (name || "").trim();
        if (!n) return false;
        const hotelKey = (h || hotel || "COM").toUpperCase();
        const key = `${hotelKey}:${n}`.toLowerCase();

        const active = scenes.find((s) => s?.id === activeSceneId) || null;
        if (!active) return false;

        const ownerKey = normalizeOwnerKey(active.ownerKey);
        if (ownerKey === key) return true;

        const spriteKeys = (active.sceneSprites || [])
            .map((sp) => `${(sp?.snap?.hotel || "COM").toUpperCase()}:${(sp?.snap?.habboName || "").trim()}`.toLowerCase())
            .filter(Boolean);

        return spriteKeys.includes(key);
    };

    const createNewImageScene = (imageName, width, height, useTileMode = false, tileSz = 500) => {
        const nextName = (imageName || "").trim();
        if (!nextName) {
            toast.error("Please enter an image name");
            return;
        }

        const finalW = useTileMode ? tileSz : width;
        const finalH = useTileMode ? tileSz : height;

        beginSceneLoading();
        restoringRef.current = true;

        const newScene = {
            id: makeId(),
            imageName: nextName,
            imageWidth: finalW,
            imageHeight: finalH,
            tileMode: useTileMode,
            tileSize: tileSz,
            ownerKey: null,
            activeSnap: null,
            ownerFigureString: null,
            sceneSprites: [],
            furniSprites: [],
            furniIds: [],
            speechBubbles: [],
            customImages: [],
            textSprites: [],
            userFurni: [],
            layers: [],
            layersStateByBg: {},
            previewBg: "default",
            zoom: 1,
            cameraPos: { x: 0, y: 0 },
            spritePos: { x: 0, y: 0 },
        };

        saveScenesState((prev) => [newScene, ...prev].slice(0, MAX_SCENES));
        syncSceneToDb(newScene);

        setSceneOwnerKey(null);
        setActiveSceneIdSafe(newScene.id);

        setSprites([]);
        setSpeechBubbles([]);
        setUserFurni([]);

        editingSceneHabboIdRef.current = null;
        setEditingSceneHabboId(null);

        setActiveSceneHabboId(null);
        setActiveFurniId(null);
        setActiveCustomImageId(null);
        setActiveTextId(null);
        setActiveLayerId(null);

        setSpritePos({ x: 0, y: 0 });
        setPreviewSize({ w: finalW, h: finalH });
        setTileMode(useTileMode);
        setTileSize(tileSz);
        preTileModeSize.current = null;
        setGridTint("");
        setWhiteBgTint("");
        setWorkspaceOffset({ x: 0, y: 0 });

        restoringRef.current = false;
        hasRealHabboRef.current = false;
        setReadyToShowHabbo(false);

        setShowNewImageModal(false);
        setNewImageName("");
        setNewImageWidth(650);
        setNewImageHeight(500);
        setNewImagePreset("Standard");
        setNewImageUsePreset(true);
        setNewImageTileMode(false);
        setNewImageTileSize(500);

        toast.success(`Image "${nextName}" created successfully`);
    };


    const loadHabbo = () => {
        const next = (habboNameInput || "").trim();
        if (!next) return;

        const nextHotel = (hotel || "COM").toUpperCase();

        addHabboToCurrentScene(next, nextHotel, newHabboSize);
        setHabboNameInput("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") loadHabbo();
    };

    // (Effective Display State)
    const effectiveHeadDirection = headDirection;
    const effectiveAction = action;
    const effectiveGesture = gesture;

    // (Preview URLs)
    const baseParams = useMemo(
        () => ({
            ...identityParams,
            hotel,
            direction,
            head_direction: effectiveHeadDirection,
            action: effectiveAction,
            size,
            shadow,
            crr: crr || null,
            custom: effectiveCustom,
        }),
        [identityParams, hotel, direction, effectiveHeadDirection, effectiveAction, size, shadow, crr, effectiveCustom]
    );

    const basePreviewUrl = useMemo(() => {
        const u = getHabboImageUrl({ ...baseParams, gesture: effectiveGesture });
        const fixed = figureString ? ensureHotelParam(u, hotel) : u;
        return withCache(fixed);
    }, [baseParams, effectiveGesture, cacheEpoch, figureString, hotel]);

    useEffect(() => {
        const allowed = action === "lay" ? [2, 4] : action === "sit" ? [0, 2, 4, 6] : [0, 1, 2, 3, 4, 5, 6, 7];
        if (!allowed.includes(direction)) setBodyDirection(allowed[0]);
    }, [action, direction]);

    useEffect(() => {
        ensureCached(basePreviewUrl);
    }, [basePreviewUrl]);
    const imageUrl = basePreviewUrl;
    const croppedURL = imageUrl + "&crop=true";

    useEffect(() => {
        setActiveSpriteSourceSize({ w: 0, h: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [croppedURL]);

    // (Refresh)
    const refreshCurrent = async () => {
        if (!isValidHabboName(habboName)) return;

        const h = (hotel || "COM").toUpperCase();
        const key = `${h}:${habboName}`.toLowerCase();

        if (lookupAbortRef.current) lookupAbortRef.current.abort();
        const ac = new AbortController();
        lookupAbortRef.current = ac;

        const cache = readFigCache();
        if (cache[key]) {
            delete cache[key];
            writeFigCache(cache);
        }

        try {
            const freshFig = await (async () => {
                const base = HOTEL_API_BASE[h] || HOTEL_API_BASE.COM;
                const url = `${base}/api/public/users?name=${encodeURIComponent(habboName)}`;

                const res = await fetch(url, { signal: ac.signal, headers: { Accept: "application/json" } });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();

                const fig = data?.figureString;
                if (!fig) throw new Error("No figureString in response");
                return fig;
            })();

            if (ac.signal.aborted) return;

            const current = figureString || null;

            if (current && freshFig === current) {
                toast.info("Figure string is already up to date - no refresh needed.");
                return;
            }

            const nextCache = readFigCache();
            nextCache[key] = { figureString: freshFig, t: Date.now() };
            writeFigCache(nextCache);

            setFigureString(freshFig);
            // Explicit refresh: force a fresh fetch even if the URL would otherwise be
            // served from cache (this is the one sanctioned place that busts the cache).
            setCacheEpoch((n) => n + 1);
            imgCacheRef.current = new Set();
            toast.success("Figure string refreshed!");
        } catch {
            if (ac.signal.aborted) return;
            toast.error("Failed to refresh figure string.");
        }
    };

    // (Selection Setters)
    const setActionSafe = (nextAction) => {
        if (crr && (nextAction === "sit" || nextAction === "lay")) return;
        setAction(nextAction);
    };

    const setCarrySafe = (nextCrr) => {
        if (!nextCrr) {
            setCrr("");
            return;
        }
        if (!canHoldAnyItem) return;
        if (nextCrr === "11" && !canHoldFishingRod) return;
        setCrr(nextCrr);
    };

    // (Reset)
    const resetAll = () => {
        shadowUserSetRef.current = false;
        shadowSuppressedByPoseRef.current = false;

        setSpriteSelected(false);
        setSpritePos({ x: 0, y: 0 });
        setSprites((prev) => prev.filter((s) => s.type !== 'habbo'));

        setDirection(2);
        setHeadDirection(2);
        setAction("std");
        setGesture("std");
        setSize("b");
        setShadow(true);
        setFrankify(false);
        setUseFrankHatForDefault(false);
        setCrr("");

        setZoom(1);
        setPreviewBg("default");
        setCameraPos({ x: 0, y: 0 });
        setShowZoomNotice(false);
        if (zoomNoticeTimerRef.current) window.clearTimeout(zoomNoticeTimerRef.current);
        zoomNoticeTimerRef.current = null;
    };

    const clearCanvas = () => {
        setShowClearSceneModal(true);
    };

    const confirmClearScene = () => {
        setShowClearSceneModal(false);
        resetAll();
        setSprites([]);
        setActiveFurniId(null);
        setSpeechBubbles([]);
        setActiveCustomImageId(null);
        setBgLayerVis({});
        setMoodlightVisible(false);
        setSpriteSelected(false);
        toast.success("Scene cleared");
    };

    const doCanvasResize = (newW, newH) => {
        pushHistory();

        const shouldForceDefault =
            (newW > 650 || newH > 500) &&
            !["default", "grid", "grey_grid", "room_tile"].includes(previewBg);

        const nextPreviewBg = shouldForceDefault ? "default" : previewBg;

        setPreviewSize({ w: newW, h: newH });
        setCanvasWInput(newW);
        setCanvasHInput(newH);

        saveScenesState((prev) =>
            prev.map((s) =>
                s.id === activeSceneId
                    ? {
                        ...s,
                        imageWidth: newW,
                        imageHeight: newH,
                        previewBg: nextPreviewBg,
                    }
                    : s
            )
        );

        if (nextPreviewBg !== previewBg) {
            setPreviewBg(nextPreviewBg);
        }
    };

    const applyCanvasResize = (w, h) => {
        const newW = Math.max(1, Math.min(2000, Math.round(Number(w) || 1)));
        const newH = Math.max(1, Math.min(2000, Math.round(Number(h) || 1)));
        if (newW === previewSize.w && newH === previewSize.h) return;
        doCanvasResize(newW, newH);
    };

    const uploadCustomImage = async () => {
        if (resolvedConfig.disableUpload) return;
        if (!uploadedImageFile) return;
        const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowed.includes(uploadedImageFile.type)) {
            toast.error('Only JPEG, PNG, GIF and WebP files are allowed');
            return;
        }
        if (uploadedImageFile.size > 5 * 1024 * 1024) {
            toast.error('File must be under 5MB');
            return;
        }
        setIsUploadingCustomImage(true);
        try {
            const formData = new FormData();
            formData.append('image', uploadedImageFile);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}upload`, {
                method: 'POST',
                credentials: 'include',
                headers: resolvedConfig.sharedKey ? { 'X-Sulake-Key': resolvedConfig.sharedKey } : undefined,
                body: formData,
            });
            const data = await response.json();
            if (data.url) {
                const newImg = { type: 'customImage', id: makeId(), url: data.url, pos: { x: 0, y: 0 }, scale: 1, crop: null, sourceSize: null, visible: true, locked: false, opacity: 100, layerName: '', effects: {} };
                pushHistory();
                setSprites((prev) => [...prev, newImg]);
                setActiveCustomImageId(newImg.id);
                setActiveLayerId(`custom_image:${newImg.id}`);
                toast.success('Image added to scene');
                setShowImageUploadPanel(false);
                setUploadedImageFile(null);
                setUploadedImagePreview(null);
            } else {
                toast.error(data.message || 'Upload failed');
            }
        } catch (err) {
            toast.error('Upload failed');
        } finally {
            setIsUploadingCustomImage(false);
        }
    };

    const handleBgFileSelect = (file) => {
        if (resolvedConfig.disableUpload) return;
        if (!file) return;
        const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowed.includes(file.type)) { toast.error('Only JPEG, PNG, GIF and WebP allowed'); return; }
        const url = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => {
            const { naturalWidth: w, naturalHeight: h } = img;
            setPendingBgFile({ url, w, h, file });
            setShowBgFitModal(true);
        };
        img.onerror = () => { URL.revokeObjectURL(url); toast.error('Could not read image'); };
        img.src = url;
    };

    const applyCustomBg = async ({ fit, createNew }) => {
        if (resolvedConfig.disableUpload) return;
        if (!pendingBgFile) return;
        const { url, w, h, file } = pendingBgFile;
        setShowBgFitModal(false);
        if (createNew) {
            doCanvasResize(w, h);
        }
        pushHistory();
        // Show the local blob straight away for instant feedback…
        if (customBgUrl && customBgUrl.startsWith('blob:')) URL.revokeObjectURL(customBgUrl);
        setCustomBgUrl(url);
        setCustomBgFit(fit);
        setPreviewBg('custom_bg');
        setPendingBgFile(null);

        if (!file) return;
        try {
            const formData = new FormData();
            formData.append('image', file);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}upload`, {
                method: 'POST',
                credentials: 'include',
                headers: resolvedConfig.sharedKey ? { 'X-Sulake-Key': resolvedConfig.sharedKey } : undefined,
                body: formData,
            });
            const data = await response.json();
            if (data.url) {
                setCustomBgUrl((prev) => {
                    if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
                    return data.url;
                });
            } else {
                toast.error(data.message || 'Background upload failed - it may not save across sessions');
            }
        } catch {
            toast.error('Background upload failed - it may not save across sessions');
        }
    };

    const removeCustomBg = () => {
        pushHistory();
        if (customBgUrl && customBgUrl.startsWith('blob:')) URL.revokeObjectURL(customBgUrl);
        setCustomBgUrl(null);
        setPreviewBg('default');
    };

    const loadAssetsManifest = async () => {
        try {
            const res = await fetch(`${process.env.PUBLIC_URL}/img/imagerassets/manifest.json`);
            if (!res.ok) return;
            const data = await res.json();
            setAssetsManifest(data);
        } catch {}
    };

    const addAssetToScene = (assetUrl) => {
        pushHistory();
        const newImg = { type: 'customImage', id: makeId(), url: assetUrl, pos: { x: 0, y: 0 }, scale: 1, crop: null, sourceSize: null, visible: true, locked: false, opacity: 100, layerName: '', effects: {} };
        setSprites((prev) => [...prev, newImg]);
        setActiveCustomImageId(newImg.id);
        setActiveLayerId(`custom_image:${newImg.id}`);
        setShowAssetsModal(false);
    };

    const addUrlToScene = (url) => {
        pushHistory();
        const newImg = { type: 'customImage', id: makeId(), url, pos: { x: 0, y: 0 }, scale: 1, crop: null, sourceSize: null, visible: true, locked: false, opacity: 100, layerName: '', effects: {} };
        setSprites((prev) => [...prev, newImg]);
        setActiveCustomImageId(newImg.id);
        setActiveLayerId(`custom_image:${newImg.id}`);
    };

    // ── Habbo Font ──
    const handleGenerateHabboFont = async () => {
        if (!habboFontText.trim() || habboFontGenerating) return;
        setHabboFontGenerating(true);
        const font = FONTS.find(f => f.folder === habboFontFolder) || FONTS[0];
        const dataUrl = await generateHabboFontDataUrl(
            habboFontText.trim(),
            font.folder,
            font.filenamePattern,
            font.offset || 0
        );
        setHabboFontPreviewUrl(dataUrl);
        setHabboFontGenerating(false);
    };

    const handleAddHabboFontToScene = () => {
        if (!habboFontPreviewUrl) return;
        addUrlToScene(habboFontPreviewUrl);
        setShowHabboFontPanel(false);
    };

    // ── Furni Colourer ──
    const loadFurniColorerGroup = async (groupName, hex) => {
        const entries = FURNI_COLOURER_GROUPS[groupName].map(normaliseFurniEntry);
        const loading = {};
        entries.forEach(({ name }) => { loading[name] = true; });
        setFurniColorerLoading(loading);
        const results = await Promise.allSettled(entries.map(async ({ name, direction }) => {
            const params = new URLSearchParams({ furni: name, direction, state: 1, frame: 0, hex });
            const res = await fetch(`${FURNI_COLOURER_API}/api/furniture/render?${params}`);
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            return { name, objectUrl: URL.createObjectURL(blob) };
        }));
        setFurniColorerPreviews(prev => {
            const next = { ...prev };
            for (const r of results) {
                if (r.status === 'fulfilled') {
                    const { name, objectUrl } = r.value;
                    if (next[name]) URL.revokeObjectURL(next[name]);
                    next[name] = objectUrl;
                }
            }
            return next;
        });
        setFurniColorerLoading({});
    };

    const handleFurniColorerApply = async () => {
        setFurniColorerApplying(true);
        await loadFurniColorerGroup(furniColorerGroup, furniColorerHex);
        setFurniColorerApplying(false);
    };

    const handleAddFurniColorToScene = (name, direction) => {
        pushHistory();
        const hex = furniColorerHex;
        const rotI = Number.isFinite(direction) ? direction : 2;
        const stateI = 0;
        const frameI = 0;

        // Build a placeholder images grid so iconrows have 7 dirs × 1 state to start
        const images = [Array(7).fill('x')];
        const rotations = images[0].map((_, i) => buildColoredFurniUrl(name, i, stateI, frameI, hex)).filter(Boolean);
        const states = images.map((_, i) => buildColoredFurniUrl(name, rotI, i, frameI, hex)).filter(Boolean);
        const url = buildColoredFurniUrl(name, rotI, stateI, frameI, hex);

        const id = makeId();
        setSprites((prev) => prev.concat([{
            type: 'furni',
            id,
            furniId: null,
            name,
            code: name,
            hex,
            url,
            images,
            shadowImages: [],
            rotations,
            rotI,
            states,
            stateI,
            frameI,
            scale: 1,
            crop: null,
            sourceSize: { w: 0, h: 0 },
            pos: { x: 0, y: 0 },
            visible: true,
            locked: false,
            opacity: 100,
            layerName: '',
            effects: {},
        }]));

        setActiveFurniId(id);
        setActiveCustomImageId(null);
        setActiveTextId(null);
        setActiveSceneHabboId(null);
        setSpriteSelected(false);
        setActiveLayerId(`furni:${id}`);

        // Fetch real variant data so direction/state counts are accurate
        const apiUrl = (process.env.REACT_APP_API_URL).trim();
        fetch(`${apiUrl}furni-imaging/furni-info/${encodeURIComponent(name)}`, {
            headers: { Accept: "application/json" },
        }).then(async (res) => {
            if (!res.ok) return;
            const data = await res.json();
            const variants = Array.isArray(data?.normal?.variants) ? data.normal.variants : [];
            if (!variants.length) return;

            const hasState = variants.some((v) => Number.isFinite(v?.state));
            const hasDir = variants.some((v) => Number.isFinite(v?.direction));

            let newImages;
            if (!hasState && !hasDir) {
                newImages = [variants.map(() => 'x')];
            } else {
                let maxState = 0;
                for (const v of variants) {
                    const st = hasState && Number.isFinite(v?.state) ? Number(v.state) : 0;
                    if (st > maxState) maxState = st;
                }
                const byState = Array.from({ length: maxState + 1 }, () => []);
                for (const v of variants) {
                    const st = hasState && Number.isFinite(v?.state) ? Number(v.state) : 0;
                    byState[st].push(v);
                }
                let maxDir = 0;
                if (hasDir) {
                    for (const v of variants) {
                        const dr = Number.isFinite(v?.direction) ? Number(v.direction) : 0;
                        if (dr > maxDir) maxDir = dr;
                    }
                } else {
                    for (const arr of byState) {
                        if (arr.length - 1 > maxDir) maxDir = arr.length - 1;
                    }
                }
                newImages = Array.from({ length: maxState + 1 }, () =>
                    Array.from({ length: maxDir + 1 }, () => 'x')
                );
            }

            const newRotations = newImages[0].map((_, ri) => buildColoredFurniUrl(name, ri, 0, 0, hex)).filter(Boolean);
            const newStates = newImages.map((_, si) => buildColoredFurniUrl(name, rotI, si, 0, hex)).filter(Boolean);
            updateFurniSprite(id, { images: newImages, rotations: newRotations, states: newStates });
        }).catch(() => {});
    };

    const deleteSelectedItem = () => {
        // Multi-selection: remove every selected sprite at once.
        const ids = selectedLayerIdsRef.current;
        if (ids.length > 1) {
            pushHistory();
            const prefixToType = { furni: "furni", habbo: "habbo", custom_image: "customImage", text: "text" };
            const removeSet = new Set();
            ids.forEach((lid) => {
                const ci = lid.indexOf(":");
                const type = prefixToType[lid.slice(0, ci)];
                if (type) removeSet.add(`${type}:${lid.slice(ci + 1)}`);
            });
            const removedHabboOwnerKeys = sprites
                .filter((s) => s.type === "habbo" && removeSet.has(`habbo:${s.id}`) && s.ownerKey)
                .map((s) => s.ownerKey);
            if (removedHabboOwnerKeys.length) {
                setEnabledOwnerKeysForActiveScene((list) =>
                    list.filter((x) => !removedHabboOwnerKeys.some((k) => normalizeOwnerKey(k) === normalizeOwnerKey(x)))
                );
            }
            setSprites((prev) => prev.filter((s) => !removeSet.has(`${s.type}:${s.id}`)));
            if (editingSceneHabboIdRef.current && removeSet.has(`habbo:${editingSceneHabboIdRef.current}`)) {
                editingSceneHabboIdRef.current = null;
                setEditingSceneHabboId(null);
            }
            setActiveFurniId(null);
            setActiveSceneHabboId(null);
            setActiveCustomImageId(null);
            setActiveTextId(null);
            setSpriteSelected(false);
            setActiveLayerId(null);
            setSelectedLayerIds([]);
            return;
        }

        const ctx = getSelectedSpriteContext();
        if (!ctx) return;
        pushHistory();

        if (ctx.type === "furni") {
            setSprites((prev) => prev.filter((s) => !(s.id === ctx.id && s.type === 'furni')));
            setActiveFurniId(null);
        } else if (ctx.type === "customImage") {
            setSprites((prev) => prev.filter((s) => !(s.id === ctx.id && s.type === 'customImage')));
            setActiveCustomImageId(null);
        } else if (ctx.type === "text") {
            setSprites((prev) => prev.filter((s) => !(s.id === ctx.id && s.type === 'text')));
            setActiveTextId(null);
        } else if (ctx.type === "sceneHabbo") {
            if (ctx.sprite.ownerKey) {
                setEnabledOwnerKeysForActiveScene((list) => list.filter((x) => normalizeOwnerKey(x) !== normalizeOwnerKey(ctx.sprite.ownerKey)));
            }
            setSprites((prev) => prev.filter((s) => !(s.id === ctx.id && s.type === 'habbo')));
            setActiveSceneHabboId(null);
            if (editingSceneHabboIdRef.current === ctx.id) {
                editingSceneHabboIdRef.current = null;
                setEditingSceneHabboId(null);
            }
        } else if (ctx.type === "activeHabbo") {
            const removedKey = `${(hotel || "COM").toUpperCase()}:${(habboName || "").trim()}`.toLowerCase();
            setEnabledOwnerKeysForActiveScene((list) => list.filter((x) => normalizeOwnerKey(x) !== removedKey));
            setHabboNameInput("");
            setHabboName("jejsjdbsbsjwieirjejsndbfhs");
            setFigureString(null);
        }

        setActiveLayerId(null);
        setSpriteSelected(false);
        setContextMenu(null);
        endSpriteDrag();
    };

    const hasActiveHabbo = isValidHabboName(habboName);

    const layerById = useMemo(() => new Map(layers.map((l) => [l.id, l])), [layers]);

    const layerZ = useMemo(() => {
        const m = new Map();
        const n = layers.length;
        for (let i = 0; i < n; i++) m.set(layers[i].id, (i + 1) * 100);
        return m;
    }, [layers]);

    // Helper to extract sprite id from a layer id (e.g. 'furni:abc' -> 'abc')
    const spriteIdFromLayerId = (layerId) => {
        const colon = (layerId || '').indexOf(':');
        return colon === -1 ? layerId : layerId.slice(colon + 1);
    };

    const isUserLayerId = (id) => {
        return (id || '').match(/^(furni:|habbo:|custom_image:|text:)/);
    };

    const moveLayer = (dragId, overId) => {
        const dragLayer = layers.find((l) => l.id === dragId);
        const overLayer = layers.find((l) => l.id === overId);
        if (!dragLayer || dragLayer.permaLock || dragLayer.locked) return;
        if (!overLayer) return;

        pushHistory();

        const selIds = selectedLayerIdsRef.current || [];
        const groupIds = (selIds.length > 1 && selIds.includes(dragId))
            ? selIds.filter((id) => {
                const l = layers.find((x) => x.id === id);
                return l && !l.permaLock && !l.locked;
            })
            : [dragId];

        setMidLayerOrder((prev) => {
            // Pull every group member out, keeping their existing relative order.
            const groupSet = new Set(groupIds);
            const orderedGroup = prev.filter((id) => groupSet.has(id));
            if (orderedGroup.length === 0) return prev;
            const reordered = prev.filter((id) => !groupSet.has(id));

            if (overLayer.permaLock === "bottom") {
                return [...orderedGroup, ...reordered];
            }
            if (overLayer.permaLock === "top" || overLayer.permaLock === "speech") {
                return [...reordered, ...orderedGroup];
            }
            if (groupSet.has(overId)) {
                const overIdx = prev.indexOf(overId);
                let anchor = null;
                for (let i = overIdx - 1; i >= 0; i--) {
                    if (!groupSet.has(prev[i])) { anchor = prev[i]; break; }
                }
                if (anchor === null) return [...orderedGroup, ...reordered];
                const ai = reordered.indexOf(anchor);
                reordered.splice(ai + 1, 0, ...orderedGroup);
                return reordered;
            }

            const ti = reordered.indexOf(overId);
            if (ti === -1) return prev;
            reordered.splice(ti + 1, 0, ...orderedGroup);
            return reordered;
        });
    };

    const moveLayerByStep = (id, dir) => {
        const layer = layers.find((l) => l.id === id);
        if (!layer || layer.permaLock || layer.locked) return;
        pushHistory();

        setMidLayerOrder((prev) => {
            const idx = prev.indexOf(id);
            if (idx === -1) return prev;
            // dir > 0 = toward end of midLayerOrder = higher z (up in visual stack)
            // dir < 0 = toward start = lower z (down in visual stack)
            const nextIdx = idx + dir;
            if (nextIdx < 0 || nextIdx >= prev.length) return prev;
            const list = prev.slice();
            list.splice(idx, 1);
            list.splice(nextIdx, 0, id);
            return list;
        });
    };

    const serialiseLayers = (layers) =>
        (Array.isArray(layers) ? layers : []).map((l) => ({
            id: String(l.id || ""),
            visible: l.visible !== false,
            locked: !!l.locked,
            name: l.name || "",
            opacity: typeof l.opacity === "number" ? l.opacity : 100,
            effects: typeof l.effects === "object" && l.effects !== null ? l.effects : {},
        }));

    useEffect(() => {
        if (!hydratedRef.current) return;
        if (!activeSceneId) return;
        if (!layers.length) return;

        const serialised = serialiseLayers(layers);

        setScenes((prev) => {
            const idx = prev.findIndex((s) => s?.id === activeSceneId);
            if (idx === -1) return prev;
            const existing = prev[idx];
            const bgKey = existing.previewBg || "default";
            const updated = [...prev];
            updated[idx] = {
                ...existing,
                layers: serialised,
                layersState: serialised,
                layersStateByBg: {
                    ...(existing.layersStateByBg && typeof existing.layersStateByBg === "object" ? existing.layersStateByBg : {}),
                    [bgKey]: serialised,
                },
            };
            return updated;
        });
    }, [layers, activeSceneId]);

    const selectLayerById = (id) => {
        if (!id) {
            setActiveLayerId(null);
            setActiveFurniId(null);
            setActiveSceneHabboId(null);
            setActiveCustomImageId(null);
            setActiveTextId(null);
            setSpriteSelected(false);
            return;
        }

        setActiveLayerId(id);

        if (id.startsWith("bg:")) {
            setActiveFurniId(null);
            setActiveSceneHabboId(null);
            setActiveCustomImageId(null);
            setActiveTextId(null);
            setSpriteSelected(false);
            endSpriteDrag();
            return;
        }

        if (id.startsWith("custom_image:")) {
            const imgId = id.slice("custom_image:".length);
            setActiveCustomImageId(imgId || null);
            setActiveFurniId(null);
            setActiveSceneHabboId(null);
            setActiveTextId(null);
            setSpriteSelected(false);
            endSpriteDrag();
            return;
        }

        if (id.startsWith("text:")) {
            const tid = id.slice("text:".length);
            setActiveTextId(tid || null);
            setActiveFurniId(null);
            setActiveSceneHabboId(null);
            setActiveCustomImageId(null);
            setSpriteSelected(false);
            endSpriteDrag();
            return;
        }

        if (id === "habbo:__active__") {
            setActiveFurniId(null);
            setActiveSceneHabboId(null);
            setActiveCustomImageId(null);
            setActiveTextId(null);
            setSpriteSelected(true);
            beginIconLoad();
            return;
        }

        if (id.startsWith("furni:")) {
            const fid = id.slice("furni:".length);
            setActiveFurniId(fid || null);
            setActiveSceneHabboId(null);
            setActiveCustomImageId(null);
            setActiveTextId(null);
            setSpriteSelected(false);
            beginIconLoad();
            endSpriteDrag();
            return;
        }

        if (id.startsWith("habbo:")) {
            const hid = id.slice("habbo:".length);
            if (!hid) return;

            const sp = sceneSprites.find((x) => x.id === hid);
            if (!sp) return;

            editingSceneHabboIdRef.current = hid;
            setEditingSceneHabboId(hid);

            setActiveSceneHabboId(hid);
            setActiveFurniId(null);
            setActiveCustomImageId(null);
            setActiveTextId(null);
            setSpriteSelected(true);
            beginIconLoad();

            applySnapshot(sp.snap, { keepView: true });
            setSpritePos({ ...sp.pos });
            endSpriteDrag();
        }
    }

    const toggleLayerVisible = (id) => {
        pushHistory();
        if (id === 'moodlight:__active__') {
            setMoodlightVisible((v) => !v);
            return;
        }
        if ((id || '').startsWith('bg:')) {
            setBgLayerVis((prev) => {
                const layer = layers.find((l) => l.id === id);
                if (!layer || !layer.canHide) return prev;
                return { ...prev, [id]: !(prev[id] !== false) };
            });
            return;
        }
        if (isUserLayerId(id)) {
            const spriteId = spriteIdFromLayerId(id);
            setSprites((prev) => prev.map((s) => {
                if (s.id !== spriteId) return s;
                return { ...s, visible: !s.visible };
            }));
        }
    };

    const toggleLayerLocked = (id) => {
        const existing = layers.find((l) => l.id === id);
        if (!existing || existing.permaLock) return;
        pushHistory();

        const nextLocked = !existing.locked;

        if (isUserLayerId(id)) {
            const spriteId = spriteIdFromLayerId(id);
            setSprites((prev) => prev.map((s) =>
                s.id === spriteId ? { ...s, locked: nextLocked } : s
            ));
        }

        if (nextLocked && activeLayerId === id) {
            if (furniDragRef.current && id === `furni:${furniDragRef.current.id}`) onFurniPointerUp();
            if (sceneHabboDragRef.current && id === `habbo:${sceneHabboDragRef.current.id}`) onSceneHabboPointerUp();

            endSpriteDrag();

            if (editingSceneHabboIdRef.current && id === `habbo:${editingSceneHabboIdRef.current}`) {
                editingSceneHabboIdRef.current = null;
                setEditingSceneHabboId(null);
            }
        }
    };


    const setLayerOpacity = (id, value) => {
        const clamped = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
        if (isUserLayerId(id)) {
            const spriteId = spriteIdFromLayerId(id);
            setSprites((prev) => prev.map((s) => s.id === spriteId ? { ...s, opacity: clamped } : s));
        }
    };

    const setLayerEffect = (id, effectName, props) => {
        if (isUserLayerId(id)) {
            const spriteId = spriteIdFromLayerId(id);
            setSprites((prev) => prev.map((s) =>
                s.id === spriteId
                    ? { ...s, effects: { ...(s.effects || {}), [effectName]: { ...(s.effects?.[effectName] || {}), ...props } } }
                    : s
            ));
        }
    };

    const removeLayerEffect = (id, effectName) => {
        pushHistory();
        // Invalidate cache for this layer's src so it reverts to original
        effectCacheRef.current.clear();
        lastOutlineBlobRef.current.clear();
        lastWaterBlobRef.current.clear();
        if (isUserLayerId(id)) {
            const spriteId = spriteIdFromLayerId(id);
            setSprites((prev) => prev.map((s) => {
                if (s.id !== spriteId) return s;
                const next = { ...(s.effects || {}) };
                delete next[effectName];
                return { ...s, effects: next };
            }));
        }
    };

    const addLayerEffect = (id, effectName) => {
        pushHistory();
        if (isUserLayerId(id)) {
            const spriteId = spriteIdFromLayerId(id);
            setSprites((prev) => prev.map((s) => {
                if (s.id !== spriteId) return s;
                if (s.effects?.[effectName]) return s;
                const defaults = effectName === 'outline' ? { enabled: true, color: '#000000', tolerance: 0 }
                    : effectName === 'water' ? { enabled: true, color: '#0a6e78', height: 20 }
                    : {};
                return { ...s, effects: { ...(s.effects || {}), [effectName]: defaults } };
            }));
        }
    };

    const effectCacheRef = useRef(new Map());
    const lastOutlineBlobRef = useRef(new Map());
    const [effectCacheTick, setEffectCacheTick] = useState(0);

    const processOutlineEffect = useCallback((src, color, tolerance) => {
        const key = `${src}::outline::${color}::${tolerance}`;
        if (effectCacheRef.current.has(key) || effectCacheRef.current.has(key + ':p')) return;
        effectCacheRef.current.set(key + ':p', true);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = applyOutlineEffect(img, color, tolerance);
            if (!canvas) { effectCacheRef.current.delete(key + ':p'); return; }
            canvas.toBlob((blob) => {
                effectCacheRef.current.delete(key + ':p');
                if (!blob) return;
                const url = URL.createObjectURL(blob);
                effectCacheRef.current.set(key, url);
                // Always keep the latest completed blob as the fallback for this src
                lastOutlineBlobRef.current.set(src, url);
                setEffectCacheTick((t) => t + 1);
            });
        };
        img.onerror = () => effectCacheRef.current.delete(key + ':p');
        img.src = src;
    }, []);

    const getLayerOutlineSrc = useCallback((src, effects) => {
        const outline = effects?.outline;
        if (!outline?.enabled || !src) return src;
        const color = /^#[0-9a-fA-F]{6}$/.test(outline.color) ? outline.color : '#000000';
        const tolerance = Math.max(0, Math.min(255, typeof outline.tolerance === 'number' ? outline.tolerance : 0));
        const key = `${src}::outline::${color}::${tolerance}`;
        const cached = effectCacheRef.current.get(key);
        if (cached) return cached;
        processOutlineEffect(src, color, tolerance);
        // Return last valid outline blob while the new one processes,
        // so the outline never flashes "off" during a colour/tolerance change.
        return lastOutlineBlobRef.current.get(src) ?? src;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processOutlineEffect, effectCacheTick]);

    // Async water effect processing - same caching pattern as outline.
    const lastWaterBlobRef = useRef(new Map());

    const processWaterEffect = useCallback((src, color, height, kind, curveMode) => {
        const key = `${src}::water::${color}::${height}::${kind}::${curveMode}`;
        if (effectCacheRef.current.has(key) || effectCacheRef.current.has(key + ':p')) return;
        effectCacheRef.current.set(key + ':p', true);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = applyWaterEffect(img, color, height, kind, curveMode);
            if (!canvas) { effectCacheRef.current.delete(key + ':p'); return; }
            canvas.toBlob((blob) => {
                effectCacheRef.current.delete(key + ':p');
                if (!blob) return;
                const url = URL.createObjectURL(blob);
                effectCacheRef.current.set(key, url);
                lastWaterBlobRef.current.set(src + '::' + kind + '::' + curveMode, url);
                setEffectCacheTick((t) => t + 1);
            });
        };
        img.onerror = () => effectCacheRef.current.delete(key + ':p');
        img.src = src;
    }, []);

    const getLayerWaterSrc = useCallback((src, effects, kind = 'habbo') => {
        const water = effects?.water;
        if (!water?.enabled || !src) return src;
        const color = /^#[0-9a-fA-F]{6}$/.test(water.color) ? water.color : '#0a6e78';
        const height = Math.max(0, Math.min(100, typeof water.height === 'number' ? water.height : 20));
        const curveMode = water.curveMode || 'adaptive';
        const key = `${src}::water::${color}::${height}::${kind}::${curveMode}`;
        const cached = effectCacheRef.current.get(key);
        if (cached) return cached;
        processWaterEffect(src, color, height, kind, curveMode);
        return lastWaterBlobRef.current.get(src + '::' + kind + '::' + curveMode) ?? src;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processWaterEffect, effectCacheTick]);

    // (UI Labels)
    const selectedActionLabel = useMemo(() => ACTIONS.find((a) => a.value === action)?.label || "Stand", [action]);
    const selectedGestureLabel = useMemo(() => GESTURES.find((g) => g.value === gesture)?.label || "Normal", [gesture]);
    const selectedCarryLabel = useMemo(() => CARRY_ITEMS.find((c) => c.value === crr)?.label || "None", [crr]);

    const activeLayer = useMemo(
        () => (activeLayerId ? layers.find((l) => l.id === activeLayerId) || null : null),
        [layers, activeLayerId]
    );
    const uiLocked = useMemo(() => {
        if (activeLayer) return !!(activeLayer.locked || activeLayer.permaLock);
        return false;
    }, [activeLayer]);

    const [forceUiLocked, setForceUiLocked] = useState(false);
    const [uiHidden, setUiHidden] = useState(false);

    // (moodlight thumbnail is now included in the layers useMemo via thumbBg)

    const controlsLocked = forceUiLocked || uiLocked;

    // (Rotation Icon Data)
    const bodyDirOptions = useMemo(() => {
        const allowed = action === "lay" ? [2, 4] : action === "sit" ? [0, 2, 4, 6] : [0, 1, 2, 3, 4, 5, 6, 7];
        const start = 2;
        return [...allowed].sort((a, b) => mod8(a - start) - mod8(b - start));
    }, [action]);

    const rotationPoseAction = action === "sit" || action === "lay" ? action : "std";
    const rotationShadow = rotationPoseAction === "sit" || rotationPoseAction === "lay" ? false : true;

    const bodyRotIconUrl = (d) =>
        withCache(
            getHabboImageUrl({
                ...identityParams,
                hotel,
                direction: d,
                head_direction: d,
                action: rotationPoseAction,
                gesture: "std",
                size: "a",
                shadow: rotationShadow,
                crr: null,
                custom: null,
            })
        );

    const headRotIconUrl = (d) =>
        withCache(
            getHabboImageUrl({
                ...identityParams,
                hotel,
                direction: rotationPoseAction === "lay" ? d : direction,
                head_direction: d,
                action: rotationPoseAction,
                gesture: "std",
                size: "b",
                shadow: rotationShadow,
                crr: null,
                custom: null,
            })
        );

    iconUrlParamsRef.current = { identityParams, hotel, action, direction, bodyDirOptions, headDirOptions, isSitOrLay };

    // (Background Rendering)
    const activeBg = previewBgConfig[previewBg] || previewBgConfig.default;

    const validRoomTileBgColorEarly = (() => {
        const c = String(roomTileBgColor || "").trim();
        if (!/^#[0-9a-fA-F]{6}$/.test(c)) return "#010101";
        const r = Math.max(0x01, parseInt(c.slice(1, 3), 16));
        const g = Math.max(0x01, parseInt(c.slice(3, 5), 16));
        const b = Math.max(0x01, parseInt(c.slice(5, 7), 16));
        return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    })();

    const [roomTilePreviewUrl, setRoomTilePreviewUrl] = useState(null);

    useEffect(() => {
        let cancelled = false;
        let objUrl = null;

        if (!activeBg || activeBg.type !== "room_tile") {
            setRoomTilePreviewUrl(null);
            return () => { cancelled = true; };
        }

        (async () => {
            const img = await loadRoomTileImage(activeBg.url);
            if (cancelled || !img) return;

            const W = previewSize.w;
            const H = previewSize.h;
            const canvas = document.createElement("canvas");
            canvas.width = W;
            canvas.height = H;
            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingEnabled = false;

            // Fill background colour
            ctx.fillStyle = validRoomTileBgColorEarly;
            ctx.fillRect(0, 0, W, H);

            const tiles = buildRoomTileGrid({ w: W, h: H }, activeBg);
            for (const t of tiles) {
                const hidden = bgLayerVis[`rt:${t.gx},${t.gy}`] === false;
                if (hidden && !roomTileEditMode) continue;
                if (hidden) ctx.globalAlpha = 0.15;
                ctx.drawImage(img, t.x, t.y, t.w, t.h);
                if (hidden) ctx.globalAlpha = 1;
            }

            await new Promise((resolve) => {
                canvas.toBlob((blob) => {
                    if (cancelled || !blob) { resolve(); return; }
                    objUrl = URL.createObjectURL(blob);
                    setRoomTilePreviewUrl(objUrl);
                    resolve();
                }, "image/png");
            });
        })();

        return () => {
            cancelled = true;
            if (objUrl) URL.revokeObjectURL(objUrl);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeBg, previewSize.w, previewSize.h, bgLayerVis, validRoomTileBgColorEarly, roomTileEditMode]);

    const tileScale = size === "a" ? 0.5 : 1;

    const changePreviewBg = (nextBgKey) => {
        const nextKey = nextBgKey || "default";
        if (resolvedConfig.disableUpload && nextKey === "custom_bg") return;
        if (nextKey === previewBg) return;
        pushHistory();

        const currentScene = scenes.find((s) => s.id === activeSceneId) || null;
        const seed =
            currentScene?.layersStateByBg && typeof currentScene.layersStateByBg === "object"
                ? currentScene.layersStateByBg[nextKey]
                : null;

        // Restore bg layer visibility from saved state for this bg key
        if (Array.isArray(seed)) {
            const bgVisSeed = {};
            seed.forEach((l) => {
                if (l.id && l.id.startsWith('bg:')) bgVisSeed[l.id] = l.visible !== false;
            });
            setBgLayerVis(bgVisSeed);
        } else {
            setBgLayerVis({});
        }

        saveScenesState((prev) => {
            const idx = prev.findIndex((s) => s.id === activeSceneId);
            if (idx === -1) return prev;

            const existing = prev[idx];
            const existingMap = existing.layersStateByBg && typeof existing.layersStateByBg === "object" ? existing.layersStateByBg : {};

            const next = prev.slice();
            next[idx] = {
                ...existing,
                layersStateByBg: {
                    ...existingMap,
                    [previewBg]: layers.map((l) => ({
                        id: l.id,
                        visible: l.visible !== false,
                        locked: !!l.locked,
                        opacity: typeof l.opacity === "number" ? l.opacity : 100,
                    })),
                },
            };
            return next;
        });

        setPreviewBg(nextKey);
        if (roomTileEditMode) setRoomTileEditMode(false);
        if (roomTileHoverKey) setRoomTileHoverKey(null);
    };

    const TRANSPARENCY_SVG_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Crect width='8' height='8' fill='%23cccccc'/%3E%3Crect x='8' y='8' width='8' height='8' fill='%23cccccc'/%3E%3Crect x='8' width='8' height='8' fill='%23ffffff'/%3E%3Crect y='8' width='8' height='8' fill='%23ffffff'/%3E%3C/svg%3E")`;

    const validGridTint = /^#[0-9a-fA-F]{6}$/.test(String(gridTint || "").trim()) ? gridTint.trim() : null;
    const validWhiteBgTint = /^#[0-9a-fA-F]{6}$/.test(String(whiteBgTint || "").trim()) ? whiteBgTint.trim() : null;

    const validRoomTileBgColor = validRoomTileBgColorEarly;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const roomTileGrid = useMemo(() => {
        if (activeBg?.type !== "room_tile") return [];
        return buildRoomTileGrid(previewSize, activeBg);
    }, [activeBg, previewSize.w, previewSize.h]);

    const tileBgPosition = useMemo(() => {
        if (activeBg?.type !== "tile") return "0px 0px";
        const rt = previewBgConfig.room_tile || {};
        const startYRatio = Number.isFinite(Number(rt.startYRatio)) ? Number(rt.startYRatio) : 0.35;
        const offsetY = Math.round(Number(rt.offsetY) || 0);
        const tw = activeBg.size.w;
        const th = activeBg.size.h;
        const originX = Math.round(previewSize.w / 2 - tw / 2);
        const originY = Math.round(previewSize.h * startYRatio) + offsetY;
        const px = (((originX % tw) + tw) % tw) * tileScale;
        const py = (((originY % th) + th) % th) * tileScale;
        return `${px}px ${py}px`;
    }, [activeBg, previewSize.w, previewSize.h, tileScale, previewBgConfig]);

    const previewBgStyle = {
        imageRendering: "pixelated",
        ...(previewBg === "default" && showTransparency && {
            backgroundImage: TRANSPARENCY_SVG_BG,
            backgroundSize: "16px 16px",
            backgroundRepeat: "repeat",
            backgroundColor: "transparent",
        }),
        ...(previewBg === "default" && !showTransparency && validWhiteBgTint && {
            backgroundColor: validWhiteBgTint,
        }),
    };

    const expressionsLocked = false;
    const actionsLocked = false;
    const headRotationLocked = false;

    // (Randomise)
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const randomise = () => {
        const nextAction = pick(ACTIONS.map((a) => a.value));
        setAction(nextAction);

        const allowedBody = nextAction === "lay" ? [2, 4] : nextAction === "sit" ? [0, 2, 4, 6] : [0, 1, 2, 3, 4, 5, 6, 7];
        const nextDir = pick(allowedBody);

        setDirection(nextDir);
        setHeadDirection(nextDir);

        const allowedHead = nextAction === "lay" ? [2, 4] : [mod8(nextDir - 1), mod8(nextDir), mod8(nextDir + 1)];
        setHeadDirection(pick(allowedHead));

        const gesturePool = GESTURES.filter((g) => g.value !== "eyb").map((g) => g.value);
        setGesture(pick(gesturePool));

        if (nextAction === "sit" || nextAction === "lay") {
            setCrr("");
        } else {
            const canRod = nextDir === 2 || nextDir === 4;
            const carryPool = CARRY_ITEMS.filter((c) => c.value !== "11" || canRod).map((c) => c.value);
            setCrr(pick(carryPool));
        }
    };

    // (Sprite Selection Styling)
    const outlineStyleFor = (on) =>
        on
            ? {
                position: "relative",
                outline: `${2 / zoom}px solid rgba(255, 214, 74, 0.85)`,
                outlineOffset: `${2 / zoom}px`,
                boxShadow: `0 0 0 ${1 / zoom}px rgba(0,0,0,0.75), inset 0 0 0 ${1 / zoom}px rgba(255,255,255,0.18)`,
                transform: "translateZ(0)",
            }
            : undefined

    const renderSpriteFrame = ({ src, selected, locked, flipped, flipY, scale = 1, crop = null, sourceSize, onLoad, children, inCropMode, layerOpacity = 1 }) => {
        const metrics = getSpriteFrameMetrics(sourceSize, scale, crop);

        const cropOffsetX = flipped ? (metrics.crop?.right || 0) * metrics.scale : metrics.offsetX;
        const cropOffsetY = flipY ? (metrics.crop?.bottom || 0) * metrics.scale : metrics.offsetY;

        const imgStyle = {
            display: "block",
            imageRendering: "pixelated",
            maxWidth: "none",
            maxHeight: "none",
            width: metrics.sourceW ? `${metrics.sourceW * metrics.scale}px` : undefined,
            height: metrics.sourceH ? `${metrics.sourceH * metrics.scale}px` : undefined,
            transform: [
                cropOffsetX || cropOffsetY ? `translate(${-cropOffsetX}px, ${-cropOffsetY}px)` : "",
                flipped && metrics.sourceW ? `translateX(${metrics.sourceW * metrics.scale}px) scaleX(-1)` : "",
                flipY && metrics.sourceH ? `translateY(${metrics.sourceH * metrics.scale}px) scaleY(-1)` : "",
            ].filter(Boolean).join(" ") || undefined,
            transformOrigin: "top left",
        };

        if (inCropMode && metrics.frameW && metrics.frameH) {
            const cropOuterStyle = {
                ...outlineStyleFor(selected && !locked),
                position: "relative",
                width: `${metrics.frameW}px`,
                height: `${metrics.frameH}px`,
                overflow: "visible",
                imageRendering: "pixelated",
            };
            return (
                <div style={cropOuterStyle}>
                    <img src={src} alt="" className="select-none" style={{ ...imgStyle, opacity: layerOpacity !== 1 ? layerOpacity : undefined }} draggable={false} onLoad={onLoad} />
                    {children}
                </div>
            );
        }

        const outerStyle = metrics.frameW && metrics.frameH
            ? {
                ...outlineStyleFor(selected && !locked),
                position: "relative",
                width: `${metrics.frameW}px`,
                height: `${metrics.frameH}px`,
                overflow: "hidden",
                imageRendering: "pixelated",
            }
            : {
                ...outlineStyleFor(selected && !locked),
                position: "relative",
                display: "inline-block",
                imageRendering: "pixelated",
            };

        const innerStyle = metrics.frameW && metrics.frameH
            ? {
                position: "relative",
                width: `${metrics.frameW}px`,
                height: `${metrics.frameH}px`,
                overflow: "hidden",
                imageRendering: "pixelated",
                opacity: layerOpacity !== 1 ? layerOpacity : undefined,
            }
            : {
                position: "relative",
                display: "inline-block",
                imageRendering: "pixelated",
                opacity: layerOpacity !== 1 ? layerOpacity : undefined,
            };

        return (
            <div style={outerStyle}>
                <div style={innerStyle}>
                    <img
                        src={src}
                        alt=""
                        className="select-none"
                        style={imgStyle}
                        draggable={false}
                        onLoad={onLoad}
                    />
                    {children}
                </div>
            </div>
        );
    };

    const updateSceneSprite = (id, patch) => {
        setSprites((prev) =>
            prev.map((s) => (s.id === id && s.type === 'habbo' ? { ...s, ...patch } : s))
        );
    };

    const patchSceneHabboSnapshot = (id, patch) => {
        setSprites((prev) =>
            prev.map((s) =>
                s.id === id && s.type === 'habbo'
                    ? { ...s, snap: { ...s.snap, ...patch } }
                    : s
            )
        );
    };

    const getSelectedSpriteContext = () => {
        if (activeFurniId) {
            const sprite = furniSpritesRef.current.find((x) => x.id === activeFurniId) || null;
            return sprite ? { type: "furni", id: sprite.id, sprite } : null;
        }

        if (activeCustomImageId) {
            const sprite = customImages.find((x) => x.id === activeCustomImageId) || null;
            return sprite ? { type: "customImage", id: sprite.id, sprite } : null;
        }

        if (activeTextId) {
            const sprite = textSprites.find((x) => x.id === activeTextId) || null;
            return sprite ? { type: "text", id: sprite.id, sprite } : null;
        }

        if (editingSceneHabboIdRef.current) {
            const sprite = sceneSprites.find((x) => x.id === editingSceneHabboIdRef.current) || null;
            return sprite ? { type: "sceneHabbo", id: sprite.id, sprite } : null;
        }

        if (activeSceneHabboId) {
            const sprite = sceneSprites.find((x) => x.id === activeSceneHabboId) || null;
            return sprite ? { type: "sceneHabbo", id: sprite.id, sprite } : null;
        }

        if (hasActiveHabbo && activeLayerId === "habbo:__active__") {
            return {
                type: "activeHabbo",
                id: "__active__",
                sprite: {
                    id: "__active__",
                    pos: spritePos,
                    scale: activeSpriteScale,
                    crop: activeSpriteCrop,
                    sourceSize: activeSpriteSourceSize,
                    snap: makeSnapshot({}),
                },
            };
        }

        return null;
    };

    // ─── Undo / Redo ───

    const getHistoryKey = () => `habboimager:history:${activeSceneId || "default"}`;

    const captureSnapshot = () => {
        const s = historyStateRef.current;
        if (!s) return null;
        return {
            spritePos: { ...s.spritePos },
            activeSpriteScale: s.activeSpriteScale,
            activeSpriteFlipX: s.activeSpriteFlipX,
            activeSpriteFlipY: s.activeSpriteFlipY,
            activeSpriteRotation: s.activeSpriteRotation,
            activeSpriteCrop: s.activeSpriteCrop ? { ...s.activeSpriteCrop } : null,
            sprites: JSON.parse(JSON.stringify(s.sprites || [])),
            bgLayerVis: { ...(s.bgLayerVis || {}) },
            moodlightVisible: !!s.moodlightVisible,
            // Omit assets/assetsColor - they are derived and regenerated automatically
            speechBubbles: (s.speechBubbles || []).map(({ assets, assetsColor, ...rest }) => ({ ...rest })),
            moodlightColor: s.moodlightColor,
            moodlightOpacity: s.moodlightOpacity,
            previewSize: s.previewSize ? { ...s.previewSize } : null,
            previewBg: s.previewBg || "default",
            customBgUrl: s.customBgUrl || null,
            customBgFit: s.customBgFit || "cover",
            showTransparency: !!s.showTransparency,
            gridTint: s.gridTint || "",
            whiteBgTint: s.whiteBgTint || "",
            roomTileBgColor: s.roomTileBgColor || "#000000",
            imageBorderEnabled: !!s.imageBorderEnabled,
            imageBorderColor: s.imageBorderColor || "#ffffff",
            imageBorderWidth: typeof s.imageBorderWidth === "number" ? s.imageBorderWidth : 10,
            midLayerOrder: (s.midLayerOrder || []).slice(),
        };
    };

    const pushHistory = () => {
        const snap = captureSnapshot();
        if (!snap) return;
        const h = historyRef.current;
        h.past = [...h.past, snap].slice(-30);
        h.future = [];
        setCanUndo(true);
        setCanRedo(false);
        try { localStorage.setItem(getHistoryKey(), JSON.stringify(h)); } catch {}
    };

    const applyHistorySnapshot = (snap) => {
        setSpritePos(snap.spritePos);
        setActiveSpriteScale(snap.activeSpriteScale);
        setActiveSpriteFlipX(snap.activeSpriteFlipX);
        setActiveSpriteFlipY(snap.activeSpriteFlipY);
        setActiveSpriteRotation(snap.activeSpriteRotation ?? 0);
        setActiveSpriteCrop(snap.activeSpriteCrop ?? null);
        setSprites(snap.sprites || []);
        setBgLayerVis(snap.bgLayerVis || {});
        setMoodlightVisible(!!snap.moodlightVisible);
        setSpeechBubbles(snap.speechBubbles ?? []);
        if (snap.moodlightColor != null) setMoodlightColor(snap.moodlightColor);
        if (snap.moodlightOpacity != null) setMoodlightOpacity(snap.moodlightOpacity);
        if (snap.previewSize?.w && snap.previewSize?.h) {
            setPreviewSize(snap.previewSize);
            setCanvasWInput(snap.previewSize.w);
            setCanvasHInput(snap.previewSize.h);
            saveScenesState((prev) => prev.map((s) => s.id === activeSceneId ? { ...s, imageWidth: snap.previewSize.w, imageHeight: snap.previewSize.h } : s));
        }
        if (snap.customBgFit != null) setCustomBgFit(snap.customBgFit);
        if (snap.customBgUrl !== undefined) setCustomBgUrl(snap.customBgUrl);
        if (snap.previewBg != null) setPreviewBg(snap.previewBg);
        if (snap.showTransparency != null) setShowTransparency(snap.showTransparency);
        if (snap.gridTint != null) setGridTint(snap.gridTint);
        if (snap.whiteBgTint != null) setWhiteBgTint(snap.whiteBgTint);
        if (snap.roomTileBgColor != null) setRoomTileBgColor(snap.roomTileBgColor);
        if (snap.imageBorderEnabled != null) setImageBorderEnabled(snap.imageBorderEnabled);
        if (snap.imageBorderColor != null) setImageBorderColor(snap.imageBorderColor);
        if (snap.imageBorderWidth != null) setImageBorderWidth(snap.imageBorderWidth);
        if (snap.midLayerOrder != null) setMidLayerOrder(snap.midLayerOrder);
    };

    const handleUndo = () => {
        const h = historyRef.current;
        if (!h.past.length) return;
        const snap = captureSnapshot();
        if (!snap) return;
        const prev = h.past[h.past.length - 1];
        h.past = h.past.slice(0, -1);
        h.future = [snap, ...h.future].slice(0, 30);
        setCanUndo(h.past.length > 0);
        setCanRedo(true);
        try { localStorage.setItem(getHistoryKey(), JSON.stringify(h)); } catch {}
        applyHistorySnapshot(prev);
    };

    const handleRedo = () => {
        const h = historyRef.current;
        if (!h.future.length) return;
        const snap = captureSnapshot();
        if (!snap) return;
        const next = h.future[0];
        h.future = h.future.slice(1);
        h.past = [...h.past, snap].slice(-30);
        setCanUndo(true);
        setCanRedo(h.future.length > 0);
        try { localStorage.setItem(getHistoryKey(), JSON.stringify(h)); } catch {}
        applyHistorySnapshot(next);
    };

    const setSelectedSpriteScale = (nextScale) => {
        const ctx = getSelectedSpriteContext();
        if (!ctx) return;
        pushHistory();

        const scaleValue = clampSpriteScale(nextScale);

        if (ctx.type === "furni") {
            updateFurniSprite(ctx.id, { scale: scaleValue });
        } else if (ctx.type === "customImage") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'customImage' ? { ...s, scale: scaleValue } : s));
        } else if (ctx.type === "sceneHabbo") {
            patchSceneHabboSnapshot(ctx.id, { scale: scaleValue });
            if (editingSceneHabboIdRef.current === ctx.id) setActiveSpriteScale(scaleValue);
        } else if (ctx.type === "activeHabbo") {
            setActiveSpriteScale(scaleValue);
        } else if (ctx.type === "text") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'text' ? { ...s, scale: scaleValue } : s));
        }

        setContextMenu(null);
    };

    const setSelectedSpriteCrop = (nextCrop) => {
        const ctx = getSelectedSpriteContext();
        if (!ctx) return;

        const sourceSize =
            ctx.type === "activeHabbo"
                ? activeSpriteSourceSize
                : ctx.type === "furni"
                    ? ctx.sprite.sourceSize
                    : ctx.type === "customImage"
                        ? ctx.sprite.sourceSize
                        : ctx.sprite.snap?.sourceSize || ctx.sprite.sourceSize;

        const safeCrop = sourceSize?.w && sourceSize?.h
            ? normaliseSpriteCrop(nextCrop, sourceSize.w, sourceSize.h)
            : null;

        if (ctx.type === "furni") {
            updateFurniSprite(ctx.id, { crop: safeCrop });
        } else if (ctx.type === "customImage") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'customImage' ? { ...s, crop: safeCrop } : s));
        } else if (ctx.type === "sceneHabbo") {
            patchSceneHabboSnapshot(ctx.id, { crop: safeCrop });
            if (editingSceneHabboIdRef.current === ctx.id) setActiveSpriteCrop(safeCrop);
        } else if (ctx.type === "activeHabbo") {
            setActiveSpriteCrop(safeCrop);
        }
    };


    const duplicateSelectedSprite = () => {
        const ctx = getSelectedSpriteContext();
        if (!ctx) return;
        pushHistory();

        if (ctx.type === "furni") {
            const source = ctx.sprite;
            const id = makeId();
            // Effects are already on the sprite - spread them into the duplicate
            setSprites((prev) => prev.concat([{ ...source, id, pos: { x: source.pos.x + 20, y: source.pos.y + 20 } }]));
            setActiveFurniId(id);
            setActiveLayerId(`furni:${id}`);
            setActiveSceneHabboId(null);
            setActiveCustomImageId(null);
            setSpriteSelected(false);
        } else if (ctx.type === "customImage") {
            const source = ctx.sprite;
            const id = makeId();
            setSprites((prev) => [...prev, { ...source, id, pos: { x: source.pos.x + 20, y: source.pos.y + 20 } }]);
            setActiveCustomImageId(id);
            setActiveLayerId(`custom_image:${id}`);
            setActiveFurniId(null);
            setActiveSceneHabboId(null);
            setSpriteSelected(false);
        } else if (ctx.type === "sceneHabbo") {
            const source = ctx.sprite;
            const id = makeId();
            const snap = { ...source.snap, id: makeId() };
            setSprites((prev) => prev.concat([{ ...source, type: 'habbo', id, snap, pos: { x: source.pos.x + 20, y: source.pos.y + 20 } }]));
            setActiveSceneHabboId(id);
            setActiveLayerId(`habbo:${id}`);
            setActiveFurniId(null);
            setSpriteSelected(false);
        } else if (ctx.type === "activeHabbo") {
            const id = makeId();
            const snap = makeSnapshot({});
            setSprites((prev) => prev.concat([{ type: 'habbo', id, snap, sourceSize: activeSpriteSourceSize, pos: { x: spritePos.x + 20, y: spritePos.y + 20 }, visible: true, locked: false, opacity: 100, layerName: '', effects: {} }]));
            setActiveSceneHabboId(id);
            setActiveLayerId(`habbo:${id}`);
            setActiveFurniId(null);
            setSpriteSelected(false);
        } else if (ctx.type === "text") {
            const source = ctx.sprite;
            const id = makeId();
            setSprites((prev) => [...prev, { ...source, id, pos: { x: source.pos.x + 20, y: source.pos.y + 20 } }]);
            setActiveTextId(id);
            setActiveLayerId(`text:${id}`);
            setActiveFurniId(null);
            setActiveSceneHabboId(null);
            setActiveCustomImageId(null);
            setSpriteSelected(false);
        }

        setContextMenu(null);
    };

    const toggleSelectedSpriteCropMode = () => {
        const ctx = getSelectedSpriteContext();
        if (!ctx) return;
        setCropMode((prev) => !prev);
        setContextMenu(null);
    };

    const setSelectedSpriteFlipX = () => {
        const ctx = getSelectedSpriteContext();
        if (!ctx) return;
        pushHistory();
        if (ctx.type === "furni") {
            updateFurniSprite(ctx.id, { flipped: !ctx.sprite.flipped });
        } else if (ctx.type === "customImage") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'customImage' ? { ...s, flipX: !s.flipX } : s));
        } else if (ctx.type === "text") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'text' ? { ...s, flipX: !s.flipX } : s));
        } else if (ctx.type === "sceneHabbo") {
            const next = !ctx.sprite.snap?.flipX;
            patchSceneHabboSnapshot(ctx.id, { flipX: next });
            if (editingSceneHabboIdRef.current === ctx.id) setActiveSpriteFlipX(next);
        } else if (ctx.type === "activeHabbo") {
            setActiveSpriteFlipX((prev) => !prev);
        }
        setContextMenu(null);
    };

    const setSelectedSpriteFlipY = () => {
        const ctx = getSelectedSpriteContext();
        if (!ctx) return;
        pushHistory();
        if (ctx.type === "furni") {
            updateFurniSprite(ctx.id, { flipY: !ctx.sprite.flipY });
        } else if (ctx.type === "customImage") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'customImage' ? { ...s, flipY: !s.flipY } : s));
        } else if (ctx.type === "text") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'text' ? { ...s, flipY: !s.flipY } : s));
        } else if (ctx.type === "sceneHabbo") {
            const next = !ctx.sprite.snap?.flipY;
            patchSceneHabboSnapshot(ctx.id, { flipY: next });
            if (editingSceneHabboIdRef.current === ctx.id) setActiveSpriteFlipY(next);
        } else if (ctx.type === "activeHabbo") {
            setActiveSpriteFlipY((prev) => !prev);
        }
        setContextMenu(null);
    };

    const setSelectedSpriteRotation = (deg) => {
        const ctx = getSelectedSpriteContext();
        if (!ctx) return;
        // Push history only once per interaction session (prevents stutter from arrow-key repeat / rapid onChange)
        if (!rotationHistoryPushedRef.current) {
            rotationHistoryPushedRef.current = true;
            pushHistory();
        }
        const rotation = Number(deg) || 0;
        if (ctx.type === "furni") {
            updateFurniSprite(ctx.id, { rotation });
        } else if (ctx.type === "customImage") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'customImage' ? { ...s, rotation } : s));
        } else if (ctx.type === "text") {
            setSprites((prev) => prev.map((s) => s.id === ctx.id && s.type === 'text' ? { ...s, rotation } : s));
        } else if (ctx.type === "sceneHabbo") {
            patchSceneHabboSnapshot(ctx.id, { rotation });
            if (editingSceneHabboIdRef.current === ctx.id) setActiveSpriteRotation(rotation);
        } else if (ctx.type === "activeHabbo") {
            setActiveSpriteRotation(rotation);
        }
        setContextMenu(null);
    };

    const selectedSpriteContext = getSelectedSpriteContext();
    const selectedSpriteScaleValue = clampSpriteScale(
        selectedSpriteContext?.type === "activeHabbo"
            ? activeSpriteScale
            : selectedSpriteContext?.type === "furni"
                ? selectedSpriteContext?.sprite?.scale || 1
                : selectedSpriteContext?.type === "customImage"
                    ? selectedSpriteContext?.sprite?.scale || 1
                    : selectedSpriteContext?.sprite?.snap?.scale || selectedSpriteContext?.sprite?.scale || 1,
    );
    const selectedSpriteFlipXActive =
        selectedSpriteContext?.type === "furni" ? !!selectedSpriteContext.sprite.flipped :
        selectedSpriteContext?.type === "customImage" ? !!selectedSpriteContext.sprite.flipX :
        selectedSpriteContext?.type === "text" ? !!selectedSpriteContext.sprite.flipX :
        selectedSpriteContext?.type === "sceneHabbo" ? !!selectedSpriteContext.sprite.snap?.flipX :
        selectedSpriteContext?.type === "activeHabbo" ? activeSpriteFlipX :
        false;
    const selectedSpriteFlipYActive =
        selectedSpriteContext?.type === "furni" ? !!selectedSpriteContext.sprite.flipY :
        selectedSpriteContext?.type === "customImage" ? !!selectedSpriteContext.sprite.flipY :
        selectedSpriteContext?.type === "text" ? !!selectedSpriteContext.sprite.flipY :
        selectedSpriteContext?.type === "sceneHabbo" ? !!selectedSpriteContext.sprite.snap?.flipY :
        selectedSpriteContext?.type === "activeHabbo" ? activeSpriteFlipY :
        false;
    const selectedSpriteRotation =
        selectedSpriteContext?.type === "furni" ? (selectedSpriteContext.sprite.rotation || 0) :
        selectedSpriteContext?.type === "customImage" ? (selectedSpriteContext.sprite.rotation || 0) :
        selectedSpriteContext?.type === "text" ? (selectedSpriteContext.sprite.rotation || 0) :
        selectedSpriteContext?.type === "sceneHabbo" ? (selectedSpriteContext.sprite.snap?.rotation || 0) :
        selectedSpriteContext?.type === "activeHabbo" ? activeSpriteRotation :
        0;

    const applyWeakSpriteSnap = (rawPos, exclude = null, options = {}) => {
        const SNAP_THRESHOLD = 6;
        const axisSnap = !!options?.axisSnap;

        if (!axisSnap) {
            setSnapGuides((prev) => (prev.x === null && prev.y === null ? prev : { x: null, y: null }));
            return rawPos;
        }

        let bestX = null;
        let bestY = null;

        const considerPoint = (point, pointExclude) => {
            if (!point) return;
            if (pointExclude) {
                if (exclude?.type === pointExclude.type && exclude?.id === pointExclude.id) return;
                if (exclude?.type === "activeHabbo" && pointExclude.type === "activeHabbo") return;
            }

            const dx = typeof point.x === "number" ? Math.abs(point.x - rawPos.x) : null;
            const dy = typeof point.y === "number" ? Math.abs(point.y - rawPos.y) : null;

            if (typeof point.x === "number" && dx <= SNAP_THRESHOLD && (!bestX || dx < bestX.diff)) {
                bestX = { value: point.x, diff: dx };
            }

            if (typeof point.y === "number" && dy <= SNAP_THRESHOLD && (!bestY || dy < bestY.diff)) {
                bestY = { value: point.y, diff: dy };
            }
        };

        if (hasActiveHabbo) {
            considerPoint({ x: spritePos.x, y: spritePos.y }, { type: "activeHabbo", id: "__active__" });
        }

        for (const sp of sceneSprites) {
            considerPoint(sp?.pos, { type: "sceneHabbo", id: sp?.id });
        }

        for (const sp of furniSpritesRef.current) {
            considerPoint(sp?.pos, { type: "furni", id: sp?.id });
        }

        const snapped = {
            x: bestX ? bestX.value : rawPos.x,
            y: bestY ? bestY.value : rawPos.y,
        };

        const nx = bestX ? bestX.value : null;
        const ny = bestY ? bestY.value : null;
        setSnapGuides((prev) => (prev.x === nx && prev.y === ny ? prev : { x: nx, y: ny }));

        return snapped;
    };

    const clearSnapGuides = () => {
        setSnapGuides((prev) => (prev.x === null && prev.y === null ? prev : { x: null, y: null }));
    };

    const handleWorkspaceContextMenu = (e) => {
        if (e.target?.closest?.("[data-capture-hide='1']")) return;
        e.preventDefault();
        e.stopPropagation();
        setContextMenu(null);
    };

    const beginCropDrag = (e, handle, spriteInfo) => {
        if (!cropMode) return;
        e.preventDefault();
        e.stopPropagation();
        pushHistory();

        const metrics = getSpriteFrameMetrics(spriteInfo.sourceSize, spriteInfo.scale, spriteInfo.crop);
        if (!metrics.sourceW || !metrics.sourceH) return;

        cropDragRef.current = {
            handle,
            flipped: !!spriteInfo.flipped,
            flipY: !!spriteInfo.flipY,
            rotation: spriteInfo.rotation || 0,
            startClientX: e.clientX,
            startClientY: e.clientY,
            startCrop: { ...metrics.crop },
            sourceW: metrics.sourceW,
            sourceH: metrics.sourceH,
            scale: metrics.scale,
        };

        const onMove = (moveEvent) => {
            const st = cropDragRef.current;
            if (!st) return;

            const rawDX = (moveEvent.clientX - st.startClientX) / (zoom || 1);
            const rawDY = (moveEvent.clientY - st.startClientY) / (zoom || 1);

            // Counter-rotate screen delta into sprite-local space
            const rotRad = -(st.rotation || 0) * Math.PI / 180;
            const cos = Math.cos(rotRad);
            const sin = Math.sin(rotRad);
            const rawDeltaX = (rawDX * cos - rawDY * sin) / (st.scale || 1);
            const rawDeltaY = (rawDX * sin + rawDY * cos) / (st.scale || 1);

            const effectiveDeltaX = st.flipped ? -Math.round(rawDeltaX) : Math.round(rawDeltaX);
            const effectiveDeltaY = st.flipY ? -Math.round(rawDeltaY) : Math.round(rawDeltaY);

            const nextCrop = { ...st.startCrop };

            const horizontalMap = st.flipped ? { w: "right", e: "left" } : { w: "left", e: "right" };
            const verticalMap = st.flipY ? { n: "bottom", s: "top" } : { n: "top", s: "bottom" };

            const applyH = (side, delta) => {
                if (side === "left") nextCrop.left = st.startCrop.left + delta;
                if (side === "right") nextCrop.right = st.startCrop.right - delta;
            };
            const applyV = (side, delta) => {
                if (side === "top") nextCrop.top = st.startCrop.top + delta;
                if (side === "bottom") nextCrop.bottom = st.startCrop.bottom - delta;
            };

            if (st.handle.includes("w")) applyH(horizontalMap.w, effectiveDeltaX);
            if (st.handle.includes("e")) applyH(horizontalMap.e, effectiveDeltaX);
            if (st.handle.includes("n")) applyV(verticalMap.n, effectiveDeltaY);
            if (st.handle.includes("s")) applyV(verticalMap.s, effectiveDeltaY);

            setSelectedSpriteCrop(nextCrop);
        };

        const onUp = () => {
            cropDragRef.current = null;
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
        };

        window.addEventListener("pointermove", onMove, { passive: false });
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
    };

    const renderCropHandles = (spriteInfo) => {
        if (!cropMode || !spriteInfo.selected || spriteInfo.locked) return null;
        const metrics = getSpriteFrameMetrics(spriteInfo.sourceSize, spriteInfo.scale, spriteInfo.crop);
        if (!metrics.frameW || !metrics.frameH) return null;

        const flipped = !!spriteInfo.flipped;
        const flipY = !!spriteInfo.flipY;
        const sc = metrics.scale;
        const frameW = metrics.frameW;
        const frameH = metrics.frameH;
        const fullW = metrics.sourceW * sc;

        const leftCrop = flipped ? metrics.crop.right : metrics.crop.left;
        const rightCrop = flipped ? metrics.crop.left : metrics.crop.right;
        const topCrop = flipY ? metrics.crop.bottom : metrics.crop.top;
        const bottomCrop = flipY ? metrics.crop.top : metrics.crop.bottom;

        const imgLeft = -(flipped ? metrics.crop.right : metrics.crop.left) * sc;
        const imgTop = -(flipY ? metrics.crop.bottom : metrics.crop.top) * sc;

        const hz = 10 / zoom;
        const hh = 5 / zoom;
        const handles = [
            { key: "nw", cursor: "nwse-resize", style: { left: -hh, top: -hh, width: hz, height: hz } },
            { key: "n",  cursor: "ns-resize",   style: { left: `calc(50% - ${hh}px)`, top: -hh, width: hz, height: hz } },
            { key: "ne", cursor: "nesw-resize", style: { right: -hh, top: -hh, width: hz, height: hz } },
            { key: "e",  cursor: "ew-resize",   style: { right: -hh, top: `calc(50% - ${hh}px)`, width: hz, height: hz } },
            { key: "se", cursor: "nwse-resize", style: { right: -hh, bottom: -hh, width: hz, height: hz } },
            { key: "s",  cursor: "ns-resize",   style: { left: `calc(50% - ${hh}px)`, bottom: -hh, width: hz, height: hz } },
            { key: "sw", cursor: "nesw-resize", style: { left: -hh, bottom: -hh, width: hz, height: hz } },
            { key: "w",  cursor: "ew-resize",   style: { left: -hh, top: `calc(50% - ${hh}px)`, width: hz, height: hz } },
        ];

        const ghost = "rgba(0,0,0,0.45)";

        return (
            <>
                {topCrop > 0 && <div className="absolute pointer-events-none" style={{ left: imgLeft, top: imgTop, width: fullW, height: topCrop * sc, background: ghost }} />}
                {bottomCrop > 0 && <div className="absolute pointer-events-none" style={{ left: imgLeft, top: frameH, width: fullW, height: bottomCrop * sc, background: ghost }} />}
                {leftCrop > 0 && <div className="absolute pointer-events-none" style={{ left: imgLeft, top: 0, width: leftCrop * sc, height: frameH, background: ghost }} />}
                {rightCrop > 0 && <div className="absolute pointer-events-none" style={{ left: frameW, top: 0, width: rightCrop * sc, height: frameH, background: ghost }} />}
                {handles.map((h) => (
                    <button
                        key={h.key}
                        type="button"
                        className="absolute z-20 bg-yellow-300"
                        style={{ ...h.style, cursor: h.cursor, border: `${1 / zoom}px solid black` }}
                        onPointerDown={(e) => beginCropDrag(e, h.key, spriteInfo)}
                    />
                ))}
            </>
        );
    };

    // (Scene Sprite URL)
    const getUrlForSnap = (snap) => {
        if (!snap) return "";
        const custom = snap.useFrankHatForDefault || snap.frankify ? "frank_hat" : null;
        const fig = snap.figureString || getCachedFigureString(snap.habboName, snap.hotel) || null;
        const u = getHabboImageUrl({
            habbo: fig ? null : snap.habboName,
            figure: fig || null,
            hotel: snap.hotel,
            direction: snap.direction,
            head_direction: snap.headDirection,
            action: snap.action,
            gesture: snap.gesture,
            size: snap.size,
            shadow: snap.shadow,
            crr: snap.crr || null,
            custom,
        });
        const fixed = fig ? ensureHotelParam(u, snap.hotel) : u;
        return withCache(fixed);
    };

    // (Loaded Habbos / Looks)
    const loadedHabboItems = useMemo(() => {
        const active = scenes.find((s) => s?.id === activeSceneId) || null;
        const activeOwnerKey = normalizeOwnerKey(active?.ownerKey);

        const enabledSet = new Set((active?.enabledOwnerKeys || []).map(normalizeOwnerKey));
        const seen = new Set();

        return scenes
            .map((s) => {
                const ownerKeyRaw = s?.ownerKey || "";
                const ownerKeyNorm = normalizeOwnerKey(ownerKeyRaw);
                const parts = ownerKeyRaw.split(":");
                const h = (parts[0] || "COM").toUpperCase();
                const n = parts.slice(1).join(":") || "";

                return {
                    id: s.id,
                    ownerKey: ownerKeyRaw,
                    habboName: n,
                    hotel: h,
                    thumbUrl: s.ownerThumbUrl,
                    scene: s,
                    isOn: enabledSet.has(ownerKeyNorm) || ownerKeyNorm === activeOwnerKey,
                    ownerKeyNorm,
                };
            })
            .filter((x) => x.habboName)
            .filter((x) => {
                if (seen.has(x.ownerKeyNorm)) return false;
                seen.add(x.ownerKeyNorm);
                return true;
            });
    }, [scenes, activeSceneId]);

    // (Scene Tip)
    const sceneTipTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (sceneTipTimerRef.current) window.clearTimeout(sceneTipTimerRef.current);
        };
    }, []);

    // (Prevent Page Scroll)
    useEffect(() => {
        const el = previewRef.current;
        if (!el) return;

        const canScroll = (node, dy) => {
            const cs = getComputedStyle(node);
            const oy = cs.overflowY;
            if (oy !== "auto" && oy !== "scroll") return false;
            if (node.scrollHeight <= node.clientHeight) return false;

            if (dy > 0) return node.scrollTop + node.clientHeight < node.scrollHeight;
            if (dy < 0) return node.scrollTop > 0;
            return false;
        };

        const onWheel = (e) => {
            let node = e.target;
            while (node && node !== el) {
                if (node instanceof HTMLElement && canScroll(node, e.deltaY)) return;
                node = node.parentElement;
            }
            e.preventDefault();

            if (zoomScrollRef.current) {
                clearTimeout(zoomScrollRef.current);
            }

            const direction = e.deltaY > 0 ? -1 : 1;
            const zoomLevels = [1, 2, 4, 16];
            const currentIndex = zoomLevels.indexOf(zoom);
            const nextIndex = Math.max(0, Math.min(2, currentIndex + direction));
            const newZoom = zoomLevels[nextIndex];

            if (newZoom !== zoom) {
                changeZoom(newZoom);
            }
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [zoom]);

    // (Render)
    return (
        <div className="relative w-full select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
            <style>{`
    .habbo-scroll::-webkit-scrollbar { height: 16px; width: 16px; }
    .habbo-scroll::-webkit-scrollbar-track { background: #cfcfcf; border: 1px solid #000; }
    .habbo-scroll::-webkit-scrollbar-thumb { background: #8f8f8f; border: 1px solid #000; }
    .habbo-scroll::-webkit-scrollbar-thumb:hover { background: #7a7a7a; }

    .habbo-vert-range {
      writing-mode: bt-lr;
      -webkit-appearance: slider-vertical;
    }
    
    *, *::before, *::after {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-tap-highlight-color: transparent !important;
        outline: none !important;
    }

    :fullscreen, ::-webkit-full-screen, ::-moz-full-screen, :-ms-fullscreen {
        -webkit-user-select: none !important;
        user-select: none !important;
    }

    input, textarea, [contenteditable="true"] {
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
    }
  `}</style>

            <ContentBox theme="proimager" icon={resolvedConfig.icon} header={resolvedConfig.header} className={resolvedConfig.containerClassName}>

                <div className="flex flex-col border border-black/50">
                    <div
                        ref={workspaceRef}
                        className="relative flex flex-col overflow-hidden"
                        style={{ height: "800px", isolation: "isolate" }}
                    >

                        {/* ===== TOP TOOLBAR ===== */}
                        {!isCapturing && !uiHidden && (
                            <div className="relative flex items-center h-10 bg-offwhite border-b-2 border-black shrink-0 z-40" data-capture-hide="1">
                                {/* New */}
                                <button
                                    type="button"
                                    onClick={() => setShowNewImageModal(true)}
                                    className="flex items-center gap-1.5 px-3 h-full border-r border-black/20 hover:bg-gray-100 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap"
                                >
                                    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                                    New
                                </button>
                                {/* Open */}
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowOpenDropdown((v) => !v)}
                                        className={["flex items-center gap-1.5 px-3 h-10 border-r border-black/20 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap", showOpenDropdown ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                    >
                                        <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                                        Open
                                    </button>
                                    {showOpenDropdown && (
                                        <div className="absolute left-0 top-full mt-0 z-50 border border-black bg-offwhite shadow w-[280px] p-3" onPointerDown={(e) => e.stopPropagation()}>
                                            <PreviousScenesPanel
                                                scenes={scenes}
                                                activeSceneId={activeSceneId}
                                                onLoad={(s) => { loadScene(s); setShowOpenDropdown(false); }}
                                                onRemove={removeScene}
                                            />
                                        </div>
                                    )}
                                </div>
                                {/* Save */}
                                {(() => {
                                    const lastSaveAt = readLastForceSaveAt();
                                    const elapsed = forceSaveNowTs - lastSaveAt;
                                    const isCoolingDown = lastSaveAt > 0 && elapsed < FORCE_SAVE_COOLDOWN_MS;
                                    const secsRemaining = isCoolingDown ? Math.ceil((FORCE_SAVE_COOLDOWN_MS - elapsed) / 1000) : 0;
                                    const isDisabled = isCoolingDown || !!dbFlushInFlightRef.current;
                                    return (
                                        <button
                                            type="button"
                                            onClick={forceSaveNow}
                                            disabled={isDisabled}
                                            className={["flex items-center gap-1.5 px-3 h-full border-r border-black/20 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap", isDisabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"].join(" ")}
                                        >
                                            <svg viewBox="0 0 24 24" className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                                                <path d="M17 21v-8H7v8" />
                                                <path d="M7 3v5h8" />
                                            </svg>
                                            {isCoolingDown ? secsRemaining : "Save"}
                                        </button>
                                    );
                                })()}
                                {/* Export */}
                                <button
                                    type="button"
                                    onClick={openExportModal}
                                    className="flex items-center gap-1.5 px-3 h-full border-r border-black/20 hover:bg-gray-100 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap"
                                >
                                    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    Export
                                </button>
                                {/* Undo */}
                                <button type="button" onClick={handleUndo} disabled={!canUndo} title="Undo" className={["flex items-center justify-center w-9 h-full shrink-0", canUndo ? "hover:bg-gray-100" : "opacity-30 cursor-not-allowed"].join(" ")}>
                                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" /></svg>
                                </button>
                                {/* Redo */}
                                <button type="button" onClick={handleRedo} disabled={!canRedo} title="Redo" className={["flex items-center justify-center w-9 h-full shrink-0 border-r border-black/20", canRedo ? "hover:bg-gray-100" : "opacity-30 cursor-not-allowed"].join(" ")}>
                                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" /></svg>
                                </button>
                                {/* Background */}
                                <BgDropdown value={previewBg} onChange={changePreviewBg} bgConfig={previewBgConfig} toolbarMode bgLabel={previewBgConfig?.[previewBg]?.label || (previewBg === "custom_bg" ? "Custom" : previewBg === "default" ? "Background" : previewBg)} disabledKeys={tileMode ? new Set(Object.keys(previewBgConfig).filter((k) => !["grid", "grey_grid", "room_tile"].includes(k))) : (previewSize.w > 650 || previewSize.h > 500) ? new Set(Object.keys(previewBgConfig).filter((k) => !["default", "grid", "grey_grid", "room_tile"].includes(k))) : undefined} />
                                {/* Grey grid tint */}
                                {previewBg === "grey_grid" && (
                                    <div className="flex items-center gap-1 px-2 shrink-0 font-['Jersey_10'] text-xs">
                                        Tint:
                                        <div className="relative w-6 h-6 border border-black/30 overflow-hidden" title="Grid tint colour">
                                            <ColorPicker value={validGridTint || "#808080"} onChange={(e) => setGridTint(e.target.value)} onOpen={() => pushHistory()} className="absolute inset-0" style={{ backgroundColor: validGridTint || "#808080" }} title="Grid tint colour" />
                                        </div>
                                        {validGridTint && <button type="button" onClick={() => { pushHistory(); setGridTint(""); }} className="font-['Jersey_10'] text-xs opacity-60 hover:opacity-100" title="Remove tint">✕</button>}
                                    </div>
                                )}
                                {/* Room tile background colour */}
                                {previewBg === "room_tile" && (
                                    <div className="flex items-center gap-1 px-2 shrink-0 font-['Jersey_10'] text-xs">
                                        BG:
                                        <div className="relative w-6 h-6 border border-black/30 overflow-hidden" title="Tile background colour">
                                            <ColorPicker value={roomTileBgColor || "#000000"} onChange={(e) => setRoomTileBgColor(e.target.value)} onOpen={() => pushHistory()} className="absolute inset-0" style={{ backgroundColor: validRoomTileBgColorEarly }} title="Tile background colour" />
                                        </div>
                                    </div>
                                )}
                                {/* White background tint */}
                                {previewBg === "default" && !showTransparency && (
                                    <div className="flex items-center gap-1 px-2 shrink-0 font-['Jersey_10'] text-xs">
                                        Tint:
                                        <div className="relative w-6 h-6 border border-black/30 overflow-hidden" title="Background colour">
                                            <ColorPicker value={validWhiteBgTint || "#f5f5f5"} onChange={(e) => setWhiteBgTint(e.target.value)} onOpen={() => pushHistory()} className="absolute inset-0" style={{ backgroundColor: validWhiteBgTint || "#f5f5f5" }} title="Background colour" />
                                        </div>
                                        {validWhiteBgTint && <button type="button" onClick={() => { pushHistory(); setWhiteBgTint(""); }} className="font-['Jersey_10'] text-xs opacity-60 hover:opacity-100" title="Reset to white">✕</button>}
                                    </div>
                                )}
                                {/* Transparency toggle */}
                                {previewBg === "default" && (
                                    <button
                                        type="button"
                                        onClick={previewBg === "default" ? () => { pushHistory(); setShowTransparency((v) => !v); } : undefined}
                                        disabled={previewBg !== "default"}
                                        title={previewBg !== "default" ? "Transparency only available for default background" : showTransparency ? "Switch to solid background" : "Switch to transparent background"}
                                        className={["flex items-center justify-center w-9 h-full shrink-0", previewBg !== "default" ? "opacity-30 cursor-not-allowed" : showTransparency ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                    >
                                        <svg viewBox="0 0 16 16" className="w-4 h-4 border border-black/20" aria-hidden="true"><rect width="8" height="8" fill="#ccc" /><rect x="8" y="8" width="8" height="8" fill="#ccc" /><rect x="8" width="8" height="8" fill="#fff" /><rect y="8" width="8" height="8" fill="#fff" /></svg>
                                    </button>
                                )}
                                {/* Image Border */}
                                <div className="relative shrink-0">
                                    <button
                                        type="button"
                                        title="Image Border"
                                        onClick={() => setShowImageBorderPanel((v) => !v)}
                                        className={["flex items-center gap-1.5 px-3 h-10 border-l border-black/20 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap", showImageBorderPanel ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                    >
                                        <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="1" /></svg>
                                        Border
                                    </button>
                                    {showImageBorderPanel && !isCapturing && (
                                        <div className="absolute left-0 top-full mt-0 z-50 border border-black bg-offwhite shadow w-52" data-capture-hide="1">
                                            <div className="flex items-center justify-between px-3 py-2 border-b border-black/20">
                                                <span className="font-['Jersey_10'] uppercase text-sm tracking-widest">Image Border</span>
                                                <button type="button" onClick={() => setShowImageBorderPanel(false)} className="text-xs opacity-50 hover:opacity-100 leading-none">✕</button>
                                            </div>
                                            <div className="p-3 flex flex-col gap-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-['Jersey_10'] uppercase text-sm opacity-60">Enabled</span>
                                                    <button type="button" onClick={() => { pushHistory(); setImageBorderEnabled((v) => !v); }} className={["relative w-10 h-5 border border-black transition-colors duration-150", imageBorderEnabled ? "bg-gold" : "bg-gray-300"].join(" ")} title={imageBorderEnabled ? "Turn off border" : "Turn on border"}>
                                                        <span className={["absolute top-[2px] w-4 h-4 border border-black bg-offwhite transition-all duration-150", imageBorderEnabled ? "left-[calc(100%-18px)]" : "left-[2px]"].join(" ")} />
                                                    </button>
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <label className="font-['Jersey_10'] uppercase text-sm opacity-60">Colour</label>
                                                    <ColorPicker value={imageBorderColor} onChange={(e) => setImageBorderColor(e.target.value)} onOpen={() => pushHistory()} className="w-full h-8 border border-black cursor-pointer" />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <label className="font-['Jersey_10'] uppercase text-sm opacity-60">Width - {imageBorderWidth}px</label>
                                                    <input type="range" min={1} max={100} value={imageBorderWidth} onPointerDown={() => pushHistory()} onChange={(e) => setImageBorderWidth(Number(e.target.value))} className="w-full accent-gold" />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* Custom BG upload */}
                                {!resolvedConfig.disableUpload && (
                                <div className="relative shrink-0">
                                    <button
                                        type="button"
                                        title="Upload custom background"
                                        onClick={() => bgUploadInputRef.current?.click()}
                                        className={["flex items-center gap-1.5 px-3 h-10 font-['Jersey_10'] border-l border-black/20 uppercase text-sm tracking-widest whitespace-nowrap", previewBg === "custom_bg" ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                    >
                                        <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M3 15l5-3 4 3 3-2 6 2" /></svg>
                                        Custom BG
                                    </button>
                                    {previewBg === "custom_bg" && (
                                        <button type="button" title="Remove custom background" onClick={removeCustomBg} className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 z-10 leading-none text-xs">×</button>
                                    )}
                                    <input ref={bgUploadInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { handleBgFileSelect(e.target.files?.[0]); e.target.value = ""; }} />
                                </div>
                                )}
                                {/* Moodlight */}
                                {(() => {
                                    const mlOn = moodlightVisible;
                                    return (
                                        <div className="relative shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => setShowMoodlight((v) => !v)}
                                                title="Moodlight"
                                                className={["flex items-center gap-1.5 px-3 h-10 border-l border-black/20 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap", !mlOn && !showMoodlight ? "hover:bg-gray-100" : "", showMoodlight && !mlOn ? "bg-gold" : ""].join(" ")}
                                            >
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" /><path d="M9 18h6" /><path d="M10 22h4" /></svg>
                                                Moodlight
                                            </button>
                                            {showMoodlight && !isCapturing && (
                                                <div className="absolute left-0 top-full mt-0 z-50 border border-black bg-offwhite shadow w-52" data-capture-hide="1">
                                                    <div className="flex items-center justify-between px-3 py-2 border-b border-black/20">
                                                        <span className="font-['Jersey_10'] uppercase text-sm tracking-widest">Moodlight</span>
                                                        <button type="button" onClick={() => setShowMoodlight(false)} className="text-xs opacity-50 hover:opacity-100 leading-none">✕</button>
                                                    </div>
                                                    <div className="p-3 flex flex-col gap-3">
                                                        <div className="flex items-center justify-between">
                                                            <span className="font-['Jersey_10'] uppercase text-sm opacity-60">Moodlight</span>
                                                            <button type="button" onClick={() => { pushHistory(); setMoodlightVisible((v) => !v); }} className={["relative w-10 h-5 border border-black transition-colors duration-150", mlOn ? "bg-gold" : "bg-gray-300"].join(" ")} title={mlOn ? "Turn off moodlight" : "Turn on moodlight"}>
                                                                <span className={["absolute top-[2px] w-4 h-4 border border-black bg-offwhite transition-all duration-150", mlOn ? "left-[calc(100%-18px)]" : "left-[2px]"].join(" ")} />
                                                            </button>
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <label className="font-['Jersey_10'] uppercase text-sm opacity-60">Colour</label>
                                                            <ColorPicker value={moodlightColor} onChange={(e) => setMoodlightColor(e.target.value)} onOpen={() => pushHistory()} className="w-full h-8 border border-black cursor-pointer" />
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <label className="font-['Jersey_10'] uppercase text-sm opacity-60">Opacity - {Math.round((moodlightOpacity / 0.8) * 100)}%</label>
                                                            <input type="range" min={0} max={100} value={Math.round((moodlightOpacity / 0.8) * 100)} onPointerDown={() => pushHistory()} onChange={(e) => setMoodlightOpacity((Number(e.target.value) / 100) * 0.8)} className="w-full accent-gold" />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                {/* Canvas resize */}
                                <div className="relative shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => { if (!tileMode) { setShowCanvasSizePanel((s) => !s); setShowImageUploadPanel(false); } }}
                                        title={tileMode ? "Canvas size is fixed in tile mode" : "Resize canvas"}
                                        disabled={tileMode}
                                        className={["flex items-center gap-1.5 px-3 h-10 border-x border-black/20 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap", tileMode ? "opacity-40 cursor-not-allowed" : showCanvasSizePanel ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                    >
                                        <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                                        Canvas
                                    </button>
                                    {showCanvasSizePanel && (
                                        <div className="absolute left-0 top-full mt-0 z-50 border border-black bg-offwhite/95 shadow w-[230px]" onPointerDown={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-between px-3 py-2 border-b border-black/10">
                                                <span className="font-['Jersey_10'] uppercase text-sm tracking-widest opacity-80">Canvas Size</span>
                                                <button type="button" onClick={() => setShowCanvasSizePanel(false)} className="text-xs opacity-40 hover:opacity-100 leading-none">✕</button>
                                            </div>
                                            <div className="p-3 flex flex-col gap-3">
                                                <div className="flex items-end gap-2">
                                                    <div className="flex-1">
                                                        <div className="font-['Jersey_10'] text-sm uppercase text-gray-500 mb-1 tracking-widest">Width (px)</div>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            value={canvasWInput}
                                                            onChange={(e) => setCanvasWInput(e.target.value)}
                                                            onBlur={() => setCanvasWInput((v) => { const n = parseInt(v); return isNaN(n) || n < 1 ? 1 : Math.min(2000, n); })}
                                                            className="w-full px-2 py-1.5 border border-black bg-white font-['Jersey_10'] text-lg tracking-widest text-center focus:outline-none focus:bg-yellow-50"
                                                        />
                                                    </div>
                                                    <span className="font-['Jersey_10'] text-base opacity-40 mb-1">×</span>
                                                    <div className="flex-1">
                                                        <div className="font-['Jersey_10'] text-sm uppercase text-gray-500 mb-1 tracking-widest">Height (px)</div>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            value={canvasHInput}
                                                            onChange={(e) => setCanvasHInput(e.target.value)}
                                                            onBlur={() => setCanvasHInput((v) => { const n = parseInt(v); return isNaN(n) || n < 1 ? 1 : Math.min(2000, n); })}
                                                            className="w-full px-2 py-1.5 border border-black bg-white font-['Jersey_10'] text-lg tracking-widest text-center focus:outline-none focus:bg-yellow-50"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="font-['Jersey_10'] text-sm uppercase text-gray-400">Max 2000 × 2000 px</div>
                                                <button type="button" onClick={() => applyCanvasResize(canvasWInput, canvasHInput)} className="h-9 border border-black bg-coal text-offwhite font-['Jersey_10'] tracking-widest uppercase text-sm flex items-center justify-center hover:bg-black transition-colors">Apply</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* Spacer */}
                                <div className="flex-1" />
                                {/* Fullscreen */}
                                <button
                                    type="button"
                                    onClick={toggleFullscreen}
                                    className={["flex items-center gap-1.5 px-3 h-full border-l border-black/20 font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap", isFullscreen ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                    title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                                    data-capture-hide="1"
                                >
                                    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8v-3a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" /></svg>
                                    Fullscreen
                                </button>
                            </div>
                        )}

                        {/* ===== MAIN AREA ===== */}
                        <div className="flex flex-1 min-h-0">

                        {/* ===== LEFT PANEL ===== */}
                        {!isCapturing && (
                            <div className="relative z-[30] flex flex-col bg-offwhite border-r-2 border-black shrink-0 w-[148px] px-2" data-capture-hide="1">
                                {/* Row 1: Add Habbo + Furni */}
                                <div className="gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest text-coal/50 mt-2">Habbo Items</div>
                                <div className="relative flex border border-black/10">
                                    <div className="flex-1 border-r border-black/10">
                                        <button
                                            type="button"
                                            onClick={() => setShowAddHabboPanel((v) => {
                                                const next = !v;
                                                if (next) window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: addHabboDropdownIdRef.current } }));
                                                return next;
                                            })}
                                            className={["w-full flex flex-col items-center justify-center py-3 gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest", showAddHabboPanel ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                            title="Add Habbo"
                                        >
                                            <img src="/img/icons/icon_61.png" alt="" className="h-[18px] w-[18px]" style={{ imageRendering: "pixelated" }} />
                                            Add Habbo
                                        </button>
                                    </div>
                                    <div className="flex-1">
                                        <FurniDropdown
                                            disabled={false}
                                            items={furniItems}
                                            addedItems={userFurni}
                                            onRemoveAdded={handleRemoveUserFurni}
                                            count={furniSprites.length}
                                            onAdd={addFurniToActiveScene}
                                            onOpenSearch={() => setShowFurniSearch(true)}
                                            searchCompleteRef={furniSearchCompleteRef}
                                            openRight
                                            toolbarMode
                                        />
                                    </div>
                                    {showAddHabboPanel && (
                                        <div className="absolute top-0 z-50 border border-black bg-offwhite/90 shadow w-[370px] p-4" style={{ left: '145px' }} onPointerDown={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="font-['Jersey_10'] uppercase text-sm tracking-widest">Add Habbo</span>
                                                <button type="button" onClick={() => setShowAddHabboPanel(false)} className="text-xs opacity-40 hover:opacity-100 leading-none">✕</button>
                                            </div>
                                            <div className="grid grid-cols-[2fr_1fr] gap-3 mb-3">
                                                <div>
                                                    <label className="block text-sm font-['Jersey_10'] uppercase mb-1">Habbo Name</label>
                                                    <div className="flex gap-2">
                                                        <input type="text" value={habboNameInput} onChange={(e) => setHabboNameInput(e.target.value)} onKeyDown={handleKeyDown} className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm placeholder:text-gray-500" placeholder="Username..." />
                                                        <button onClick={loadHabbo} className="bg-maroon text-white px-4 py-2 font-['Jersey_10'] uppercase text-sm tracking-widest hover:opacity-90 whitespace-nowrap" style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)" }}>Add</button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-['Jersey_10'] uppercase mb-1">Hotel</label>
                                                    <select value={hotel} onChange={(e) => setHotel(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm uppercase">
                                                        <option value="COM">COM</option>
                                                        <option value="BR">BR</option>
                                                        <option value="ES">ES</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 mt-3">
                                                <div>
                                                    <label className="block text-sm font-['Jersey_10'] uppercase mb-1">Size</label>
                                                    <select value={newHabboSize} onChange={(e) => setNewHabboSize(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                                                        <option value="a">Small</option>
                                                        <option value="b">Normal</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* Divider */}
                                <div className="border-t border-black/20 my-1 border-dashed" />
                                <div className="gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest text-coal/50">Text / Speech</div>
                                <div className="relative flex border border-black/10">
                                    <div className="flex-1 border-r border-black/10">
                                        <SpeechBubbleDropdown
                                            disabled={false}
                                            sceneHabbos={sceneSprites.map((sp) => ({ id: sp.id, habboName: ((sp?.snap?.habboName || "Habbo") + "").trim(), hotel: (sp?.snap?.hotel || "COM").toUpperCase(), thumbUrl: getLayerThumbForSnap(sp.snap) }))}
                                            bubbles={speechBubbles}
                                            onAddBubble={(sceneHabboId, t, c, isShout) => addSpeechBubble(sceneHabboId, t, c, isShout)}
                                            onRemoveBubble={(id) => removeSpeechBubble(id)}
                                            onReorder={(fromId, toId) => reorderSpeechBubbles(fromId, toId)}
                                            onSelectHabbo={(spId) => {
                                                setActiveSceneHabboId(spId);
                                                setActiveLayerId(`habbo:${spId}`);
                                                setActiveFurniId(null);
                                                setSpriteSelected(false);
                                                setCropMode(false);
                                                setContextMenu(null);
                                            }}
                                            openRight
                                            openRightOffset="145px"
                                            toolbarMode
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <TextDropdown
                                            disabled={false}
                                            activeText={getActiveTextSprite()}
                                            onAdd={addTextSprite}
                                            onUpdate={updateTextSprite}
                                            onCommit={pushHistory}
                                            openRight
                                            toolbarMode
                                        />
                                    </div>
                                </div>
                                {/* Divider */}
                                <div className="border-t border-black/20 my-1 border-dashed" />
                                {/* Custom Asset Upload */}
                                <div className="border-b-2 border-black/20">
                                    <div className="gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest text-coal/50">Extra Assets</div>
                                    <div className="flex border border-black/10">
                                        <button
                                            type="button"
                                            title="Assets library"
                                            onClick={() => setShowAssetsModal(true)}
                                            className="flex-1 flex flex-col items-center justify-center py-3 gap-1 hover:bg-gray-100 font-['Jersey_10'] uppercase text-xs tracking-widest border-r border-black/10"
                                        >
                                            <img src="/img/icons/icon_47.png" alt="" className="h-[18px] w-[18px]" style={{ imageRendering: "pixelated" }} />
                                            Assets
                                        </button>

                                        {!resolvedConfig.disableUpload && <div className="relative flex-1 flex flex-col">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowImageUploadPanel((s) => {
                                                    const next = !s;
                                                    if (next) {
                                                        window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: auxDropdownIdRef.current } }));
                                                        setShowAssetsModal(false);
                                                    }
                                                    return next;
                                                });
                                                setShowCanvasSizePanel(false);
                                            }}
                                            title="Custom image upload"
                                            className={["flex-1 flex flex-col items-center justify-center py-3 gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest", showImageUploadPanel ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                        >
                                            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 15l5-5 4 4 3-3 6 6" /><circle cx="8.5" cy="8.5" r="1.5" /></svg>
                                            Custom
                                        </button>

                                            {showImageUploadPanel && (
                                                <div className="absolute z-50 border border-black bg-offwhite/90 shadow w-[260px] left-[81px] top-0" onPointerDown={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-between px-3 py-2 border-b border-black/10">
                                                    <span className="font-['Jersey_10'] uppercase text-sm tracking-widest opacity-80">Custom Image</span>
                                                    <button type="button" onClick={() => setShowImageUploadPanel(false)} className="text-xs opacity-40 hover:opacity-100 leading-none">✕</button>
                                                </div>
                                                <div className="p-3 flex flex-col gap-3">
                                                    <label htmlFor="canvas-image-upload" className="border-2 border-dashed border-black/30 bg-gray-50 hover:bg-gray-100 cursor-pointer flex flex-col items-center justify-center py-4 gap-2 transition-colors">
                                                        {uploadedImagePreview ? (
                                                            <img src={uploadedImagePreview} alt="Preview" className="max-h-24 max-w-full object-contain" style={{ imageRendering: "pixelated" }} />
                                                        ) : (
                                                            <>
                                                                <svg viewBox="0 0 24 24" className="w-8 h-8 opacity-30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                                                                <span className="font-['Jersey_10'] text-sm uppercase opacity-40 text-center">Click to select image</span>
                                                            </>
                                                        )}
                                                        <input id="canvas-image-upload" type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; if (uploadedImagePreview) URL.revokeObjectURL(uploadedImagePreview); setUploadedImageFile(file); setUploadedImagePreview(URL.createObjectURL(file)); }} />
                                                    </label>
                                                    {uploadedImageFile && <div className="font-['Jersey_10'] text-sm uppercase text-gray-500 truncate">{uploadedImageFile.name}</div>}
                                                    <button type="button" onClick={uploadCustomImage} disabled={!uploadedImageFile || isUploadingCustomImage} className={["h-9 border font-['Jersey_10'] tracking-widest uppercase text-sm flex items-center justify-center transition-colors", uploadedImageFile && !isUploadingCustomImage ? "border-black bg-maroon text-white hover:opacity-90 cursor-pointer" : "border-black/30 bg-gray-200 text-gray-400 cursor-not-allowed"].join(" ")}>{isUploadingCustomImage ? "Uploading..." : "Add to Scene"}</button>
                                                </div>
                                            </div>
                                        )}
                                        </div>}
                                    </div>

                                    {/* Row 2: Habbo Font + Furni Colourer */}
                                    <div className="relative flex border border-black/10 border-t-0">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowHabboFontPanel(s => {
                                                    const next = !s;
                                                    if (next) {
                                                        window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: auxDropdownIdRef.current } }));
                                                        setShowFurniColorerPanel(false);
                                                    }
                                                    return next;
                                                });
                                                setShowCanvasSizePanel(false);
                                            }}
                                            title="Generate Habbo font image"
                                            className={["flex-1 flex flex-col items-center justify-center py-3 gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest border-r border-black/10", showHabboFontPanel ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                        >
                                            <img src="/img/icons/icon_101.png" alt="" className="h-[18px] w-[18px]" style={{ imageRendering: "pixelated" }} />
                                            Habbo Font
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowFurniColorerPanel(s => {
                                                    const next = !s;
                                                    if (next) {
                                                        window.dispatchEvent(new CustomEvent("habboimager:dropdown", { detail: { id: auxDropdownIdRef.current } }));
                                                        setShowHabboFontPanel(false);
                                                    }
                                                    return next;
                                                });
                                                setShowCanvasSizePanel(false);
                                            }}
                                            title="Recolour furniture and add to scene"
                                            className={["flex-1 flex flex-col items-center justify-center py-3 gap-1 font-['Jersey_10'] uppercase text-xs tracking-widest", showFurniColorerPanel ? "bg-gold" : "hover:bg-gray-100"].join(" ")}
                                        >
                                            <img src="/img/icons/icon_299.png" alt="" className="h-[18px] w-[18px]" style={{ imageRendering: "pixelated" }} />
                                            Furni Recolourer
                                        </button>

                                        {/* Habbo Font Panel */}
                                        {showHabboFontPanel && (
                                            <div className="absolute z-50 border border-black bg-offwhite/90 shadow w-[280px] top-0" style={{ left: '145px' }} onPointerDown={e => e.stopPropagation()}>
                                                <div className="flex items-center justify-between px-3 py-2 border-b border-black/10">
                                                    <span className="font-['Jersey_10'] uppercase text-sm tracking-widest opacity-80">Habbo Font</span>
                                                    <button type="button" onClick={() => setShowHabboFontPanel(false)} className="text-xs opacity-40 hover:opacity-100 leading-none">✕</button>
                                                </div>
                                                <div className="p-3 flex flex-col gap-3">
                                                    <div className="flex flex-col gap-1">
                                                        <label className="font-['Jersey_10'] uppercase text-xs tracking-widest text-coal/60">Text</label>
                                                        <input
                                                            type="text"
                                                            value={habboFontText}
                                                            onChange={e => { setHabboFontText(e.target.value); setHabboFontPreviewUrl(null); }}
                                                            onKeyDown={e => e.key === 'Enter' && handleGenerateHabboFont()}
                                                            maxLength={60}
                                                            placeholder="A–Z and spaces..."
                                                            className="w-full px-2 py-1.5 border border-black/30 bg-white font-['Jersey_10'] text-sm focus:outline-none focus:border-black"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="font-['Jersey_10'] uppercase text-xs tracking-widest text-coal/60">Font</label>
                                                        <select
                                                            value={habboFontFolder}
                                                            onChange={e => { setHabboFontFolder(e.target.value); setHabboFontPreviewUrl(null); }}
                                                            className="w-full px-2 py-1.5 border border-black/30 bg-white font-['Jersey_10'] text-sm focus:outline-none"
                                                        >
                                                            {FONTS.map(f => (
                                                                <option key={f.folder} value={f.folder}>{f.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleGenerateHabboFont}
                                                        disabled={!habboFontText.trim() || habboFontGenerating}
                                                        className={["h-9 border font-['Jersey_10'] tracking-widest uppercase text-sm flex items-center justify-center", habboFontText.trim() && !habboFontGenerating ? "border-black bg-coal text-white hover:opacity-90 cursor-pointer" : "border-black/30 bg-gray-200 text-gray-400 cursor-not-allowed"].join(" ")}
                                                    >{habboFontGenerating ? "Generating…" : "Preview"}</button>
                                                    {habboFontPreviewUrl && (
                                                        <div className="border border-black/20 bg-coal_dark flex items-center justify-center p-2 min-h-[48px] overflow-x-auto habbo-scroll">
                                                            <img src={habboFontPreviewUrl} alt="Font preview" style={{ imageRendering: 'pixelated', display: 'block', maxHeight: 80 }} />
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={handleAddHabboFontToScene}
                                                        disabled={!habboFontPreviewUrl}
                                                        className={["h-9 border font-['Jersey_10'] tracking-widest uppercase text-sm flex items-center justify-center", habboFontPreviewUrl ? "border-black bg-maroon text-white hover:opacity-90 cursor-pointer" : "border-black/30 bg-gray-200 text-gray-400 cursor-not-allowed"].join(" ")}
                                                    >Add to Scene</button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Furni Recolourer Panel */}
                                        {showFurniColorerPanel && (
                                            <div className="absolute z-50 border border-black bg-offwhite/90 shadow w-[310px] top-0" style={{ left: '145px' }} onPointerDown={e => e.stopPropagation()}>
                                                <div className="flex items-center justify-between px-3 py-2 border-b border-black/10">
                                                    <span className="font-['Jersey_10'] uppercase text-sm tracking-widest opacity-80">Furni Colourer</span>
                                                    <button type="button" onClick={() => setShowFurniColorerPanel(false)} className="text-xs opacity-40 hover:opacity-100 leading-none">✕</button>
                                                </div>
                                                <div className="p-3 flex flex-col gap-3">
                                                    <div className="flex gap-2 items-end">
                                                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                                                            <label className="font-['Jersey_10'] uppercase text-xs tracking-widest text-coal/60">Group</label>
                                                            <select
                                                                value={furniColorerGroup}
                                                                onChange={e => setFurniColorerGroup(e.target.value)}
                                                                disabled={furniColorerApplying}
                                                                className="w-full px-2 py-1.5 border border-black/30 bg-white font-['Jersey_10'] text-xs focus:outline-none focus:border-black disabled:opacity-50"
                                                            >
                                                                {Object.keys(FURNI_COLOURER_GROUPS).map(g => (
                                                                    <option key={g} value={g}>{g}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <label className="font-['Jersey_10'] uppercase text-xs tracking-widest text-coal/60">Colour</label>
                                                            <ColorPicker
                                                                value={furniColorerHex}
                                                                onChange={e => setFurniColorerHex(e.target.value)}
                                                                className="w-8 h-8 border border-black/30 cursor-pointer"
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={handleFurniColorerApply}
                                                            disabled={furniColorerApplying}
                                                            className={["px-3 h-8 border font-['Jersey_10'] uppercase text-xs tracking-widest shrink-0 self-end", furniColorerApplying ? "border-black/30 bg-gray-200 text-gray-400 cursor-not-allowed" : "border-black bg-coal text-white hover:opacity-90 cursor-pointer"].join(" ")}
                                                        >{furniColorerApplying ? "…" : "Apply"}</button>
                                                    </div>
                                                    <div className="max-h-[320px] overflow-y-auto habbo-scroll-v">
                                                        <div className="grid grid-cols-3 gap-1">
                                                            {FURNI_COLOURER_GROUPS[furniColorerGroup].map(entry => {
                                                                const { name, direction } = normaliseFurniEntry(entry);
                                                                const isLoading = !!furniColorerLoading[name];
                                                                const previewUrl = furniColorerPreviews[name];
                                                                return (
                                                                    <button
                                                                        key={name}
                                                                        type="button"
                                                                        disabled={isLoading || !previewUrl}
                                                                        onClick={() => handleAddFurniColorToScene(name, direction)}
                                                                        className="relative bg-coal_dark border border-black/20 flex items-center justify-center h-20 hover:border-gold disabled:cursor-not-allowed group"
                                                                        title={`Add ${name} to scene`}
                                                                    >
                                                                        {previewUrl ? (
                                                                            <img src={previewUrl} alt={name} className="max-w-full max-h-full" style={{ imageRendering: 'pixelated' }} />
                                                                        ) : (
                                                                            <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                                                                        )}
                                                                        {isLoading && (
                                                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                                            </div>
                                                                        )}
                                                                        <div className="absolute inset-0 bg-gold/20 opacity-0 group-hover:opacity-100 pointer-events-none" />
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="font-['Jersey_10'] text-xs text-coal/40 uppercase text-center">Click a furni to add it to the scene</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="border-t border-black/20 my-1 border-dashed" />

                                    <button
                                        type="button"
                                        onClick={refreshCurrent}
                                        className="w-[130px] h-12 border border-black bg-gray-200 text-black hover:bg-sky-800 hover:text-white font-['Jersey_10'] uppercase text-sm tracking-widest whitespace-nowrap"
                                        style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)" }}
                                        title="Refresh String"
                                    >
                                        Refresh
                                        <br />
                                        Current String
                                    </button>

                                </div>

                            </div>
                        )}

                        {/* ===== CANVAS AREA ===== */}
                        <div
                            className="flex-1 relative bg-coal overflow-hidden flex items-center justify-center"
                            onContextMenu={(e) => {
                                if (!e.target?.closest?.('[data-preview-capture="1"]')) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setContextMenu(null);
                                }
                            }}
                            onPointerDown={(e) => {
                                if (e.target?.closest?.('[data-capture-hide="1"]')) return;
                                if (e.target?.closest?.('[data-preview-capture="1"]')) return;
                                if (isCapturing || sceneLoading) return;
                                workspacePanRef.current = {
                                    startClientX: e.clientX,
                                    startClientY: e.clientY,
                                    startOffset: { ...workspaceOffset },
                                };
                                window.addEventListener("pointermove", onWorkspacePanMove, { passive: false });
                                window.addEventListener("pointerup", onWorkspacePanUp);
                                window.addEventListener("pointercancel", onWorkspacePanUp);
                            }}
                        >
                            {/* ── Tile preview background ── */}
                            {showTilePreview && tilePreviewUrl && (
                                <div
                                    className="absolute inset-0 pointer-events-none"
                                    data-capture-hide="1"
                                    style={{
                                        backgroundImage: `url(${tilePreviewUrl})`,
                                        backgroundRepeat: "repeat",
                                        backgroundSize: `${previewSize.w * zoom}px ${previewSize.h * zoom}px`,
                                        backgroundPosition: `calc(50% + ${Math.round(workspaceOffset.x)}px) calc(50% + ${Math.round(workspaceOffset.y)}px)`,
                                        opacity: 0.65,
                                        imageRendering: "pixelated",
                                    }}
                                />
                            )}

                            {/* ── Add Effect button + Auto-save indicator (top-left row) ── */}
                            {!isCapturing && (
                                <div className="absolute top-2 left-2 z-10 flex items-center gap-2" data-capture-hide="1">
                                    {/* Add Effect */}
                                    {(() => {
                                        const canAddEffect = !!(activeLayerId && !layerById.get(activeLayerId)?.permaLock);
                                        return (
                                            <div className="relative" ref={(el) => { if (showAddEffectDropdown && el) { const onDown = (e) => { if (!el.contains(e.target)) setShowAddEffectDropdown(false); }; document.addEventListener('mousedown', onDown, { once: true }); } }}>
                                                <button
                                                    type="button"
                                                    disabled={!canAddEffect}
                                                    onClick={() => canAddEffect && setShowAddEffectDropdown((v) => !v)}
                                                    className={["flex items-center gap-1.5 px-2.5 py-1.5 border border-black/30 font-['Jersey_10'] uppercase text-xs tracking-widest transition-opacity", showAddEffectDropdown ? "bg-gold text-coal" : "bg-black/50 text-white", !canAddEffect ? "opacity-40 cursor-not-allowed" : "hover:bg-black/70 cursor-pointer"].join(" ")}
                                                    title="Add an effect to the selected layer"
                                                >
                                                    <svg viewBox="0 0 24 24" className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                                                    </svg>
                                                    Add Effect
                                                </button>
                                                {showAddEffectDropdown && canAddEffect && (
                                                    <div className="absolute top-full left-0 mt-1 bg-offwhite border border-black shadow-md z-10 min-w-[140px]">
                                                        <button
                                                            type="button"
                                                            onClick={() => { addLayerEffect(activeLayerId, 'outline'); setShowAddEffectDropdown(false); }}
                                                            className="w-full text-left px-3 py-2 font-['Jersey_10'] uppercase text-xs tracking-widest hover:bg-gray-200 flex items-center gap-2"
                                                        >
                                                            <svg viewBox="0 0 12 12" className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                                                                <rect x="1.5" y="1.5" width="9" height="9"/>
                                                                <rect x="3.5" y="3.5" width="5" height="5" strokeDasharray="1.5 1" opacity="0.45"/>
                                                            </svg>
                                                            Outline Sprite
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => { addLayerEffect(activeLayerId, 'water'); setShowAddEffectDropdown(false); }}
                                                            className="w-full text-left px-3 py-2 font-['Jersey_10'] uppercase text-xs tracking-widest hover:bg-gray-200 flex items-center gap-2"
                                                        >
                                                            <svg viewBox="0 0 12 12" className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                                                                <path d="M1 4.5 Q2.5 3 4 4.5 Q5.5 6 7 4.5 Q8.5 3 10 4.5"/>
                                                                <path d="M1 7.5 Q2.5 6 4 7.5 Q5.5 9 7 7.5 Q8.5 6 10 7.5"/>
                                                            </svg>
                                                            Water
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    {/* Edit Tile toggle - only shown for room_tile bg */}
                                    {previewBg === "room_tile" && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const exiting = roomTileEditMode;
                                                setRoomTileEditMode((v) => !v);
                                                if (exiting && activeSceneId) {
                                                    saveScenesState((prev) => {
                                                        const idx = findSceneIndexById(prev, activeSceneId);
                                                        if (idx === -1) return prev;
                                                        const next = prev.slice();
                                                        next[idx] = {
                                                            ...prev[idx],
                                                            roomTileCellVis: Object.fromEntries(
                                                                Object.entries(bgLayerVisRef.current).filter(([k]) => k.startsWith("rt:"))
                                                            ),
                                                        };
                                                        return next;
                                                    });
                                                }
                                            }}
                                            className={["flex items-center gap-1.5 px-2.5 py-1.5 border border-black/30 font-['Jersey_10'] uppercase text-xs tracking-widest", roomTileEditMode ? "bg-gold text-coal" : "bg-black/50 text-white hover:bg-black/70"].join(" ")}
                                            title={roomTileEditMode ? "Exit tile edit mode" : "Enter tile edit mode - click tiles to show/hide"}
                                        >
                                            Edit Tiles
                                        </button>
                                    )}
                                    {/* Scene size indicator */}
                                    {activeSceneSizeInfo && activeSceneSizeInfo.pct >= 80 && (
                                        <div className="pointer-events-none flex items-center gap-1.5 px-2.5 py-1.5 bg-black/50 backdrop-blur-sm">
                                            <span className={["font-['Jersey_10'] uppercase text-xs tracking-widest", activeSceneSizeInfo.pct >= 99 ? "text-red-400" : activeSceneSizeInfo.pct >= 90 ? "text-orange-400" : "text-yellow-400"].join(" ")}>
                                                Scene {activeSceneSizeInfo.pct >= 99 ? "100% Full" : `${activeSceneSizeInfo.pct}% Full`}
                                            </span>
                                        </div>
                                    )}
                                    {/* Auto-save indicator */}
                                    {autoSaveStatus && (
                                        <div className="pointer-events-none flex items-center gap-1.5 px-2.5 py-1.5 bg-black/50 backdrop-blur-sm">
                                    {autoSaveStatus === 'saving' && (
                                        <>
                                            <svg className="w-3 h-3 animate-spin shrink-0 text-white/80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2a10 10 0 0 1 10 10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
                                            <span className="font-['Jersey_10'] uppercase text-xs tracking-widest text-white/80">Auto Saving</span>
                                        </>
                                    )}
                                    {autoSaveStatus === 'ok' && (
                                        <>
                                            <svg className="w-3 h-3 shrink-0 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                                            <span className="font-['Jersey_10'] uppercase text-xs tracking-widest text-green-400">Saved</span>
                                        </>
                                    )}
                                    {autoSaveStatus === 'error' && (
                                        <>
                                            <svg className="w-3 h-3 shrink-0 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                                            <span className="font-['Jersey_10'] uppercase text-xs tracking-widest text-red-400">Save Failed</span>
                                        </>
                                    )}
                                </div>
                            )}
                                </div>
                            )}

                            <SpriteActionBar
                                visible={!!selectedSpriteContext}
                                cropMode={cropMode}
                                scaleValue={selectedSpriteScaleValue}
                                onDuplicate={duplicateSelectedSprite}
                                onScale={setSelectedSpriteScale}
                                onCropToggle={toggleSelectedSpriteCropMode}
                                showCrop={true}
                                flipXActive={selectedSpriteFlipXActive}
                                flipYActive={selectedSpriteFlipYActive}
                                onFlipX={setSelectedSpriteFlipX}
                                onFlipY={setSelectedSpriteFlipY}
                                rotationValue={selectedSpriteRotation}
                                onRotation={setSelectedSpriteRotation}
                                onRotationBlur={() => { rotationHistoryPushedRef.current = false; }}
                                onRemove={deleteSelectedItem}
                                multiSelect={multiSelectActive}
                            />

                            <FurniSearchModal
                                open={showFurniSearch}
                                onClose={() => setShowFurniSearch(false)}
                                onSelect={(opt) => {
                                    handleSearchSelect(opt);
                                    setShowFurniSearch(false);
                                    furniSearchCompleteRef.current?.();
                                }}
                            />

                            {showNewImageModal && (
                                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70">
                                    <div
                                        className="bg-offwhite border-2 border-black w-[min(480px,94%)]"
                                        style={{ boxShadow: "4px 4px 0 0 rgba(0,0,0,0.9)" }}
                                    >
                                        {/* Header bar */}
                                        <div className="bg-coal text-offwhite px-4 py-2 flex items-center justify-between">
                                            <span className="font-['Jersey_10'] uppercase text-lg tracking-widest">New Image</span>
                                            {scenes.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="font-['Jersey_10'] text-offwhite/60 hover:text-offwhite leading-none text-lg"
                                                    onClick={() => { setShowNewImageModal(false); setNewImageName(""); }}
                                                >✕</button>
                                            )}
                                        </div>

                                        <div className="p-5 space-y-5">
                                            {/* Name field */}
                                            <div>
                                                <label className="block font-['Jersey_10'] uppercase text-xs tracking-widest mb-1 text-gray-500">Image Name</label>
                                                <input
                                                    type="text"
                                                    value={newImageName}
                                                    onChange={(e) => setNewImageName(e.target.value)}
                                                    onKeyDown={(e) => e.key === "Enter" && createNewImageScene(newImageName, Math.max(1, parseInt(newImageWidth) || 650), Math.max(1, parseInt(newImageHeight) || 500), newImageTileMode, newImageTileSize)}
                                                    className="w-full px-3 py-2 border-2 border-black bg-white font-['Jersey_10'] text-sm focus:outline-none focus:bg-yellow-50"
                                                    placeholder="My Image..."
                                                    autoFocus
                                                />
                                            </div>

                                            {/* Size presets */}
                                            <div className={newImageTileMode ? "opacity-40 pointer-events-none" : ""}>
                                                <label className="block font-['Jersey_10'] uppercase text-xs tracking-widest mb-2 text-gray-500">Preset Sizes</label>
                                                <div className="grid grid-cols-3 gap-2">
                                                    {(resolvedConfig.presets || IMAGE_PRESETS).map((p) => {
                                                        const active = newImageUsePreset && newImagePreset === p.name;
                                                        return (
                                                            <button
                                                                key={p.name}
                                                                type="button"
                                                                onClick={() => { setNewImageUsePreset(true); setNewImagePreset(p.name); setNewImageWidth(p.width); setNewImageHeight(p.height); }}
                                                                className={["border-2 py-2 px-1 font-['Jersey_10'] uppercase text-sm tracking-widest transition-none", active ? "border-black bg-gold" : "border-gray-300 bg-white hover:border-black"].join(" ")}
                                                            >
                                                                <div className="text-sm">{p.name}</div>
                                                                <div className="opacity-60">{p.width}×{p.height}</div>
                                                            </button>
                                                        );
                                                    })}
                                                    <button
                                                        type="button"
                                                        onClick={() => setNewImageUsePreset(false)}
                                                        className={["border-2 py-2 px-1 font-['Jersey_10'] uppercase text-sm tracking-widest transition-none", !newImageUsePreset ? "border-black bg-gold" : "border-gray-300 bg-white hover:border-black"].join(" ")}
                                                    >
                                                        <div className="text-sm">Custom</div>
                                                        <div className="opacity-60">{newImageWidth}×{newImageHeight}</div>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Custom size inputs - always visible, highlighted when active */}
                                            <div className={["border-2 p-3 transition-none", newImageTileMode ? "border-gray-200 bg-gray-50 opacity-40 pointer-events-none" : !newImageUsePreset ? "border-black bg-yellow-50" : "border-gray-200 bg-gray-50 opacity-60"].join(" ")}>
                                                <label className="block font-['Jersey_10'] uppercase text-xs tracking-widest mb-2 text-gray-500">Custom Dimensions (px)</label>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1">
                                                        <div className="font-['Jersey_10'] uppercase text-sm tracking-widest text-gray-400 mb-1">W</div>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            value={newImageWidth}
                                                            onChange={(e) => { setNewImageWidth(e.target.value); setNewImageUsePreset(false); }}
                                                            onBlur={() => setNewImageWidth((v) => { const n = parseInt(v); return isNaN(n) || n < 1 ? 1 : Math.min(2000, n); })}
                                                            className="w-full px-2 py-2 border-2 border-black bg-white font-['Jersey_10'] text-lg tracking-widest text-center focus:outline-none focus:bg-yellow-50"
                                                        />
                                                    </div>
                                                    <div className="font-['Jersey_10'] text-lg mt-4">×</div>
                                                    <div className="flex-1">
                                                        <div className="font-['Jersey_10'] uppercase text-sm tracking-widest text-gray-400 mb-1">H</div>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            value={newImageHeight}
                                                            onChange={(e) => { setNewImageHeight(e.target.value); setNewImageUsePreset(false); }}
                                                            onBlur={() => setNewImageHeight((v) => { const n = parseInt(v); return isNaN(n) || n < 1 ? 1 : Math.min(2000, n); })}
                                                            className="w-full px-2 py-2 border-2 border-black bg-white font-['Jersey_10'] text-lg tracking-widest text-center focus:outline-none focus:bg-yellow-50"
                                                        />
                                                    </div>
                                                    <div className="mt-4">
                                                        <div className="font-['Jersey_10'] uppercase text-sm tracking-widest text-gray-400 mb-1 invisible">_</div>
                                                        <div className="px-2 py-2 border-2 border-gray-200 bg-gray-100 font-['Jersey_10'] text-sm tracking-widest text-gray-500 whitespace-nowrap">
                                                            {parseInt(newImageWidth) || "?"} × {parseInt(newImageHeight) || "?"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Tile Mode */}
                                            <div>
                                                <label className="block font-['Jersey_10'] uppercase text-xs tracking-widest mb-2 text-gray-500">Tile Mode</label>
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setNewImageTileMode((v) => !v)}
                                                        className={[
                                                            "h-9 px-4 border-2 font-['Jersey_10'] uppercase text-sm tracking-widest transition-none",
                                                            newImageTileMode ? "border-black bg-yellow-300 text-coal" : "border-gray-300 bg-white hover:border-black",
                                                        ].join(" ")}
                                                    >
                                                        {newImageTileMode ? "On" : "Off"}
                                                    </button>
                                                    {newImageTileMode && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-['Jersey_10'] uppercase text-xs tracking-widest text-gray-500">Size</span>
                                                            <div className="flex gap-1">
                                                                {[192, 256, 320, 384, 512].map((s) => (
                                                                    <button
                                                                        key={s}
                                                                        type="button"
                                                                        onClick={() => setNewImageTileSize(s)}
                                                                        className={[
                                                                            "h-9 px-2 border-2 font-['Jersey_10'] text-xs tracking-widest transition-none",
                                                                            newImageTileSize === s ? "border-black bg-gold text-coal" : "border-gray-300 bg-white hover:border-black",
                                                                        ].join(" ")}
                                                                    >
                                                                        {s}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                {newImageTileMode && (
                                                    <p className="mt-2 font-['Jersey_10'] text-xs text-gray-400 tracking-wide">Canvas will be {newImageTileSize}×{newImageTileSize}px. Sprites dragged off an edge reappear on the opposite side.</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Footer buttons */}
                                        <div className="px-5 pb-5 flex gap-2">
                                            <button
                                                type="button"
                                                className="flex-1 h-11 border-2 border-black bg-green-500 hover:bg-green-400 font-['Jersey_10'] uppercase text-sm tracking-widest font-bold disabled:opacity-40"
                                                disabled={!newImageName.trim()}
                                                onClick={() => createNewImageScene(newImageName, Math.max(1, parseInt(newImageWidth) || 650), Math.max(1, parseInt(newImageHeight) || 500), newImageTileMode, newImageTileSize)}
                                                style={{ boxShadow: "3px 3px 0 0 rgba(0,0,0,0.8)" }}
                                            >
                                                Create
                                            </button>
                                            {scenes.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="w-24 h-11 border-2 border-black bg-gray-200 hover:bg-gray-300 font-['Jersey_10'] uppercase text-sm tracking-widest"
                                                    onClick={() => { setShowNewImageModal(false); setNewImageName(""); setNewImageWidth(650); setNewImageHeight(500); setNewImagePreset("Standard"); setNewImageUsePreset(true); setNewImageTileMode(false); setNewImageTileSize(500); }}
                                                    style={{ boxShadow: "3px 3px 0 0 rgba(0,0,0,0.8)" }}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}


                            {!isCapturing && !uiHidden && (
                                <div className="absolute top-2 right-2 flex items-start gap-2 z-[100]" data-capture-hide="1">
                                    {/* Tile Preview Toggle - only shown when in tile mode */}
                                    {tileMode && (
                                        <div className="flex flex-col items-center gap-1" data-capture-hide="1">
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    if (showTilePreview) {
                                                        setShowTilePreview(false);
                                                        setTilePreviewUrl(null);
                                                    } else {
                                                        const url = await savePreviewPng({ returnUrl: true });
                                                        if (url) {
                                                            setTilePreviewUrl(url);
                                                            setShowTilePreview(true);
                                                        }
                                                    }
                                                }}
                                                className={[
                                                    "w-10 h-10 border border-black flex items-center justify-center",
                                                    "font-['Jersey_10'] text-xs uppercase tracking-widest",
                                                    showTilePreview ? "bg-yellow-300 text-coal" : "bg-offwhite/50 hover:bg-gray-200",
                                                ].join(" ")}
                                                title={showTilePreview ? "Hide tile preview" : "Preview tiled"}
                                            >
                                                <svg viewBox="0 0 16 16" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                                                    <rect x="0" y="0" width="7" height="7"/><rect x="9" y="0" width="7" height="7"/><rect x="0" y="9" width="7" height="7"/><rect x="9" y="9" width="7" height="7"/>
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                    {/* Zoom Controls */}
                                    <div className="flex flex-col items-center gap-1" data-capture-hide="1">
                                        <button
                                            type="button"
                                            onClick={() => changeZoom(zoom === 1 ? 2 : zoom === 2 ? 4 : 16)}
                                            disabled={zoom >= 16}
                                            className={[
                                                "w-10 h-10 border border-black bg-offwhite/50",
                                                "flex items-center justify-center hover:bg-gray-200",
                                                zoom >= 16 ? "opacity-40 cursor-not-allowed" : "",
                                                "font-['Jersey_10'] text-xl tracking-widest",
                                            ].join(" ")}
                                            title="Zoom in"
                                        >
                                            +
                                        </button>
                                        <div className="font-['Jersey_10'] text-sm uppercase bg-offwhite/50 tracking-widest px-2 py-1 border border-black/30">{zoom}x</div>
                                        <button
                                            type="button"
                                            onClick={() => zoom > 1 ? changeZoom(zoom === 16 ? 4 : zoom === 4 ? 2 : zoom === 2 ? 1 : 1) : null}
                                            disabled={zoom <= 1}
                                            className={[
                                                "w-10 h-10 border border-black bg-offwhite/50",
                                                "flex items-center justify-center hover:bg-gray-200",
                                                zoom <= 1 ? "opacity-40 cursor-not-allowed" : "",
                                                "font-['Jersey_10'] text-xl tracking-widest",
                                            ].join(" ")}
                                            title="Zoom out"
                                        >
                                            −
                                        </button>
                                    </div>
                                </div>
                            )}


                            {/* Bottom-left: UI toggle */}
                            {!isCapturing && (
                                <div className="absolute bottom-2 left-2 z-10 hidden" data-capture-hide="1">
                                    <div className={["transition-opacity", uiHidden ? "opacity-20 hover:opacity-100" : "opacity-100"].join(" ")}>
                                        <button
                                            type="button"
                                            onClick={() => { setUiHidden((v) => !v); setShowMoodlight(false); }}
                                            title={uiHidden ? "Show UI" : "Hide UI"}
                                            className="w-10 h-10 border border-black bg-offwhite/50 flex items-center justify-center hover:bg-gray-200 font-['Jersey_10'] text-sm uppercase tracking-wide text-coal"
                                        >
                                            UI
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div
                                ref={previewRef}
                                data-preview-capture="1"
                                className={[
                                    "relative bg-offwhite py-0 px-0 flex items-center justify-center overflow-hidden flex-shrink-0",
                                    canPanBg ? "cursor-grab active:cursor-grabbing" : "",
                                ].join(" ")}
                                style={{
                                    ...previewBgStyle,
                                    width: `${previewSize.w}px`,
                                    height: `${previewSize.h}px`,
                                    transform: `translate(${Math.round(workspaceOffset.x)}px, ${Math.round(workspaceOffset.y)}px) scale(${zoom})`,
                                    transformOrigin: "center",
                                    imageRendering: "pixelated",
                                }}
                                onContextMenu={handleWorkspaceContextMenu}
                                onMouseMove={activeBg?.type === "room_tile" && roomTileGrid.length ? (e) => {
                                    roomTileHoverPosRef.current = { x: e.clientX, y: e.clientY };
                                    if (roomTileHoverRafRef.current) return;
                                    roomTileHoverRafRef.current = requestAnimationFrame(() => {
                                        roomTileHoverRafRef.current = null;
                                        const el = previewRef.current;
                                        if (!el) return;
                                        const rect = el.getBoundingClientRect();
                                        const { x: cx, y: cy } = roomTileHoverPosRef.current;
                                        const px = (cx - rect.left) / rect.width * previewSize.w;
                                        const py = (cy - rect.top) / rect.height * previewSize.h;
                                        const hit = hitTestRoomTile(px, py, roomTileGrid, roomTileHoverImgSize.w, roomTileHoverImgSize.h);
                                        const nextKey = hit ? hit.key : null;
                                        setRoomTileHoverKey((prev) => prev === nextKey ? prev : nextKey);
                                    });
                                } : undefined}
                                onMouseLeave={activeBg?.type === "room_tile" ? () => {
                                    if (roomTileHoverRafRef.current) { cancelAnimationFrame(roomTileHoverRafRef.current); roomTileHoverRafRef.current = null; }
                                    setRoomTileHoverKey(null);
                                } : undefined}
                                onPointerDown={(e) => {
                                    if (e.button !== 0) return;
                                    if (e.target?.closest?.('[data-capture-hide="1"]')) return;

                                    if (canPanBg) {
                                        workspacePanRef.current = {
                                            startClientX: e.clientX,
                                            startClientY: e.clientY,
                                            startOffset: { ...workspaceOffset },
                                        };
                                        window.addEventListener("pointermove", onWorkspacePanMove, { passive: false });
                                        window.addEventListener("pointerup", onWorkspacePanUp);
                                        window.addEventListener("pointercancel", onWorkspacePanUp);
                                        return;
                                    }

                                    if (editingSceneHabboIdRef.current) endEditSceneHabbo({ restore: false });

                                    setSpriteSelected(false);

                                    const active = activeLayerId ? layerById.get(activeLayerId) : null;
                                    const isActiveLocked = !!active && (active.locked || active.permaLock);

                                    endSpriteDrag();

                                    if (isActiveLocked) return;

                                    setActiveFurniId(null);
                                    setActiveSceneHabboId(null);
                                    setActiveCustomImageId(null);
                                    setActiveTextId(null);
                                    setActiveLayerId(null);
                                    setContextMenu(null);
                                    setCropMode(false);
                                    clearSnapGuides();
                                    setShowAddEffectDropdown(false);

                                }}
                            >
                                {sceneLoading && (
                                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-coal">
                                        <div className="flex items-center gap-3">
                                            <div className="w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                            <div className="text-white font-['Jersey_10'] uppercase text-sm tracking-widest tracking-wide">Loading</div>
                                        </div>
                                    </div>
                                )}

                                <div
                                    className="absolute inset-0"
                                    style={{
                                        transform: `translate(${Math.round(cameraPos.x)}px, ${Math.round(cameraPos.y)}px)`,
                                        transformOrigin: "center",
                                    }}
                                >
                                    {snapGuides.x !== null && (
                                        <div
                                            className="pointer-events-none absolute bottom-0 top-0 z-[5] border-l border-dashed border-gold/60"
                                            style={{ left: `calc(50% + ${Math.round(snapGuides.x)}px)` }}
                                        />
                                    )}

                                    {snapGuides.y !== null && (
                                        <div
                                            className="pointer-events-none absolute left-0 right-0 z-[5] border-t border-dashed border-gold/60"
                                            style={{ top: `calc(50% + ${Math.round(snapGuides.y)}px)` }}
                                        />
                                    )}
                                    {previewBg === "custom_bg" && customBgUrl && (
                                        <div
                                            className="absolute inset-0 pointer-events-none"
                                            style={customBgFit === "tile"
                                                ? { backgroundImage: `url(${customBgUrl})`, backgroundRepeat: "repeat", backgroundSize: "auto", backgroundPosition: "0px 0px" }
                                                : customBgFit === "stretch"
                                                    ? { backgroundImage: `url(${customBgUrl})`, backgroundSize: "100% 100%", backgroundRepeat: "no-repeat" }
                                                    : { backgroundImage: `url(${customBgUrl})`, backgroundSize: customBgFit, backgroundRepeat: "no-repeat", backgroundPosition: "center" }
                                            }
                                        />
                                    )}

                                    {activeBg.type === "tile" && previewBg !== "custom_bg" && (
                                        <div
                                            className="absolute inset-0 pointer-events-none"
                                            style={{
                                                backgroundImage: `url(${activeBg.url})`,
                                                backgroundRepeat: "repeat",
                                                backgroundSize: `${activeBg.size.w * tileScale}px ${activeBg.size.h * tileScale}px`,
                                                backgroundPosition: tileBgPosition,
                                                imageRendering: "pixelated",
                                                ...(previewBg === "grey_grid" && validGridTint && {
                                                    backgroundColor: `${validGridTint}80`,
                                                    backgroundBlendMode: "screen",
                                                }),
                                            }}
                                        />
                                    )}

                                    {activeBg?.type === "room_tile" && roomTilePreviewUrl && (
                                        <img
                                            src={roomTilePreviewUrl}
                                            alt=""
                                            className="absolute inset-0 pointer-events-none block"
                                            style={{ width: "100%", height: "100%", imageRendering: "pixelated" }}
                                            draggable={false}
                                        />
                                    )}
                                    {activeBg?.type === "room_tile" && roomTileEditMode && (
                                        <div
                                            className="absolute inset-0"
                                            data-capture-hide="1"
                                            style={{ cursor: "crosshair", zIndex: 0 }}
                                            onPointerDown={(e) => {
                                                if (e.button !== 0) return;
                                                e.stopPropagation();
                                                e.currentTarget.setPointerCapture(e.pointerId);
                                                const rect = previewRef.current?.getBoundingClientRect();
                                                if (!rect) return;
                                                const px = (e.clientX - rect.left) / rect.width * previewSize.w;
                                                const py = (e.clientY - rect.top) / rect.height * previewSize.h;
                                                const hit = hitTestRoomTile(px, py, roomTileGrid, roomTileHoverImgSize.w, roomTileHoverImgSize.h);
                                                if (!hit) return;
                                                pushHistory();
                                                const key = `rt:${hit.gx},${hit.gy}`;
                                                const drag = roomTileDragRef.current;
                                                drag.active = true;
                                                drag.visited = new Set([key]);
                                                drag.changes = {};
                                                const currentVis = bgLayerVis[key] !== false;
                                                drag.targetVis = !currentVis;
                                                drag.changes[key] = drag.targetVis;
                                                const oc = roomTileOverlayCanvasRef.current;
                                                if (oc) drawRoomTileDragHighlight(oc.getContext("2d"), hit, drag.targetVis, (activeBg.rowStepY ?? 16) * 2);
                                            }}
                                            onPointerMove={(e) => {
                                                const drag = roomTileDragRef.current;
                                                if (!drag.active) return;
                                                const rect = previewRef.current?.getBoundingClientRect();
                                                if (!rect) return;
                                                const px = (e.clientX - rect.left) / rect.width * previewSize.w;
                                                const py = (e.clientY - rect.top) / rect.height * previewSize.h;
                                                const hit = hitTestRoomTile(px, py, roomTileGrid, roomTileHoverImgSize.w, roomTileHoverImgSize.h);
                                                if (!hit) return;
                                                const key = `rt:${hit.gx},${hit.gy}`;
                                                if (drag.visited.has(key)) return;
                                                drag.visited.add(key);
                                                drag.changes[key] = drag.targetVis;
                                                const oc = roomTileOverlayCanvasRef.current;
                                                if (oc) drawRoomTileDragHighlight(oc.getContext("2d"), hit, drag.targetVis, (activeBg.rowStepY ?? 16) * 2);
                                            }}
                                            onPointerUp={() => {
                                                const drag = roomTileDragRef.current;
                                                if (!drag.active) return;
                                                drag.active = false;
                                                const changes = { ...drag.changes };
                                                drag.changes = {};
                                                drag.visited = null;
                                                if (Object.keys(changes).length > 0) {
                                                    setBgLayerVis((prev) => ({ ...prev, ...changes }));
                                                }
                                                setTimeout(() => {
                                                    const oc = roomTileOverlayCanvasRef.current;
                                                    if (oc) oc.getContext("2d").clearRect(0, 0, oc.width, oc.height);
                                                }, 220);
                                            }}
                                            onPointerCancel={() => {
                                                const drag = roomTileDragRef.current;
                                                drag.active = false;
                                                drag.changes = {};
                                                drag.visited = null;
                                                const oc = roomTileOverlayCanvasRef.current;
                                                if (oc) oc.getContext("2d").clearRect(0, 0, oc.width, oc.height);
                                            }}
                                        />
                                    )}
                                    {/* Room tile drag overlay canvas - highlights painted synchronously via canvas API, no React render cycle */}
                                    {activeBg?.type === "room_tile" && roomTileEditMode && (
                                        <canvas
                                            ref={roomTileOverlayCanvasRef}
                                            className="absolute inset-0 pointer-events-none"
                                            data-capture-hide="1"
                                            width={previewSize.w}
                                            height={previewSize.h}
                                            style={{ imageRendering: "pixelated", zIndex: 6 }}
                                        />
                                    )}
                                    {/* Room tile hover overlay - only visible in edit mode */}
                                    {activeBg?.type === "room_tile" && roomTileEditMode && roomTileHoverKey && (() => {
                                        const ht = roomTileGrid.find((t) => t.key === roomTileHoverKey);
                                        if (!ht) return null;
                                        return (
                                            <img
                                                key="rt-hover"
                                                src={`${process.env.PUBLIC_URL}/img/roomTileHover.gif`}
                                                alt=""
                                                className="absolute pointer-events-none"
                                                data-capture-hide="1"
                                                style={{
                                                    left: ht.x - 1,
                                                    top: ht.y,
                                                    width: roomTileHoverImgSize.w,
                                                    height: roomTileHoverImgSize.h,
                                                    imageRendering: "pixelated",
                                                    zIndex: 5,
                                                }}
                                                draggable={false}
                                            />
                                        );
                                    })()}

                                    {layers.map((l) => {
                                        if (l.visible === false) return null;
                                        if (l.kind === "speech") return null;

                                        const layerOpacityVal = (typeof l.opacity === "number" ? l.opacity : 100) / 100;

                                        if (l.kind === "moodlight") {
                                            return (
                                                <div
                                                    key={l.id}
                                                    className="absolute inset-0 pointer-events-none"
                                                    style={{
                                                        backgroundColor: moodlightColor,
                                                        opacity: moodlightOpacity * layerOpacityVal,
                                                        zIndex: layerZ.get(l.id) || 0,
                                                    }}
                                                />
                                            );
                                        }

                                        if (l.kind === "bg") {
                                            if (previewBg === "custom_bg") return null;
                                            if (activeBg.type !== "layered") return null;
                                            const k = (l.id.split(":")[1] || "").trim();
                                            const src = activeBg.layers?.[k];
                                            if (!src) return null;
                                            return (
                                                <div
                                                    key={l.id}
                                                    className="absolute left-1/2 top-1/2 pointer-events-none"
                                                    style={{
                                                        transform: "translate(-50%, -50%)",
                                                        transformOrigin: "center",
                                                        imageRendering: "pixelated",
                                                        zIndex: layerZ.get(l.id) || 0,
                                                        opacity: layerOpacityVal,
                                                    }}
                                                >
                                                    <img src={src} alt="" className="block max-w-none max-h-none select-none" style={{ imageRendering: "pixelated" }} draggable={false} />
                                                </div>
                                            );
                                        }

                                        if (l.kind === "furni") {
                                            const spId = l.id.split(":")[1];
                                            const sp = furniSprites.find((x) => x.id === spId) || null;
                                            if (!sp) return null;

                                            const spUrlRaw = getFurniRenderUrl(sp);
                                            const spUrl = getLayerWaterSrc(getLayerOutlineSrc(spUrlRaw, l.effects), l.effects, l.kind);
                                            const isActive = activeFurniId === sp.id || selectedLayerIds.includes(`furni:${sp.id}`);
                                            const isLocked = !!(layerById.get(l.id)?.locked || layerById.get(l.id)?.permaLock);
                                            const spriteInfo = {
                                                selected: isActive,
                                                locked: isLocked,
                                                flipped: !!sp.flipped,
                                                flipY: !!sp.flipY,
                                                rotation: sp.rotation || 0,
                                                scale: clampSpriteScale(sp.scale),
                                                crop: sp.crop || null,
                                                sourceSize: sp.sourceSize,
                                            };

                                            return (
                                                <React.Fragment key={l.id}>
                                                {tileMode && [-previewSize.w, 0, previewSize.w].flatMap((ox) =>
                                                    [-previewSize.h, 0, previewSize.h].map((oy) => {
                                                        if (ox === 0 && oy === 0) return null;
                                                        const ghostPos = { x: sp.pos.x + ox, y: sp.pos.y + oy };
                                                        return (
                                                            <div
                                                                key={`${l.id}_ghost_${ox}_${oy}`}
                                                                className={["absolute left-1/2 top-1/2", isLocked ? "pointer-events-none" : "touch-none cursor-pointer"].join(" ")}
                                                                style={{
                                                                    transform: spriteWrapperTransform(ghostPos, sp.sourceSize, sp.scale, sp.crop, sp.rotation || 0),
                                                                    imageRendering: "pixelated",
                                                                    zIndex: 10000 + (layerZ.get(l.id) || 0),
                                                                    opacity: layerOpacityVal,
                                                                }}
                                                                onPointerDown={isLocked ? undefined : (e) => {
                                                                    if (e.button !== 0) return;
                                                                    if (handleSpriteSelectPointerDown(e, `furni:${sp.id}`)) return;
                                                                    setForceUiLocked(false);
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
                                                                    if (editingSceneHabboIdRef.current) endEditSceneHabbo({ restore: false });
                                                                    setSpriteSelected(false);
                                                                    setActiveSceneHabboId(null);
                                                                    setActiveCustomImageId(null);
                                                                    setActiveTextId(null);
                                                                    setCropMode(false);
                                                                    setContextMenu(null);
                                                                    beginIconLoad();
                                                                    setActiveFurniId(sp.id);
                                                                    setActiveLayerId(`furni:${sp.id}`);
                                                                    const _fsc = clampSpriteScale(sp.scale);
                                                                    furniDragRef.current = {
                                                                        id: sp.id,
                                                                        pointerId: e.pointerId,
                                                                        el: e.currentTarget,
                                                                        raf: 0,
                                                                        startClientX: e.clientX,
                                                                        startClientY: e.clientY,
                                                                        startPos: { x: sp.pos.x + ox, y: sp.pos.y + oy },
                                                                        nextX: sp.pos.x + ox,
                                                                        nextY: sp.pos.y + oy,
                                                                        moved: false,
                                                                        sc: _fsc,
                                                                        sw: (sp.sourceSize?.w || 0) * _fsc,
                                                                        sh: (sp.sourceSize?.h || 0) * _fsc,
                                                                        cl: (sp.crop?.left || 0) * _fsc,
                                                                        ct: (sp.crop?.top  || 0) * _fsc,
                                                                        rotation: sp.rotation || 0,
                                                                    };
                                                                    window.addEventListener("pointermove", onFurniPointerMove, { passive: false });
                                                                    window.addEventListener("pointerup", onFurniPointerUp, { passive: false });
                                                                    window.addEventListener("pointercancel", onFurniPointerUp, { passive: false });
                                                                }}
                                                            >
                                                                {renderSpriteFrame({
                                                                    src: spUrl,
                                                                    selected: isActive,
                                                                    locked: isLocked,
                                                                    flipped: !!sp.flipped,
                                                                    flipY: !!sp.flipY,
                                                                    scale: sp.scale,
                                                                    crop: sp.crop,
                                                                    sourceSize: sp.sourceSize,
                                                                    inCropMode: false,
                                                                })}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                                <div
                                                    className={["absolute left-1/2 top-1/2", isLocked ? "pointer-events-none" : "touch-none cursor-pointer"].join(" ")}
                                                    style={{
                                                        transform: spriteWrapperTransform(sp.pos, sp.sourceSize, sp.scale, sp.crop, sp.rotation || 0),
                                                        imageRendering: "pixelated",
                                                        zIndex: layerZ.get(l.id) || 0,
                                                    }}
                                                    onPointerDown={(e) => {
                                                        if (e.button !== 0 || isLocked) return;
                                                        if (handleSpriteSelectPointerDown(e, `furni:${sp.id}`)) return;
                                                        setForceUiLocked(false);
                                                        e.preventDefault();
                                                        e.stopPropagation();

                                                        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}

                                                        if (editingSceneHabboIdRef.current) endEditSceneHabbo({ restore: false });

                                                        setSpriteSelected(false);
                                                        setActiveSceneHabboId(null);
                                                        setActiveCustomImageId(null);
                                                        setActiveTextId(null);
                                                        setCropMode(false);
                                                        setContextMenu(null);

                                                        beginIconLoad();
                                                        setActiveFurniId(sp.id);
                                                        setActiveLayerId(`furni:${sp.id}`);

                                                        const _fsc = clampSpriteScale(sp.scale);
                                                        furniDragRef.current = {
                                                            id: sp.id,
                                                            pointerId: e.pointerId,
                                                            el: e.currentTarget,
                                                            raf: 0,
                                                            startClientX: e.clientX,
                                                            startClientY: e.clientY,
                                                            startPos: { ...sp.pos },
                                                            nextX: sp.pos.x,
                                                            nextY: sp.pos.y,
                                                            moved: false,
                                                            sc: _fsc,
                                                            sw: (sp.sourceSize?.w || 0) * _fsc,
                                                            sh: (sp.sourceSize?.h || 0) * _fsc,
                                                            cl: (sp.crop?.left || 0) * _fsc,
                                                            ct: (sp.crop?.top  || 0) * _fsc,
                                                            rotation: sp.rotation || 0,
                                                        };

                                                        window.addEventListener("pointermove", onFurniPointerMove, { passive: false });
                                                        window.addEventListener("pointerup", onFurniPointerUp, { passive: false });
                                                        window.addEventListener("pointercancel", onFurniPointerUp, { passive: false });
                                                    }}
                                                >
                                                    {renderSpriteFrame({
                                                        src: spUrl,
                                                        selected: isActive,
                                                        locked: isLocked,
                                                        flipped: !!sp.flipped,
                                                        flipY: !!sp.flipY,
                                                        scale: sp.scale,
                                                        crop: sp.crop,
                                                        sourceSize: sp.sourceSize,
                                                        inCropMode: cropMode && isActive && !isLocked,
                                                        layerOpacity: layerOpacityVal,
                                                        onLoad: (e) => {
                                                            const img = e.currentTarget;
                                                            const nextSize = {
                                                                w: img.naturalWidth || img.width || 0,
                                                                h: img.naturalHeight || img.height || 0,
                                                            };
                                                            if (
                                                                nextSize.w &&
                                                                nextSize.h &&
                                                                (nextSize.w !== Number(sp.sourceSize?.w || 0) || nextSize.h !== Number(sp.sourceSize?.h || 0))
                                                            ) {
                                                                updateFurniSprite(sp.id, { sourceSize: nextSize });
                                                            }
                                                        },
                                                        children: renderCropHandles(spriteInfo),
                                                    })}
                                                </div>
                                                </React.Fragment>
                                            );
                                        }

                                        if (l.kind === "custom_image") {
                                            const imgId = l.id.split(":")[1];
                                            const img = customImages.find((x) => x.id === imgId) || null;
                                            if (!img) return null;
                                            const imgDisplayUrl = getLayerWaterSrc(getLayerOutlineSrc(img.url, l.effects), l.effects, l.kind);
                                            const isActive = activeCustomImageId === img.id || selectedLayerIds.includes(l.id);
                                            const isLocked = !!(layerById.get(l.id)?.locked || layerById.get(l.id)?.permaLock);
                                            const spriteInfo = {
                                                selected: isActive,
                                                locked: isLocked,
                                                flipped: !!img.flipX,
                                                flipY: !!img.flipY,
                                                rotation: img.rotation || 0,
                                                scale: clampSpriteScale(img.scale),
                                                crop: img.crop || null,
                                                sourceSize: img.sourceSize,
                                            };
                                            return (
                                                <React.Fragment key={l.id}>
                                                {tileMode && [-previewSize.w, 0, previewSize.w].flatMap((ox) =>
                                                    [-previewSize.h, 0, previewSize.h].map((oy) => {
                                                        if (ox === 0 && oy === 0) return null;
                                                        const ghostPos = { x: img.pos.x + ox, y: img.pos.y + oy };
                                                        return (
                                                            <div
                                                                key={`${l.id}_ghost_${ox}_${oy}`}
                                                                className={["absolute left-1/2 top-1/2", isLocked ? "pointer-events-none" : "touch-none cursor-pointer"].join(" ")}
                                                                style={{
                                                                    transform: spriteWrapperTransform(ghostPos, img.sourceSize, img.scale, img.crop, img.rotation || 0),
                                                                    zIndex: 10000 + (layerZ.get(l.id) || 0),
                                                                    opacity: layerOpacityVal,
                                                                }}
                                                                onPointerDown={isLocked ? undefined : (e) => {
                                                                    if (e.button !== 0) return;
                                                                    if (handleSpriteSelectPointerDown(e, l.id)) return;
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
                                                                    setActiveCustomImageId(img.id);
                                                                    setActiveLayerId(l.id);
                                                                    setActiveFurniId(null);
                                                                    setSpriteSelected(false);
                                                                    setActiveSceneHabboId(null);
                                                                    setActiveTextId(null);
                                                                    const _cisc = clampSpriteScale(img.scale);
                                                                    customImgDragRef.current = {
                                                                        id: img.id,
                                                                        el: e.currentTarget,
                                                                        sc: _cisc,
                                                                        startClientX: e.clientX,
                                                                        startClientY: e.clientY,
                                                                        startPos: { x: img.pos.x + ox, y: img.pos.y + oy },
                                                                        nextX: img.pos.x + ox,
                                                                        nextY: img.pos.y + oy,
                                                                        moved: false,
                                                                        raf: 0,
                                                                        sw: (img.sourceSize?.w || 0) * _cisc,
                                                                        sh: (img.sourceSize?.h || 0) * _cisc,
                                                                        cl: (img.crop?.left || 0) * _cisc,
                                                                        ct: (img.crop?.top  || 0) * _cisc,
                                                                        rotation: img.rotation || 0,
                                                                    };
                                                                    window.addEventListener("pointermove", onCustomImgPointerMove, { passive: false });
                                                                    window.addEventListener("pointerup", onCustomImgPointerUp, { passive: false });
                                                                    window.addEventListener("pointercancel", onCustomImgPointerUp, { passive: false });
                                                                }}
                                                            >
                                                                {renderSpriteFrame({
                                                                    src: imgDisplayUrl,
                                                                    selected: isActive,
                                                                    locked: isLocked,
                                                                    flipped: !!img.flipX,
                                                                    flipY: !!img.flipY,
                                                                    scale: img.scale,
                                                                    crop: img.crop,
                                                                    sourceSize: img.sourceSize,
                                                                    inCropMode: false,
                                                                })}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                                <div
                                                    className={["absolute left-1/2 top-1/2", isLocked ? "pointer-events-none" : "touch-none cursor-pointer"].join(" ")}
                                                    style={{
                                                        transform: spriteWrapperTransform(img.pos, img.sourceSize, img.scale, img.crop, img.rotation || 0),
                                                        zIndex: layerZ.get(l.id) || 0,
                                                    }}
                                                    onPointerDown={(e) => {
                                                        if (e.button !== 0 || isLocked) return;
                                                        if (handleSpriteSelectPointerDown(e, l.id)) return;
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        e.currentTarget.setPointerCapture(e.pointerId);
                                                        setActiveCustomImageId(img.id);
                                                        setActiveLayerId(l.id);
                                                        setActiveFurniId(null);
                                                        setSpriteSelected(false);
                                                        setActiveSceneHabboId(null);
                                                        setActiveTextId(null);
                                                        const _cisc = clampSpriteScale(img.scale);
                                                        customImgDragRef.current = {
                                                            id: img.id,
                                                            el: e.currentTarget,
                                                            sc: _cisc,
                                                            startClientX: e.clientX,
                                                            startClientY: e.clientY,
                                                            startPos: { ...img.pos },
                                                            nextX: img.pos.x,
                                                            nextY: img.pos.y,
                                                            moved: false,
                                                            raf: 0,
                                                            sw: (img.sourceSize?.w || 0) * _cisc,
                                                            sh: (img.sourceSize?.h || 0) * _cisc,
                                                            cl: (img.crop?.left || 0) * _cisc,
                                                            ct: (img.crop?.top  || 0) * _cisc,
                                                            rotation: img.rotation || 0,
                                                        };
                                                        window.addEventListener("pointermove", onCustomImgPointerMove, { passive: false });
                                                        window.addEventListener("pointerup", onCustomImgPointerUp, { passive: false });
                                                        window.addEventListener("pointercancel", onCustomImgPointerUp, { passive: false });
                                                    }}
                                                >
                                                    {renderSpriteFrame({
                                                        src: imgDisplayUrl,
                                                        selected: isActive,
                                                        locked: isLocked,
                                                        flipped: !!img.flipX,
                                                        flipY: !!img.flipY,
                                                        scale: img.scale,
                                                        crop: img.crop,
                                                        sourceSize: img.sourceSize,
                                                        inCropMode: cropMode && isActive && !isLocked,
                                                        layerOpacity: layerOpacityVal,
                                                        onLoad: (e) => {
                                                            const el = e.currentTarget;
                                                            const nextSize = {
                                                                w: el.naturalWidth || el.width || 0,
                                                                h: el.naturalHeight || el.height || 0,
                                                            };
                                                            if (
                                                                nextSize.w &&
                                                                nextSize.h &&
                                                                (nextSize.w !== Number(img.sourceSize?.w || 0) || nextSize.h !== Number(img.sourceSize?.h || 0))
                                                            ) {
                                                                setSprites((prev) => prev.map((s) => s.id === img.id && s.type === 'customImage' ? { ...s, sourceSize: nextSize } : s));
                                                            }
                                                        },
                                                        children: renderCropHandles(spriteInfo),
                                                    })}
                                                </div>
                                                </React.Fragment>
                                            );
                                        }

                                        if (l.kind === "text") {
                                            const textId = l.id.split(":")[1];
                                            const t = textSprites.find((x) => x.id === textId) || null;
                                            if (!t) return null;

                                            const isActive = activeTextId === t.id || selectedLayerIds.includes(l.id);
                                            const isLocked = !!(layerById.get(l.id)?.locked || layerById.get(l.id)?.permaLock);
                                            const metrics = getTextBoxMetrics(t);

                                            // Opacity is on the inner content wrapper so the selection outline
                                            // (which is a sibling overlay) always renders at full opacity.
                                            const textContentInner = (
                                                <div
                                                    className="relative flex items-center justify-center px-1"
                                                    style={{
                                                        minWidth: `${metrics.w}px`,
                                                        minHeight: `${metrics.h}px`,
                                                        whiteSpace: "pre",
                                                        pointerEvents: "none",
                                                        lineHeight: 1,
                                                        opacity: layerOpacityVal !== 1 ? layerOpacityVal : undefined,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontFamily: t.fontFamily,
                                                            fontSize: `${metrics.fontPx}px`,
                                                            fontWeight: t.fontWeight || "normal",
                                                            fontStyle: t.italic ? "italic" : "normal",
                                                            color: t.color,
                                                            ...(metrics.strokeWidth > 0 ? {
                                                                WebkitTextStroke: `${metrics.strokeWidth * 2}px ${t.strokeColor}`,
                                                                paintOrder: "stroke fill",
                                                            } : {}),
                                                            whiteSpace: "pre",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {String(t.text || "")}
                                                    </span>
                                                </div>
                                            );
                                            // Selection outline sits outside the opacity wrapper so it stays fully opaque
                                            const textSelectionOverlay = isActive && !isLocked
                                                ? <div className="absolute inset-0 outline outline-2 outline-yellow-300 pointer-events-none" />
                                                : null;
                                            const textContent = (
                                                <div className="relative">
                                                    {textContentInner}
                                                    {textSelectionOverlay}
                                                </div>
                                            );
                                            return (
                                                <React.Fragment key={l.id}>
                                                {tileMode && [-previewSize.w, 0, previewSize.w].flatMap((ox) =>
                                                    [-previewSize.h, 0, previewSize.h].map((oy) => {
                                                        if (ox === 0 && oy === 0) return null;
                                                        const ghostPos = { x: t.pos.x + ox, y: t.pos.y + oy };
                                                        return (
                                                            <div
                                                                key={`${l.id}_ghost_${ox}_${oy}`}
                                                                className={["absolute left-1/2 top-1/2 select-none", isLocked ? "pointer-events-none" : "touch-none cursor-pointer"].join(" ")}
                                                                style={{
                                                                    transform: `translate(calc(-50% + ${ghostPos.x}px), calc(-50% + ${ghostPos.y}px)) rotate(${t.rotation || 0}deg) scale(${t.scale || 1}) scaleX(${t.flipX ? -1 : 1}) scaleY(${t.flipY ? -1 : 1})`,
                                                                    zIndex: 10000 + (layerZ.get(l.id) || 0),
                                                                    opacity: layerOpacityVal,
                                                                }}
                                                                onPointerDown={isLocked ? undefined : (e) => {
                                                                    if (e.button !== 0) return;
                                                                    if (handleSpriteSelectPointerDown(e, l.id)) return;
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}

                                                                    setActiveTextId(t.id);
                                                                    setActiveLayerId(l.id);
                                                                    setActiveCustomImageId(null);
                                                                    setActiveFurniId(null);
                                                                    setActiveSceneHabboId(null);
                                                                    setSpriteSelected(false);
                                                                    setCropMode(false);
                                                                    setContextMenu(null);

                                                                    textDragRef.current = {
                                                                        id: t.id,
                                                                        el: e.currentTarget,
                                                                        startClientX: e.clientX,
                                                                        startClientY: e.clientY,
                                                                        startPos: { x: t.pos.x + ox, y: t.pos.y + oy },
                                                                        nextX: t.pos.x + ox,
                                                                        nextY: t.pos.y + oy,
                                                                        rotation: t.rotation || 0,
                                                                        scale: t.scale || 1,
                                                                        flipX: !!t.flipX,
                                                                        flipY: !!t.flipY,
                                                                        moved: false,
                                                                        raf: 0,
                                                                    };

                                                                    window.addEventListener("pointermove", onTextPointerMove, { passive: false });
                                                                    window.addEventListener("pointerup", onTextPointerUp, { passive: false });
                                                                    window.addEventListener("pointercancel", onTextPointerUp, { passive: false });
                                                                }}
                                                            >
                                                                {textContent}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                                <div
                                                    className={["absolute left-1/2 top-1/2 select-none", isLocked ? "pointer-events-none" : "touch-none cursor-pointer"].join(" ")}
                                                    style={{
                                                        transform: `translate(calc(-50% + ${t.pos.x}px), calc(-50% + ${t.pos.y}px)) rotate(${t.rotation || 0}deg) scale(${t.scale || 1}) scaleX(${t.flipX ? -1 : 1}) scaleY(${t.flipY ? -1 : 1})`,
                                                        zIndex: layerZ.get(l.id) || 0,
                                                    }}
                                                    onPointerDown={(e) => {
                                                        if (e.button !== 0 || isLocked) return;
                                                        if (handleSpriteSelectPointerDown(e, l.id)) return;
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}

                                                        setActiveTextId(t.id);
                                                        setActiveLayerId(l.id);
                                                        setActiveCustomImageId(null);
                                                        setActiveFurniId(null);
                                                        setActiveSceneHabboId(null);
                                                        setSpriteSelected(false);
                                                        setCropMode(false);
                                                        setContextMenu(null);

                                                        textDragRef.current = {
                                                            id: t.id,
                                                            el: e.currentTarget,
                                                            startClientX: e.clientX,
                                                            startClientY: e.clientY,
                                                            startPos: { ...t.pos },
                                                            nextX: t.pos.x,
                                                            nextY: t.pos.y,
                                                            rotation: t.rotation || 0,
                                                            scale: t.scale || 1,
                                                            flipX: !!t.flipX,
                                                            flipY: !!t.flipY,
                                                            moved: false,
                                                            raf: 0,
                                                        };

                                                        window.addEventListener("pointermove", onTextPointerMove, { passive: false });
                                                        window.addEventListener("pointerup", onTextPointerUp, { passive: false });
                                                        window.addEventListener("pointercancel", onTextPointerUp, { passive: false });
                                                    }}
                                                >
                                                    {textContent}
                                                </div>
                                                </React.Fragment>
                                            );
                                        }

                                        if (l.kind === "habbo") {
                                            if (editingSceneHabboId && l.id === "habbo:__active__") return null;

                                            if (l.id === "habbo:__active__") {
                                                if (!hasActiveHabbo || !readyToShowHabbo) return null;
                                                const isLocked = !!(layerById.get(l.id)?.locked || layerById.get(l.id)?.permaLock);
                                                const habboDisplayUrl = getLayerWaterSrc(getLayerOutlineSrc(croppedURL, l.effects), l.effects, l.kind);
                                                const spriteInfo = {
                                                    selected: spriteSelected,
                                                    locked: isLocked,
                                                    flipped: activeSpriteFlipX,
                                                    flipY: activeSpriteFlipY,
                                                    rotation: activeSpriteRotation || 0,
                                                    scale: clampSpriteScale(activeSpriteScale),
                                                    crop: activeSpriteCrop || null,
                                                    sourceSize: activeSpriteSourceSize,
                                                };

                                                return (
                                                    <React.Fragment key={l.id}>
                                                    {tileMode && [-previewSize.w, 0, previewSize.w].flatMap((ox) =>
                                                        [-previewSize.h, 0, previewSize.h].map((oy) => {
                                                            if (ox === 0 && oy === 0) return null;
                                                            const ghostPos = { x: spritePos.x + ox, y: spritePos.y + oy };
                                                            return (
                                                                <div
                                                                    key={`${l.id}_ghost_${ox}_${oy}`}
                                                                    className="absolute left-1/2 top-1/2 pointer-events-none"
                                                                    style={{
                                                                        transform: spriteWrapperTransform(ghostPos, activeSpriteSourceSize, activeSpriteScale, activeSpriteCrop, activeSpriteRotation),
                                                                        imageRendering: "pixelated",
                                                                        zIndex: 10000 + (layerZ.get(l.id) || 0),
                                                                        opacity: layerOpacityVal,
                                                                    }}
                                                                >
                                                                    {renderSpriteFrame({
                                                                        src: habboDisplayUrl,
                                                                        selected: spriteSelected,
                                                                        locked: isLocked,
                                                                        flipped: activeSpriteFlipX,
                                                                        flipY: activeSpriteFlipY,
                                                                        scale: activeSpriteScale,
                                                                        crop: activeSpriteCrop,
                                                                        sourceSize: activeSpriteSourceSize,
                                                                        inCropMode: false,
                                                                    })}
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                    <div
                                                        ref={spriteElRef}
                                                        className={["absolute left-1/2 top-1/2", isLocked ? "pointer-events-none cursor-default" : "touch-none cursor-grab active:cursor-grabbing"].join(" ")}
                                                        style={{
                                                            transform: spriteWrapperTransform(spritePos, activeSpriteSourceSize, activeSpriteScale, activeSpriteCrop, activeSpriteRotation),
                                                            imageRendering: "pixelated",
                                                            zIndex: layerZ.get(l.id) || 0,
                                                        }}
                                                        onPointerDown={(e) => {
                                                            if (e.button !== 0 || isLocked) return;
                                                            setForceUiLocked(false);
                                                            setCropMode(false);
                                                            setContextMenu(null);
                                                            onSpritePointerDown(e, { layerId: l.id });
                                                        }}
                                                        onPointerMove={onSpritePointerMove}
                                                        onPointerUp={onSpritePointerUp}
                                                        onPointerCancel={onSpritePointerUp}
                                                        onPointerLeave={onSpritePointerUp}
                                                    >
                                                        {renderSpriteFrame({
                                                            src: habboDisplayUrl,
                                                            selected: spriteSelected,
                                                            locked: isLocked,
                                                            flipped: activeSpriteFlipX,
                                                            flipY: activeSpriteFlipY,
                                                            scale: activeSpriteScale,
                                                            crop: activeSpriteCrop,
                                                            sourceSize: activeSpriteSourceSize,
                                                            inCropMode: cropMode && spriteSelected && !isLocked,
                                                            layerOpacity: layerOpacityVal,
                                                            onLoad: (e) => {
                                                                const img = e.currentTarget;
                                                                const nextSize = {
                                                                    w: img.naturalWidth || img.width || 0,
                                                                    h: img.naturalHeight || img.height || 0,
                                                                };
                                                                if (
                                                                    nextSize.w &&
                                                                    nextSize.h &&
                                                                    (nextSize.w !== Number(activeSpriteSourceSize?.w || 0) || nextSize.h !== Number(activeSpriteSourceSize?.h || 0))
                                                                ) {
                                                                    setActiveSpriteSourceSize(nextSize);
                                                                }
                                                            },
                                                            children: renderCropHandles(spriteInfo),
                                                        })}
                                                    </div>
                                                    </React.Fragment>
                                                );
                                            }

                                            const spId = l.id.split(":")[1];
                                            const sp = sceneSprites.find((x) => x.id === spId) || null;
                                            if (!sp) return null;

                                            const spUrlRaw2 = getUrlForSnap(sp.snap) + "&crop=true";
                                            const spUrl = getLayerWaterSrc(getLayerOutlineSrc(spUrlRaw2, l.effects), l.effects, l.kind);
                                            const isSelected = activeSceneHabboId === sp.id || selectedLayerIds.includes(`habbo:${sp.id}`);
                                            const isLocked = !!(layerById.get(l.id)?.locked || layerById.get(l.id)?.permaLock);

                                            return (
                                                <React.Fragment key={l.id}>
                                                {tileMode && [-previewSize.w, 0, previewSize.w].flatMap((ox) =>
                                                    [-previewSize.h, 0, previewSize.h].map((oy) => {
                                                        if (ox === 0 && oy === 0) return null;
                                                        const ghostPos = { x: sp.pos.x + ox, y: sp.pos.y + oy };
                                                        return (
                                                            <div
                                                                key={`${l.id}_ghost_${ox}_${oy}`}
                                                                className={["absolute left-1/2 top-1/2", isLocked ? "pointer-events-none" : "touch-none cursor-pointer"].join(" ")}
                                                                style={{
                                                                    transform: spriteWrapperTransform(ghostPos, sp.snap?.sourceSize || sp.sourceSize, sp.snap?.scale, sp.snap?.crop, sp.snap?.rotation || 0),
                                                                    imageRendering: "pixelated",
                                                                    zIndex: 10000 + (layerZ.get(l.id) || 0),
                                                                    opacity: layerOpacityVal,
                                                                }}
                                                                onPointerDown={isLocked ? undefined : (e) => {
                                                                    if (e.button !== 0) return;
                                                                    if (handleSpriteSelectPointerDown(e, `habbo:${sp.id}`)) return;
                                                                    setForceUiLocked(false);
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}

                                                                    setActiveFurniId(null);
                                                                    setSpriteSelected(false);
                                                                    setActiveTextId(null);
                                                                    beginIconLoad();
                                                                    setActiveSceneHabboId(sp.id);
                                                                    setActiveLayerId(`habbo:${sp.id}`);
                                                                    setCropMode(false);
                                                                    setContextMenu(null);

                                                                    const _shsc = clampSpriteScale(sp.snap?.scale || 1);
                                                                    const _shSrc = sp.snap?.sourceSize || sp.sourceSize || { w: 0, h: 0 };
                                                                    const _shCrop = sp.snap?.crop;
                                                                    sceneHabboDragRef.current = {
                                                                        id: sp.id,
                                                                        snap: sp.snap,
                                                                        sc: _shsc,
                                                                        el: e.currentTarget,
                                                                        startClientX: e.clientX,
                                                                        startClientY: e.clientY,
                                                                        startPos: { x: sp.pos.x + ox, y: sp.pos.y + oy },
                                                                        nextX: sp.pos.x + ox,
                                                                        nextY: sp.pos.y + oy,
                                                                        moved: false,
                                                                        raf: 0,
                                                                        sw: (_shSrc.w || 0) * _shsc,
                                                                        sh: (_shSrc.h || 0) * _shsc,
                                                                        cl: (_shCrop?.left || 0) * _shsc,
                                                                        ct: (_shCrop?.top  || 0) * _shsc,
                                                                        rotation: sp.snap?.rotation || 0,
                                                                    };
                                                                    window.addEventListener("pointermove", onSceneHabboPointerMove, { passive: false });
                                                                    window.addEventListener("pointerup", onSceneHabboPointerUp, { passive: false });
                                                                    window.addEventListener("pointercancel", onSceneHabboPointerUp, { passive: false });
                                                                }}
                                                            >
                                                                {renderSpriteFrame({
                                                                    src: spUrl,
                                                                    selected: isSelected,
                                                                    locked: isLocked,
                                                                    flipped: !!sp.snap?.flipX,
                                                                    flipY: !!sp.snap?.flipY,
                                                                    scale: sp.snap?.scale,
                                                                    crop: sp.snap?.crop,
                                                                    sourceSize: sp.snap?.sourceSize || sp.sourceSize,
                                                                    inCropMode: false,
                                                                })}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                                <div
                                                    className={["absolute left-1/2 top-1/2", isLocked ? "pointer-events-none" : "touch-none cursor-pointer"].join(" ")}
                                                    style={{
                                                        transform: spriteWrapperTransform(sp.pos, sp.snap?.sourceSize || sp.sourceSize, sp.snap?.scale, sp.snap?.crop, sp.snap?.rotation || 0),
                                                        imageRendering: "pixelated",
                                                        zIndex: layerZ.get(l.id) || 0,
                                                    }}
                                                    onPointerDown={(e) => {
                                                        if (e.button !== 0 || isLocked) return;
                                                        if (handleSpriteSelectPointerDown(e, `habbo:${sp.id}`)) return;
                                                        setForceUiLocked(false);
                                                        e.preventDefault();
                                                        e.stopPropagation();

                                                        setActiveFurniId(null);
                                                        setSpriteSelected(false);
                                                        setActiveTextId(null);
                                                        beginIconLoad();
                                                        setActiveSceneHabboId(sp.id);
                                                        setActiveLayerId(`habbo:${sp.id}`);
                                                        setCropMode(false);
                                                        setContextMenu(null);

                                                        const _shsc = clampSpriteScale(sp.snap?.scale || 1);
                                                        const _shSrc = sp.snap?.sourceSize || sp.sourceSize || { w: 0, h: 0 };
                                                        const _shCrop = sp.snap?.crop;
                                                        sceneHabboDragRef.current = {
                                                            id: sp.id,
                                                            snap: sp.snap,
                                                            sc: _shsc,
                                                            el: e.currentTarget,
                                                            startClientX: e.clientX,
                                                            startClientY: e.clientY,
                                                            startPos: { ...sp.pos },
                                                            nextX: sp.pos.x,
                                                            nextY: sp.pos.y,
                                                            moved: false,
                                                            raf: 0,
                                                            sw: (_shSrc.w || 0) * _shsc,
                                                            sh: (_shSrc.h || 0) * _shsc,
                                                            cl: (_shCrop?.left || 0) * _shsc,
                                                            ct: (_shCrop?.top  || 0) * _shsc,
                                                            rotation: sp.snap?.rotation || 0,
                                                        };

                                                        window.addEventListener("pointermove", onSceneHabboPointerMove, { passive: false });
                                                        window.addEventListener("pointerup", onSceneHabboPointerUp, { passive: false });
                                                        window.addEventListener("pointercancel", onSceneHabboPointerUp, { passive: false });
                                                    }}
                                                >
                                                    {renderSpriteFrame({
                                                        src: spUrl,
                                                        selected: isSelected,
                                                        locked: isLocked,
                                                        flipped: !!sp.snap?.flipX,
                                                        flipY: !!sp.snap?.flipY,
                                                        scale: sp.snap?.scale,
                                                        crop: sp.snap?.crop,
                                                        sourceSize: sp.snap?.sourceSize || sp.sourceSize,
                                                        inCropMode: cropMode && isSelected && !isLocked,
                                                        layerOpacity: layerOpacityVal,
                                                        onLoad: (e) => {
                                                            const img = e.currentTarget;
                                                            const nextSize = {
                                                                w: img.naturalWidth || img.width || 0,
                                                                h: img.naturalHeight || img.height || 0,
                                                            };
                                                            if (
                                                                nextSize.w &&
                                                                nextSize.h &&
                                                                (
                                                                    nextSize.w !== Number(sp.snap?.sourceSize?.w || sp.sourceSize?.w || 0) ||
                                                                    nextSize.h !== Number(sp.snap?.sourceSize?.h || sp.sourceSize?.h || 0)
                                                                )
                                                            ) {
                                                                updateSceneSprite(sp.id, { sourceSize: nextSize });
                                                                patchSceneHabboSnapshot(sp.id, { sourceSize: nextSize });
                                                            }
                                                        },
                                                        children: renderCropHandles({
                                                            selected: isSelected,
                                                            locked: isLocked,
                                                            flipped: !!sp.snap?.flipX,
                                                            flipY: !!sp.snap?.flipY,
                                                            rotation: sp.snap?.rotation || 0,
                                                            scale: clampSpriteScale(sp.snap?.scale),
                                                            crop: sp.snap?.crop || null,
                                                            sourceSize: sp.snap?.sourceSize || sp.sourceSize,
                                                        }),
                                                    })}
                                                </div>
                                                </React.Fragment>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>

                                {/* Speech bubbles rendered above the preview content. */}
                                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 35 }} data-capture-hide="1">
                                    {(() => {
                                        const speechLayer = layers.find((l) => l.id === "speech:__all__");
                                        const speechVis = speechLayer ? speechLayer.visible !== false : true;
                                        const bubbles = speechVis && Array.isArray(speechBubbles) ? speechBubbles.slice(-5) : [];
                                        // Use canvas CSS size (not rendered/zoomed size) - absolute children position in pre-zoom space
                                        const previewW = Math.max(1, previewSize.w);

                                        if (!bubbles.length || !previewW) return null;

                                        const scale = 0.5;
                                        const rowH = Math.round(40 * scale);

                                        let topHabboY = null;
                                        const approxHabboH = 100;
                                        sceneSprites.forEach((sp) => {
                                            const cy = previewSize.h / 2 + (cameraPos.y || 0) + sp.pos.y;
                                            const t = cy - approxHabboH / 2;
                                            if (topHabboY === null || t < topHabboY) topHabboY = t;
                                        });

                                        const gapY = 2;
                                        const minTopMargin = previewSize.h * 0.2; // 20% cap
                                        const stackOffset = Math.max(0, bubbles.length - 1) * (rowH + gapY);
                                        const minBaseY = minTopMargin + stackOffset;

                                        const baseY = topHabboY !== null ? Math.max(minBaseY, Math.round(topHabboY) - 35) : Math.max(minBaseY, 120);

                                        const leftW = Math.round(33 * scale);
                                        const rightW = Math.round(33 * scale);
                                        const padX = Math.max(8, Math.round(16 * scale));
                                        const maxMidW = Math.max(20, Math.floor(previewW - 10 - (leftW + rightW)));

                                        return bubbles.map((b, i) => {
                                            const sp = sceneSprites.find((x) => x.id === b.sceneHabboId) || null;
                                            const username = (((b.displayName && b.displayName !== "Habbo")
                                                ? b.displayName
                                                : sp?.snap?.habboName || "Habbo") + "").trim();
                                            const msg = String(b.text || "").replace(/\s+/g, " ").trim();
                                            const namePart = `${username}: `;
                                            const regFont = "9px VolterGoldfish, sans-serif";
                                            const boldFont = "9px VolterBoldGoldfish, VolterGoldfish, sans-serif";
                                            const textW = measureTextW(namePart, boldFont) + measureTextW(msg, b.shout ? boldFont : regFont);
                                            const midW = Math.min(maxMidW, Math.max(20, textW + padX * 2 + Math.round(14 * scale)));
                                            const totalW = leftW + midW + rightW;
                                            const spriteX = Number(sp?.pos?.x || 0);
                                            const xCenter = Math.round(previewW / 2 + (cameraPos.x || 0) + spriteX);
                                            let left = Math.round(xCenter - totalW / 2);
                                            const minL = 5;
                                            const maxL = Math.max(minL, Math.round(previewW - 5 - totalW));

                                            if (left < minL) left = minL;
                                            if (left > maxL) left = maxL;

                                            const y = baseY - (bubbles.length - 1 - i) * (rowH + gapY);
                                            const frnt = b.assets?.frnt || speechPartsBase.frnt;
                                            const mid = b.assets?.mid || speechPartsBase.mid;
                                            const end = b.assets?.end || speechPartsBase.end;

                                            return (
                                                <div
                                                    key={b.id}
                                                    className="absolute"
                                                    style={{
                                                        left,
                                                        top: y,
                                                        height: rowH,
                                                        width: totalW,
                                                        transform: "translateZ(0)",
                                                        imageRendering: "pixelated",
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    {/* Frame layer - every bubble part (frnt / mid / end) sits BEHIND the text */}
                                                    <div className="flex items-stretch absolute inset-0" style={{ height: rowH + 1, width: totalW, imageRendering: "pixelated", zIndex: 0 }}>
                                                        <img src={frnt} alt="" draggable={false} style={{ width: leftW, height: rowH + 1, imageRendering: "pixelated", display: "block" }} />
                                                        <div
                                                            style={{
                                                                width: midW,
                                                                height: rowH + 1,
                                                                boxSizing: "border-box",
                                                                backgroundImage: `url(${mid})`,
                                                                backgroundRepeat: "repeat-x",
                                                                backgroundSize: `${Math.round(4 * scale)}px ${rowH + 1}px`,
                                                                imageRendering: "pixelated",
                                                            }}
                                                        />
                                                        <img src={end} alt="" draggable={false} style={{ width: rightW, height: rowH + 1, imageRendering: "pixelated", display: "block" }} />
                                                    </div>
                                                    {/* Text layer - always painted on top of the whole frame. Full-width
                                                        overlay with left/right padding so the first letter starts cleanly
                                                        past the left cap and is never clipped or covered. */}
                                                    <div
                                                        className="absolute flex items-center"
                                                        style={{
                                                            left: 0,
                                                            top: 0,
                                                            width: totalW,
                                                            height: rowH,
                                                            zIndex: 2,
                                                            overflow: "hidden",
                                                            whiteSpace: "nowrap",
                                                            boxSizing: "border-box",
                                                            paddingLeft: leftW + padX,
                                                            paddingRight: rightW + padX,
                                                            justifyContent: "flex-start",
                                                            textAlign: "left",
                                                        }}
                                                    >
                                                        <div className="text-[9px] font-volter" style={{ width: "100%", minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", lineHeight: `${rowH}px`, textAlign: "left" }}>
                                                            <span className="font-volterBold">{username}</span>
                                                            <span className="font-volter">: </span>
                                                            <span className={b.shout ? "font-volterBold" : "font-volter"}>{msg}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                {/* Image Border live preview overlay - always top layer, drawn separately in canvas export */}
                                {imageBorderEnabled && (
                                    <div
                                        className="absolute inset-0 pointer-events-none"
                                        style={{
                                            boxSizing: "border-box",
                                            border: `${imageBorderWidth}px solid ${imageBorderColor}`,
                                            zIndex: 40,
                                        }}
                                    />
                                )}

                                {showLoadScenePrompt && pendingLoad && (
                                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
                                        <div className="border border-black bg-offwhite shadow p-4 w-[min(420px,92%)]">
                                            <div className="font-['Jersey_10'] uppercase text-lg mb-2">Add to current scene?</div>
                                            <div className="text-xs mb-4 opacity-80">
                                                You already have a sprite(s) in this scene. Do you want to add <b>{pendingLoad.name}</b> to this scene, or create their own saved scene?
                                            </div>

                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    className="flex-1 h-10 border border-black bg-yellow-500 hover:bg-yellow-400 font-['Jersey_10'] uppercase text-sm tracking-widest"
                                                    onClick={() => {
                                                        setShowLoadScenePrompt(false);
                                                        const p = pendingLoad;
                                                        setPendingLoad(null);
                                                        addHabboToCurrentScene(p.name, p.hotel);
                                                    }}
                                                >
                                                    Add to scene
                                                </button>

                                                <button
                                                    type="button"
                                                    className="flex-1 h-10 border border-black bg-gray-200 hover:bg-gray-300 font-['Jersey_10'] uppercase text-sm tracking-widest"
                                                    onClick={() => {
                                                        setShowLoadScenePrompt(false);
                                                        const p = pendingLoad;
                                                        setPendingLoad(null);
                                                        createNewSceneForHabbo(p.name, p.hotel);
                                                    }}
                                                >
                                                    New Scene
                                                </button>

                                                <button
                                                    type="button"
                                                    className="w-16 h-10 border border-black bg-red-500 text-white hover:bg-red-400 font-['Jersey_10'] uppercase text-sm tracking-widest"
                                                    onClick={() => {
                                                        setShowLoadScenePrompt(false);
                                                        setPendingLoad(null);
                                                    }}
                                                >
                                                    X
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showAlreadyInScenePrompt && pendingLoad && (
                                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
                                        <div className="border border-black bg-offwhite shadow p-4 w-[min(420px,92%)]">
                                            <div className="font-['Jersey_10'] uppercase text-lg mb-2">Already in this scene</div>

                                            <div className="text-xs mb-4 opacity-80">
                                                <b>{pendingLoad.name}</b> is already in this scene. Would you like to create a new scene with <b>{pendingLoad.name}</b> instead?
                                            </div>

                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    className="flex-1 h-10 border border-black bg-yellow-500 hover:bg-yellow-400 font-['Jersey_10'] uppercase text-sm tracking-widest"
                                                    onClick={() => {
                                                        setShowAlreadyInScenePrompt(false);
                                                        const p = pendingLoad;
                                                        setPendingLoad(null);
                                                        createNewSceneForHabbo(p.name, p.hotel);
                                                    }}
                                                >
                                                    Create new scene
                                                </button>

                                                <button
                                                    type="button"
                                                    className="w-24 h-10 border border-black bg-gray-200 hover:bg-gray-300 font-['Jersey_10'] uppercase text-sm tracking-widest"
                                                    onClick={() => {
                                                        setShowAlreadyInScenePrompt(false);
                                                        setPendingLoad(null);
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}


                            </div>
                        </div>

                        {/* ===== RIGHT PANEL (IconRows + Layers) ===== */}
                        {!isCapturing && (
                            <div className="relative z-[30] flex flex-col bg-offwhite border-l-2 border-black shrink-0 w-[300px] min-h-0" data-capture-hide="1">
                                {/* IconRows - always visible, spinner while repopulating */}
                                <div className="overflow-y-auto habbo-scroll shrink-0" style={{ maxHeight: "300px" }}>
                                        {!iconRowsLoaded ? (
                                            <div className="flex items-center justify-center" style={{ minHeight: "300px" }}>
                                                <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" />
                                            </div>
                                        ) : (
                                        <div className="p-2" style={{ minHeight: "300px" }}>
                                            {activeLayerId?.startsWith("habbo:") && !activeFurniId && (
                                                <div className={(controlsLocked || multiSelectActive) ? "opacity-40 pointer-events-none cursor-not-allowed" : ""} title={multiSelectActive ? "Pose controls are disabled while multiple sprites are selected" : (uiLocked ? "Layer locked" : undefined)}>
                                                    <IconRow title="Body Rotation" selectedLabel={String(direction)}>
                                                        {bodyDirOptions.map((d) => (<IconButton key={`body-${d}`} selected={direction === d} title={`Direction ${d}`} onClick={() => setBodyDirection(d)} src={bodyRotIconUrl(d)} imgStyle={iconStyleFor("body")} />))}
                                                    </IconRow>
                                                    <IconRow title="Head Rotation" selectedLabel={String(headDirection)}>
                                                        {headDirOptions.map((d) => (<IconButton key={`head-${d}`} selected={headDirection === d} disabled={headRotationLocked} title={`Head ${d}`} onClick={() => setHeadDirection(d)} src={headRotIconUrl(d)} imgStyle={iconStyleFor("head")} />))}
                                                    </IconRow>
                                                    <IconRow title="Actions" selectedLabel={selectedActionLabel}>
                                                        {ACTIONS.map((a) => { const disabled = actionsLocked || (!!crr && (a.value === "sit" || a.value === "lay")); return (<IconButton key={a.value} selected={action === a.value} disabled={disabled} title={a.label} onClick={() => setActionSafe(a.value)} src={iconUrl({ action: a.value })} imgStyle={iconStyleFor("actions", a.value)} />); })}
                                                    </IconRow>
                                                    <IconRow title="Expressions" selectedLabel={selectedGestureLabel}>
                                                        {GESTURES.map((g) => (<IconButton key={g.value} selected={gesture === g.value} disabled={expressionsLocked} title={g.label} onClick={() => setGesture(g.value)} src={iconUrl({ gesture: g.value })} imgStyle={iconStyleFor("expressions")} />))}
                                                    </IconRow>
                                                    {!isSitOrLay && (
                                                        <IconRow title="Carry Item" selectedLabel={selectedCarryLabel}>
                                                            {CARRY_ITEMS.map((it) => { const disabled = !!it.value && (!canHoldAnyItem || (it.value === "11" && !canHoldFishingRod)); const previewCarry = disabled ? null : it.value || null; const offsetKey = it.value === "" ? "none" : "item"; return <IconButton key={`crr-${it.value || "none"}`} selected={crr === it.value} disabled={disabled} title={it.label} onClick={() => setCarrySafe(it.value)} src={iconUrl({ crr: previewCarry })} imgStyle={iconStyleFor("carry", offsetKey)} />; })}
                                                        </IconRow>
                                                    )}
                                                    <div className="flex flex-row">
                                                        <IconRow title="Shadow" selectedLabel={shadow ? "On" : "Off"} noScroll>
                                                            <IconButton selected={shadow === true} title="Shadow On" onClick={() => setShadowSafe(true)} src={iconUrl({ shadow: true })} imgStyle={iconStyleFor("shadow")} />
                                                            <IconButton selected={shadow === false} title="Shadow Off" onClick={() => setShadowSafe(false)} src={iconUrl({ shadow: false })} imgStyle={iconStyleFor("shadow")} />
                                                        </IconRow>
                                                        <IconRow title="Frankify" selectedLabel={frankify ? "On" : "Off"} noScroll>
                                                            <IconButton selected={frankify === false} title="Frankify Off" onClick={() => setFrankify(false)} src={iconUrl({ custom: null })} imgStyle={iconStyleFor("frankify")} />
                                                            <IconButton selected={frankify === true} title="Frankify On" onClick={() => setFrankify(true)} src={iconUrl({ custom: "frank_hat" })} imgStyle={iconStyleFor("frankify")} />
                                                        </IconRow>
                                                    </div>
                                                </div>
                                            )}
                                            {activeFurniId && (
                                                <div className={(uiLocked || multiSelectActive) ? "opacity-40 pointer-events-none cursor-not-allowed" : ""} title={multiSelectActive ? "Furni controls are disabled while multiple sprites are selected" : (uiLocked ? "Layer locked" : undefined)}>
                                                    {(() => {
                                                        const sp = furniSprites.find((x) => x.id === activeFurniId) || null;
                                                        const code = String(sp?.code || "").trim();
                                                        const hex = sp?.hex || null;
                                                        const rotI = Number.isFinite(sp?.rotI) ? sp.rotI : 0;
                                                        const stateI = Number.isFinite(sp?.stateI) ? sp.stateI : 0;
                                                        const frameI = Number.isFinite(sp?.frameI) ? sp.frameI : 0;
                                                        const indices = [0, 1, 2, 3, 4, 5, 6];
                                                        const furniIconUrl = (dir, st, fr) => hex
                                                            ? buildColoredFurniUrl(code, dir, st, fr, hex)
                                                            : buildFoFurniUrl(code, dir, st, fr);
                                                        return (
                                                            <>
                                                                <IconRow title="Direction" selectedLabel={String(rotI)}>
                                                                    {indices.map((i) => (<IconButton key={`fd-${i}`} selected={i === rotI} title={`Direction ${i}`} onClick={() => setActiveFurniRotationIndex(i)} src={furniIconUrl(i, stateI, frameI)} imgStyle={{ imageRendering: "pixelated" }} />))}
                                                                </IconRow>
                                                                <IconRow title="State" selectedLabel={String(stateI)}>
                                                                    {indices.map((i) => (<IconButton key={`fs-${i}`} selected={i === stateI} title={`State ${i}`} onClick={() => setActiveFurniStateIndex(i)} src={furniIconUrl(rotI, i, frameI)} imgStyle={{ imageRendering: "pixelated" }} />))}
                                                                </IconRow>
                                                                <IconRow title="Frame" selectedLabel={String(frameI)}>
                                                                    {indices.map((i) => (<IconButton key={`ff-${i}`} selected={i === frameI} title={`Frame ${i}`} onClick={() => setActiveFurniFrameIndex(i)} src={furniIconUrl(rotI, stateI, i)} imgStyle={{ imageRendering: "pixelated" }} />))}
                                                                </IconRow>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                        )}
                                    </div>
                                {/* Divider + Layers header */}
                                <div className="border-t-2 border-black shrink-0 px-3 py-1.5 bg-offwhite flex items-center gap-2">
                                    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 opacity-60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M12 3 3 8l9 5 9-5-9-5Z" />
                                        <path d="M3 12l9 5 9-5" />
                                        <path d="M3 16l9 5 9-5" />
                                    </svg>
                                    <span className="font-['Jersey_10'] uppercase text-sm tracking-widest">Layers</span>
                                </div>
                                <LayersDropdown
                                    disabled={false}
                                    layers={[...layers].reverse().filter((l) => l.kind !== 'moodlight' || l.visible !== false)}
                                    onMove={moveLayer}
                                    onToggleVisible={toggleLayerVisible}
                                    onToggleLocked={toggleLayerLocked}
                                    selectedId={activeLayerId}
                                    selectedIds={selectedLayerIds}
                                    onSelectLayer={(id, shift) => shift ? toggleLayerSelection(id) : selectLayerById(id)}
                                    onMoveStep={moveLayerByStep}
                                    onRename={(id, name) => {
                                        pushHistory();
                                        if (isUserLayerId(id)) {
                                            const spriteId = spriteIdFromLayerId(id);
                                            setSprites((prev) => prev.map((s) => s.id === spriteId ? { ...s, layerName: name } : s));
                                        }
                                    }}
                                    onPropertiesOpen={() => pushHistory()}
                                    onOpacityChange={setLayerOpacity}
                                    onEffectChange={setLayerEffect}
                                    onRemoveEffect={removeLayerEffect}
                                    alwaysOpen
                                />
                            </div>
                        )}
                        </div>

                        <div className="w-full">
                            <div className="flex flex-col sm:flex-row gap-2">
                                {/* Space under workspace */}
                            </div>
                        </div>

                        {/* Custom Background Fit Modal */}
                        {showBgFitModal && pendingBgFile && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center">
                            <div className="absolute inset-0 bg-black/60" onClick={() => { setShowBgFitModal(false); if (pendingBgFile) { URL.revokeObjectURL(pendingBgFile.url); setPendingBgFile(null); } }} />
                            <div className="relative bg-offwhite border-2 border-gray-800 p-5 w-[min(420px,94%)] z-[101]" style={{ boxShadow: "4px 4px 0 0 rgba(0,0,0,0.5)" }}>
                        <div className="font-['Jersey_10'] uppercase text-lg mb-1 text-maroon tracking-widest">Custom Background</div>
                        <div className="text-xs opacity-60 mb-4 font-['Jersey_10']">
                            Image: {pendingBgFile.w}×{pendingBgFile.h}px &nbsp;·&nbsp; Canvas: {previewSize.w}×{previewSize.h}px
                        </div>

                        {pendingBgFile.w !== previewSize.w || pendingBgFile.h !== previewSize.h ? (
                            <div className="mb-4">
                                <div className="font-['Jersey_10'] uppercase text-sm mb-2 opacity-70">Dimensions differ - would you like to:</div>
                                <button
                                    type="button"
                                    onClick={() => applyCustomBg({ fit: customBgFit, createNew: true })}
                                    className="w-full h-10 border border-black bg-yellow-400 hover:bg-yellow-300 font-['Jersey_10'] uppercase text-sm mb-2"
                                >
                                    Resize canvas to {pendingBgFile.w}×{pendingBgFile.h}px
                                </button>
                            </div>
                        ) : null}

                        <div className="font-['Jersey_10'] uppercase text-sm mb-2 opacity-70">Fit mode:</div>
                        <div className="grid grid-cols-2 gap-2 mb-4">
                            {[
                                { value: "cover", label: "Cover", desc: "Fill canvas, crop edges" },
                                { value: "contain", label: "Contain", desc: "Fit inside canvas" },
                                { value: "stretch", label: "Stretch", desc: "Stretch to fill exactly" },
                                { value: "tile", label: "Tile", desc: "Repeat as pattern" },
                            ].map(({ value, label, desc }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setCustomBgFit(value)}
                                    className={["border p-2 text-left transition-colors font-['Jersey_10']", customBgFit === value ? "border-black bg-gold" : "border-black/30 bg-gray-100 hover:bg-gray-200"].join(" ")}
                                >
                                    <div className="uppercase text-sm">{label}</div>
                                    <div className="text-sm opacity-60">{desc}</div>
                                </button>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => applyCustomBg({ fit: customBgFit, createNew: false })}
                                className="flex-1 h-10 border border-black bg-maroon text-white hover:opacity-90 font-['Jersey_10'] uppercase text-sm"
                            >
                                Apply Background
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowBgFitModal(false); if (pendingBgFile) { URL.revokeObjectURL(pendingBgFile.url); setPendingBgFile(null); } }}
                                className="w-16 h-10 border border-black bg-gray-200 hover:bg-gray-300 font-['Jersey_10'] uppercase text-sm"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
                )}

                        {/* Reusable Assets Modal */}
                        {showAssetsModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center">
                            <div className="absolute inset-0 bg-black/60" onClick={() => setShowAssetsModal(false)} />
                    <div className="relative bg-offwhite border-2 border-gray-800 w-[min(860px,96%)] max-h-[80vh] flex flex-col z-[101]" style={{ boxShadow: "4px 4px 0 0 rgba(0,0,0,0.5)" }}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 bg-coal text-offwhite">
                            <span className="font-['Jersey_10'] uppercase tracking-widest text-lg">Assets Library</span>
                            <button type="button" onClick={() => setShowAssetsModal(false)} className="font-['Jersey_10'] text-sm opacity-60 hover:opacity-100">✕</button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-3">
                            {!assetsManifest ? (
                                <div className="text-center py-8 font-['Jersey_10'] uppercase text-sm opacity-40">Loading assets…</div>
                            ) : assetsManifest.categories.length === 0 ? (
                                <div className="text-center py-8 font-['Jersey_10'] uppercase text-sm opacity-40">No assets found</div>
                            ) : (
                                assetsManifest.categories.filter((cat) => !resolvedConfig.hiddenAssetCategories.includes(cat.name)).map((cat) => (
                                    <div key={cat.name} className="mb-2 border border-black/20">
                                        <button
                                            type="button"
                                            onClick={() => setOpenAssetCategory(openAssetCategory === cat.name ? null : cat.name)}
                                            className="w-full flex items-center justify-between px-3 py-2 bg-coal/90 text-offwhite hover:bg-coal font-['Jersey_10'] uppercase text-sm tracking-widest"
                                        >
                                            <span>{cat.name}</span>
                                            <svg viewBox="0 0 24 24" className={`w-4 h-4 transition-transform ${openAssetCategory === cat.name ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <path d="M6 9l6 6 6-6" />
                                            </svg>
                                        </button>
                                        {openAssetCategory === cat.name && (
                                            <div className="grid grid-cols-6 gap-2 p-3 bg-white">
                                                {cat.images.map((img) => (
                                                    <button
                                                        key={img.file}
                                                        type="button"
                                                        title={img.name || img.file}
                                                        onClick={() => addAssetToScene(`${process.env.PUBLIC_URL}/img/imagerassets/${cat.name}/${img.file}`)}
                                                        className="border border-black/20 hover:border-black bg-gray-50 hover:bg-gray-100 flex flex-col items-center gap-1 p-1 transition-colors"
                                                    >
                                                        <img
                                                            src={`${process.env.PUBLIC_URL}/img/imagerassets/${cat.name}/${img.file}`}
                                                            alt={img.name || img.file}
                                                            className="w-full h-16 object-contain"
                                                            style={{ imageRendering: "pixelated" }}
                                                        />
                                                        <span className="font-['Jersey_10'] text-sm uppercase opacity-60 truncate w-full text-center">{img.name || img.file.replace(/\.[^.]+$/, "")}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
                )}

                        {/* Export Modal */}
                        {showExportModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center">
                            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowExportModal(false)} />
                            <div
                                className="relative bg-offwhite border-2 border-gray-800 p-6 w-full mx-4 z-[101]"
                                style={{ boxShadow: "0 4px 0 0 rgba(0, 0, 0, 0.5)", maxWidth: 420 }}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-maroon uppercase font-['Jersey_10'] text-xl tracking-[1px]">Export Image</h2>
                                    <button type="button" onClick={() => setShowExportModal(false)} className="text-sm opacity-40 hover:opacity-100 leading-none">✕</button>
                                </div>
                                <hr className="border-gold mb-4" />

                                <div className="flex flex-col gap-4">
                                    {/* File name */}
                                    <div>
                                        <label className="block font-['Jersey_10'] uppercase text-sm mb-1 opacity-70">File Name</label>
                                        <input
                                            type="text"
                                            value={exportFileName}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setExportFileName(v);
                                                if (activeSceneId) exportSettingsPerSceneRef.current[activeSceneId] = { ...exportSettingsPerSceneRef.current[activeSceneId], fileName: v, scale: exportScale, format: exportFormat, crispText: exportCrispText };
                                            }}
                                            className="w-full h-10 border border-black px-3 bg-white text-sm font-mono"
                                            placeholder={`franklyorigins_${Date.now()}`}
                                        />
                                        <p className="text-xs opacity-50 mt-0.5">File extension is added automatically</p>
                                    </div>

                                    {/* Format */}
                                    <div>
                                        <label className="block font-['Jersey_10'] uppercase text-sm mb-1 opacity-70">Format</label>
                                        <div className="flex gap-2">
                                            {["png", "webp"].map((f) => {
                                                const isDisabled = f === "webp";
                                                return (
                                                <button
                                                    key={f}
                                                    type="button"
                                                    disabled={isDisabled}
                                                    onClick={() => {
                                                        if (isDisabled) return;
                                                        setExportFormat(f);
                                                        if (activeSceneId) exportSettingsPerSceneRef.current[activeSceneId] = { ...exportSettingsPerSceneRef.current[activeSceneId], format: f, scale: exportScale, fileName: exportFileName, crispText: exportCrispText };
                                                    }}
                                                    className={[
                                                        "flex-1 h-10 border font-['Jersey_10'] uppercase text-sm tracking-widest",
                                                        isDisabled ? "border-black/20 bg-gray-100 text-black/30 cursor-not-allowed" : exportFormat === f ? "border-gray-800 bg-gold" : "border-black bg-white hover:bg-gray-100",
                                                    ].join(" ")}
                                                >
                                                    {f.toUpperCase()}
                                                </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Scale */}
                                    <div>
                                        <label className="block font-['Jersey_10'] uppercase text-sm mb-1 opacity-70">
                                            Scale - {previewSize.w * parseInt(exportScale)} × {previewSize.h * parseInt(exportScale)}px
                                        </label>
                                        <div className="flex gap-2">
                                            {[["1", "1×"], ["2", "2×"], ["4", "4×"]].map(([val, label]) => (
                                                <button
                                                    key={val}
                                                    type="button"
                                                    onClick={() => {
                                                        setExportScale(val);
                                                        if (activeSceneId) exportSettingsPerSceneRef.current[activeSceneId] = { ...exportSettingsPerSceneRef.current[activeSceneId], scale: val, format: exportFormat, fileName: exportFileName, crispText: exportCrispText };
                                                    }}
                                                    className={[
                                                        "flex-1 h-10 border font-['Jersey_10'] uppercase text-sm tracking-widest",
                                                        exportScale === val ? "border-gray-800 bg-gold" : "border-black bg-white hover:bg-gray-100",
                                                    ].join(" ")}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Crisp Text */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-['Jersey_10'] uppercase text-sm opacity-70">Crisp Text</p>
                                            <p className="text-xs opacity-40 mt-0.5">When on, renders all text without antialiasing. When off, only pixelated text renders without antialiasing (such as Volter & Jersey).</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const next = !exportCrispText;
                                                setExportCrispText(next);
                                                if (activeSceneId) exportSettingsPerSceneRef.current[activeSceneId] = { ...exportSettingsPerSceneRef.current[activeSceneId], crispText: next, scale: exportScale, format: exportFormat, fileName: exportFileName };
                                            }}
                                            className={[
                                                "w-10 h-6 pb-2 px-2 pt-1 border font-['Jersey_10'] uppercase text-xs tracking-widest transition-colors",
                                                exportCrispText ? "border-gray-800 bg-gold" : "border-black bg-white hover:bg-gray-100",
                                            ].join(" ")}
                                        >
                                            {exportCrispText ? "ON" : "OFF"}
                                        </button>
                                    </div>

                                    {/* Custom Sprite Render - uploaded/asset images only (staff/Sulake) */}
                                    {!resolvedConfig.disableUpload && (
                                        <div>
                                            <p className="font-['Jersey_10'] uppercase text-sm opacity-70">Custom Sprite Render</p>
                                            <p className="text-xs opacity-40 mt-0.5 mb-1.5">How uploaded images & library assets are scaled on export. Pixelated keeps hard edges (best for pixel art); Smooth anti-aliases (best for photos & logos). Does not affect Habbo or furni sprites.</p>
                                            <div className="flex gap-2">
                                                {[["pixelated", "Pixelated"], ["smooth", "Smooth"]].map(([val, label]) => (
                                                    <button
                                                        key={val}
                                                        type="button"
                                                        onClick={() => {
                                                            setExportCustomSpriteRender(val);
                                                            if (activeSceneId) exportSettingsPerSceneRef.current[activeSceneId] = { ...exportSettingsPerSceneRef.current[activeSceneId], customSpriteRender: val, crispText: exportCrispText, scale: exportScale, format: exportFormat, fileName: exportFileName };
                                                        }}
                                                        className={[
                                                            "flex-1 h-10 border font-['Jersey_10'] uppercase text-sm tracking-widest",
                                                            exportCustomSpriteRender === val ? "border-gray-800 bg-gold" : "border-black bg-white hover:bg-gray-100",
                                                        ].join(" ")}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowExportModal(false)}
                                        className="flex-1 h-10 border border-black bg-white hover:bg-gray-100 font-['Jersey_10'] uppercase text-sm tracking-widest"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isCapturing}
                                        onClick={() => {
                                            if (activeSceneId) {
                                                exportSettingsPerSceneRef.current[activeSceneId] = {
                                                    fileName: exportFileName,
                                                    scale: exportScale,
                                                    format: exportFormat,
                                                    crispText: exportCrispText,
                                                    customSpriteRender: exportCustomSpriteRender,
                                                };
                                            }
                                            setShowExportModal(false);
                                            savePreviewPng({
                                                scale: parseInt(exportScale),
                                                format: exportFormat,
                                                fileName: exportFileName,
                                                crispText: exportCrispText,
                                                customSpriteRender: exportCustomSpriteRender,
                                            });
                                        }}
                                        className="flex-1 h-10 border border-gray-800 bg-maroon text-white hover:opacity-90 font-['Jersey_10'] uppercase text-sm tracking-widest disabled:opacity-40"
                                    >
                                        {isCapturing ? "Exporting..." : "Export"}
                                    </button>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Clear Current Scene Confirmation Modal */}
                        {showClearSceneModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center">
                            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowClearSceneModal(false)} />
                            <div
                                className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-md w-full mx-4 z-[101]"
                                style={{ boxShadow: "0 4px 0 0 rgba(0, 0, 0, 0.5)" }}
                            >
                                <div className="flex items-center gap-2 mb-4">
                                    <h2 className="text-maroon uppercase font-['Jersey_10'] text-xl tracking-[1px]">
                                        Clear Current Scene
                                    </h2>
                                </div>

                                <hr className="border-gold mb-4" />

                                <div className="mb-6 space-y-3">
                                    <p className="text-gray-800 font-['Jersey_10'] text-lg">
                                        This will remove all Habbos, furni, and speech bubbles from the current scene.
                                    </p>
                                    <p className="text-gray-800 font-['Jersey_10'] text-lg">
                                        Your other saved scenes will not be affected.
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3 justify-between">
                                    <button
                                        onClick={() => setShowClearSceneModal(false)}
                                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-lg tracking-[1px] transition-colors order-2 sm:order-1"
                                        style={{ boxShadow: "0 3px 0 0 rgba(0, 0, 0, 0.3)" }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmClearScene}
                                        className="text-white px-6 py-2 font-['Jersey_10'] uppercase text-lg tracking-[1px] transition-opacity order-1 sm:order-2 hover:opacity-90"
                                        style={{ boxShadow: "0 3px 0 0 rgba(0, 0, 0, 0.3)", backgroundColor: "#5A2929" }}
                                    >
                                        Clear Scene
                                    </button>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Updates Modal */}
                        {showUpdatesModal && updatesData && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center">
                            <div className="absolute inset-0 bg-black/60" onClick={() => {
                                const meta = readMeta();
                                writeMeta({ ...meta, lastDismissedUpdateAt: updatesData.versions[0].updatedAt, dirty: true });
                                touchMetaDirty();
                                setShowUpdatesModal(false);
                            }} />
                            <div
                                className="relative bg-offwhite border-2 border-black w-[min(520px,94%)] flex flex-col z-[101]"
                                style={{ boxShadow: "4px 4px 0 0 rgba(0,0,0,0.9)", maxHeight: "80%" }}
                            >
                                {/* Header */}
                                <div className="bg-coal text-offwhite px-4 py-2 flex items-center justify-between shrink-0">
                                    <span className="font-['Jersey_10'] uppercase text-lg tracking-widest">What's New</span>
                                    <button
                                        type="button"
                                        className="font-['Jersey_10'] text-offwhite/60 hover:text-offwhite leading-none text-lg"
                                        onClick={() => {
                                            const meta = readMeta();
                                            writeMeta({ ...meta, lastDismissedUpdateAt: updatesData.versions[0].updatedAt, dirty: true });
                                            touchMetaDirty();
                                            setShowUpdatesModal(false);
                                        }}
                                    >✕</button>
                                </div>

                                {/* Scrollable versioned list */}
                                <div className="overflow-y-scroll flex-1 max-h-[400px] habbo-scroll-v">
                                    {updatesData.versions.map((ver, vi) => {
                                        const typeOrder = { new: 0, update: 1, fix: 2 };
                                        const visibleEntries = (ver.entries || []).filter((e) => isStaffUpdatesPage || !e.staff);
                                        if (visibleEntries.length === 0) return null;
                                        const sorted = [...visibleEntries].sort((a, b) =>
                                            (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9)
                                        );
                                        return (
                                            <div key={vi} className={vi > 0 ? "border-t border-black/10" : ""}>
                                                {/* Version label */}
                                                <div className="px-4 pt-3 pb-2 flex items-center gap-2">
                                                    <span className="font-['Jersey_10'] uppercase text-xs tracking-widest bg-gold text-coal px-2 py-0.5">{ver.version}</span>
                                                </div>
                                                {/* Entries */}
                                                <div className="px-4 pb-3">
                                                    {sorted.map((entry, i) => (
                                                        <div key={i} className="flex items-center gap-2 py-1 border-b border-black/30">
                                                            <div className="shrink-0 w-14 flex flex-col gap-0.5">
                                                                <span className={[
                                                                    "text-center font-['Jersey_10'] uppercase text-xs tracking-widest py-0.5 border",
                                                                    entry.type === "new" ? "bg-green-100 border-green-400 text-green-700"
                                                                        : entry.type === "update" ? "bg-blue-50 border-blue-400 text-blue-700"
                                                                        : "bg-yellow-50 border-yellow-400 text-yellow-700",
                                                                ].join(" ")}>
                                                                    {entry.type === "new" ? "New" : entry.type === "update" ? "Update" : "Fix"}
                                                                </span>
                                                                {entry.staff && (
                                                                    <span className="text-center font-['Jersey_10'] uppercase text-[10px] tracking-widest py-0.5 border border-maroon/50 bg-maroon/10 text-maroon" title="Only shown on staff & Sulake pages">Staff</span>
                                                                )}
                                                            </div>
                                                            <span className="text-xs leading-relaxed" style={{ fontFamily: "Verdana, sans-serif" }}>{entry.text}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Footer */}
                                <div className="px-4 py-3 border-t border-black/10 shrink-0">
                                    <button
                                        type="button"
                                        className="w-full h-10 border-2 border-black bg-coal text-offwhite font-['Jersey_10'] uppercase text-sm tracking-widest hover:opacity-90"
                                        style={{ boxShadow: "3px 3px 0 0 rgba(0,0,0,0.8)" }}
                                        onClick={() => {
                                            const meta = readMeta();
                                            writeMeta({ ...meta, lastDismissedUpdateAt: updatesData.versions[0].updatedAt, dirty: true });
                                            touchMetaDirty();
                                            setShowUpdatesModal(false);
                                        }}
                                    >
                                        Got it
                                    </button>
                                </div>
                            </div>
                        </div>
                        )}

                    </div>{/* end workspaceRef */}

                </div>
            </ContentBox>
        </div>
    );
}

export default HabboImager;